import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import ErrorBoundary from "../components/ErrorBoundary";
import CanvasEditor from "../components/CanvasEditor";
import AuthModal from "../components/AuthModal";
import AutoMarkupInput from "../components/AutoMarkupInput";
import SubscriptionPage from "./SubscriptionPage";
import BingoCallerPage from "./BingoCallerPage";
import { generateBingoNumbers } from "../lib/bingoUtils";
import { ENHANCED_LOTERIA_FIGURES } from "../data/enhancedLoteriaFigures";
import { SQUARE_LOTERIA_FIGURES } from "../data/squareLoteriaFigures";
import { ZoomIn, ZoomOut, Sun, Moon, Undo2, Redo2, RotateCcw, PanelLeftClose, PanelLeft, Pencil, Maximize, Minimize, Target, PanelRightClose, PanelRight, LogOut, CreditCard, Eraser, Lock, Users } from "lucide-react";

const API = "/api";

// Normalize custom vertical pairs to {a, b, gap, offsetX, offsetY, badgeShiftX, badgeShiftY}.
// Accepts both the legacy [[a, b], ...] array shape and the new object shape
// so templates saved before these controls were added still load correctly.
// `offsetX`/`offsetY` move the entire pair within the canvas. `gap` controls
// vertical separation. `badgeShiftX`/`badgeShiftY` shift the board number
// badge ONLY on the two boards in this pair (percent of board w/h).
const normalizeCustomPairs = (input) => {
  if (!Array.isArray(input)) return [];
  const out = [];
  for (const entry of input) {
    if (Array.isArray(entry) && entry.length >= 2) {
      const a = parseInt(entry[0], 10);
      const b = parseInt(entry[1], 10);
      if (a > 0 && b > 0 && a !== b) out.push({ a, b, gap: 20, offsetX: 0, offsetY: 0, badgeShiftX: 0, badgeShiftY: 0 });
    } else if (entry && typeof entry === 'object') {
      const a = parseInt(entry.a, 10);
      const b = parseInt(entry.b, 10);
      const gap = typeof entry.gap === 'number' ? entry.gap : 20;
      const offsetX = typeof entry.offsetX === 'number' ? entry.offsetX : 0;
      const offsetY = typeof entry.offsetY === 'number' ? entry.offsetY : 0;
      const badgeShiftX = typeof entry.badgeShiftX === 'number' ? entry.badgeShiftX : 0;
      const badgeShiftY = typeof entry.badgeShiftY === 'number' ? entry.badgeShiftY : 0;
      if (a > 0 && b > 0 && a !== b) out.push({ a, b, gap, offsetX, offsetY, badgeShiftX, badgeShiftY });
    }
  }
  return out;
};

// Template size dimensions in inches
const TEMPLATE_SIZES = {
  "4x6": { width: 4, height: 6, label: '4" x 6"' },
  "5x7": { width: 5, height: 7, label: '5" x 7"' },
  "8x10": { width: 8, height: 10, label: '8" x 10"' },
  "8x11": { width: 8, height: 11, label: '8" x 11"' },
  "8.5x11": { width: 8.5, height: 11, label: '8.5" x 11" (Letter)' },
  "8x12": { width: 8, height: 12, label: '8" x 12"' },
  "8.5x12": { width: 8.5, height: 12, label: '8.5" x 12"' },
};

// Board styles
const BOARD_STYLES = [
  { id: "soft", label: "Soft (Rounded)" },
  { id: "sharp", label: "Sharp" },
  { id: "pill", label: "Pill" },
  { id: "dotted", label: "Dotted" },
  { id: "double", label: "Double Border" },
  { id: "shadowed", label: "Shadowed" },
];

// Font options
const FONT_OPTIONS = [
  { id: "outfit", label: "Outfit", family: "'Outfit', sans-serif" },
  { id: "arial", label: "Arial", family: "Arial, sans-serif" },
  { id: "georgia", label: "Georgia", family: "Georgia, serif" },
  { id: "courier", label: "Courier", family: "'Courier New', monospace" },
  { id: "impact", label: "Impact", family: "Impact, sans-serif" },
  { id: "comic", label: "Comic Sans", family: "'Comic Sans MS', cursive" },
  { id: "verdana", label: "Verdana", family: "Verdana, sans-serif" },
  { id: "trebuchet", label: "Trebuchet", family: "'Trebuchet MS', sans-serif" },
  { id: "palatino", label: "Palatino", family: "'Palatino Linotype', 'Book Antiqua', serif" },
  { id: "lucida", label: "Lucida", family: "'Lucida Console', monospace" },
  { id: "garamond", label: "Garamond", family: "Garamond, 'Times New Roman', serif" },
  { id: "tahoma", label: "Tahoma", family: "Tahoma, Geneva, sans-serif" },
  { id: "brush", label: "Brush Script", family: "'Brush Script MT', cursive" },
  { id: "times", label: "Times New Roman", family: "'Times New Roman', Times, serif" },
  { id: "century", label: "Century Gothic", family: "'Century Gothic', CenturyGothic, sans-serif" },
  { id: "franklin", label: "Franklin Gothic", family: "'Franklin Gothic Medium', sans-serif" },
  { id: "fredoka", label: "Fredoka", family: "'Fredoka', sans-serif" },
  { id: "lilita", label: "Lilita One", family: "'Lilita One', sans-serif" },
  { id: "shrikhand", label: "Shrinkhand", family: "'Shrikhand', serif" },
  { id: "borsok", label: "Borsok", family: "'Borsok', sans-serif" },
  { id: "spicy-rice", label: "Spicy Rice", family: "'Spicy Rice', serif" },
  { id: "just-another-hand", label: "Just Another Hand", family: "'Just Another Hand', cursive" },
  { id: "londrina-solid", label: "Londrina Solid", family: "'Londrina Solid', sans-serif" },
  { id: "maiden-orange", label: "Maiden Orange", family: "'Maiden Orange', serif" },
  { id: "mouse-memoirs", label: "Mouse Memoirs", family: "'Mouse Memoirs', sans-serif" },
  { id: "patrick-hand", label: "Patrick Hand", family: "'Patrick Hand', cursive" },
  { id: "rum-raisin", label: "Rum Raisin", family: "'Rum Raisin', sans-serif" },
  { id: "skranji", label: "Skranji", family: "'Skranji', sans-serif" },
  { id: "adlam-display", label: "ADLaM Display", family: "'ADLaM Display', system-ui, sans-serif" },
  // ─── System-installed fonts (rendered only if user's device has them) ───
  // These aren't bundled (commercial / Apple-only). Browsers use the font if
  // installed locally, otherwise gracefully fall back to the listed alternatives.
  { id: "aharoni",            label: "Aharoni (if installed)",           family: "'Aharoni', 'Assistant', 'Arial Hebrew', sans-serif", systemOnly: true },
  { id: "aptos-black",        label: "Aptos Black (if installed)",       family: "'Aptos Black', 'Aptos', 'Segoe UI Black', sans-serif", systemOnly: true },
  { id: "bauhaus-93",         label: "Bauhaus 93 (if installed)",        family: "'Bauhaus 93', 'Poiret One', sans-serif", systemOnly: true },
  { id: "cooper-black",       label: "Cooper Black (if installed)",      family: "'Cooper Black', 'Cooper Std', 'Bowlby One', serif", systemOnly: true },
  { id: "dreaming-outloud",   label: "Dreaming Outloud (if installed)",  family: "'Dreaming Outloud Pro', 'Dreaming Outloud', 'Caveat', cursive", systemOnly: true },
  { id: "harlow-italic",      label: "Harlow Solid Italic (if installed)", family: "'Harlow Solid Italic', 'Harlow', 'Sacramento', cursive", systemOnly: true },
  { id: "jumble",             label: "Jumble (if installed)",            family: "'Jumble', 'Fascinate', sans-serif", systemOnly: true },
  { id: "kermit-semibold",    label: "Kermit Semibold (if installed)",   family: "'Kermit Semibold', 'Kermit', 'Fredoka', sans-serif", systemOnly: true },
  { id: "rockwell",           label: "Rockwell (if installed)",          family: "'Rockwell', 'Rockwell Nova', 'Roboto Slab', serif", systemOnly: true },
  { id: "liberator",     label: "Liberator (if installed)",      family: "'Liberator', 'Impact', sans-serif", systemOnly: true },
  { id: "vincent",       label: "Vincent (if installed)",        family: "'Vincent', 'Playfair Display', serif", systemOnly: true },
  { id: "chalkboard-se", label: "Chalkboard SE Bold (Apple)",    family: "'Chalkboard SE', 'Comic Sans MS', cursive", systemOnly: true },
  { id: "chick",         label: "Chick (if installed)",          family: "'Chick', 'Patrick Hand', cursive", systemOnly: true },
  { id: "cinecaption",   label: "Cinecaption (if installed)",    family: "'Cinecaption', 'Special Elite', monospace", systemOnly: true },
];

// Canvas-based detection: returns true if the named font renders differently
// from the monospace baseline — meaning the user's device has it installed.
const detectFontAvailable = (fontName) => {
  try {
    const baseline = 'monospace';
    const testText = 'mmmmmmmmmlli';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.font = `72px ${baseline}`;
    const baseW = ctx.measureText(testText).width;
    ctx.font = `72px "${fontName}", ${baseline}`;
    return ctx.measureText(testText).width !== baseW;
  } catch { return false; }
};

// Color palette
const COLOR_PALETTE = [
  { id: "pink", value: "#FF69B4", label: "Pink" },
  { id: "light-pink", value: "#FFB6C1", label: "Light Pink" },
  { id: "yellow", value: "#FFD700", label: "Yellow" },
  { id: "pastel-yellow", value: "#FFFACD", label: "Pastel Yellow" },
  { id: "red", value: "#FF0000", label: "Red" },
  { id: "orange", value: "#FFA500", label: "Orange" },
  { id: "green", value: "#228B22", label: "Green" },
  { id: "pastel-green", value: "#98FB98", label: "Pastel Green" },
  { id: "navy-blue", value: "#000080", label: "Navy Blue" },
  { id: "royal-blue", value: "#4169E1", label: "Royal Blue" },
  { id: "baby-blue", value: "#89CFF0", label: "Baby Blue" },
  { id: "aqua", value: "#00FFFF", label: "Aqua" },
  { id: "magenta", value: "#FF00FF", label: "Magenta" },
  { id: "pastel-purple", value: "#E6E6FA", label: "Pastel Purple" },
  { id: "neon-green", value: "#39FF14", label: "Neon Green" },
  { id: "neon-pink", value: "#FF6EC7", label: "Neon Pink" },
  { id: "neon-orange", value: "#FF5F1F", label: "Neon Orange" },
  { id: "white", value: "#FFFFFF", label: "White" },
  { id: "black", value: "#000000", label: "Black" },
];

// Emoji options
const EMOJI_OPTIONS = [
  { id: 'star', emoji: '\u2B50', label: 'Star' },
  { id: 'thumbsup', emoji: '\uD83D\uDC4D', label: 'Thumbs Up' },
  { id: 'thumbsdown', emoji: '\uD83D\uDC4E', label: 'Thumbs Down' },
  { id: 'happy', emoji: '\uD83D\uDE0A', label: 'Happy' },
  { id: 'winner', emoji: '\uD83C\uDF89', label: 'Winner' },
  { id: 'laughing', emoji: '\uD83D\uDE02', label: 'Laughing' },
  { id: 'wink', emoji: '\uD83D\uDE09', label: 'Wink' },
  { id: 'cool', emoji: '\uD83D\uDE0E', label: 'Cool' },
  { id: 'love', emoji: '\uD83D\uDE0D', label: 'Love Eyes' },
  { id: 'thinking', emoji: '\uD83E\uDD14', label: 'Thinking' },
  { id: 'surprised', emoji: '\uD83D\uDE32', label: 'Surprised' },
  { id: 'cry', emoji: '\uD83D\uDE22', label: 'Cry' },
  { id: 'angry', emoji: '\uD83D\uDE21', label: 'Angry' },
  { id: 'trophy', emoji: '\uD83C\uDFC6', label: 'Trophy' },
  { id: 'medal', emoji: '\uD83C\uDFC5', label: 'Medal' },
  { id: 'money', emoji: '\uD83D\uDCB0', label: 'Money' },
  { id: 'dollar', emoji: '\uD83D\uDCB5', label: 'Dollar' },
  { id: 'fire', emoji: '\uD83D\uDD25', label: 'Fire' },
  { id: 'heart', emoji: '\u2764\uFE0F', label: 'Heart' },
  { id: 'broken-heart', emoji: '\uD83D\uDC94', label: 'Broken Heart' },
  { id: 'sparkles', emoji: '\u2728', label: 'Sparkles' },
  { id: 'crown', emoji: '\uD83D\uDC51', label: 'Crown' },
  { id: 'hundred', emoji: '\uD83D\uDCAF', label: '100' },
  { id: 'rocket', emoji: '\uD83D\uDE80', label: 'Rocket' },
  { id: 'gift', emoji: '\uD83C\uDF81', label: 'Gift' },
  { id: 'clap', emoji: '\uD83D\uDC4F', label: 'Clap' },
  { id: 'muscle', emoji: '\uD83D\uDCAA', label: 'Strong' },
  { id: 'rainbow', emoji: '\uD83C\uDF08', label: 'Rainbow' },
  { id: 'sun', emoji: '\u2600\uFE0F', label: 'Sun' },
  { id: 'moon', emoji: '\uD83C\uDF19', label: 'Moon' },
  { id: 'cloud', emoji: '\u2601\uFE0F', label: 'Cloud' },
  { id: 'lightning', emoji: '\u26A1', label: 'Lightning' },
  { id: 'balloon', emoji: '\uD83C\uDF88', label: 'Balloon' },
  { id: 'confetti', emoji: '\uD83C\uDF8A', label: 'Confetti' },
  { id: 'dice', emoji: '\uD83C\uDFB2', label: 'Dice' },
  { id: 'checkmark', emoji: '\u2705', label: 'Check' },
  { id: 'cross', emoji: '\u274C', label: 'Cross' },
  { id: 'diamond', emoji: '\uD83D\uDC8E', label: 'Diamond' },
  { id: 'peace', emoji: '\u270C\uFE0F', label: 'Peace' },
  { id: 'wave', emoji: '\uD83D\uDC4B', label: 'Wave' },
  { id: 'pray', emoji: '\uD83D\uDE4F', label: 'Pray' },
  { id: 'eyes', emoji: '\uD83D\uDC40', label: 'Eyes' },
  { id: 'ghost', emoji: '\uD83D\uDC7B', label: 'Ghost' },
  { id: 'skull', emoji: '\uD83D\uDC80', label: 'Skull' },
  { id: 'alien', emoji: '\uD83D\uDC7D', label: 'Alien' },
  { id: 'dog', emoji: '\uD83D\uDC36', label: 'Dog' },
  { id: 'cat', emoji: '\uD83D\uDC31', label: 'Cat' },
  { id: 'butterfly', emoji: '\uD83E\uDD8B', label: 'Butterfly' },
  { id: 'scissors', emoji: '\u2702\uFE0F', label: 'Scissors' },
];

// Filter presets
const FILTER_PRESETS = [
  { id: 'none', label: 'None', settings: { brightness: 100, contrast: 100, saturate: 100, grayscale: 0, sepia: 0, blur: 0, hueRotate: 0 } },
  { id: 'vintage', label: 'Vintage', settings: { brightness: 110, contrast: 85, saturate: 70, grayscale: 0, sepia: 40, blur: 0, hueRotate: 0 } },
  { id: 'bw', label: 'B&W', settings: { brightness: 100, contrast: 110, saturate: 0, grayscale: 100, sepia: 0, blur: 0, hueRotate: 0 } },
  { id: 'warm', label: 'Warm', settings: { brightness: 105, contrast: 100, saturate: 130, grayscale: 0, sepia: 20, blur: 0, hueRotate: -10 } },
  { id: 'cool', label: 'Cool', settings: { brightness: 100, contrast: 100, saturate: 90, grayscale: 0, sepia: 0, blur: 0, hueRotate: 30 } },
  { id: 'dramatic', label: 'Dramatic', settings: { brightness: 90, contrast: 150, saturate: 120, grayscale: 0, sepia: 0, blur: 0, hueRotate: 0 } },
  { id: 'soft', label: 'Soft', settings: { brightness: 110, contrast: 90, saturate: 80, grayscale: 0, sepia: 10, blur: 1, hueRotate: 0 } },
  { id: 'vivid', label: 'Vivid', settings: { brightness: 105, contrast: 115, saturate: 160, grayscale: 0, sepia: 0, blur: 0, hueRotate: 0 } },
];

const DEFAULT_FILTER = { brightness: 100, contrast: 100, saturate: 100, grayscale: 0, sepia: 0, blur: 0, hueRotate: 0 };

// Background color presets
const BACKGROUND_COLORS = [
  { id: "white", value: "#FFFFFF", label: "White" },
  { id: "sky-blue", value: "#87CEEB", label: "Sky Blue" },
  { id: "navy", value: "#0a1628", label: "Starry Night" },
  { id: "sunset", value: "#FF6B6B", label: "Sunset" },
  { id: "forest", value: "#228B22", label: "Forest" },
  { id: "lavender", value: "#E6E6FA", label: "Lavender" },
  { id: "cream", value: "#FFFDD0", label: "Cream" },
  { id: "black", value: "#000000", label: "Black" },
];

export default function BingoGenerator() {
  // Auth state: null = checking, false = not logged in, object = user
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [showSubscription, setShowSubscription] = useState(false);

  // Fullscreen UI auto-hide
  const [fsUiVisible, setFsUiVisible] = useState(true);
  const fsUiTimerRef = useRef(null);

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Zoom state
  const [zoom, setZoom] = useState(100);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCallerPage, setShowCallerPage] = useState(false);
  const [callerCanvasImage, setCallerCanvasImage] = useState(null);

  // Game Mode: 'bingo' or 'loteria'
  const [gameMode, setGameMode] = useState('bingo');

  // Loteria state
  const [loteriaFigures, setLoteriaFigures] = useState([]); // [{id, name, src}]
  const [loteriaGridSize, setLoteriaGridSize] = useState(4); // number (NxN) or string like "5x4" for non-square
  const [loteriaBoards, setLoteriaBoards] = useState([]); // [{id, x, y, width, gridRow, figures: [indices]}]
  // "One board different size" — lets a single board use its own grid size
  // (e.g. all boards 7x7 but board #6 is 7x5). Others keep loteriaGridSize.
  const [loteriaOneOffEnabled, setLoteriaOneOffEnabled] = useState(false);
  const [loteriaOneOffBoardIndex, setLoteriaOneOffBoardIndex] = useState(0); // 0-based
  const [loteriaOneOffGridSize, setLoteriaOneOffGridSize] = useState('7x5');
  // Independent numbering for the one-off board (separate from the rest).
  const [loteriaOneOffNumberingEnabled, setLoteriaOneOffNumberingEnabled] = useState(false);
  const [loteriaOneOffShowRowNums, setLoteriaOneOffShowRowNums] = useState(true);   // vertical (down the left)
  const [loteriaOneOffShowColNums, setLoteriaOneOffShowColNums] = useState(true);   // horizontal (along the bottom)
  const [loteriaOneOffStartingNum, setLoteriaOneOffStartingNum] = useState(1);
  // Single-board Doubles/Triples — one chosen board shows ONE image repeated
  // across a row AND a column (doubles=2, triples=3). Independent of the global
  // repeat mode. Its grid is capped to <= 54 cells and its focal image is kept
  // unique across all boards.
  const [loteriaSingleRepeatEnabled, setLoteriaSingleRepeatEnabled] = useState(false);
  const [loteriaSingleRepeatBoardIndex, setLoteriaSingleRepeatBoardIndex] = useState(0); // 0-based
  const [loteriaSingleRepeatMode, setLoteriaSingleRepeatMode] = useState('triples'); // 'doubles' | 'triples'
  const [loteriaSingleRepeatGridSize, setLoteriaSingleRepeatGridSize] = useState(7); // 7x7 = 49 (<=54)
  // AI-vision template import — tracks the in-flight POST to
  // /api/templates/import-image so the Sidebar button can show a spinner
  // and prevent double-submits.
  const [importingTemplate, setImportingTemplate] = useState(false);

  // Derive cols/rows from loteriaGridSize (supports "5x4" style non-square grids)
  const loteriaGridCols = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x')
    ? parseInt(loteriaGridSize.split('x')[0]) : loteriaGridSize;
  const loteriaGridRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x')
    ? parseInt(loteriaGridSize.split('x')[1]) : loteriaGridSize;
  const [loteriaBoardCount, setLoteriaBoardCount] = useState(0);
  const [loteriaCellColors, setLoteriaCellColors] = useState(['#87CEEB', '#FFFFFF']); // alternating colors
  // Multi-Color Rows & Columns: 'checkerboard' (default 2-color), 'rows' (per-row), 'columns' (per-col), 'rowStripes' (zebra by row), 'colStripes' (zebra by column)
  const [loteriaColorMode, setLoteriaColorMode] = useState('checkerboard');
  // Per-row / per-column color arrays. Length adapts to current grid size at render time.
  const [loteriaRowColors, setLoteriaRowColors] = useState(['#FFE5E5', '#FFF4E5', '#FFFCE5', '#E5FFE5', '#E5F4FF', '#F4E5FF', '#FFE5F4']);
  const [loteriaColColors, setLoteriaColColors] = useState(['#E5F4FF', '#E5FFE5', '#FFFCE5', '#FFE5E5', '#F4E5FF', '#FFE5F4', '#FFF4E5']);
  // Two-color stripe pattern (used for rowStripes / colStripes modes).
  const [loteriaStripeColors, setLoteriaStripeColors] = useState(['#87CEEB', '#FFFFFF']);
  const [loteriaBorderColor, setLoteriaBorderColor] = useState('#000000');
  const [loteriaGridColor, setLoteriaGridColor] = useState('#000000');
  // Decorative center highlight — draws a colored ring around the center block
  // of the board (2x2 for 4x4 grids, 3x3 for 5x5 grids). Loteria only.
  const [loteriaCenterHighlight, setLoteriaCenterHighlight] = useState(false);
  const [loteriaCenterHighlightColor, setLoteriaCenterHighlightColor] = useState('#FF1493');
  const [loteriaCenterHighlightThickness, setLoteriaCenterHighlightThickness] = useState(100); // % of base ring width
  // Position offset (in cells) of the center ring from the true center. 0 = centered.
  const [loteriaCenterHighlightOffsetX, setLoteriaCenterHighlightOffsetX] = useState(0);
  const [loteriaCenterHighlightOffsetY, setLoteriaCenterHighlightOffsetY] = useState(0);
  // Doubles/Triples highlight — rings each run of 2+ identical adjacent figures
  // (horizontal across a row, vertical down a column). Loteria only.
  const [loteriaRepeatHighlight, setLoteriaRepeatHighlight] = useState(false);
  const [loteriaRepeatHighlightColor, setLoteriaRepeatHighlightColor] = useState('#00E5FF');
  const [loteriaRepeatHighlightThickness, setLoteriaRepeatHighlightThickness] = useState(100); // % of base ring width
  const [loteriaBorderStyle, setLoteriaBorderStyle] = useState('sharp'); // soft, sharp, dotted, double, shadowed
  // Optional override for board corner radius (% of cell width). null = follow
  // the legacy style-based default (soft=8%, pill=20%, others=0%).
  const [loteriaCornerRadius, setLoteriaCornerRadius] = useState(null);
  const [loteriaBorderWidth, setLoteriaBorderWidth] = useState(100); // percentage of default border thickness
  const [loteriaGridWidth, setLoteriaGridWidth] = useState(100); // percentage of default grid line thickness
  const [loteriaCellShape, setLoteriaCellShape] = useState('narrow'); // 'narrow' (1.5 ratio) or 'square' (1:1)
  // Loteria deck source: 'original' (user-uploaded via API), 'enhanced' (built-in)
  // or 'square' (Square Images built-in). Persisted across reloads.
  const [loteriaDeck, setLoteriaDeck] = useState(() => sessionStorage.getItem('loteriaDeck') || 'original');
  const [loteriaBoardShadow, setLoteriaBoardShadow] = useState('none'); // 'none', 'light', 'medium', 'heavy'
  const [loteriaGapH, setLoteriaGapH] = useState(4); // horizontal spacing between boards
  const [loteriaGapV, setLoteriaGapV] = useState(4); // vertical spacing between boards
  const [loteriaRepeatMode, setLoteriaRepeatMode] = useState('none'); // 'none' | 'doubles' | 'triples' | 'centerDoubles' | 'centerTriples'
  // Direction of the center cluster (only used when repeatMode is centerDoubles/centerTriples).
  const [loteriaCenterOrientation, setLoteriaCenterOrientation] = useState('vertical'); // 'vertical' | 'horizontal' | 'slantRight' | 'slantLeft'
  const [loteriaNumbering, setLoteriaNumbering] = useState({
    showBoardNum: true, showRowNums: false, showColNums: false,
    badgeShape: 'circle', badgeBgColor: '#8B4513', badgeTextColor: '#FFFFFF',
    badgeFont: 'outfit', badgeSize: 100, startingNum: 1,
    badgeOffset: 100, badgeNoBg: false,
    badgeTextEffect: 'none', // 'none','outline','white-outline','neon','shadow','glow'
    badgeTextThickness: 2,
    badgeTextOutlineColor: '#000000', // outline color for the FONT outline (separate from badge-shape outline)
    badgeLetterSpacing: 0, // px — negative pulls digits together, positive spaces them apart
  });
  const loteriaFigureImagesRef = useRef({}); // cached HTMLImageElement per figure id

  // Free Space text effects
  const [freeSpaceTextEffect, setFreeSpaceTextEffect] = useState('none'); // 'none','outline','white-outline','neon','shadow','glow'
  const [freeSpaceTextThickness, setFreeSpaceTextThickness] = useState(2);

  // Board opacity
  const [boardOpacity, setBoardOpacity] = useState(100);
  const [transparentBoards, setTransparentBoards] = useState(false);
  const [transparentKeepHeader, setTransparentKeepHeader] = useState(false);
  const [transparentKeepFreeSpace, setTransparentKeepFreeSpace] = useState(false);

  // Template settings
  const [templateSize, setTemplateSize] = useState("8.5x11");
  
  // Background settings (Step 1 - completely separate)
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImageLoading, setBackgroundImageLoading] = useState(false);
  // Fit mode for the background image:
  //   'cover'   = fill canvas, may crop sides (default for back-compat)
  //   'contain' = fit whole image inside canvas, may show side bars (no crop)
  //   'stretch' = stretch to canvas dimensions, may distort
  const [backgroundImageFit, setBackgroundImageFit] = useState('cover');
  // User-controlled zoom multiplier (1.0 = default sizing per fit mode).
  // Range: 0.25x → 4x. Lets the user pinch / scale the uploaded background
  // image so it visually fits their template however they prefer.
  const [backgroundImageScale, setBackgroundImageScale] = useState(1.0);

  // Overlay images (Step 2 - separate with blend options)
  const [overlayImages, setOverlayImages] = useState([]);
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [overlayImageLoading, setOverlayImageLoading] = useState(false);
  const [cutoutLoading, setCutoutLoading] = useState(false);
  const [overlayShadow, setOverlayShadow] = useState('none'); // 'none', 'light', 'medium', 'heavy'
  // Shadow offset (% of overlay size). Default 2/2 reproduces the original
  // down-right drop. Range -20 to 20. Used by the canvas shadow renderer
  // to override the preset's baked-in ox/oy.
  const [overlayShadowOffsetX, setOverlayShadowOffsetX] = useState(2);
  const [overlayShadowOffsetY, setOverlayShadowOffsetY] = useState(2);

  // Board settings
  const [boardCount, setBoardCount] = useState(0);
  const [boardStyle, setBoardStyle] = useState("soft");
  const [boardColor, setBoardColor] = useState("#FF69B4");
  const [boardFont, setBoardFont] = useState("outfit");
  const [headerFont, setHeaderFont] = useState("outfit"); // separate font for BINGO header letters
  const [headerColor, setHeaderColor] = useState("#FF0000");
  const [headerLetterColor, setHeaderLetterColor] = useState("#FFFFFF");
  const [cellColor, setCellColor] = useState("#FFFFFF");
  const [textColor, setTextColor] = useState("#000000");
  const [multiColorRows, setMultiColorRows] = useState(false);
  const [boardRowColors, setBoardRowColors] = useState([]); // [{headerColor, headerLetterColor, cellColor, boardColor, textColor}]
  const [headerOpacity, setHeaderOpacity] = useState(100);
  const [cellOpacity, setCellOpacity] = useState(100);
  const [freeSpaceOpacity, setFreeSpaceOpacity] = useState(100);
  const [manualBoardSize, setManualBoardSize] = useState(null); // null = auto-fit
  const [boardLayout, setBoardLayout] = useState(null); // null = auto grid, or { cols, rows, disabled: Set of "row-col" strings }

  // Free space settings
  const [freeSpaceText, setFreeSpaceText] = useState("FREE");
  const [showFreeSpaceEditor, setShowFreeSpaceEditor] = useState(false);
  const [freeSpaceColor, setFreeSpaceColor] = useState("#FFFFFF");
  const [freeSpaceTextColor, setFreeSpaceTextColor] = useState("#000000");
  const [freeSpaceNumbering, setFreeSpaceNumbering] = useState(false);

  // Watermark settings
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [watermarkText, setWatermarkText] = useState("SAMPLE");

  // Live Caller animated background video. Only renders inside the Live Bingo
  // Caller (not on the template editor canvas). Persists in sessionStorage so
  // it survives reloads on the same tab. Stored either as a static asset path
  // (preset library) or a data: URL (uploaded file).
  const [callerBgVideoSrc, setCallerBgVideoSrc] = useState(() => {
    try { return sessionStorage.getItem('caller_bg_video_src') || ''; } catch { return ''; }
  });
  const [callerBgVideoOpacity, setCallerBgVideoOpacity] = useState(() => {
    try { return parseInt(sessionStorage.getItem('caller_bg_video_opacity') || '60', 10); } catch { return 60; }
  });
  const [callerBgVideoFit, setCallerBgVideoFit] = useState(() => {
    try { return sessionStorage.getItem('caller_bg_video_fit') || 'cover'; } catch { return 'cover'; }
  });
  // OBS Mode — reduces video decode load for streamers:
  //   - Halves playback rate (0.5x) → halves decoder CPU
  //   - Promotes the video to its own GPU layer + paint containment so it
  //     doesn't repaint when canvas state updates above it
  //   - Disables the auto-mark fullscreen 1.5x size boost which forces extra
  //     canvas redraws during live games
  const [obsMode, setObsMode] = useState(() => {
    try { return sessionStorage.getItem('obs_mode') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('obs_mode', obsMode ? '1' : '0'); } catch {}
  }, [obsMode]);
  useEffect(() => {
    try {
      if (callerBgVideoSrc) sessionStorage.setItem('caller_bg_video_src', callerBgVideoSrc);
      else sessionStorage.removeItem('caller_bg_video_src');
    } catch {}
  }, [callerBgVideoSrc]);
  useEffect(() => { try { sessionStorage.setItem('caller_bg_video_opacity', String(callerBgVideoOpacity)); } catch {} }, [callerBgVideoOpacity]);
  useEffect(() => { try { sessionStorage.setItem('caller_bg_video_fit', callerBgVideoFit); } catch {} }, [callerBgVideoFit]);

  // Convert a base64 data URL into an in-memory Blob URL once per source
  // change. Browsers (and OBS' embedded Chromium) decode Blob URLs *much*
  // more efficiently than data URLs — no base64 parsing, no string memory
  // duplication, and the video element can stream decode. Without this,
  // large data URLs caused severe lag and frame glitches in OBS.
  const callerBgVideoObjectUrl = useMemo(() => {
    if (!callerBgVideoSrc) return '';
    if (!callerBgVideoSrc.startsWith('data:')) return callerBgVideoSrc; // CDN / static asset path
    try {
      const m = callerBgVideoSrc.match(/^data:([^;]+);base64,(.+)$/);
      if (!m) return callerBgVideoSrc;
      const mime = m[1];
      const bin = atob(m[2]);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return URL.createObjectURL(new Blob([bytes], { type: mime }));
    } catch (e) {
      return callerBgVideoSrc; // fallback — keep original on decode failure
    }
  }, [callerBgVideoSrc]);
  // Revoke the previous Blob URL when the source changes / unmounts to free memory.
  useEffect(() => {
    return () => {
      if (callerBgVideoObjectUrl && callerBgVideoObjectUrl.startsWith('blob:')) {
        try { URL.revokeObjectURL(callerBgVideoObjectUrl); } catch {}
      }
    };
  }, [callerBgVideoObjectUrl]);
  const handleCallerBgVideoUpload = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) { alert('Please choose a video file (MP4, WebM, MOV).'); return; }
    if (file.size > 50 * 1024 * 1024) { alert(`Video is ${(file.size / 1024 / 1024).toFixed(1)} MB. Max 50 MB.`); return; }
    const reader = new FileReader();
    reader.onload = (e) => setCallerBgVideoSrc(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  // AI animation generation — text-to-video via Sora 2 / Emergent Universal Key.
  // Slow (2-5 mins). We POST to start a job, then poll for status to bypass the
  // 60s ingress timeout. UI shows elapsed seconds while pending.
  const [aiVideoPrompt, setAiVideoPrompt] = useState('');
  const [aiVideoLoading, setAiVideoLoading] = useState(false);
  const [aiVideoElapsed, setAiVideoElapsed] = useState(0);
  const aiVideoCancelRef = useRef(false);
  const generateAiCallerVideo = useCallback(async () => {
    const p = (aiVideoPrompt || '').trim();
    if (p.length < 3) { alert('Please enter a longer prompt (3+ characters).'); return; }
    setAiVideoLoading(true);
    setAiVideoElapsed(0);
    aiVideoCancelRef.current = false;
    const tok = localStorage.getItem('access_token');
    // Defensive read helper: clones first so a body-stream-already-read TypeError
    // from a proxy/extension that buffered the response doesn't sink the call.
    const readJsonSafe = async (resp) => {
      try { return await resp.clone().json(); } catch (_) {
        try { return JSON.parse(await resp.clone().text()); } catch (_) { return null; }
      }
    };
    const readTextSafe = async (resp) => {
      try { return await resp.clone().text(); } catch (_) { return ''; }
    };
    try {
      // Kick off the job
      const startRes = await fetch(`${API}/caller-bg/generate-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
        body: JSON.stringify({ prompt: p, duration: 4, size: '1280x720', model: 'sora-2' }),
      });
      if (!startRes.ok) {
        const errText = await readTextSafe(startRes);
        throw new Error(errText || `HTTP ${startRes.status}`);
      }
      const startData = await readJsonSafe(startRes);
      const jobId = startData && startData.job_id;
      if (!jobId) throw new Error('No job_id returned by server');

      // Poll every 5 seconds, up to 8 minutes (96 polls)
      const MAX_POLLS = 96;
      const POLL_INTERVAL_MS = 5000;
      let polls = 0;
      while (polls < MAX_POLLS) {
        if (aiVideoCancelRef.current) throw new Error('Cancelled');
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        polls += 1;
        let sRes;
        try {
          sRes = await fetch(`${API}/caller-bg/generate-video/status/${jobId}`, {
            headers: { 'Authorization': 'Bearer ' + tok },
          });
        } catch (_) {
          // network blip — keep polling
          continue;
        }
        if (!sRes.ok) {
          if (sRes.status === 404) throw new Error('Job expired or not found');
          continue;
        }
        const sData = await readJsonSafe(sRes);
        if (!sData) continue;
        if (typeof sData.elapsed_seconds === 'number') setAiVideoElapsed(sData.elapsed_seconds);
        if (sData.status === 'done' && sData.video_data_url) {
          setCallerBgVideoSrc(sData.video_data_url);
          setAiVideoPrompt('');
          return;
        }
        if (sData.status === 'error') {
          throw new Error(sData.error || 'Generation failed');
        }
      }
      throw new Error('Timed out after 8 minutes — Sora is taking longer than expected. Try again with a simpler prompt.');
    } catch (e) {
      if (String(e).includes('Cancelled')) return;
      alert(`AI generation failed: ${String(e).slice(0, 280)}`);
    } finally {
      setAiVideoLoading(false);
      setAiVideoElapsed(0);
      aiVideoCancelRef.current = false;
    }
  }, [aiVideoPrompt]);

  // Owner-only Saved Caller Background library — backed by MongoDB so owners
  // can rebuild their library without re-uploading or re-paying for Sora clips.
  const [savedCallerVideos, setSavedCallerVideos] = useState([]);
  const [savedCallerVideoBusy, setSavedCallerVideoBusy] = useState(false);
  const refreshSavedCallerVideos = useCallback(async () => {
    try {
      const tok = localStorage.getItem('access_token');
      if (!tok) return;
      const res = await fetch(`${API}/caller-bg/saved`, {
        headers: { 'Authorization': 'Bearer ' + tok },
      });
      if (!res.ok) return; // silent — non-owners get 403 here
      const data = await res.json();
      setSavedCallerVideos(Array.isArray(data.items) ? data.items : []);
    } catch (_) { /* network blip — ignore */ }
  }, []);
  const saveCallerVideo = useCallback(async () => {
    if (!callerBgVideoSrc || !callerBgVideoSrc.startsWith('data:')) {
      alert('Only uploaded files or AI-generated clips can be saved (not external URLs).');
      return;
    }
    const name = window.prompt('Name this animation:', `Background ${new Date().toLocaleDateString()}`);
    if (!name || !name.trim()) return;
    setSavedCallerVideoBusy(true);
    try {
      const tok = localStorage.getItem('access_token');
      const res = await fetch(`${API}/caller-bg/saved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
        body: JSON.stringify({ name: name.trim(), video_data_url: callerBgVideoSrc }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      await refreshSavedCallerVideos();
    } catch (e) {
      alert(`Save failed: ${String(e).slice(0, 240)}`);
    } finally {
      setSavedCallerVideoBusy(false);
    }
  }, [callerBgVideoSrc, refreshSavedCallerVideos]);
  const loadCallerVideo = useCallback(async (videoId) => {
    try {
      const tok = localStorage.getItem('access_token');
      const res = await fetch(`${API}/caller-bg/saved/${videoId}`, {
        headers: { 'Authorization': 'Bearer ' + tok },
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      const doc = await res.json();
      if (doc.video_data_url) setCallerBgVideoSrc(doc.video_data_url);
    } catch (e) {
      alert(`Load failed: ${String(e).slice(0, 240)}`);
    }
  }, []);
  const deleteCallerVideo = useCallback(async (videoId) => {
    try {
      const tok = localStorage.getItem('access_token');
      const res = await fetch(`${API}/caller-bg/saved/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + tok },
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      await refreshSavedCallerVideos();
    } catch (e) {
      alert(`Delete failed: ${String(e).slice(0, 240)}`);
    }
  }, [refreshSavedCallerVideos]);

  const [watermarkOpacity, setWatermarkOpacity] = useState(30);
  const [watermarkColor, setWatermarkColor] = useState("#000000");
  const [watermarkFont, setWatermarkFont] = useState("outfit");

  // Template Text — a customizable title/header drawn at the top of the canvas
  const [templateTextEnabled, setTemplateTextEnabled] = useState(true);
  const [templateText, setTemplateText] = useState("");
  const [templateTextFont, setTemplateTextFont] = useState("outfit");
  const [templateTextColor, setTemplateTextColor] = useState("#000000");
  const [templateTextOutlineEnabled, setTemplateTextOutlineEnabled] = useState(true);
  const [templateTextOutlineColor, setTemplateTextOutlineColor] = useState("#FFFFFF");
  const [templateTextOutlineWidth, setTemplateTextOutlineWidth] = useState(4);
  const [templateTextSize, setTemplateTextSize] = useState(64); // px at canvas-native res
  // Text position in canvas-native coordinates. null = use default (top-center).
  const [templateTextX, setTemplateTextX] = useState(null);
  const [templateTextY, setTemplateTextY] = useState(null);
  // Letter-spacing in px applied to the singleton template text. Negative values
  // pull characters together; positive values push them apart. Uses the modern
  // ctx.letterSpacing API (Chrome 99+/FF 112+/Safari 16+).
  const [templateTextLetterSpacing, setTemplateTextLetterSpacing] = useState(0);

  // Prize Dots — a horizontal row of colored circles with a per-dot inside
  // text and an automatic "Rd. 1 / Rd. 2 / ... / NP" label above each dot.
  // Draggable as a group. Used by hosts to show prize amounts / round tags
  // on the template. All positions in canvas-native pixel coords.
  // Baseline / reset default — reused by loadTemplate so switching templates
  // never leaks Prize Dot settings from a previously-loaded template.
  const DEFAULT_PRIZE_DOTS = useMemo(() => ({
    enabled: false,
    count: 4,
    x: null,
    y: null,
    spacing: 40,
    dotSize: 60,
    dotColor: '#0F3B7C',
    textColor: '#FFFFFF',
    labelColor: '#000000',
    // Circle outline (stroke around each dot). Width 0 = no outline.
    dotOutlineColor: '#000000',
    dotOutlineWidth: 0,
    // Independent outlines: Round # label vs. inside-circle text.
    labelOutlineColor: '#000000',
    labelOutlineWidth: 0,
    textOutlineColor: '#000000',
    textOutlineWidth: 0,
    labelSize: null,
    textSize: null,
    font: 'outfit',
    labelFont: 'outfit',
    showLabels: true,
    texts: ['Text', 'Text', 'Text', 'Text'],
  }), []);
  const [prizeDots, setPrizeDots] = useState(DEFAULT_PRIZE_DOTS);
  // Opacity 0-100% — applied uniformly to fill + outline of the singleton text.
  const [templateTextOpacity, setTemplateTextOpacity] = useState(100);

  // Additional template texts — unlimited list, each with its own text/font/color/etc.
  // The original singleton (templateText/X/Y/...) is kept for backward compat with
  // existing saved templates. New texts go here. Each item: { id, text, font, color,
  // outlineEnabled, outlineColor, outlineWidth, size, x, y }.
  const [extraTemplateTexts, setExtraTemplateTexts] = useState([]);
  const [savedTemplateTexts, setSavedTemplateTexts] = useState([]);
  const [savedTemplateTextBusy, setSavedTemplateTextBusy] = useState(false);
  const [saveTemplateTextName, setSaveTemplateTextName] = useState('');
  const addExtraTemplateText = useCallback(() => {
    const id = `tt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setExtraTemplateTexts(prev => [...prev, {
      id,
      text: 'New Text',
      font: 'outfit',
      color: '#000000',
      outlineEnabled: true,
      outlineColor: '#FFFFFF',
      outlineWidth: 4,
      size: 64,
      letterSpacing: 0,
      opacity: 100,
      x: null,
      y: null,
    }]);
  }, []);
  const updateExtraTemplateText = useCallback((id, patch) => {
    setExtraTemplateTexts(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  }, []);
  const removeExtraTemplateText = useCallback((id) => {
    setExtraTemplateTexts(prev => prev.filter(t => t.id !== id));
  }, []);
  const moveExtraTemplateText = useCallback((id, x, y) => {
    setExtraTemplateTexts(prev => prev.map(t => t.id === id ? { ...t, x, y } : t));
  }, []);

  // Saved Template Texts library — paid + owner. Persists a full bundle
  // (singleton + extras) so users can quickly reuse text setups.
  const refreshSavedTemplateTexts = useCallback(async () => {
    try {
      const tok = localStorage.getItem('access_token');
      if (!tok) return;
      const res = await fetch(`${API}/template-texts/saved`, {
        headers: { 'Authorization': 'Bearer ' + tok },
      });
      if (!res.ok) return; // silent for non-paid users (403)
      const data = await res.json();
      setSavedTemplateTexts(Array.isArray(data.items) ? data.items : []);
    } catch (_) { /* ignore */ }
  }, []);
  const saveTemplateTextBundle = useCallback(async (customName) => {
    // Avoid window.prompt — it can be blocked in fullscreen / OBS / some browser
    // environments, which made users think Save did nothing. Auto-generate a
    // default name; callers may pass a custom name from an inline input.
    const candidate = (customName && String(customName).trim())
      || `Texts ${new Date().toLocaleString()}`;
    const name = candidate.slice(0, 80);
    setSavedTemplateTextBusy(true);
    try {
      const tok = localStorage.getItem('access_token');
      const payload = {
        singleton: {
          enabled: templateTextEnabled,
          text: templateText,
          font: templateTextFont,
          color: templateTextColor,
          outlineEnabled: templateTextOutlineEnabled,
          outlineColor: templateTextOutlineColor,
          outlineWidth: templateTextOutlineWidth,
          size: templateTextSize,
          letterSpacing: templateTextLetterSpacing,
          opacity: templateTextOpacity,
          x: templateTextX,
          y: templateTextY,
        },
        extras: extraTemplateTexts,
      };
      const res = await fetch(`${API}/template-texts/saved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
        body: JSON.stringify({ name, payload }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      await refreshSavedTemplateTexts();
      setSaveTemplateTextName('');
      const extrasCount = Array.isArray(extraTemplateTexts) ? extraTemplateTexts.length : 0;
      toast.success(`Saved "${name}" — singleton + ${extrasCount} extra${extrasCount === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(`Save failed: ${String(e).slice(0, 240)}`);
    } finally {
      setSavedTemplateTextBusy(false);
    }
  }, [templateTextEnabled, templateText, templateTextFont, templateTextColor,
      templateTextOutlineEnabled, templateTextOutlineColor, templateTextOutlineWidth,
      templateTextSize, templateTextLetterSpacing, templateTextOpacity,
      templateTextX, templateTextY, extraTemplateTexts, refreshSavedTemplateTexts]);
  const loadTemplateTextBundle = useCallback((bundle) => {
    if (!bundle || !bundle.payload) return;
    const s = bundle.payload.singleton;
    if (s) {
      if (s.enabled != null) setTemplateTextEnabled(!!s.enabled);
      if (s.text != null) setTemplateText(s.text);
      if (s.font) setTemplateTextFont(s.font);
      if (s.color) setTemplateTextColor(s.color);
      if (s.outlineEnabled != null) setTemplateTextOutlineEnabled(!!s.outlineEnabled);
      if (s.outlineColor) setTemplateTextOutlineColor(s.outlineColor);
      if (s.outlineWidth != null) setTemplateTextOutlineWidth(s.outlineWidth);
      if (s.size != null) setTemplateTextSize(s.size);
      setTemplateTextX(s.x != null ? s.x : null);
      setTemplateTextY(s.y != null ? s.y : null);
    }
    if (Array.isArray(bundle.payload.extras)) {
      setExtraTemplateTexts(bundle.payload.extras);
    }
  }, []);
  const deleteTemplateTextBundle = useCallback(async (id) => {
    try {
      const tok = localStorage.getItem('access_token');
      const res = await fetch(`${API}/template-texts/saved/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + tok },
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      await refreshSavedTemplateTexts();
    } catch (e) {
      alert(`Delete failed: ${String(e).slice(0, 240)}`);
    }
  }, [refreshSavedTemplateTexts]);

  // Detect which "system-only" fonts (Apple Chalkboard, Liberator, Vincent,
  // Chick, Cinecaption) are installed on the user's device and only show those
  // in font pickers. Runs once on mount.
  const [systemFontsAvailable, setSystemFontsAvailable] = useState({});
  useEffect(() => {
    const map = {};
    FONT_OPTIONS.filter(f => f.systemOnly).forEach(f => {
      const probe = (f.family.match(/^'([^']+)'/) || [])[1] || f.id;
      map[f.id] = detectFontAvailable(probe);
    });
    setSystemFontsAvailable(map);
  }, []);
  const visibleFontOptions = useMemo(
    () => FONT_OPTIONS.filter(f => !f.systemOnly || systemFontsAvailable[f.id]),
    [systemFontsAvailable]
  );

  // BINGO boards data
  const [bingoBoards, setBingoBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);

  // Markup state
  const [markupElements, setMarkupElements] = useState([]);
  const [activeMarkupTool, setActiveMarkupTool] = useState(null);
  // Currently-selected markup element id (when the Select tool is active and
  // the user has clicked a markup). Used by the sidebar to show Duplicate /
  // Delete buttons, and by CanvasEditor to draw a selection outline + allow
  // dragging the element to a new position.
  const [selectedMarkupId, setSelectedMarkupId] = useState(null);
  // Image Stamp markup — a user-uploaded image used to "stamp-mark" boards.
  // First click drops at click position with a default size; subsequent
  // clicks on other boards auto-place at the same relative offset + size
  // captured into `markupStampTemplate`. The image is shared across all
  // stamps in the template (one image per project). Cleared by Clear Markup
  // (template only — uploaded image persists across clears).
  const [markupStampImage, setMarkupStampImage] = useState(null);   // data URL
  const [markupStampTemplate, setMarkupStampTemplate] = useState(null); // {relX, relY, width, height}
  const [markupColor, setMarkupColor] = useState('#000000');
  const [markupThickness, setMarkupThickness] = useState(3);
  const [eraserThickness, setEraserThickness] = useState(10);
  const [markupTipStyle, setMarkupTipStyle] = useState('round');
  const [markupOpacity, setMarkupOpacity] = useState(100);

  // Emoji state
  const [emojis, setEmojis] = useState([]);
  const [selectedEmojiId, setSelectedEmojiId] = useState(null);

  // Filter state
  const [filterSettings, setFilterSettings] = useState({ ...DEFAULT_FILTER });

  // Auto-Markup state
  // Reset signal for AutoMarkupInput (local-state input). Bumping this clears
  // all instances of the input — used by the global "Clear All" reset.
  const [autoMarkupResetKey, setAutoMarkupResetKey] = useState(0);
  const [calledNumbers, setCalledNumbers] = useState([]);
  const [autoMarkupPanelOpen, setAutoMarkupPanelOpen] = useState(false);
  // Fullscreen Auto-Markup panel: draggable position + opacity for streamers
  // who want the input out of their camera shot or overlaid less prominently.
  const [fsAutoMarkupPos, setFsAutoMarkupPos] = useState(null); // null = default (top-right anchor)
  const [fsAutoMarkupOpacity, setFsAutoMarkupOpacity] = useState(100); // 100 = visible, 15 = transparent
  // Auto-hide after 1s of no activity. Hover on the panel brings it back.
  const [fsAutoMarkupHidden, setFsAutoMarkupHidden] = useState(false);
  const fsAutoMarkupHideTimerRef = useRef(null);
  const fsAutoMarkupDragRef = useRef({ dragging: false, offX: 0, offY: 0 });
  // Fullscreen Hammer Mode floating panel — mirrors the Auto-Markup pattern.
  const [fsHammerPos, setFsHammerPos] = useState(null); // null = default (top-right, below auto-markup)
  const [fsHammerHidden, setFsHammerHidden] = useState(false);
  const fsHammerHideTimerRef = useRef(null);
  const fsHammerDragRef = useRef({ dragging: false, offX: 0, offY: 0 });
  const [showMarkNotifications, setShowMarkNotifications] = useState(true);
  const [autoMarkupScale, setAutoMarkupScale] = useState(100); // 50-300%
  const [autoMarkupMode, setAutoMarkupMode] = useState('both'); // 'both' | 'highlight' | 'border'
  // Highlight the Board Number badge background when its board has been
  // auto-marked. Useful for streamers showcasing which boards just got hit.
  const [highlightBoardNumOnAutoMarkup, setHighlightBoardNumOnAutoMarkup] = useState(false);
  // Hammer Mode — when ON, clicking a board plays a knock sound, briefly
  // shakes that board, and flashes a hammer icon at the click point. Works
  // in editor, fullscreen, and Live Caller (all share the same CanvasEditor).
  const [hammerMode, setHammerMode] = useState(false);
  // Which effect plays when hammer mode is active: classic hammer (default)
  // or tiger claw (scratching). Both share the same click-on-board trigger.
  const [boardEffectType, setBoardEffectType] = useState('hammer'); // 'hammer' | 'tigerClaw'
  const [shakingBoards, setShakingBoards] = useState({}); // { boardId: { ts: ms, type: 'hammer'|'tigerClaw' } }
  const [hammerStrike, setHammerStrike] = useState(null); // { x, y, ts, type } in canvas pixel coords
  // Transient scratch decals — DOM-rendered claw scratches that flash and
  // fade at each of the 3 strike apexes during a Tiger Claw effect.
  const [scratchDecals, setScratchDecals] = useState([]); // [{ id, x, y, angle, ts }]
  // Shamrock particles — falling neon green 4-leaf clovers for the
  // 'shamrocks' board effect. Each particle has its own X, sway phase,
  // size, rotation, and fall-duration so the field of clovers looks
  // organic, not lockstep.
  const [shamrockParticles, setShamrockParticles] = useState([]);
  // Water-gun strikes — one per click. Each carries the board rect so
  // CanvasEditor can place the spray inside that board and animate it.
  const [waterStrikes, setWaterStrikes] = useState([]);
  const [shakeTick, setShakeTick] = useState(0);

  // Web Audio "thunk" — woody knock with three subtle harmonics. Tuned to
  // sound less bright than a metallic click and more like a hammer on wood.
  const audioCtxRef = useRef(null);
  const playKnock = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ac = audioCtxRef.current;
      const now = ac.currentTime;
      // Body: low triangle thump that decays over 250ms.
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = 'triangle';
      o.frequency.setValueAtTime(140, now);
      o.frequency.exponentialRampToValueAtTime(55, now + 0.2);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.9, now + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
      const lpf = ac.createBiquadFilter();
      lpf.type = 'lowpass';
      lpf.frequency.value = 900;
      o.connect(lpf); lpf.connect(g); g.connect(ac.destination);
      o.start(now); o.stop(now + 0.3);
      // Soft attack: short low-passed noise burst (≤30ms) for the "wood click".
      const bufSize = Math.floor(ac.sampleRate * 0.03);
      const buf = ac.createBuffer(1, bufSize, ac.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 3);
      const src = ac.createBufferSource(); src.buffer = buf;
      const nfilt = ac.createBiquadFilter(); nfilt.type = 'lowpass'; nfilt.frequency.value = 1800;
      const ng = ac.createGain(); ng.gain.value = 0.35;
      src.connect(nfilt); nfilt.connect(ng); ng.connect(ac.destination);
      src.start(now);
    } catch (e) { /* audio may be blocked until user gesture */ }
  }, []);

  // RAF loop while a shake or hammer strike is active. Bumps `shakeTick` so
  // CanvasEditor re-renders with current shake offsets, and prunes finished
  // shakes / strikes. Total animation is ~1500ms (three strikes).
  useEffect(() => {
    const SHAKE_DUR = 1500;
    const STRIKE_DUR = 1500;
    const anyActive = Object.keys(shakingBoards).length > 0 || hammerStrike;
    if (!anyActive) return;
    let raf;
    const tick = () => {
      const now = Date.now();
      let changed = false;
      const next = {};
      for (const [id, start] of Object.entries(shakingBoards)) {
        if (now - start < SHAKE_DUR) next[id] = start;
        else changed = true;
      }
      if (changed) setShakingBoards(next);
      if (hammerStrike && now - hammerStrike.ts > STRIKE_DUR) setHammerStrike(null);
      setShakeTick(t => (t + 1) % 1000000);
      if (Object.keys(next).length > 0 || (hammerStrike && now - hammerStrike.ts <= STRIKE_DUR)) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [shakingBoards, hammerStrike]);

  // Web Audio "scratch" — short rasping noise burst, band-passed and fast
  // amplitude-modulated to mimic a tiger claw raking across cardboard.
  const playScratch = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ac = audioCtxRef.current;
      const now = ac.currentTime;
      const dur = 0.32;
      // Noise buffer (white noise)
      const bufSize = Math.floor(ac.sampleRate * dur);
      const buf = ac.createBuffer(1, bufSize, ac.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) {
        // Add amplitude modulation (~70 Hz) for that rough rasp texture.
        const t = i / ac.sampleRate;
        const am = 0.55 + 0.45 * Math.sin(2 * Math.PI * 70 * t);
        data[i] = (Math.random() * 2 - 1) * am;
      }
      const src = ac.createBufferSource(); src.buffer = buf;
      // Band-pass focuses on 1.2–4 kHz: where dry-paper scratching lives.
      const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2200; bp.Q.value = 0.9;
      const hp = ac.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 700;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.85, now + 0.012);
      g.gain.exponentialRampToValueAtTime(0.001, now + dur);
      src.connect(bp); bp.connect(hp); hp.connect(g); g.connect(ac.destination);
      src.start(now); src.stop(now + dur + 0.02);
    } catch (e) { /* audio may be blocked until user gesture */ }
  }, []);

  // Web Audio "wind chime" — pluck a soft sine bell at a random pitch
  // from a pentatonic scale (C / D / E / G / A across two octaves), with
  // a slight detuned overtone for the metallic shimmer. Fast attack and
  // long exponential decay so each ping rings out like a real chime.
  const playChime = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ac = audioCtxRef.current;
      const now = ac.currentTime;
      // C major pentatonic frequencies across 2 octaves (Hz)
      const scale = [523.25, 587.33, 659.25, 783.99, 880.0,
                     1046.50, 1174.66, 1318.51, 1567.98, 1760.0];
      const f = scale[Math.floor(Math.random() * scale.length)];
      const dur = 1.4;
      // Fundamental — sine for purity
      const o1 = ac.createOscillator();
      o1.type = 'sine'; o1.frequency.value = f;
      // Overtone an octave up + 7 cents detune for shimmer
      const o2 = ac.createOscillator();
      o2.type = 'sine'; o2.frequency.value = f * 2; o2.detune.value = 7;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.32, now + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0008, now + dur);
      const g2 = ac.createGain(); g2.gain.value = 0.35;
      o1.connect(g);
      o2.connect(g2); g2.connect(g);
      g.connect(ac.destination);
      o1.start(now); o1.stop(now + dur + 0.05);
      o2.start(now); o2.stop(now + dur + 0.05);
    } catch (e) { /* audio may be blocked until user gesture */ }
  }, []);

  // Web Audio "water spray" — band-passed white noise with a slow
  // downward frequency sweep so it reads as a pressurized water jet
  // hitting a surface. Fast attack, gradual decay over ~600 ms.
  const playWaterSpray = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ac = audioCtxRef.current;
      const now = ac.currentTime;
      const dur = 2.0;
      const bufSize = Math.floor(ac.sampleRate * dur);
      const buf = ac.createBuffer(1, bufSize, ac.sampleRate);
      const data = buf.getChannelData(0);
      // Pink-ish noise: amplitude flutter for organic "sploosh" texture
      for (let i = 0; i < bufSize; i++) {
        const t = i / ac.sampleRate;
        const flutter = 0.65 + 0.35 * Math.sin(2 * Math.PI * 45 * t + Math.random() * 0.6);
        data[i] = (Math.random() * 2 - 1) * flutter;
      }
      const src = ac.createBufferSource(); src.buffer = buf;
      const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.1;
      // Downward sweep 2400 Hz → 700 Hz over the duration
      bp.frequency.setValueAtTime(2400, now);
      bp.frequency.exponentialRampToValueAtTime(700, now + dur);
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.55, now + 0.05);   // fast attack
      g.gain.setValueAtTime(0.55, now + dur - 0.3);            // sustain
      g.gain.exponentialRampToValueAtTime(0.0008, now + dur);  // quick tail
      src.connect(bp); bp.connect(g); g.connect(ac.destination);
      src.start(now); src.stop(now + dur + 0.05);
    } catch (e) { /* audio may be blocked until user gesture */ }
  }, []);

  // Strike sequence: 3 thunks at ~300ms, ~750ms, ~1200ms. Each thunk plays
  // the knock sound. The shake offset curve (in CanvasEditor) is already
  // built around the same timing so visuals + audio line up.
  // For the Tiger Claw variant, the same timing is reused: 3 paw swipes,
  // each leaving a scratch decal at a randomized offset from the click.
  const handleHammerStrike = useCallback((boardId, posInCanvas, screenPos) => {
    if (!boardId) return;
    const start = Date.now();
    const effect = boardEffectType === 'shamrocks' ? 'shamrocks' : 'hammer';
    const strikeXFracs = [0.22, 0.5, 0.78];
    setHammerStrike({ x: screenPos.x, y: screenPos.y, ts: start, type: effect, boardRect: screenPos.boardRect, strikeXFracs });
    // Skip the board shake for ambient effects (shamrocks, watergun) so
    // they read as overlays, not impacts.
    if (!['shamrocks', 'watergun'].includes(effect)) {
      setShakingBoards(prev => ({ ...prev, [boardId]: start }));
    }
    if (effect === 'tigerClaw') {
      const br = screenPos.boardRect;
      const times = [300, 750, 1200];
      times.forEach((delay, i) => {
        setTimeout(() => {
          playScratch();
          const decal = {
            id: `${start}-${i}`,
            ts: Date.now(),
            boardRect: br,
            strikeIndex: i,
            strikeXFracs,
            fallbackX: screenPos.x,
            fallbackY: screenPos.y,
          };
          setScratchDecals(prev => [...prev, decal]);
        }, delay);
      });
    } else if (effect === 'shamrocks') {
      // Spawn 18 falling shamrocks at staggered times across a ~1500ms
      // window. Each shamrock gets a random X within the board's width,
      // a random size, sway phase, rotation speed, and fall duration.
      // Wind chime sound plays 3 times during the spawn window.
      const br = screenPos.boardRect;
      const COUNT = 24;
      const TOTAL_SPAWN_MS = 1500;
      const particles = [];
      // Three size tiers (small / medium / large) sampled with a bias
      // toward medium so the field has clear variety without one tier
      // dominating. boardMinSide scales the absolute pixel sizes.
      const minSide = br ? Math.min(br.width, br.height) : 200;
      const tiers = [
        { min: minSide * 0.06, max: minSide * 0.11 }, // small
        { min: minSide * 0.13, max: minSide * 0.19 }, // medium
        { min: minSide * 0.22, max: minSide * 0.32 }, // large
      ];
      for (let i = 0; i < COUNT; i++) {
        const delay = (i / COUNT) * TOTAL_SPAWN_MS;
        const id = `${start}-sh-${i}`;
        const xFrac = 0.04 + Math.random() * 0.92;
        // Bias: 35% small, 45% medium, 20% large.
        const r = Math.random();
        const tier = r < 0.35 ? tiers[0] : r < 0.8 ? tiers[1] : tiers[2];
        const size = tier.min + Math.random() * (tier.max - tier.min);
        const swayPhase = Math.random() * Math.PI * 2;
        const swayAmp = 8 + Math.random() * 22;
        const rotSpin = (Math.random() < 0.5 ? -1 : 1) * (120 + Math.random() * 260);
        const fallDur = 1900 + Math.random() * 1600;
        particles.push({ id, delay, xFrac, size, swayPhase, swayAmp, rotSpin, fallDur, ts: start, boardRect: br });
      }
      setShamrockParticles(prev => [...prev, ...particles]);
      [80, 380, 720].forEach(t => setTimeout(playChime, t));
    } else if (effect === 'watergun') {
      // Water gun fires a CONTINUOUS 2-second spray from the left side
      // of the board, soaking the entire board. The 3-recoil + click-
      // anchored behavior was replaced with one steady soak.
      const br = screenPos.boardRect;
      const DROPLETS = 36;
      const droplets = [];
      for (let i = 0; i < DROPLETS; i++) {
        // Distribute splashes across the entire board (a few extra
        // toward the right side since that's where the stream lands
        // most heavily). Staggered delays span the 2 s spray window.
        droplets.push({
          id: `${start}-drop-${i}`,
          delay: 80 + (i / DROPLETS) * 1600 + Math.random() * 220,
          tx: 0.04 + Math.random() * 0.92,
          ty: 0.04 + Math.random() * 0.92,
          size: 32 + Math.random() * 78,
          life: 700 + Math.random() * 800,
          spin: (Math.random() < 0.5 ? -1 : 1) * Math.random() * 60,
        });
      }
      setWaterStrikes(prev => [...prev, { id: start, ts: start, boardRect: br, droplets }]);
      // One sustained spray sound (~2 s) instead of 3 short shots.
      playWaterSpray();
    } else {
      const schedule = (delay) => setTimeout(() => { playKnock(); }, delay);
      schedule(300);
      schedule(750);
      schedule(1200);
    }
  }, [playKnock, playScratch, playChime, playWaterSpray, boardEffectType]);

  // Prune water strikes after the soaking patch fades (~3.5s).
  useEffect(() => {
    if (waterStrikes.length === 0) return;
    const id = setTimeout(() => {
      const cutoff = Date.now() - 3500;
      setWaterStrikes(prev => prev.filter(s => s.ts > cutoff));
    }, 1500);
    return () => clearTimeout(id);
  }, [waterStrikes]);

  // Prune shamrock particles after their fall + safety buffer (~4s).
  useEffect(() => {
    if (shamrockParticles.length === 0) return;
    const id = setTimeout(() => {
      const cutoff = Date.now() - 4500;
      setShamrockParticles(prev => prev.filter(p => p.ts > cutoff));
    }, 1500);
    return () => clearTimeout(id);
  }, [shamrockParticles]);

  // Prune scratch decals after their fade-out lifetime (~1500ms).
  useEffect(() => {
    if (scratchDecals.length === 0) return;
    const id = setTimeout(() => {
      const cutoff = Date.now() - 1500;
      setScratchDecals(prev => prev.filter(d => d.ts > cutoff));
    }, 400);
    return () => clearTimeout(id);
  }, [scratchDecals]);
  const [highlightedCells, setHighlightedCells] = useState([]); // [{boardId, row, col}] for border highlight mode
  const [autoMarkupTimed, setAutoMarkupTimed] = useState(false); // sequential timed markup
  const [autoMarkupDelay, setAutoMarkupDelay] = useState(1000); // ms between each board
  const timedMarkupRef = useRef(null); // timer for sequential markup
  // While any timed auto-markup sequence is animating, suppress winner
  // celebrations so they all fire ONCE at the end of the batch instead of
  // interrupting mid-animation. Implemented as state so useEffect-based
  // winner detectors re-run when it flips back to false.
  const [autoMarkupInProgress, setAutoMarkupInProgress] = useState(false);
  const [highlightColor, setHighlightColor] = useState("#00FF00");
  const [highlightedBoards, setHighlightedBoards] = useState(new Set());
  // Accumulator: every board that has been auto-marked at least once during
  // the current call session. Unlike `highlightedBoards` (which only holds
  // the current call's matches), this set grows across calls and only
  // clears on Clear Calls / Clear Markup / Reset.
  const [autoMarkedBoardsEver, setAutoMarkedBoardsEver] = useState(new Set());
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const [bingoWinner, setBingoWinner] = useState(null); // array of { boardIndex, boardNum } for all winning boards
  const winnerShownOnceRef = useRef(false);
  const shownWinnerBoardsRef = useRef(new Set()); // track which boards already triggered celebration
  // Boards whose Almost-Winner star should remain "locked" even after the
  // winner celebration dismisses or the board becomes fully marked. Cleared
  // only on explicit call-clear / reset.
  const lockedAlmostBoardsRef = useRef(new Set());
  const [loteriaLineWinner, setLoteriaLineWinner] = useState(null); // Array<{ type: 'row'|'col', boardIndex, lineNum }> | null
  const lineWinnersShownRef = useRef(new Set()); // track already-celebrated row/col winners
  const [almostWinnerBoards, setAlmostWinnerBoards] = useState(new Set());
  const [almostWinnerRows, setAlmostWinnerRows] = useState({}); // { boardId: Set<rowIndex> }
  const [almostWinnerCols, setAlmostWinnerCols] = useState({}); // { boardId: Set<colIndex> }
  const [starColor, setStarColor] = useState("#FFD700");
  const [starOpacity, setStarOpacity] = useState(100);
  // Almost-winner star style — 'filled' (star fills the badge cell — legacy
  // default), 'circle' (circle outline surrounds the badge), or 'star'
  // (star outline surrounds the badge). Same color/opacity apply to all styles.
  const [starStyle, setStarStyle] = useState('filled');
  const [loteriaStarBoard, setLoteriaStarBoard] = useState(true);
  const [loteriaStarRowCol, setLoteriaStarRowCol] = useState(true);
  const [winnerBoardEnabled, setWinnerBoardEnabled] = useState(true);
  const [winnerRowEnabled, setWinnerRowEnabled] = useState(true);
  const [winnerColEnabled, setWinnerColEnabled] = useState(true);
  const [continueAnnouncing, setContinueAnnouncing] = useState(false);

  // Pattern-based Winner Celebrations (Loteria only). Each pattern fires an
  // independent celebration the FIRST time any board completes that specific
  // shape. Marked cells outside the pattern are ignored — the shape just has
  // to be fully covered. After a pattern celebration is dismissed, the
  // separate Full-Board (First to Fill) celebration is still allowed to fire
  // when a board completely fills. Each pattern celebrates at most once
  // (per pattern) unless `continueAnnouncing` is on.
  const [patternWinModes, setPatternWinModes] = useState({
    fourCorners: false,
    l: false,
    square: false,
    t: false,
    u: false,
    x: false,
  });
  // Per-pattern recolor: when a pattern completes, only the marks on THAT
  // pattern's cells get repainted with this color so the shape stands out
  // against the rest of the (unchanged) marks. Defaults are all blue; user
  // can override each independently in the sidebar.
  const [patternWinColors, setPatternWinColors] = useState({
    fourCorners: '#0033CC',
    l: '#0033CC',
    square: '#0033CC',
    t: '#0033CC',
    u: '#0033CC',
    x: '#0033CC',
  });
  const [patternWinCollapsed, setPatternWinCollapsed] = useState(true);
  const [patternWinner, setPatternWinner] = useState(null); // Array<{ boardIndex, boardNum, boardId, pattern }> | null
  const shownPatternWinnersRef = useRef(new Set()); // "boardId-pattern" so each fires once
  // Once the user dismisses the pattern-winner overlay, no further pattern
  // celebrations should ever fire (until Clear All / Reset). This is a hard
  // global gate on top of `shownPatternWinnersRef`.
  const patternWinnerDismissedRef = useRef(false);
  // Winner celebrations: the very first batch celebrates, subsequent batches
  // are suppressed unless `continueAnnouncing` is on. `winnerCelebrationsShownRef`
  // counts how many celebration overlays have opened (multiple boards in the
  // same batch = one celebration).
  const winnerCelebrationsShownRef = useRef(0);
  const [isolatedBoardNums, setIsolatedBoardNums] = useState(null); // null = show all, Set = only show these
  const [isolateInput, setIsolateInput] = useState('');
  const [isolateMarkupOnly, setIsolateMarkupOnly] = useState(false); // only auto-markup isolated boards
  const isolateMarkupOnlyRef = useRef(false);
  const isolatedBoardNumsRef = useRef(null);
  isolateMarkupOnlyRef.current = isolateMarkupOnly;
  isolatedBoardNumsRef.current = isolatedBoardNums;
  const [loteriaMoveMode, setLoteriaMoveMode] = useState('individual'); // 'individual' | 'horizontal' | 'vertical'
  
  // Neighbors Boards feature
  const [neighborsEnabled, setNeighborsEnabled] = useState(false);
  const [neighborsPosition, setNeighborsPosition] = useState('middle'); // 'top' | 'middle' | 'bottom'
  // Direction the connecting image straddles. 'horizontal' pairs boards
  // sitting side-by-side and spans last column of left + first column of
  // right. 'vertical' pairs boards stacked above/below and spans bottom
  // row of top + top row of bottom.
  const [neighborsDirection, setNeighborsDirection] = useState('horizontal');
  const [neighborsHighlightColor, setNeighborsHighlightColor] = useState('#FFD700');
  const [neighborsShape, setNeighborsShape] = useState('square');
  const [neighborsBorderThickness, setNeighborsBorderThickness] = useState(3);
  const [neighborsShadowEnabled, setNeighborsShadowEnabled] = useState(false);
  const [neighborsOutlineStyle, setNeighborsOutlineStyle] = useState('sharp'); // 'sharp' | 'rounded' | 'soft'
  const [neighborsRowsPerBoard, setNeighborsRowsPerBoard] = useState(2); // 2 | 3 — cells each board contributes to the connecting image
  // Custom Connecting Image — when set, ALL neighbor pairs draw this uploaded
  // image instead of a Loteria figure card. Size scale + alpha-silhouette
  // outline are independent so users can frame or shrink the custom image.
  // Undefined image = fall back to the standard Loteria figure lookup.
  const [neighborsCustomImage, setNeighborsCustomImage] = useState(null);
  const [neighborsCustomImageSize, setNeighborsCustomImageSize] = useState(100);
  const [neighborsCustomOutlineWidth, setNeighborsCustomOutlineWidth] = useState(0);
  const [neighborsCustomOutlineColor, setNeighborsCustomOutlineColor] = useState('#000000');
  // Bump counter that re-rolls the seeded shuffled figure-pool used to pick
  // connecting-image figures. Decoupled from board.figures so the dedup
  // useEffect can swap conflicting cells WITHOUT changing the connecting
  // figure (which would cause a loop).
  const [neighborSeedVersion, setNeighborSeedVersion] = useState(1);
  // When ON and neighborsDirection === 'vertical', board number badges are
  // re-numbered column-major within each row-pair: pair 1 top=1, bottom=2;
  // pair 2 top=3, bottom=4; ... continuing across row-pairs.
  const [neighborsVerticalPairNumbering, setNeighborsVerticalPairNumbering] = useState(false);
  // Horizontal alignment of the VPN badge along the top/bottom edge of each
  // paired board: 'left' | 'center' | 'right'. Only applies when
  // neighborsVerticalPairNumbering is ON. Default 'center'.
  const [verticalPairBadgeAlign, setVerticalPairBadgeAlign] = useState('center');
  // Stack-above pair numbers — when ON (vertical pairs), both of a pair's
  // numbers render STACKED (top board's number on top, bottom board's just
  // below) centered above the TOP board's top edge, instead of top/bottom.
  // Movable as a group via the X/Y sliders (percent of board w/h).
  const [pairStackNumbersEnabled, setPairStackNumbersEnabled] = useState(false);
  const [pairStackOffsetX, setPairStackOffsetX] = useState(0); // % of board width
  const [pairStackOffsetY, setPairStackOffsetY] = useState(0); // % of board height
  // Quad mode: a single image joins 4 boards arranged in a 2x2 layout at their meeting point.
  // When enabled, supersedes the pair-based (horizontal/vertical) logic.
  const [neighborsQuadEnabled, setNeighborsQuadEnabled] = useState(false);
  // Custom vertical pair overrides. Each entry is { a, b, gap } where `a` and
  // `b` are 1-based board numbers and `gap` is the vertical distance in
  // canvas pixels between board A's bottom edge and board B's top edge.
  // Legacy saves with the older `[[a, b]]` shape are normalized to objects on
  // load with a default gap of 20px.
  const [customVerticalPairs, setCustomVerticalPairs] = useState([]); // [{a, b, gap}]
  const [neighborsQuadCellsPerBoard, setNeighborsQuadCellsPerBoard] = useState(1); // 1 (2x2 area) or 2 (4x4 area)
  const [neighborsPairShiftX, setNeighborsPairShiftX] = useState(0);
  const [neighborsPairShiftY, setNeighborsPairShiftY] = useState(0);
  const prevNeighborsPairShiftRef = useRef({ x: 0, y: 0 });
  const neighborPairsRef = useRef([]);
  // Last Card Standing feature — one big focal figure per board
  const [lastCardEnabled, setLastCardEnabled] = useState(false);
  const [lastCardCorner, setLastCardCorner] = useState('top-left'); // top-left | top-right | bottom-left | bottom-right
  const [lastCardSize, setLastCardSize] = useState(2); // 2 | 3 | 4 cells
  const [lastCardShape, setLastCardShape] = useState('square');
  const [lastCardHighlightColor, setLastCardHighlightColor] = useState('#FFD700');
  const [lastCardBorderThickness, setLastCardBorderThickness] = useState(3);
  const [lastCardShadowEnabled, setLastCardShadowEnabled] = useState(false);
  const [lastCardOutlineStyle, setLastCardOutlineStyle] = useState('sharp');
  const [lastCardTilt, setLastCardTilt] = useState(0);
  const [lastCardOffsetX, setLastCardOffsetX] = useState(0);
  const [lastCardOffsetY, setLastCardOffsetY] = useState(0);
  const [lastCardOrientation, setLastCardOrientation] = useState('vertical'); // 'vertical' | 'horizontal'
  // Last Card Standing scale: % of natural cell-coverage size (50-200).
  // 100 = original (matches lastCardSize cells exactly). >100 makes the focal
  // image bigger than its underlying cells; <100 makes it smaller.
  const [lastCardScale, setLastCardScale] = useState(100);
  const lastCardAssignmentsRef = useRef([]);
  const autoMarkupQueueRef = useRef([]);
  const autoMarkupTimerRef = useRef(null);

  // History (undo/redo)
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [historyCount, setHistoryCount] = useState(0);
  const stateRef = useRef({});

  // Saved templates
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [currentTemplateName, setCurrentTemplateName] = useState("");
  // Flat-folder organization for Saved Templates. `folders` is the user's
  // custom folder list; `selectedFolderId` filters the displayed templates
  // (null = "All", "__unfiled__" = templates without folder_id, otherwise
  // the actual folder id). `templateSearchQuery` is the freeform name/folder
  // filter typed into the search box.
  const [folders, setFolders] = useState([]);
  // Owner-only Subscribers panel — lightweight modal listing every user's
  // subscription status + next payment due date. Loaded on demand only.
  // The `openSubscribersPanel` callback is defined AFTER xhrRequest lower
  // in the file to avoid a temporal-dead-zone reference error.
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [subscribersData, setSubscribersData] = useState(null);
  const [isLoadingSubscribers, setIsLoadingSubscribers] = useState(false);
  const [subscriberFilter, setSubscriberFilter] = useState('all'); // all | free | paying | blocked
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");

  // Canvas ref for export
  const canvasRef = useRef(null);

  // When true, the canvas skips drawing the background color + image so we can
  // export a transparent PNG containing only boards, overlays, markup, etc.
  const [skipBackgroundForExport, setSkipBackgroundForExport] = useState(false);

  // Canvas quality (DPI). 150 = Standard (fast), 220 = High, 300 = Ultra (crisp for
  // screenshots/recording; heavier on the CPU during live edit). Persisted to
  // sessionStorage so it survives refresh.
  const [canvasQuality, setCanvasQuality] = useState(() => {
    const saved = sessionStorage.getItem('canvasQuality');
    return saved ? parseInt(saved, 10) : 150;
  });
  useEffect(() => {
    sessionStorage.setItem('canvasQuality', String(canvasQuality));
  }, [canvasQuality]);

  // DPI and canvas dimensions
  const DPI = canvasQuality;
  const currentSize = TEMPLATE_SIZES[templateSize];
  const canvasWidth = currentSize.width * DPI;
  const canvasHeight = currentSize.height * DPI;

  // Compute display dimensions (must match CanvasEditor for consistent coordinate space)
  const maxDisplayWidth = 550;
  const maxDisplayHeight = 650;
  const canvasAspectRatio = canvasWidth / canvasHeight;
  const displayWidth = canvasAspectRatio > maxDisplayWidth / maxDisplayHeight
    ? maxDisplayWidth : maxDisplayHeight * canvasAspectRatio;
  const displayHeight = canvasAspectRatio > maxDisplayWidth / maxDisplayHeight
    ? maxDisplayWidth / canvasAspectRatio : maxDisplayHeight;

  // Apply dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // --- SESSION PERSISTENCE: auto-save state so work survives tab switches ---
  const SESSION_KEY = 'bingogrid-session';
  const sessionRestoredRef = useRef(false);

  // Restore session on mount (once)
  useEffect(() => {
    if (sessionRestoredRef.current) return;
    sessionRestoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s.gameMode) setGameMode(s.gameMode);
      if (s.templateSize) setTemplateSize(s.templateSize);
      if (s.backgroundColor) setBackgroundColor(s.backgroundColor);
      if (s.backgroundImage) setBackgroundImage(s.backgroundImage);
      if (s.backgroundImageFit) setBackgroundImageFit(s.backgroundImageFit);
      if (typeof s.backgroundImageScale === 'number') setBackgroundImageScale(s.backgroundImageScale);
      if (s.boardCount != null) setBoardCount(s.boardCount);
      if (s.boardStyle) setBoardStyle(s.boardStyle);
      if (s.boardColor) setBoardColor(s.boardColor);
      if (s.boardFont) setBoardFont(s.boardFont);
      if (s.headerFont) setHeaderFont(s.headerFont);
      if (s.headerColor) setHeaderColor(s.headerColor);
      if (s.headerLetterColor) setHeaderLetterColor(s.headerLetterColor);
      if (s.cellColor) setCellColor(s.cellColor);
      if (s.textColor) setTextColor(s.textColor);
      if (s.headerOpacity != null) setHeaderOpacity(s.headerOpacity);
      if (s.cellOpacity != null) setCellOpacity(s.cellOpacity);
      if (s.freeSpaceOpacity != null) setFreeSpaceOpacity(s.freeSpaceOpacity);
      if (s.freeSpaceText) setFreeSpaceText(s.freeSpaceText);
      if (s.freeSpaceColor) setFreeSpaceColor(s.freeSpaceColor);
      if (s.freeSpaceTextColor) setFreeSpaceTextColor(s.freeSpaceTextColor);
      if (s.boardOpacity != null) setBoardOpacity(s.boardOpacity);
      if (s.multiColorRows != null) setMultiColorRows(s.multiColorRows);
      if (s.boardRowColors) setBoardRowColors(s.boardRowColors);
      if (s.watermarkEnabled != null) setWatermarkEnabled(s.watermarkEnabled);
      if (s.watermarkText) setWatermarkText(s.watermarkText);
      if (s.templateTextEnabled != null) setTemplateTextEnabled(s.templateTextEnabled);
      if (s.templateText != null) setTemplateText(s.templateText);
      if (s.templateTextFont) setTemplateTextFont(s.templateTextFont);
      if (s.templateTextColor) setTemplateTextColor(s.templateTextColor);
      if (s.templateTextOutlineEnabled != null) setTemplateTextOutlineEnabled(s.templateTextOutlineEnabled);
      if (s.templateTextOutlineColor) setTemplateTextOutlineColor(s.templateTextOutlineColor);
      if (s.templateTextOutlineWidth != null) setTemplateTextOutlineWidth(s.templateTextOutlineWidth);
      if (s.templateTextSize != null) setTemplateTextSize(s.templateTextSize);
      if (s.templateTextX !== undefined) setTemplateTextX(s.templateTextX);
      if (s.templateTextY !== undefined) setTemplateTextY(s.templateTextY);
      if (s.templateTextLetterSpacing != null) setTemplateTextLetterSpacing(s.templateTextLetterSpacing);
      if (s.templateTextOpacity != null) setTemplateTextOpacity(s.templateTextOpacity);
      if (Array.isArray(s.extraTemplateTexts)) setExtraTemplateTexts(s.extraTemplateTexts);
      if (s.watermarkOpacity != null) setWatermarkOpacity(s.watermarkOpacity);
      if (s.watermarkColor) setWatermarkColor(s.watermarkColor);
      if (s.watermarkFont) setWatermarkFont(s.watermarkFont);
      if (s.bingoBoards) setBingoBoards(s.bingoBoards);
      if (s.overlayImages) setOverlayImages(s.overlayImages);
      if (s.overlayShadow) setOverlayShadow(s.overlayShadow);
      if (s.overlayShadowOffsetX != null) setOverlayShadowOffsetX(s.overlayShadowOffsetX);
      if (s.overlayShadowOffsetY != null) setOverlayShadowOffsetY(s.overlayShadowOffsetY);
      if (s.markupElements) setMarkupElements(s.markupElements);
      if (s.calledNumbers) setCalledNumbers(s.calledNumbers);
      if (s.filterSettings) setFilterSettings(s.filterSettings);
      if (s.emojis) setEmojis(s.emojis);
      // Loteria state
      if (s.loteriaGridSize) setLoteriaGridSize(s.loteriaGridSize);
      if (s.loteriaBoardCount != null) setLoteriaBoardCount(s.loteriaBoardCount);
      if (s.loteriaBoards) setLoteriaBoards(s.loteriaBoards);
      if (s.loteriaOneOffEnabled != null) setLoteriaOneOffEnabled(s.loteriaOneOffEnabled);
      if (s.loteriaOneOffBoardIndex != null) setLoteriaOneOffBoardIndex(s.loteriaOneOffBoardIndex);
      if (s.loteriaOneOffGridSize != null) setLoteriaOneOffGridSize(s.loteriaOneOffGridSize);
      if (s.loteriaOneOffNumberingEnabled != null) setLoteriaOneOffNumberingEnabled(s.loteriaOneOffNumberingEnabled);
      if (s.loteriaOneOffShowRowNums != null) setLoteriaOneOffShowRowNums(s.loteriaOneOffShowRowNums);
      if (s.loteriaOneOffShowColNums != null) setLoteriaOneOffShowColNums(s.loteriaOneOffShowColNums);
      if (s.loteriaOneOffStartingNum != null) setLoteriaOneOffStartingNum(s.loteriaOneOffStartingNum);
      if (s.loteriaSingleRepeatEnabled != null) setLoteriaSingleRepeatEnabled(s.loteriaSingleRepeatEnabled);
      if (s.loteriaSingleRepeatBoardIndex != null) setLoteriaSingleRepeatBoardIndex(s.loteriaSingleRepeatBoardIndex);
      if (s.loteriaSingleRepeatMode) setLoteriaSingleRepeatMode(s.loteriaSingleRepeatMode);
      if (s.loteriaSingleRepeatGridSize != null) setLoteriaSingleRepeatGridSize(s.loteriaSingleRepeatGridSize);
      if (s.loteriaCellColors) setLoteriaCellColors(s.loteriaCellColors);
      if (s.loteriaColorMode) setLoteriaColorMode(s.loteriaColorMode);
      if (s.loteriaRowColors) setLoteriaRowColors(s.loteriaRowColors);
      if (s.loteriaColColors) setLoteriaColColors(s.loteriaColColors);
      if (s.loteriaStripeColors) setLoteriaStripeColors(s.loteriaStripeColors);
      if (s.loteriaBorderColor) setLoteriaBorderColor(s.loteriaBorderColor);
      if (s.loteriaGridColor) setLoteriaGridColor(s.loteriaGridColor);
      if (s.loteriaCenterHighlight != null) setLoteriaCenterHighlight(s.loteriaCenterHighlight);
      if (s.loteriaCenterHighlightColor) setLoteriaCenterHighlightColor(s.loteriaCenterHighlightColor);
      if (s.loteriaCenterHighlightThickness != null) setLoteriaCenterHighlightThickness(s.loteriaCenterHighlightThickness);
      if (s.loteriaCenterHighlightOffsetX != null) setLoteriaCenterHighlightOffsetX(s.loteriaCenterHighlightOffsetX);
      if (s.loteriaCenterHighlightOffsetY != null) setLoteriaCenterHighlightOffsetY(s.loteriaCenterHighlightOffsetY);
      if (s.loteriaRepeatHighlight != null) setLoteriaRepeatHighlight(s.loteriaRepeatHighlight);
      if (s.loteriaRepeatHighlightColor) setLoteriaRepeatHighlightColor(s.loteriaRepeatHighlightColor);
      if (s.loteriaRepeatHighlightThickness != null) setLoteriaRepeatHighlightThickness(s.loteriaRepeatHighlightThickness);
      if (s.loteriaBorderStyle) setLoteriaBorderStyle(s.loteriaBorderStyle);
      if (s.loteriaCornerRadius !== undefined) setLoteriaCornerRadius(s.loteriaCornerRadius);
      if (s.loteriaBorderWidth != null) setLoteriaBorderWidth(s.loteriaBorderWidth);
      if (s.loteriaGridWidth != null) setLoteriaGridWidth(s.loteriaGridWidth);
      if (s.loteriaCellShape) setLoteriaCellShape(s.loteriaCellShape);
      if (s.loteriaBoardShadow) setLoteriaBoardShadow(s.loteriaBoardShadow);
      if (s.loteriaGapH != null) setLoteriaGapH(s.loteriaGapH);
      if (s.loteriaGapV != null) setLoteriaGapV(s.loteriaGapV);
      if (s.loteriaRepeatMode) setLoteriaRepeatMode(s.loteriaRepeatMode);
      if (s.loteriaCenterOrientation) setLoteriaCenterOrientation(s.loteriaCenterOrientation);
      if (s.loteriaNumbering) setLoteriaNumbering(s.loteriaNumbering);
      if (s.highlightColor) setHighlightColor(s.highlightColor);
      if (s.starColor) setStarColor(s.starColor);
      if (s.isDarkMode != null) setIsDarkMode(s.isDarkMode);
      if (s.boardLayout) setBoardLayout(s.boardLayout);
      if (s.loteriaMoveMode) setLoteriaMoveMode(s.loteriaMoveMode);
      if (s.markupColor) setMarkupColor(s.markupColor);
      if (s.markupThickness != null) setMarkupThickness(s.markupThickness);
      if (s.autoMarkupScale != null) setAutoMarkupScale(s.autoMarkupScale);
      // Neighbors Boards state
      if (s.neighborsEnabled != null) setNeighborsEnabled(s.neighborsEnabled);
      if (s.neighborsPosition) setNeighborsPosition(s.neighborsPosition);
      if (s.neighborsDirection) setNeighborsDirection(s.neighborsDirection);
      if (s.neighborsVerticalPairNumbering != null) setNeighborsVerticalPairNumbering(s.neighborsVerticalPairNumbering);
      if (s.verticalPairBadgeAlign) setVerticalPairBadgeAlign(s.verticalPairBadgeAlign);
      if (s.pairStackNumbersEnabled != null) setPairStackNumbersEnabled(s.pairStackNumbersEnabled);
      if (s.pairStackOffsetX != null) setPairStackOffsetX(s.pairStackOffsetX);
      if (s.pairStackOffsetY != null) setPairStackOffsetY(s.pairStackOffsetY);
      if (s.neighborsHighlightColor) setNeighborsHighlightColor(s.neighborsHighlightColor);
      if (s.neighborsShape) setNeighborsShape(s.neighborsShape);
      if (s.neighborsBorderThickness != null) setNeighborsBorderThickness(s.neighborsBorderThickness);
      if (s.neighborsShadowEnabled != null) setNeighborsShadowEnabled(s.neighborsShadowEnabled);
      if (s.neighborsOutlineStyle) setNeighborsOutlineStyle(s.neighborsOutlineStyle);
      if (s.neighborsRowsPerBoard != null) setNeighborsRowsPerBoard(s.neighborsRowsPerBoard);
      if (s.neighborSeedVersion != null) setNeighborSeedVersion(s.neighborSeedVersion);
      if (s.markupStampImage !== undefined) setMarkupStampImage(s.markupStampImage);
      if (s.markupStampTemplate !== undefined) setMarkupStampTemplate(s.markupStampTemplate);
      if (s.neighborsQuadEnabled != null) setNeighborsQuadEnabled(s.neighborsQuadEnabled);
      if (Array.isArray(s.customVerticalPairs)) setCustomVerticalPairs(normalizeCustomPairs(s.customVerticalPairs));
      if (s.neighborsQuadCellsPerBoard != null) setNeighborsQuadCellsPerBoard(s.neighborsQuadCellsPerBoard);
      // Last Card Standing
      if (s.lastCardEnabled != null) setLastCardEnabled(s.lastCardEnabled);
      if (s.lastCardCorner) setLastCardCorner(s.lastCardCorner);
      if (s.lastCardSize != null) setLastCardSize(s.lastCardSize);
      if (s.lastCardShape) setLastCardShape(s.lastCardShape);
      if (s.lastCardHighlightColor) setLastCardHighlightColor(s.lastCardHighlightColor);
      if (s.lastCardBorderThickness != null) setLastCardBorderThickness(s.lastCardBorderThickness);
      if (s.lastCardShadowEnabled != null) setLastCardShadowEnabled(s.lastCardShadowEnabled);
      if (s.lastCardOutlineStyle) setLastCardOutlineStyle(s.lastCardOutlineStyle);
      if (s.lastCardTilt != null) setLastCardTilt(s.lastCardTilt);
      if (s.lastCardOffsetX != null) setLastCardOffsetX(s.lastCardOffsetX);
      if (s.lastCardOffsetY != null) setLastCardOffsetY(s.lastCardOffsetY);
      if (s.lastCardScale != null) setLastCardScale(s.lastCardScale);
      if (s.lastCardOrientation) setLastCardOrientation(s.lastCardOrientation);
      if (s.neighborsPairShiftX != null) {
        setNeighborsPairShiftX(s.neighborsPairShiftX);
        prevNeighborsPairShiftRef.current.x = s.neighborsPairShiftX;
      }
      if (s.neighborsPairShiftY != null) {
        setNeighborsPairShiftY(s.neighborsPairShiftY);
        prevNeighborsPairShiftRef.current.y = s.neighborsPairShiftY;
      }
      // Prize Dots + star style + custom neighbor image auto-restore
      if (s.prizeDots && typeof s.prizeDots === 'object') {
        setPrizeDots(prev => ({ ...prev, ...s.prizeDots }));
      }
      if (s.starStyle) setStarStyle(s.starStyle);
      if (s.neighborsCustomImage !== undefined) setNeighborsCustomImage(s.neighborsCustomImage);
      if (s.neighborsCustomImageSize != null) setNeighborsCustomImageSize(s.neighborsCustomImageSize);
      if (s.neighborsCustomOutlineWidth != null) setNeighborsCustomOutlineWidth(s.neighborsCustomOutlineWidth);
      if (s.neighborsCustomOutlineColor) setNeighborsCustomOutlineColor(s.neighborsCustomOutlineColor);
    } catch (e) {
      // Silently ignore corrupt session data
    }
  }, []);

  // Auto-save session on every significant state change (debounced)
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        const session = {
          gameMode, templateSize, backgroundColor, backgroundImage, backgroundImageFit, backgroundImageScale,
          boardCount, boardStyle, boardColor, boardFont, headerFont,
          headerColor, headerLetterColor, cellColor, textColor,
          headerOpacity, cellOpacity, freeSpaceOpacity,
          freeSpaceText, freeSpaceColor, freeSpaceTextColor,
          boardOpacity, multiColorRows, boardRowColors,
          watermarkEnabled, watermarkText, watermarkOpacity, watermarkColor, watermarkFont,
          templateTextEnabled, templateText, templateTextFont, templateTextColor,
          templateTextOutlineEnabled, templateTextOutlineColor, templateTextOutlineWidth, templateTextSize,
          templateTextX, templateTextY, templateTextLetterSpacing, templateTextOpacity,
          extraTemplateTexts,
          bingoBoards, overlayImages, overlayShadow,
          markupElements, calledNumbers, filterSettings, emojis,
          loteriaGridSize, loteriaBoardCount, loteriaBoards,
          loteriaOneOffEnabled, loteriaOneOffBoardIndex, loteriaOneOffGridSize,
          loteriaOneOffNumberingEnabled, loteriaOneOffShowRowNums, loteriaOneOffShowColNums, loteriaOneOffStartingNum,
          loteriaSingleRepeatEnabled, loteriaSingleRepeatBoardIndex, loteriaSingleRepeatMode, loteriaSingleRepeatGridSize,
          loteriaCellColors, loteriaBorderColor, loteriaGridColor, loteriaBorderStyle,
          loteriaCenterHighlight, loteriaCenterHighlightColor,
          loteriaRepeatHighlight, loteriaRepeatHighlightColor,
          loteriaCenterHighlightThickness, loteriaRepeatHighlightThickness,
          loteriaCenterHighlightOffsetX, loteriaCenterHighlightOffsetY,
          loteriaColorMode, loteriaRowColors, loteriaColColors, loteriaStripeColors,
          loteriaBorderWidth, loteriaGridWidth, loteriaCellShape,
          loteriaBoardShadow, loteriaGapH, loteriaGapV, loteriaRepeatMode, loteriaCenterOrientation,
          loteriaNumbering, highlightColor, starColor, isDarkMode,
          boardLayout, loteriaMoveMode, markupColor, markupThickness, autoMarkupScale,
          // Neighbors Boards state
          neighborsEnabled, neighborsPosition, neighborsDirection, neighborsHighlightColor,
          neighborsShape, neighborsBorderThickness, neighborsShadowEnabled, neighborsOutlineStyle,
          neighborsRowsPerBoard,
          neighborsQuadEnabled, neighborsQuadCellsPerBoard,
          customVerticalPairs,
          neighborSeedVersion,
          neighborsVerticalPairNumbering,
          verticalPairBadgeAlign,
          pairStackNumbersEnabled, pairStackOffsetX, pairStackOffsetY,
          neighborsPairShiftX, neighborsPairShiftY,
          lastCardEnabled, lastCardCorner, lastCardSize, lastCardShape,
          lastCardHighlightColor, lastCardBorderThickness, lastCardShadowEnabled, lastCardOutlineStyle,
          lastCardTilt, lastCardOffsetX, lastCardOffsetY, lastCardOrientation,
          lastCardScale,
          loteriaCornerRadius,
          // Prize Dots + star style + custom neighbor image live-persist
          prizeDots,
          starStyle,
          neighborsCustomImage,
          neighborsCustomImageSize,
          neighborsCustomOutlineWidth,
          neighborsCustomOutlineColor,
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      } catch (e) {
        // sessionStorage full or unavailable — silently ignore
      }
    }, 500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [gameMode, templateSize, backgroundColor, backgroundImage,
    boardCount, boardStyle, boardColor, boardFont,
    headerColor, headerLetterColor, cellColor, textColor,
    headerOpacity, cellOpacity, freeSpaceOpacity,
    freeSpaceText, freeSpaceColor, freeSpaceTextColor,
    boardOpacity, multiColorRows, boardRowColors,
    watermarkEnabled, watermarkText, watermarkOpacity, watermarkColor, watermarkFont,
    templateTextEnabled, templateText, templateTextFont, templateTextColor,
    templateTextOutlineEnabled, templateTextOutlineColor, templateTextOutlineWidth, templateTextSize,
    templateTextX, templateTextY, templateTextLetterSpacing, templateTextOpacity,
    extraTemplateTexts,
    bingoBoards, overlayImages, overlayShadow,
    markupElements, calledNumbers, filterSettings, emojis,
    loteriaGridSize, loteriaBoardCount, loteriaBoards,
    loteriaOneOffEnabled, loteriaOneOffBoardIndex, loteriaOneOffGridSize,
    loteriaOneOffNumberingEnabled, loteriaOneOffShowRowNums, loteriaOneOffShowColNums, loteriaOneOffStartingNum,
    loteriaSingleRepeatEnabled, loteriaSingleRepeatBoardIndex, loteriaSingleRepeatMode, loteriaSingleRepeatGridSize,
    loteriaCellColors, loteriaBorderColor, loteriaBorderStyle,
    loteriaCenterHighlight, loteriaCenterHighlightColor,
    loteriaRepeatHighlight, loteriaRepeatHighlightColor,
    loteriaCenterHighlightThickness, loteriaRepeatHighlightThickness,
    loteriaCenterHighlightOffsetX, loteriaCenterHighlightOffsetY,
    loteriaColorMode, loteriaRowColors, loteriaColColors, loteriaStripeColors,
    loteriaBorderWidth, loteriaGridWidth, loteriaCellShape,
    loteriaBoardShadow, loteriaGapH, loteriaGapV, loteriaRepeatMode, loteriaCenterOrientation,
    loteriaNumbering, highlightColor, starColor, isDarkMode,
    boardLayout, loteriaMoveMode, markupColor, markupThickness, autoMarkupScale,
    neighborsEnabled, neighborsPosition, neighborsDirection, neighborsHighlightColor,
    neighborsShape, neighborsBorderThickness, neighborsShadowEnabled, neighborsOutlineStyle,
    neighborsRowsPerBoard,
    neighborsQuadEnabled, neighborsQuadCellsPerBoard,
    neighborsVerticalPairNumbering, verticalPairBadgeAlign,
    pairStackNumbersEnabled, pairStackOffsetX, pairStackOffsetY,
    lastCardOffsetX, lastCardOffsetY, lastCardOrientation,
    neighborsPairShiftX, neighborsPairShiftY,
    lastCardEnabled, lastCardCorner, lastCardSize, lastCardShape,
    lastCardHighlightColor, lastCardBorderThickness, lastCardShadowEnabled, lastCardOutlineStyle,
    lastCardTilt,
    lastCardScale, loteriaCornerRadius,
  ]);

  // Try to refresh the access token using the refresh token.
  // Only wipe tokens on a definitive auth failure (401/403) — keep them on
  // 5xx / network errors so a transient backend outage doesn't log the user out.
  const tryRefreshToken = useCallback(() => {
    const refreshTok = localStorage.getItem("refresh_token");
    if (!refreshTok) return null;
    let status = 0;
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/auth/refresh`, false);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(JSON.stringify({ refresh_token: refreshTok }));
      status = xhr.status;
      if (xhr.status >= 200 && xhr.status < 300) {
        const data = JSON.parse(xhr.responseText);
        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
          return data;
        }
      }
    } catch {}
    // Only clear tokens if the server explicitly rejected them as invalid.
    // For network errors (status === 0) or 5xx, keep them so we can retry later.
    if (status === 401 || status === 403) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
    return null;
  }, []);

  // XHR helper to bypass platform fetch interception — auto-refreshes token on 401
  const xhrRequest = useCallback((method, url, data, options = {}) => {
    const doRequest = (token) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url, true);
        if (options.responseType) xhr.responseType = options.responseType;
        if (data && !(data instanceof FormData)) {
          xhr.setRequestHeader("Content-Type", "application/json");
        }
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
        xhr.onload = () => {
          if (options.responseType === 'blob') {
            resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data: xhr.response });
          } else {
            let json = null;
            try { json = JSON.parse(xhr.responseText); } catch {}
            resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data: json, text: xhr.responseText });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        if (data instanceof FormData) {
          xhr.send(data);
        } else {
          xhr.send(data ? JSON.stringify(data) : null);
        }
      });
    };

    const token = localStorage.getItem("access_token");
    return doRequest(token).then(res => {
      // Auto-refresh on 401 and retry once
      if (res.status === 401 && !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
        const refreshResult = tryRefreshToken();
        if (refreshResult && refreshResult.access_token) {
          return doRequest(refreshResult.access_token);
        }
      }
      return res;
    });
  }, [tryRefreshToken]);

  // Check auth on mount, with auto-retry on transient 5xx so a brief backend
  // hiccup doesn't bounce the user to the login screen. Tokens are kept intact;
  // we only set currentUser=false (= logged out) on a definite 401/403.
  useEffect(() => {
    const checkAuth = async () => {
      let attempts = 0;
      const maxAttempts = 4;
      try {
        while (attempts < maxAttempts) {
          attempts += 1;
          try {
            const res = await xhrRequest("GET", `${API}/auth/me`);
            if (res.ok && res.data) {
              setCurrentUser(res.data);
              // Persist a lightweight snapshot so downstream callbacks can
              // read the user's id without relying on closure state.
              try { localStorage.setItem('auth_user_snapshot', JSON.stringify({ id: res.data.id, email: res.data.email })); } catch { /* ignore */ }
              return;
            }
            if (res.status === 401 || res.status === 403) {
              // Genuine auth failure — token invalid/expired, refresh already tried.
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              setCurrentUser(false);
              return;
            }
            // 5xx / network — wait and retry up to maxAttempts times
            if (attempts < maxAttempts) {
              await new Promise(r => setTimeout(r, 1500 * attempts));
              continue;
            }
            // Out of retries — keep tokens, show login (user can hard-refresh)
            setCurrentUser(false);
            return;
          } catch (_) {
            if (attempts < maxAttempts) {
              await new Promise(r => setTimeout(r, 1500 * attempts));
              continue;
            }
            setCurrentUser(false);
            return;
          }
        }
      } finally {
        setAuthChecking(false);
      }
    };
    checkAuth();
    // Check if returning from Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id") || params.get("canceled")) {
      setShowSubscription(true);
    }
  }, [xhrRequest]);

  // Fullscreen UI auto-hide: hide after 2.5s of inactivity
  useEffect(() => {
    if (!isFullscreen) {
      setFsUiVisible(true);
      if (fsUiTimerRef.current) clearTimeout(fsUiTimerRef.current);
      return;
    }
    const handleActivity = () => {
      setFsUiVisible(true);
      if (fsUiTimerRef.current) clearTimeout(fsUiTimerRef.current);
      fsUiTimerRef.current = setTimeout(() => setFsUiVisible(false), 2500);
    };
    // Start timer immediately
    fsUiTimerRef.current = setTimeout(() => setFsUiVisible(false), 2500);
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("click", handleActivity);
    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("click", handleActivity);
      if (fsUiTimerRef.current) clearTimeout(fsUiTimerRef.current);
    };
  }, [isFullscreen]);

  // Auto-Markup panel auto-hide: 1s timer starts when the panel becomes
  // un-hovered. Only direct mouseenter on the panel reveals it again — no
  // global mousemove triggers, so the panel stays gone during gameplay.
  useEffect(() => {
    if (!isFullscreen) {
      setFsAutoMarkupHidden(false);
      if (fsAutoMarkupHideTimerRef.current) clearTimeout(fsAutoMarkupHideTimerRef.current);
      return;
    }
    // Start hidden timer immediately on entering fullscreen.
    if (fsAutoMarkupHideTimerRef.current) clearTimeout(fsAutoMarkupHideTimerRef.current);
    fsAutoMarkupHideTimerRef.current = setTimeout(() => setFsAutoMarkupHidden(true), 1000);
    return () => {
      if (fsAutoMarkupHideTimerRef.current) clearTimeout(fsAutoMarkupHideTimerRef.current);
    };
  }, [isFullscreen]);

  // Owner-only: fetch subscribers list (deferred until user clicks the
  // Users icon). Placed after xhrRequest to avoid TDZ.
  const openSubscribersPanel = useCallback(async () => {
    setShowSubscribers(true);
    setIsLoadingSubscribers(true);
    try {
      const res = await xhrRequest("GET", `${API}/admin/subscribers`);
      if (res.ok && res.data) setSubscribersData(res.data);
      else toast.error("Couldn't load subscribers");
    } catch (err) {
      console.error("Load subscribers failed:", err);
      toast.error("Couldn't load subscribers");
    } finally {
      setIsLoadingSubscribers(false);
    }
  }, [xhrRequest]);

  // Owner-only: revoke a specific subscriber's access immediately. Cancels
  // their live Stripe subscription (if any) and flips them to
  // `needs_recurring` so the app blocks access until they subscribe again.
  const revokeSubscriber = useCallback(async (email) => {
    if (!email) return;
    const confirmed = window.confirm(
      `Remove subscriber benefits from ${email}?\n\nThis will:\n• Cancel their Stripe subscription immediately (no more charges)\n• Block their access until they subscribe again\n\nContinue?`
    );
    if (!confirmed) return;
    try {
      const res = await xhrRequest("POST", `${API}/admin/revoke-subscription`, { email, reason: "manual owner revoke" });
      if (res.ok) {
        toast.success(`Access revoked for ${email}`);
        // Refresh the list so the row shows the new status.
        openSubscribersPanel();
      } else {
        toast.error(res.data?.detail || "Failed to revoke access");
      }
    } catch (err) {
      console.error("Revoke failed:", err);
      toast.error("Couldn't revoke access — check network");
    }
  }, [xhrRequest, openSubscribersPanel]);

  // Owner-only: set a subscriber's password directly. Used when a user
  // can't receive their reset email (e.g. Resend domain not verified).
  const setSubscriberPassword = useCallback(async (email) => {
    if (!email) return;
    const newPassword = window.prompt(
      `Set a new password for ${email}?\n\nMin 6 characters. Share with the user securely (they can change it after logging in).`,
      ""
    );
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      const res = await xhrRequest("POST", `${API}/admin/set-user-password`, { email, new_password: newPassword });
      if (res.ok) {
        toast.success(`Password updated for ${email}`);
      } else {
        toast.error(res.data?.detail || "Failed to set password");
      }
    } catch (err) {
      console.error("Set password failed:", err);
      toast.error("Couldn't set password — check network");
    }
  }, [xhrRequest]);

  // Hammer panel — same hover-only reveal pattern as Auto-Markup.
  useEffect(() => {
    if (!isFullscreen) {
      setFsHammerHidden(false);
      if (fsHammerHideTimerRef.current) clearTimeout(fsHammerHideTimerRef.current);
      return;
    }
    if (fsHammerHideTimerRef.current) clearTimeout(fsHammerHideTimerRef.current);
    fsHammerHideTimerRef.current = setTimeout(() => setFsHammerHidden(true), 1000);
    return () => { if (fsHammerHideTimerRef.current) clearTimeout(fsHammerHideTimerRef.current); };
  }, [isFullscreen]);

  // Load saved templates + folders on mount (only when authenticated).
  // Hydrates from a per-user localStorage snapshot FIRST so the list
  // appears instantly on refresh/login (Cloudflare edge + auth roundtrip
  // used to leave the UI blank for 1-3 seconds). The network fetch runs
  // in parallel and updates state + cache when fresh data arrives.
  useEffect(() => {
    if (currentUser && currentUser.id) {
      try {
        const uid = currentUser.id;
        const cachedT = localStorage.getItem(`savedTemplates_${uid}`);
        const cachedF = localStorage.getItem(`folders_${uid}`);
        if (cachedT) {
          const parsed = JSON.parse(cachedT);
          if (Array.isArray(parsed) && parsed.length > 0) setSavedTemplates(parsed);
        }
        if (cachedF) {
          const parsed = JSON.parse(cachedF);
          if (Array.isArray(parsed)) setFolders(parsed);
        }
      } catch (err) {
        // Corrupt cache — safe to ignore; network fetch below will refill.
      }
      // Kick off both network refreshes in parallel — they don't depend on
      // each other, so no reason to wait sequentially.
      Promise.all([loadTemplatesList(), loadFolders()]);
    } else {
      setSavedTemplates([]);
      setFolders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Tracks whether the templates list is currently loading so the UI can
  // show a tiny inline spinner (replaces the red error banner the user
  // hated). Failures auto-retry indefinitely with backoff up to 5s — the
  // user no longer sees a "0 templates" lie or a noisy error message;
  // they just see the list appear once the call succeeds.
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Load templates list — retries up to 8 times on 5xx / network errors with
  // exponential backoff (0.5s → 1s → 2s → 4s capped at 5s) before giving
  // up silently. Avoids ever showing "Saved (0)" to a user who actually has
  // saved templates because of a transient backend hiccup.
  const loadTemplatesList = useCallback(async (attempt = 0) => {
    if (attempt === 0) setIsLoadingTemplates(true);
    try {
      const res = await xhrRequest("GET", `${API}/templates`);
      if (res.ok && Array.isArray(res.data)) {
        // Empty-payload retry guard: on cold-load a transient upstream/CDN
        // hiccup can occasionally return an empty array as a "successful"
        // response. If the FIRST attempt comes back empty, retry once quickly
        // before trusting it — legitimate empty accounts still resolve to []
        // after the second attempt.
        if (res.data.length === 0 && attempt === 0) {
          setTimeout(() => loadTemplatesList(attempt + 1), 400);
          return;
        }
        setSavedTemplates(res.data);
        // Persist to per-user cache so next refresh/login shows the list
        // instantly. Uses localStorage directly (not `currentUser` from
        // closure) so this always writes even if the callback was created
        // with a stale user reference.
        try {
          const rawUser = localStorage.getItem('auth_user_snapshot');
          const parsed = rawUser ? JSON.parse(rawUser) : null;
          const uid = parsed && parsed.id;
          if (uid) localStorage.setItem(`savedTemplates_${uid}`, JSON.stringify(res.data));
        } catch { /* quota exceeded — ignore, cache is best-effort */ }
        setIsLoadingTemplates(false);
      } else if (res.status === 401 || res.status === 403) {
        console.warn('templates list: auth failed, status', res.status);
        toast.error("Session expired — please log out and back in to see your saved templates.");
        setIsLoadingTemplates(false);
      } else if (attempt < 8) {
        console.warn('templates list: unexpected response, retrying. status=', res.status, 'body=', (res.text || '').slice(0, 200));
        const delay = Math.min(5000, 500 * Math.pow(2, attempt));
        setTimeout(() => loadTemplatesList(attempt + 1), delay);
      } else {
        console.error('templates list: gave up after 8 attempts. status=', res.status, 'body=', (res.text || '').slice(0, 200));
        toast.error("Couldn't load your templates — please refresh the page.");
        setIsLoadingTemplates(false);
      }
    } catch (error) {
      console.error("Error loading templates (attempt " + attempt + "):", error);
      if (attempt < 8) {
        const delay = Math.min(5000, 500 * Math.pow(2, attempt));
        setTimeout(() => loadTemplatesList(attempt + 1), delay);
      } else {
        toast.error("Network error loading templates — please check your connection and refresh.");
        setIsLoadingTemplates(false);
      }
    }
  }, [xhrRequest]);

  // Logout
  const handleLogout = async () => {
    try {
      await xhrRequest("POST", `${API}/auth/logout`);
    } catch {}
    // Clear the per-user template/folder cache so the next login starts
    // clean and never shows the previous account's list.
    try {
      const uid = currentUser && currentUser.id;
      if (uid) {
        localStorage.removeItem(`savedTemplates_${uid}`);
        localStorage.removeItem(`folders_${uid}`);
      }
      localStorage.removeItem('auth_user_snapshot');
    } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setCurrentUser(false);
    setSavedTemplates([]);
    setFolders([]);
    setShowSubscription(false);
    toast.success("Logged out");
  };

  // ── Folder management for Saved Templates ──────────────────────────────
  // All folder operations use optimistic UI to avoid the per-action latency
  // that previously made the templates list feel slow. Rollback on error.
  const loadFolders = useCallback(async () => {
    try {
      const res = await xhrRequest("GET", `${API}/folders`);
      if (res.ok && Array.isArray(res.data)) {
        setFolders(res.data);
        // Cache for instant hydration on next refresh/login. Reads uid from
        // the auth snapshot in localStorage rather than the current closure
        // to avoid stale-user bugs.
        try {
          const rawUser = localStorage.getItem('auth_user_snapshot');
          const parsed = rawUser ? JSON.parse(rawUser) : null;
          const uid = parsed && parsed.id;
          if (uid) localStorage.setItem(`folders_${uid}`, JSON.stringify(res.data));
        } catch { /* ignore quota */ }
      }
    } catch (e) {
      // Folders are optional — failure shouldn't block templates from loading.
      console.error("Failed to load folders:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createFolder = useCallback(async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Folder name is required"); return null; }
    try {
      const res = await xhrRequest("POST", `${API}/folders`, { name: trimmed });
      if (!res.ok) {
        toast.error(res.data?.detail || "Failed to create folder");
        return null;
      }
      setFolders(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Folder created");
      return res.data;
    } catch (e) {
      toast.error("Failed to create folder");
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renameFolder = useCallback(async (folderId, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) { toast.error("Folder name is required"); return; }
    const prev = folders;
    setFolders(p => p.map(f => f.id === folderId ? { ...f, name: trimmed } : f).sort((a, b) => a.name.localeCompare(b.name)));
    try {
      const res = await xhrRequest("PATCH", `${API}/folders/${folderId}`, { name: trimmed });
      if (!res.ok) {
        toast.error(res.data?.detail || "Failed to rename folder");
        setFolders(prev);
      }
    } catch (e) {
      toast.error("Failed to rename folder");
      setFolders(prev);
    }
  }, [folders]);

  const deleteFolder = useCallback(async (folderId) => {
    const prevF = folders;
    const prevT = savedTemplates;
    setFolders(p => p.filter(f => f.id !== folderId));
    // Templates in the deleted folder become Unfiled (no folder_id).
    setSavedTemplates(p => p.map(t => t.folder_id === folderId ? { ...t, folder_id: null } : t));
    if (selectedFolderId === folderId) setSelectedFolderId(null);
    try {
      const res = await xhrRequest("DELETE", `${API}/folders/${folderId}`);
      if (!res.ok) {
        toast.error("Failed to delete folder");
        setFolders(prevF); setSavedTemplates(prevT);
      } else {
        toast.success("Folder deleted");
      }
    } catch (e) {
      toast.error("Failed to delete folder");
      setFolders(prevF); setSavedTemplates(prevT);
    }
  }, [folders, savedTemplates, selectedFolderId]);

  const moveTemplateToFolder = useCallback(async (templateId, targetFolderId) => {
    // targetFolderId may be null (Unfiled) or a folder id.
    const prev = savedTemplates;
    setSavedTemplates(p => p.map(t => t.id === templateId ? { ...t, folder_id: targetFolderId || null } : t));
    try {
      const res = await xhrRequest("PATCH", `${API}/templates/${templateId}/folder`, { folder_id: targetFolderId || null });
      if (!res.ok) {
        toast.error("Failed to move template");
        setSavedTemplates(prev);
      }
    } catch (e) {
      toast.error("Failed to move template");
      setSavedTemplates(prev);
    }
  }, [savedTemplates]);

  // Owner check: both owners always have full access
  const isOwner = useMemo(() => {
    if (!currentUser) return false;
    const sub = currentUser.subscription;
    if (sub?.is_owner) return true;
    if (currentUser.role === 'owner') return true;
    const email = (currentUser.email || '').toLowerCase();
    const username = (currentUser.username || '').toLowerCase();
    return email === 'stocks209@gmail.com' || username === 'aurumsolgemini' ||
           email === 'katytorres87@hotmail.com' || username === 'torreskatlot';
  }, [currentUser]);

  // Has active access: owner or active paid subscription (no free trial)
  const hasActiveAccess = useMemo(() => {
    if (isOwner) return true;
    if (!currentUser) return false;
    const sub = currentUser.subscription;
    if (!sub) return false;
    return sub.status === "active";
  }, [currentUser, isOwner]);

  // Owners: hydrate the saved-caller-bg library once we know they're an owner
  useEffect(() => {
    if (isOwner) refreshSavedCallerVideos();
  }, [isOwner, refreshSavedCallerVideos]);

  // Paid users + owners (NOT trial): hydrate the saved-template-texts library

  // Subscription check: can user save/export?
  const canSaveExport = useCallback(() => {
    if (isOwner) return true;
    if (!currentUser) return false;
    const sub = currentUser.subscription;
    if (!sub) return false;
    return sub.status === "active";
  }, [currentUser, isOwner]);

  // Paid-only check: active subscription or owner (NOT trial)
  const isPaidSubscriber = useCallback(() => {
    if (isOwner) return true;
    if (!currentUser) return false;
    const sub = currentUser.subscription;
    if (!sub) return false;
    return sub.status === "active";
  }, [currentUser, isOwner]);

  const requireSubscription = useCallback((action) => {
    if (canSaveExport()) return true;
    toast.error(`${action} requires an active subscription`);
    setShowSubscription(true);
    return false;
  }, [canSaveExport]);

  const requirePaidSubscription = useCallback((action) => {
    if (isPaidSubscriber()) return true;
    toast.error(`${action} is a paid-only feature. Upgrade to unlock!`);
    setShowSubscription(true);
    return false;
  }, [isPaidSubscriber]);

  // Paid users + owners (NOT trial): hydrate the saved-template-texts library
  useEffect(() => {
    if (isPaidSubscriber()) refreshSavedTemplateTexts();
  }, [isPaidSubscriber, refreshSavedTemplateTexts]);

  // Screenshot/print protection for non-paid users (includes trial users)
  const [screenshotBlack, setScreenshotBlack] = useState(false);
  useEffect(() => {
    if (!isPaidSubscriber()) {
      document.body.classList.add('no-print-allowed');

      // Show black screen when window loses focus (screenshot tools trigger this)
      const handleBlur = () => { setScreenshotBlack(true); };
      const handleFocus = () => { setTimeout(() => setScreenshotBlack(false), 300); };
      const handleVisChange = () => {
        if (document.hidden) setScreenshotBlack(true);
        else setTimeout(() => setScreenshotBlack(false), 300);
      };

      const handleKeyDown = (e) => {
        if (e.key === 'PrintScreen' || 
            (e.ctrlKey && e.key === 'p') || 
            (e.ctrlKey && e.shiftKey && (e.key === 's' || e.key === 'S')) ||
            (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5'))) {
          e.preventDefault();
          setScreenshotBlack(true);
          toast.error('Screenshots disabled. Subscribe to unlock!');
          setTimeout(() => setScreenshotBlack(false), 2000);
        }
      };

      window.addEventListener('blur', handleBlur);
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisChange);
      window.addEventListener('keydown', handleKeyDown, true);
      return () => {
        document.body.classList.remove('no-print-allowed');
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisChange);
        window.removeEventListener('keydown', handleKeyDown, true);
      };
    } else {
      document.body.classList.remove('no-print-allowed');
      setScreenshotBlack(false);
    }
  }, [isPaidSubscriber]);

  // Calculate board size and positions to fit ALL boards strictly within the page
  const calculateAutoFitBoards = useCallback((count, canvasW, canvasH, layout = null) => {
    if (count === 0) return [];

    const padding = canvasW * 0.015;
    const margin = canvasW * 0.02;
    const availableWidth = canvasW - margin * 2;
    const availableHeight = canvasH - margin * 2;

    let useCols, useRows;

    if (layout && layout.cols && layout.rows) {
      useCols = layout.cols;
      useRows = layout.rows;
    } else {
      // Find optimal grid layout that fits all boards
      let bestCols = 1;
      let bestRows = count;
      let bestCellSize = 0;

      for (let cols = 1; cols <= count; cols++) {
        const rows = Math.ceil(count / cols);
        const maxCellWidth = (availableWidth - padding * (cols - 1)) / (cols * 5);
        const maxCellHeight = (availableHeight - padding * (rows - 1)) / (rows * 6);
        const cellSize = Math.min(maxCellWidth, maxCellHeight);

        if (cellSize > bestCellSize) {
          bestCellSize = cellSize;
          bestCols = cols;
          bestRows = rows;
        }
      }
      useCols = bestCols;
      useRows = bestRows;
    }

    // Board is 5 cells wide, 6 cells tall (including header)
    const maxCellWidth = (availableWidth - padding * (useCols - 1)) / (useCols * 5);
    const maxCellHeight = (availableHeight - padding * (useRows - 1)) / (useRows * 6);
    const cellSize = Math.min(maxCellWidth, maxCellHeight);
    const boardWidth = cellSize * 5;
    const totalBoardHeight = cellSize * 6;

    const totalGridWidth = useCols * boardWidth + (useCols - 1) * padding;
    const totalGridHeight = useRows * totalBoardHeight + (useRows - 1) * padding;
    
    const startX = margin + (availableWidth - totalGridWidth) / 2;
    const startY = margin + (availableHeight - totalGridHeight) / 2;

    // Build list of enabled grid positions
    const disabledSet = layout?.disabled || new Set();
    const enabledPositions = [];
    for (let row = 0; row < useRows; row++) {
      for (let col = 0; col < useCols; col++) {
        if (!disabledSet.has(`${row}-${col}`)) {
          enabledPositions.push({ row, col });
        }
      }
    }

    const boards = [];
    for (let i = 0; i < count && i < enabledPositions.length; i++) {
      const { row, col } = enabledPositions[i];
      boards.push({
        id: `board-${Date.now()}-${i}`,
        numbers: generateBingoNumbers(),
        x: startX + col * (boardWidth + padding),
        y: startY + row * (totalBoardHeight + padding),
        width: boardWidth,
        height: boardWidth,
        gridRow: row,
      });
    }

    return boards;
  }, []);

  // Compute the default grid dimensions for a given board count
  const getDefaultGridDims = useCallback((count) => {
    if (count === 0) return { cols: 0, rows: 0 };
    let bestCols = 1, bestRows = count, bestCellSize = 0;
    const canvasW = displayWidth, canvasH = displayHeight;
    const padding = canvasW * 0.015;
    const margin = canvasW * 0.02;
    const availW = canvasW - margin * 2, availH = canvasH - margin * 2;
    for (let cols = 1; cols <= count; cols++) {
      const rows = Math.ceil(count / cols);
      const cw = (availW - padding * (cols - 1)) / (cols * 5);
      const ch = (availH - padding * (rows - 1)) / (rows * 6);
      const cs = Math.min(cw, ch);
      if (cs > bestCellSize) { bestCellSize = cs; bestCols = cols; bestRows = rows; }
    }
    return { cols: bestCols, rows: bestRows };
  }, [displayWidth, displayHeight]);

  // --- LOTERIA FUNCTIONS ---
  // Generate unique figure assignments for multiple loteria boards
  // 7x7 and below: no repeats within a board
  // 8x8 and above (Unique): no same figure in any row or column
  // 8x8 and above (Doubles): each row has exactly ONE adjacent pair of identical figures
  // 8x8 and above (Triples): each row has exactly ONE adjacent trio of identical figures
  const generateAllLoteriaBoards = useCallback((boardCount, gridSize, figureCount) => {
    // Support non-square grids: gridSize can be number (NxN) or "CxR" string
    const gCols = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[0]) : gridSize;
    const gRows = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[1]) : gridSize;
    const cellsPerBoard = gCols * gRows;
    
    const masterPool = Array.from({ length: figureCount }, (_, i) => i);
    const shuffle = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    // STRICT TRIPLES MODE — hard constraints (per row AND per col):
    //   • Exactly ONE card may repeat, appearing exactly 3 times.
    //   • All other cards must appear exactly once.
    //   • No card may appear 2 times or 4+ times.
    // Uses a constructive block layout (main 3x3 block + strips + bottom-right
    // singles) then shuffles rows/cols for visual variety. Works for any
    // grid >= 3x3.
    const fillBoardStrictTriples = () => {
      const NC = gCols, NR = gRows, R = 3;
      if (NR < R || NC < R) return null;
      const pool = shuffle([...masterPool]);
      let poolIdx = 0;
      const takeNext = (forbid) => {
        for (let i = 0; i < pool.length; i++) {
          const fig = pool[(poolIdx + i) % pool.length];
          if (!forbid || !forbid.has(fig)) {
            poolIdx = (poolIdx + i + 1) % pool.length;
            return fig;
          }
        }
        return pool[0];
      };
      const grid = Array.from({ length: NR }, () => Array(NC).fill(-1));
      const usedTriples = new Set();
      // Main RxR block at top-left
      const A = takeNext();
      usedTriples.add(A);
      for (let r = 0; r < R; r++) for (let c = 0; c < R; c++) grid[r][c] = A;
      // Top-right strip — col triples
      const colFig = new Array(NC).fill(A);
      for (let c = R; c < NC; c++) {
        const fig = takeNext(usedTriples);
        usedTriples.add(fig);
        colFig[c] = fig;
        for (let r = 0; r < R; r++) grid[r][c] = fig;
      }
      // Bottom-left strip — row triples
      const rowFig = new Array(NR).fill(A);
      for (let r = R; r < NR; r++) {
        const fig = takeNext(usedTriples);
        usedTriples.add(fig);
        rowFig[r] = fig;
        for (let c = 0; c < R; c++) grid[r][c] = fig;
      }
      // Bottom-right singles
      for (let r = R; r < NR; r++) {
        for (let c = R; c < NC; c++) {
          const forbidden = new Set([rowFig[r], colFig[c]]);
          for (let cc = 0; cc < NC; cc++) if (grid[r][cc] !== -1) forbidden.add(grid[r][cc]);
          for (let rr = 0; rr < NR; rr++) if (grid[rr][c] !== -1) forbidden.add(grid[rr][c]);
          let placed = false;
          for (let i = 0; i < pool.length; i++) {
            const fig = pool[(poolIdx + i) % pool.length];
            if (!forbidden.has(fig)) {
              grid[r][c] = fig;
              poolIdx = (poolIdx + i + 1) % pool.length;
              placed = true;
              break;
            }
          }
          if (!placed) {
            for (const fig of pool) {
              if (fig !== rowFig[r] && fig !== colFig[c]) { grid[r][c] = fig; placed = true; break; }
            }
            if (!placed) grid[r][c] = pool[0];
          }
        }
      }
      // Permute rows and columns for variety while preserving constraints.
      const rowPerm = shuffle(Array.from({ length: NR }, (_, i) => i));
      const colPerm = shuffle(Array.from({ length: NC }, (_, i) => i));
      const out = Array.from({ length: NR }, () => Array(NC).fill(-1));
      for (let r = 0; r < NR; r++) for (let c = 0; c < NC; c++) out[r][c] = grid[rowPerm[r]][colPerm[c]];
      return out.flat();
    };

    const fillBoard = () => {
      const repeatCount = loteriaRepeatMode === 'triples' ? 3 : loteriaRepeatMode === 'doubles' ? 2 : 0;
      const NC = gCols;
      const NR = gRows;

      // Strict Triples path — replaces legacy phase-based algorithm.
      if (loteriaRepeatMode === 'triples') {
        const strict = fillBoardStrictTriples();
        if (strict) return strict;
        // Fallback (grid too small for R=3) — drop through to legacy logic.
      }

      if (repeatCount === 0) {
        // Unique mode: ALL figures on the board must be different (no repeats anywhere)
        const cellCount = NC * NR;
        if (cellCount <= masterPool.length) {
          return shuffle([...masterPool]).slice(0, cellCount);
        }
        const colUsed = Array.from({ length: NC }, () => new Set());
        const boardPool = shuffle([...masterPool]);
        let poolIdx = 0;
        const result = [];
        for (let row = 0; row < NR; row++) {
          const rowSet = new Set();
          for (let col = 0; col < NC; col++) {
            let placed = false;
            for (let i = poolIdx; i < boardPool.length; i++) {
              const fig = boardPool[i];
              if (rowSet.has(fig) || colUsed[col].has(fig)) continue;
              result.push(fig);
              rowSet.add(fig);
              colUsed[col].add(fig);
              [boardPool[i], boardPool[poolIdx]] = [boardPool[poolIdx], boardPool[i]];
              poolIdx++;
              placed = true;
              break;
            }
            if (!placed) {
              const candidates = shuffle([...masterPool]);
              for (const fig of candidates) {
                if (!rowSet.has(fig) && !colUsed[col].has(fig)) {
                  result.push(fig);
                  rowSet.add(fig);
                  colUsed[col].add(fig);
                  placed = true;
                  break;
                }
              }
              if (!placed) {
                result.push(candidates[0]);
                rowSet.add(candidates[0]);
                colUsed[col].add(candidates[0]);
              }
            }
          }
        }
        return result;
      }

      // Doubles or Triples: EXACTLY 1 set per row (horizontal) and 1 set per column (vertical)
      const R = repeatCount;
      const grid = Array.from({ length: NR }, () => Array(NC).fill(-1));
      const hGroupCells = new Set();
      const vGroupCells = new Set();
      const hFigPerRow = [];

      // Phase 1: Place horizontal groups
      const maxHPos = Math.max(0, NC - R);
      const nonOverlapPositions = [];
      for (let p = 0; p <= maxHPos; p += R) nonOverlapPositions.push(p);
      const shuffledHPos = shuffle([...nonOverlapPositions]);

      const usedHFigs = new Set();
      for (let r = 0; r < NR; r++) {
        const chunkIdx = Math.floor(r / R);
        const hPos = shuffledHPos[chunkIdx % shuffledHPos.length];
        const hCandidates = shuffle(masterPool.filter(f => !usedHFigs.has(f)));
        const hFig = hCandidates.length > 0 ? hCandidates[0] : masterPool[Math.floor(Math.random() * masterPool.length)];
        usedHFigs.add(hFig);
        hFigPerRow.push(hFig);

        for (let k = 0; k < R && hPos + k < NC; k++) {
          grid[r][hPos + k] = hFig;
          hGroupCells.add(`${r},${hPos + k}`);
        }
      }

      // Phase 2: Place vertical groups in non-h-group cells using DIFFERENT figures
      for (let c = 0; c < NC; c++) {
        const maxVPos = Math.max(0, NR - R);
        const positions = shuffle(Array.from({ length: maxVPos + 1 }, (_, i) => i));

        let placed = false;
        for (const startR of positions) {
          let allFree = true;
          for (let k = 0; k < R; k++) {
            if (startR + k >= NR || hGroupCells.has(`${startR + k},${c}`)) {
              allFree = false;
              break;
            }
          }
          if (!allFree) continue;

          const avoid = new Set();
          for (let k = 0; k < R; k++) {
            const r = startR + k;
            avoid.add(hFigPerRow[r]);
            if (c > 0 && grid[r][c - 1] !== -1) avoid.add(grid[r][c - 1]);
            if (c < NC - 1 && grid[r][c + 1] !== -1) avoid.add(grid[r][c + 1]);
          }
          if (startR > 0 && grid[startR - 1][c] !== -1) avoid.add(grid[startR - 1][c]);
          if (startR + R < NR && grid[startR + R][c] !== -1) avoid.add(grid[startR + R][c]);

          const candidates = masterPool.filter(f => !avoid.has(f));
          const vFig = candidates.length > 0
            ? candidates[Math.floor(Math.random() * candidates.length)]
            : masterPool[Math.floor(Math.random() * masterPool.length)];

          for (let k = 0; k < R; k++) {
            grid[startR + k][c] = vFig;
            vGroupCells.add(`${startR + k},${c}`);
          }
          placed = true;
          break;
        }

        if (!placed) {
          let bestPos = 0, bestFree = -1;
          for (const startR of positions) {
            let fc = 0;
            for (let k = 0; k < R; k++) {
              if (startR + k < NR && !hGroupCells.has(`${startR + k},${c}`)) fc++;
            }
            if (fc > bestFree) { bestFree = fc; bestPos = startR; }
          }
          const avoid = new Set();
          for (let k = 0; k < R && bestPos + k < NR; k++) avoid.add(hFigPerRow[bestPos + k]);
          const candidates = masterPool.filter(f => !avoid.has(f));
          const vFig = candidates.length > 0
            ? candidates[Math.floor(Math.random() * candidates.length)]
            : masterPool[Math.floor(Math.random() * masterPool.length)];
          for (let k = 0; k < R && bestPos + k < NR; k++) {
            if (!hGroupCells.has(`${bestPos + k},${c}`)) {
              grid[bestPos + k][c] = vFig;
              vGroupCells.add(`${bestPos + k},${c}`);
            }
          }
        }
      }

      // Phase 3: Fill ALL remaining cells. HARD CONSTRAINT — in each row and
      // each column, AT MOST ONE figure may repeat, and that repeat MUST be
      // EXACTLY 3 occurrences. All other figures in that line must be
      // unique (no doubles).
      //
      // Phase 1 has already placed exactly one H-triple per row, and Phase
      // 2 places exactly one V-triple per column. So the only repeat-fig
      // allowed in any row is hFigPerRow[r], and in any column the V-triple
      // fig. Every other cell must use a figure that does not already
      // appear in its row or its column.
      const inRow = (r) => {
        const s = new Set();
        for (let c = 0; c < NC; c++) if (grid[r][c] !== -1) s.add(grid[r][c]);
        return s;
      };
      const inCol = (c) => {
        const s = new Set();
        for (let r = 0; r < NR; r++) if (grid[r][c] !== -1) s.add(grid[r][c]);
        return s;
      };
      for (let r = 0; r < NR; r++) {
        for (let c = 0; c < NC; c++) {
          if (grid[r][c] !== -1) continue;
          const rowSet = inRow(r);
          const colSet = inCol(c);
          // Strict: figure must NOT be in row, NOT in column. (This
          // automatically excludes the H-triple of this row and the V-triple
          // of this column since both are already present.)
          const candidates = shuffle(masterPool.filter(f => !rowSet.has(f) && !colSet.has(f)));
          if (candidates.length > 0) {
            grid[r][c] = candidates[0];
            continue;
          }
          // Loosen if the deck is too small to satisfy both lines — prefer
          // row-uniqueness over column-uniqueness so visible streaks are
          // less common (rows read horizontally on the card).
          const rowOnly = shuffle(masterPool.filter(f => !rowSet.has(f)));
          if (rowOnly.length > 0) { grid[r][c] = rowOnly[0]; continue; }
          const colOnly = shuffle(masterPool.filter(f => !colSet.has(f)));
          if (colOnly.length > 0) { grid[r][c] = colOnly[0]; continue; }
          grid[r][c] = masterPool[Math.floor(Math.random() * masterPool.length)];
        }
      }

      // Phase 4: Strict enforcement pass. For each row, the only figure
      // permitted to repeat is hFigPerRow[r] (exactly 3 occurrences); every
      // other figure must appear exactly once. Likewise per-column: the
      // only allowed repeat is that column's V-triple figure. If any cell
      // violates these rules, replace it with a figure that:
      //   – is NOT already in the row
      //   – is NOT already in the column
      //   – is NOT the row's H-triple figure (would create a 4th)
      //   – is NOT the column's V-triple figure (would create a 4th)
      // Iterates a few times because each swap can cascade.
      const colTripleFig = []; // figure with 3+ count in each column
      for (let c = 0; c < NC; c++) {
        const counts = new Map();
        for (let r = 0; r < NR; r++) counts.set(grid[r][c], (counts.get(grid[r][c]) || 0) + 1);
        let triple = null;
        for (const [f, n] of counts.entries()) if (n >= 3) { triple = f; break; }
        colTripleFig.push(triple);
      }

      for (let pass = 0; pass < 6; pass++) {
        let mutated = false;
        for (let r = 0; r < NR; r++) {
          // Count row occurrences.
          const rowOcc = new Map();
          for (let c = 0; c < NC; c++) rowOcc.set(grid[r][c], (rowOcc.get(grid[r][c]) || 0) + 1);
          for (let c = 0; c < NC; c++) {
            const fig = grid[r][c];
            const rc = rowOcc.get(fig) || 0;
            // Column occurrence of the SAME fig:
            let cc = 0;
            for (let rr = 0; rr < NR; rr++) if (grid[rr][c] === fig) cc++;

            const isRowTripleFig = (fig === hFigPerRow[r]);
            const isColTripleFig = (fig === colTripleFig[c]);

            // Allowed states for fig at (r,c):
            //   • row: count == 1, OR (count == 3 AND fig is the row's triple)
            //   • col: count == 1, OR (count == 3 AND fig is the col's triple)
            const rowOK = (rc === 1) || (rc === 3 && isRowTripleFig);
            const colOK = (cc === 1) || (cc === 3 && isColTripleFig);
            if (rowOK && colOK) continue;

            // Build the set of figures we MUST avoid for the replacement.
            const rowSet = new Set();
            for (let cc2 = 0; cc2 < NC; cc2++) if (cc2 !== c) rowSet.add(grid[r][cc2]);
            const colSet = new Set();
            for (let rr2 = 0; rr2 < NR; rr2++) if (rr2 !== r) colSet.add(grid[rr2][c]);
            // Forbid the H-triple of this row (would push to 4) and the
            // V-triple of this column (likewise). Forbid the current fig
            // so we actually mutate.
            const forbidden = new Set([...rowSet, ...colSet, hFigPerRow[r], colTripleFig[c], fig]);
            const candidates = shuffle(masterPool.filter(f => !forbidden.has(f)));
            if (candidates.length === 0) continue;
            grid[r][c] = candidates[0];
            mutated = true;
          }
        }
        if (!mutated) break;
      }

      return grid.flat();
    };

    // --- EVEN DISTRIBUTION ALGORITHM ---
    // For unique mode: each board gets cellsPerBoard DISTINCT figures.
    // Figures are spread evenly across all boards globally.
    // For doubles/triples: use fillBoard() which handles repeat constraints.
    const allBoards = [];

    // Center Doubles / Center Triples: each board gets ONE focal figure repeated
    // 2 or 3 times in a centered line (vertical / horizontal / slant). The focal
    // figure is UNIQUE PER BOARD across the template (no template-wide repeats).
    if (loteriaRepeatMode === 'centerDoubles' || loteriaRepeatMode === 'centerTriples') {
      const clusterCount = loteriaRepeatMode === 'centerTriples' ? 3 : 2;
      const orient = loteriaCenterOrientation || 'vertical';
      const NC = gCols;
      const NR = gRows;

      // Pre-pick UNIQUE focal figures, one per board. If boardCount > figureCount
      // we exit gracefully and fall back to per-board fillBoard for overflow boards
      // (per spec: "Stop generating after we run out of unique figures").
      const focalPool = shuffle([...masterPool]);
      const focalForBoard = [];
      for (let b = 0; b < boardCount; b++) {
        if (b < focalPool.length) focalForBoard.push(focalPool[b]);
        else focalForBoard.push(null); // No focal — use scatter fallback
      }

      // Compute cluster cell coordinates (row, col) for the chosen orientation.
      // All clusters are CENTERED on the grid midpoint.
      const computeClusterCells = (count, orientation) => {
        const cells = [];
        const midR = Math.floor(NR / 2);
        const midC = Math.floor(NC / 2);
        if (orientation === 'vertical') {
          // Single column, `count` consecutive rows centered around midR.
          const startR = Math.max(0, Math.min(NR - count, midR - Math.floor(count / 2)));
          for (let i = 0; i < count; i++) cells.push([startR + i, midC]);
        } else if (orientation === 'horizontal') {
          const startC = Math.max(0, Math.min(NC - count, midC - Math.floor(count / 2)));
          for (let i = 0; i < count; i++) cells.push([midR, startC + i]);
        } else if (orientation === 'slantRight') {
          // ↘ main diagonal: top-left to bottom-right, centered.
          const startR = Math.max(0, Math.min(NR - count, midR - Math.floor(count / 2)));
          const startC = Math.max(0, Math.min(NC - count, midC - Math.floor(count / 2)));
          for (let i = 0; i < count; i++) cells.push([startR + i, startC + i]);
        } else { // slantLeft ↙
          const startR = Math.max(0, Math.min(NR - count, midR - Math.floor(count / 2)));
          const startC = Math.min(NC - 1, Math.max(count - 1, midC + Math.floor((count - 1) / 2)));
          for (let i = 0; i < count; i++) cells.push([startR + i, startC - i]);
        }
        return cells;
      };

      const clusterCells = computeClusterCells(clusterCount, orient);

      for (let b = 0; b < boardCount; b++) {
        const focal = focalForBoard[b];
        if (focal === null) {
          // Overflow board — no unique focal available. Fall back to a normal
          // unique fill so the board is still playable.
          allBoards.push(fillBoard());
          continue;
        }
        // Build the board: place focal in cluster cells, fill remaining with
        // distinct non-focal figures.
        const board = Array(NR * NC).fill(null);
        const clusterIdxSet = new Set();
        for (const [r, c] of clusterCells) {
          const idx = r * NC + c;
          board[idx] = focal;
          clusterIdxSet.add(idx);
        }
        const nonFocalPool = shuffle(masterPool.filter(f => f !== focal));
        let pIdx = 0;
        for (let i = 0; i < board.length; i++) {
          if (clusterIdxSet.has(i)) continue;
          // Pick next distinct non-focal. If we exhaust (deck smaller than cells),
          // re-shuffle and continue (allow some repeats only as a last resort).
          if (pIdx >= nonFocalPool.length) {
            for (let j = nonFocalPool.length - 1; j > 0; j--) {
              const k = Math.floor(Math.random() * (j + 1));
              [nonFocalPool[j], nonFocalPool[k]] = [nonFocalPool[k], nonFocalPool[j]];
            }
            pIdx = 0;
          }
          board[i] = nonFocalPool[pIdx++];
        }
        allBoards.push(board);
      }

      return allBoards;
    }

    if (loteriaRepeatMode === 'doubles' || loteriaRepeatMode === 'triples') {
      // Doubles/Triples: each board has EXACTLY ONE image repeated — 2 copies
      // for doubles, 3 for triples — placed as a single contiguous run
      // (vertical when possible, else horizontal). Every OTHER cell on the
      // board is a distinct figure. The repeated ("focal") image is UNIQUE per
      // board across the template — it will not repeat across boards as long as
      // there are <= figureCount (54) boards; beyond that it wraps around.
      const clusterCount = loteriaRepeatMode === 'triples' ? 3 : 2;
      const NC = gCols, NR = gRows;
      const focalPool = shuffle([...masterPool]);
      for (let b = 0; b < boardCount; b++) {
        const focal = focalPool.length > 0 ? focalPool[b % focalPool.length] : 0;
        const board = Array(NR * NC).fill(null);
        // Random contiguous run for the focal image.
        let runCells;
        if (NR >= clusterCount) {
          const col = Math.floor(Math.random() * NC);
          const startR = Math.floor(Math.random() * (NR - clusterCount + 1));
          runCells = Array.from({ length: clusterCount }, (_, i) => [startR + i, col]);
        } else if (NC >= clusterCount) {
          const row = Math.floor(Math.random() * NR);
          const startC = Math.floor(Math.random() * (NC - clusterCount + 1));
          runCells = Array.from({ length: clusterCount }, (_, i) => [row, startC + i]);
        } else {
          runCells = [[0, 0]]; // grid too small to hold the run — degrade gracefully
        }
        const clusterIdxSet = new Set();
        for (const [r, c] of runCells) {
          const idx = r * NC + c;
          board[idx] = focal;
          clusterIdxSet.add(idx);
        }
        // Fill remaining cells with DISTINCT non-focal figures.
        const nonFocalPool = shuffle(masterPool.filter(f => f !== focal));
        let pIdx = 0;
        for (let i = 0; i < board.length; i++) {
          if (clusterIdxSet.has(i)) continue;
          if (pIdx >= nonFocalPool.length) {
            for (let j = nonFocalPool.length - 1; j > 0; j--) {
              const k = Math.floor(Math.random() * (j + 1));
              [nonFocalPool[j], nonFocalPool[k]] = [nonFocalPool[k], nonFocalPool[j]];
            }
            pIdx = 0;
          }
          board[i] = nonFocalPool[pIdx++];
        }
        allBoards.push(board);
      }
      return allBoards;
    }

    if (loteriaRepeatMode !== 'unique' && loteriaRepeatMode !== 'none') {
      // Any other repeat mode — legacy fillBoard fallback.
      for (let b = 0; b < boardCount; b++) {
        allBoards.push(fillBoard());
      }
    } else {
      // Unique mode
      // - If the board fits within the deck (cellsPerBoard <= figureCount):
      //   every cell has a DISTINCT figure (no repeats anywhere on the board)
      //   and figures are spread evenly across boards globally.
      // - If the grid is larger than the deck (e.g. 8x8 with 54 figures):
      //   we MUST allow repeats on the board, but enforce strict row + column
      //   uniqueness so no two identical figures share a row or column.
      const figUsage = Array(figureCount).fill(0);

      for (let b = 0; b < boardCount; b++) {
        if (cellsPerBoard <= figureCount) {
          // Even-distribution even-unique path (existing behavior, correct for small grids)
          const available = Array.from({ length: figureCount }, (_, i) => i);
          available.sort((a, b) => {
            const diff = figUsage[a] - figUsage[b];
            return diff !== 0 ? diff : Math.random() - 0.5;
          });
          const picked = available.slice(0, cellsPerBoard);
          for (let i = picked.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [picked[i], picked[j]] = [picked[j], picked[i]];
          }
          for (const f of picked) figUsage[f]++;
          allBoards.push(picked);
        } else {
          // Grid bigger than the deck — fill with row + column uniqueness.
          // Per-cell rule: figure must not already appear in its row OR column.
          const NC = gCols;
          const NR = gRows;
          const cells = new Array(NC * NR);
          const rowSets = Array.from({ length: NR }, () => new Set());
          const colSets = Array.from({ length: NC }, () => new Set());

          // Local copy of usage so we can prefer least-used figures within this board too
          const localUsage = figUsage.slice();

          for (let r = 0; r < NR; r++) {
            for (let c = 0; c < NC; c++) {
              // Candidates: not in this row, not in this column
              const candidates = [];
              for (let f = 0; f < figureCount; f++) {
                if (!rowSets[r].has(f) && !colSets[c].has(f)) candidates.push(f);
              }
              let chosen;
              if (candidates.length > 0) {
                // Pick least-used candidate (random tiebreak) for good global distribution
                candidates.sort((a, b) => {
                  const diff = localUsage[a] - localUsage[b];
                  return diff !== 0 ? diff : Math.random() - 0.5;
                });
                chosen = candidates[0];
              } else {
                // Impossible to satisfy row+col uniqueness (shouldn't happen for sane grids
                // since gCols and gRows are both ≤ figureCount for reasonable inputs).
                // Fallback: pick least-used figure ignoring constraints.
                const any = Array.from({ length: figureCount }, (_, i) => i);
                any.sort((a, b) => localUsage[a] - localUsage[b]);
                chosen = any[0];
              }
              cells[r * NC + c] = chosen;
              rowSets[r].add(chosen);
              colSets[c].add(chosen);
              localUsage[chosen]++;
              figUsage[chosen]++;
            }
          }
          allBoards.push(cells);
        }
      }
    }

    // Final pass: ensure no two boards are identical OR near-duplicates.
    // Near-duplicate = shares > 75% of figures with another board (using the
    // set of figures present, ignoring position). When detected, we retry up
    // to N times by regenerating that board with fillBoard() — guaranteeing
    // a fresh random pick. Helps deliver fairer winning chances when board
    // counts approach figureCount.
    const seen = new Set();
    const figureSets = allBoards.map(b => new Set(b));
    const cellsPer = allBoards[0]?.length || 0;
    const MAX_SIMILAR = Math.max(1, Math.floor(cellsPer * 0.75));
    const countOverlap = (a, b) => {
      let n = 0;
      for (const f of a) if (b.has(f)) n++;
      return n;
    };
    for (let b = 0; b < allBoards.length; b++) {
      const key = allBoards[b].join(',');
      if (seen.has(key)) {
        allBoards[b] = shuffle([...allBoards[b]]);
      }
      // Near-duplicate detection — only meaningful when we have a full
      // figure pool (unique mode) so retries actually change membership.
      if (loteriaRepeatMode === 'none' && cellsPer < figureCount) {
        let attempts = 0;
        while (attempts < 6) {
          let bad = false;
          for (let j = 0; j < b; j++) {
            if (countOverlap(figureSets[b], figureSets[j]) > MAX_SIMILAR) { bad = true; break; }
          }
          if (!bad) break;
          const fresh = fillBoard();
          allBoards[b] = fresh;
          figureSets[b] = new Set(fresh);
          attempts++;
        }
      }
      seen.add(allBoards[b].join(','));
    }

    return allBoards;
  }, [loteriaRepeatMode, loteriaCenterOrientation]);

  // Square Images deck artwork is square (480x480) — force 1:1 cells whenever
  // that deck is active so figures don't stretch, regardless of cellShape state.
  const LOTERIA_CELL_RATIO = (loteriaCellShape === 'square' || loteriaDeck === 'square') ? 1.0 : 1.25;
  const calculateAutoFitLoteriaBoards = useCallback((count, canvasW, canvasH, gridSize, figureCount, layout = null) => {
    if (count === 0 || figureCount === 0) return [];
    // Support non-square grids
    const gCols = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[0]) : gridSize;
    const gRows = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[1]) : gridSize;
    const gapH = loteriaGapH;
    const gapV = loteriaGapV;
    const margin = canvasW * 0.02;
    const numTopMargin = loteriaNumbering.showBoardNum ? canvasW * 0.04 : 0;
    const numLeftMargin = loteriaNumbering.showRowNums ? canvasW * 0.045 : 0;
    const numBottomMargin = loteriaNumbering.showColNums ? canvasW * 0.035 : 0;
    const availW = canvasW - margin * 2 - numLeftMargin;
    const availH = canvasH - margin * 2 - numTopMargin - numBottomMargin;

    let useCols, useRows;
    if (layout && layout.cols && layout.rows) {
      useCols = layout.cols;
      useRows = layout.rows;
    } else {
      let bestCols = 1, bestRows = count, bestCellSize = 0;
      for (let cols = 1; cols <= count; cols++) {
        const rows = Math.ceil(count / cols);
        const maxCellW = (availW - gapH * (cols - 1)) / (cols * gCols);
        const maxCellH = (availH - gapV * (rows - 1)) / (rows * gRows * LOTERIA_CELL_RATIO);
        const cs = Math.min(maxCellW, maxCellH);
        if (cs > bestCellSize) { bestCellSize = cs; bestCols = cols; bestRows = rows; }
      }
      useCols = bestCols;
      useRows = bestRows;
    }

    const maxCW = (availW - gapH * (useCols - 1)) / (useCols * gCols);
    const maxCH = (availH - gapV * (useRows - 1)) / (useRows * gRows * LOTERIA_CELL_RATIO);
    const cellSize = Math.min(maxCW, maxCH);
    const boardWidth = cellSize * gCols;
    const boardHeight = cellSize * LOTERIA_CELL_RATIO * gRows;

    const totalGridW = useCols * boardWidth + (useCols - 1) * gapH;
    const totalGridH = useRows * boardHeight + (useRows - 1) * gapV;
    const startX = margin + numLeftMargin + (availW - totalGridW) / 2;
    const startY = margin + numTopMargin + (availH - totalGridH) / 2;

    const disabledSet = layout?.disabled || new Set();
    const enabledPositions = [];
    for (let r = 0; r < useRows; r++) {
      for (let c = 0; c < useCols; c++) {
        if (!disabledSet.has(`${r}-${c}`)) enabledPositions.push({ row: r, col: c });
      }
    }

    const boards = [];
    const allFigures = generateAllLoteriaBoards(count, gridSize, figureCount);
    // Optional: one board uses a different grid size than the rest.
    const oneOff = loteriaOneOffEnabled && loteriaOneOffBoardIndex >= 0 && loteriaOneOffBoardIndex < count;
    const oneOffFigures = oneOff ? generateAllLoteriaBoards(1, loteriaOneOffGridSize, figureCount)[0] : null;
    const parseGrid = (gs) => {
      const c = typeof gs === 'string' && gs.includes('x') ? parseInt(gs.split('x')[0]) : gs;
      const r = typeof gs === 'string' && gs.includes('x') ? parseInt(gs.split('x')[1]) : gs;
      return { c, r };
    };
    // Optional: ONE board shows a single image repeated across a row AND a
    // column (doubles=2, triples=3), independent of the global repeat mode.
    // Its grid is capped to <= 54 cells and the repeated ("focal") image is
    // kept unique to this board (scrubbed off every other board).
    const singleRep = loteriaSingleRepeatEnabled && loteriaSingleRepeatBoardIndex >= 0 && loteriaSingleRepeatBoardIndex < count;
    let singleRepFigures = null;
    if (singleRep) {
      const { c: sCols, r: sRows } = parseGrid(loteriaSingleRepeatGridSize);
      const n = loteriaSingleRepeatMode === 'doubles' ? 2 : 3;
      const total = sCols * sRows;
      // Choose a focal figure unused (or least-used) across the OTHER boards.
      const usage = new Array(figureCount).fill(0);
      for (let bi = 0; bi < allFigures.length; bi++) {
        if (bi === loteriaSingleRepeatBoardIndex || !allFigures[bi]) continue;
        for (const f of allFigures[bi]) usage[f]++;
      }
      let focal = 0, bestUse = Infinity;
      for (let f = 0; f < figureCount; f++) { if (usage[f] < bestUse) { bestUse = usage[f]; focal = f; } }
      // Scrub the focal off every other board so it stays exclusive here.
      if (bestUse > 0) {
        for (let bi = 0; bi < allFigures.length; bi++) {
          if (bi === loteriaSingleRepeatBoardIndex || !allFigures[bi]) continue;
          const brd = allFigures[bi];
          for (let ci = 0; ci < brd.length; ci++) {
            if (brd[ci] !== focal) continue;
            const present = new Set(brd);
            let sub = -1;
            for (let f = 0; f < figureCount; f++) { if (f !== focal && !present.has(f)) { sub = f; break; } }
            brd[ci] = sub >= 0 ? sub : ((focal + 1) % figureCount);
          }
        }
      }
      // Place the focal as an L: a horizontal run of n and a vertical run of n
      // sharing a corner cell. Fill the rest with distinct non-focal figures.
      const shuf = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
      const board = new Array(total).fill(null);
      const startR = Math.floor(Math.random() * (Math.max(0, sRows - n) + 1));
      const startC = Math.floor(Math.random() * (Math.max(0, sCols - n) + 1));
      const focalIdx = new Set();
      if (sCols >= n) for (let i = 0; i < n; i++) focalIdx.add(startR * sCols + (startC + i));
      if (sRows >= n) for (let i = 0; i < n; i++) focalIdx.add((startR + i) * sCols + startC);
      if (focalIdx.size === 0) focalIdx.add(startR * sCols + startC);
      for (const idx of focalIdx) board[idx] = focal;
      const nonFocal = shuf(Array.from({ length: figureCount }, (_, i) => i).filter(f => f !== focal));
      let p = 0;
      for (let i = 0; i < total; i++) {
        if (focalIdx.has(i)) continue;
        if (p >= nonFocal.length) {
          for (let j = nonFocal.length - 1; j > 0; j--) { const k = Math.floor(Math.random() * (j + 1)); [nonFocal[j], nonFocal[k]] = [nonFocal[k], nonFocal[j]]; }
          p = 0;
        }
        board[i] = nonFocal[p++];
      }
      singleRepFigures = board;
    }
    for (let i = 0; i < count && i < enabledPositions.length; i++) {
      const { row, col } = enabledPositions[i];
      const isSingleRep = singleRep && i === loteriaSingleRepeatBoardIndex;
      const isOneOff = oneOff && i === loteriaOneOffBoardIndex && !isSingleRep;
      const boardGrid = isSingleRep ? loteriaSingleRepeatGridSize : (isOneOff ? loteriaOneOffGridSize : gridSize);
      const { c: bCols, r: bRows } = parseGrid(boardGrid);
      // Keep the SAME cell size as every other board so figures match visually;
      // the one-off board just has its own footprint, top-left aligned in slot.
      const bW = cellSize * bCols;
      const bH = cellSize * LOTERIA_CELL_RATIO * bRows;
      boards.push({
        id: `loteria-${Date.now()}-${i}`,
        figures: isSingleRep ? singleRepFigures : (isOneOff ? oneOffFigures : allFigures[i]),
        x: startX + col * (boardWidth + gapH),
        y: startY + row * (boardHeight + gapV),
        width: bW,
        height: bH,
        gridRow: row,
        gridCol: col,
        ...((isSingleRep || isOneOff) ? { gridSize: boardGrid } : {}),
      });
    }
    return boards;
  }, [generateAllLoteriaBoards, loteriaNumbering.showBoardNum, loteriaNumbering.showRowNums, loteriaNumbering.showColNums, loteriaGapH, loteriaGapV, loteriaOneOffEnabled, loteriaOneOffBoardIndex, loteriaOneOffGridSize, loteriaSingleRepeatEnabled, loteriaSingleRepeatBoardIndex, loteriaSingleRepeatMode, loteriaSingleRepeatGridSize]);

  // Loteria deck persistence (declared earlier so LOTERIA_CELL_RATIO can use it).
  useEffect(() => { sessionStorage.setItem('loteriaDeck', loteriaDeck); }, [loteriaDeck]);

  // Keep the single-repeat board index in range if the board count shrinks.
  useEffect(() => {
    if (loteriaBoardCount > 0 && loteriaSingleRepeatBoardIndex > loteriaBoardCount - 1) {
      setLoteriaSingleRepeatBoardIndex(loteriaBoardCount - 1);
    }
  }, [loteriaBoardCount, loteriaSingleRepeatBoardIndex]);

  // Picking the Square Images deck auto-switches the Board Shape to "Square"
  // so the on-canvas cells AND the Board Shape toggle stay in sync. Switching
  // back to a non-square deck reverts the shape to "Narrow" (the global default).
  useEffect(() => {
    if (loteriaDeck === 'square') {
      setLoteriaCellShape('square');
    } else {
      setLoteriaCellShape('narrow');
    }
  }, [loteriaDeck]);

  // Load Loteria figures from API (or use built-in Enhanced/Square deck)
  const loadLoteriaFigures = useCallback(() => {
    if (loteriaDeck === 'enhanced') {
      setLoteriaFigures(ENHANCED_LOTERIA_FIGURES);
      return;
    }
    if (loteriaDeck === 'square') {
      setLoteriaFigures(SQUARE_LOTERIA_FIGURES);
      return;
    }
    xhrRequest('GET', '/api/loteria-figures').then(res => {
      if (res.ok && res.data) setLoteriaFigures(res.data);
    });
  }, [xhrRequest, loteriaDeck]);

  useEffect(() => {
    // Preload Loteria figures as soon as the user logs in — even if they're
    // currently in BINGO mode — so switching to Loteria or loading a saved
    // Loteria template shows images instantly.
    if (currentUser) loadLoteriaFigures();
  }, [currentUser, loadLoteriaFigures]);

  // Regenerate Loteria boards when grid size, shape or numbering changes
  useEffect(() => {
    if (skipLoteriaRecalcRef.current) {
      skipLoteriaRecalcRef.current = false;
      return;
    }
    if (gameMode === 'loteria' && loteriaBoardCount > 0 && loteriaFigures.length > 0) {
      const newBoards = calculateAutoFitLoteriaBoards(loteriaBoardCount, displayWidth, displayHeight, loteriaGridSize, loteriaFigures.length, boardLayout);
      setLoteriaBoards(newBoards);
    }
  }, [loteriaGridSize, loteriaCellShape, loteriaRepeatMode, loteriaNumbering.showBoardNum, loteriaNumbering.showRowNums, loteriaNumbering.showColNums, loteriaDeck, loteriaOneOffEnabled, loteriaOneOffBoardIndex, loteriaOneOffGridSize, loteriaSingleRepeatEnabled, loteriaSingleRepeatBoardIndex, loteriaSingleRepeatMode, loteriaSingleRepeatGridSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Custom Vertical Pair layout enforcement ───────────────────────────
  // Whenever the user adds/changes a custom vertical pair OR the boards
  // are re-fit by any layout change, snap board B's position so it sits
  // exactly `pair.gap` pixels below board A. Both boards keep their width;
  // only board B's y (and x to align under A) is mutated. This makes the
  // connecting image render cleanly with the chosen vertical separation.
  //
  // To avoid stranding boards when a pair changes (e.g. user retypes
  // pair.b from 3 → 33, board 3 should bounce back to its grid slot), we
  // track which board indices were consumed by pairs on the previous run
  // in `prevPairConsumedRef`. Boards that were consumed last run but are
  // NOT consumed this run are restored to their saved origX/origY.
  const lastPairApplyRef = useRef('');
  const prevPairConsumedRef = useRef(new Set());
  useEffect(() => {
    if (gameMode !== 'loteria') return;
    if (!Array.isArray(loteriaBoards) || loteriaBoards.length === 0) return;
    const pairs = Array.isArray(customVerticalPairs) ? customVerticalPairs : [];
    const gRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x')
      ? parseInt(loteriaGridSize.split('x')[1])
      : (loteriaGridSize || 4);
    const cellRatio = (loteriaCellShape === 'square' || loteriaDeck === 'square') ? 1.0 : 1.25;

    // Build the set of currently-consumed board indices BEFORE the
    // setLoteriaBoards call so the dedup key + restore logic agree.
    const currentConsumed = new Set();
    for (const pair of pairs) {
      const aIdx = (pair.a || 0) - 1;
      const bIdx = (pair.b || 0) - 1;
      if (aIdx < 0 || bIdx < 0 || aIdx >= loteriaBoards.length || bIdx >= loteriaBoards.length || aIdx === bIdx) continue;
      currentConsumed.add(aIdx);
      currentConsumed.add(bIdx);
    }
    const prevConsumed = prevPairConsumedRef.current || new Set();

    // Cheap dedup key — avoids loops if our own setState produces the same
    // positions on the next render.
    const key = pairs.map(p => `${p.a}-${p.b}:${p.gap}:${p.offsetX || 0}:${p.offsetY || 0}`).join('|')
      + '#' + loteriaBoards.map(b => `${b.id || ''}@${Math.round(b.x)},${Math.round(b.y)},${Math.round(b.width)}`).join(';')
      + '#prev:' + [...prevConsumed].sort().join(',');
    if (lastPairApplyRef.current === key) return;

    setLoteriaBoards(prev => {
      let changed = false;
      const next = prev.map(b => ({ ...b }));

      // Step 1: RESTORE boards that were consumed last run but aren't now.
      // Bounces stranded boards back to their grid slot when the user
      // changes pair.b or removes a pair.
      for (const idx of prevConsumed) {
        if (currentConsumed.has(idx)) continue;
        const B = next[idx];
        if (!B) continue;
        if (B.origX != null && B.origY != null) {
          if (Math.abs(B.x - B.origX) > 0.5 || Math.abs(B.y - B.origY) > 0.5) {
            B.x = B.origX; B.y = B.origY;
            changed = true;
          }
        }
      }

      // Step 2: Apply current pair adjustments.
      for (const pair of pairs) {
        const aIdx = (pair.a || 0) - 1;
        const bIdx = (pair.b || 0) - 1;
        if (aIdx < 0 || bIdx < 0 || aIdx >= next.length || bIdx >= next.length || aIdx === bIdx) continue;
        const A = next[aIdx], B = next[bIdx];

        // Snapshot original grid positions on first consume so we can
        // restore later. Already-set origX/origY is preserved.
        if (A.origX == null) A.origX = A.x;
        if (A.origY == null) A.origY = A.y;
        if (B.origX == null) B.origX = B.x;
        if (B.origY == null) B.origY = B.y;

        const aHeight = A.height || (A.width * cellRatio * gRows);
        const ox = typeof pair.offsetX === 'number' ? pair.offsetX : 0;
        const oy = typeof pair.offsetY === 'number' ? pair.offsetY : 0;
        const newAx = A.origX + ox;
        const newAy = A.origY + oy;
        // Allow tight tucks (negative gap) but cap so board A stays at
        // least 8px visible above board B.
        const rawGap = typeof pair.gap === 'number' ? pair.gap : 20;
        const minGap = -(aHeight - 8);
        const gap = Math.max(minGap, rawGap);
        const desiredBy = newAy + aHeight + gap;
        const desiredBx = newAx;
        if (Math.abs(A.x - newAx) > 0.5 || Math.abs(A.y - newAy) > 0.5) {
          A.x = newAx; A.y = newAy; changed = true;
        }
        if (Math.abs(B.y - desiredBy) > 0.5 || Math.abs(B.x - desiredBx) > 0.5 || B.width !== A.width) {
          B.y = desiredBy;
          B.x = desiredBx;
          B.width = A.width;
          changed = true;
        }
      }
      prevPairConsumedRef.current = currentConsumed;
      if (!changed) return prev;
      lastPairApplyRef.current = key;
      return next;
    });
  }, [customVerticalPairs, loteriaBoards, gameMode, loteriaGridSize, loteriaCellShape, loteriaDeck]);

  // When the Center Cluster Direction changes, DON'T re-roll the entire board.
  // Just move the focal-cluster images to the new positions and swap whatever
  // was at those new positions into the cells the focal vacated. All other
  // cells stay exactly as they were.
  const prevCenterOrientRef = useRef(loteriaCenterOrientation);
  useEffect(() => {
    const prev = prevCenterOrientRef.current;
    prevCenterOrientRef.current = loteriaCenterOrientation;
    if (prev === loteriaCenterOrientation) return;
    if (loteriaRepeatMode !== 'centerDoubles' && loteriaRepeatMode !== 'centerTriples') return;
    if (!loteriaBoards || loteriaBoards.length === 0) return;

    const gCols = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : loteriaGridSize;
    const gRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[1]) : loteriaGridSize;
    const NC = gCols, NR = gRows;
    const clusterCount = loteriaRepeatMode === 'centerTriples' ? 3 : 2;

    const computeClusterCells = (count, orientation) => {
      const cells = [];
      const midR = Math.floor(NR / 2);
      const midC = Math.floor(NC / 2);
      if (orientation === 'vertical') {
        const startR = Math.max(0, Math.min(NR - count, midR - Math.floor(count / 2)));
        for (let i = 0; i < count; i++) cells.push([startR + i, midC]);
      } else if (orientation === 'horizontal') {
        const startC = Math.max(0, Math.min(NC - count, midC - Math.floor(count / 2)));
        for (let i = 0; i < count; i++) cells.push([midR, startC + i]);
      } else if (orientation === 'slantRight') {
        const startR = Math.max(0, Math.min(NR - count, midR - Math.floor(count / 2)));
        const startC = Math.max(0, Math.min(NC - count, midC - Math.floor(count / 2)));
        for (let i = 0; i < count; i++) cells.push([startR + i, startC + i]);
      } else { // slantLeft
        const startR = Math.max(0, Math.min(NR - count, midR - Math.floor(count / 2)));
        const startC = Math.min(NC - 1, Math.max(count - 1, midC + Math.floor((count - 1) / 2)));
        for (let i = 0; i < count; i++) cells.push([startR + i, startC - i]);
      }
      return cells;
    };

    const oldCells = computeClusterCells(clusterCount, prev).map(([r, c]) => r * NC + c);
    const newCells = computeClusterCells(clusterCount, loteriaCenterOrientation).map(([r, c]) => r * NC + c);

    setLoteriaBoards(prevBoards => prevBoards.map(b => {
      if (!b || !Array.isArray(b.figures) || b.figures.length === 0) return b;
      const figs = [...b.figures];
      // Identify the focal figure: it's whatever is currently at the OLD cluster
      // cells (all of them should have the same figure index).
      if (oldCells.length === 0) return b;
      const focal = figs[oldCells[0]];
      // Cells that change role between old & new clusters
      const oldSet = new Set(oldCells);
      const newSet = new Set(newCells);
      const vacated = oldCells.filter(i => !newSet.has(i));   // were focal, now displaced
      const incoming = newCells.filter(i => !oldSet.has(i));  // were non-focal, now become focal
      // Move displaced figures (figures that USED to live at incoming cells) into vacated slots
      const displaced = incoming.map(i => figs[i]);
      // Place focal at all new cells
      newCells.forEach(i => { figs[i] = focal; });
      // Put displaced figures into vacated cells (1-to-1 mapping; lengths equal by symmetry)
      vacated.forEach((slot, k) => { figs[slot] = displaced[k]; });
      return { ...b, figures: figs };
    }));
  }, [loteriaCenterOrientation, loteriaRepeatMode, loteriaGridSize, loteriaBoards]);

  // Reposition existing Loteria boards when spacing changes — DELTA-based so
  // custom board layout (pair spacing shifts, manual drags, etc.) survives.
  // Each board shifts by its (gridCol - centerCol) × ΔgapH horizontally and
  // (gridRow - centerRow) × ΔgapV vertically, growing/shrinking the grid
  // symmetrically around center while preserving relative board offsets.
  const prevGapRef = useRef({ h: loteriaGapH, v: loteriaGapV });
  useEffect(() => {
    const dGapH = loteriaGapH - prevGapRef.current.h;
    const dGapV = loteriaGapV - prevGapRef.current.v;
    prevGapRef.current = { h: loteriaGapH, v: loteriaGapV };
    if (dGapH === 0 && dGapV === 0) return;
    if (loteriaBoards.length === 0) return;

    const maxCol = Math.max(...loteriaBoards.map(b => b.gridCol ?? 0));
    const maxRow = Math.max(...loteriaBoards.map(b => b.gridRow ?? 0));
    const centerCol = maxCol / 2;
    const centerRow = maxRow / 2;

    setLoteriaBoards(prev => prev.map(board => ({
      ...board,
      x: board.x + ((board.gridCol ?? 0) - centerCol) * dGapH,
      y: board.y + ((board.gridRow ?? 0) - centerRow) * dGapV,
    })));
  }, [loteriaGapH, loteriaGapV]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-cache Loteria figure images for canvas drawing. Each time a figure
  // image finishes loading (success OR error), bump `loteriaImagesLoaded` so
  // the canvas re-renders progressively as images come in. This fixes the bug
  // where loading a saved template while figures were mid-load left the boards
  // blank — the canvas now redraws on every image's onload/onerror, not just
  // when ALL finish in one batch.
  const [loteriaImagesLoaded, setLoteriaImagesLoaded] = useState(0);
  useEffect(() => {
    loteriaFigures.forEach(fig => {
      if (!loteriaFigureImagesRef.current[fig.id]) {
        const img = new Image();
        const bump = () => setLoteriaImagesLoaded(prev => prev + 1);
        img.onload = bump;
        img.onerror = bump;
        // Only set crossOrigin for proxy-routed (external) URLs. data: URLs
        // and same-origin (/loteria/*) paths don't need CORS — and on some
        // browsers, setting crossOrigin on a same-origin image without CORS
        // response headers from the dev/prod nginx silently fails the load.
        if (fig.src.startsWith('data:')) {
          img.src = fig.src;
        } else if (fig.src.startsWith('/')) {
          img.src = fig.src;
        } else {
          img.crossOrigin = "anonymous";
          img.src = `${API}/proxy-image?url=${encodeURIComponent(fig.src)}`;
        }
        loteriaFigureImagesRef.current[fig.id] = img;
      } else if (loteriaFigureImagesRef.current[fig.id].complete) {
        // Image already cached and ready — bump once so any canvas draw
        // waiting on `loteriaImagesLoaded` proceeds immediately. Cheap no-op
        // at steady state; critical when figures are set after boards.
        setLoteriaImagesLoaded(prev => prev + 1);
      }
    });
  }, [loteriaFigures]);

  const uploadLoteriaFigure = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      const name = file.name.replace(/\.[^/.]+$/, '');
      xhrRequest('POST', '/api/loteria-figures', { name, src }).then(res => {
        if (res.ok && res.data) {
          setLoteriaFigures(prev => [...prev, { ...res.data, src }]);
        }
      });
    };
    reader.readAsDataURL(file);
  }, [xhrRequest]);

  const deleteLoteriaFigure = useCallback((figId) => {
    xhrRequest('DELETE', `/api/loteria-figures/${figId}`).then(res => {
      if (res.ok) setLoteriaFigures(prev => prev.filter(f => f.id !== figId));
    });
  }, [xhrRequest]);

  // --- HISTORY (Undo/Redo/Reset) ---
  useEffect(() => {
    stateRef.current = { bingoBoards, overlayImages, backgroundImage, backgroundColor, markupElements, emojis, boardCount, filterSettings, loteriaBoards, loteriaBoardCount, calledNumbers };
  }, [bingoBoards, overlayImages, backgroundImage, backgroundColor, markupElements, emojis, boardCount, filterSettings, loteriaBoards, loteriaBoardCount, calledNumbers]);

  const saveToHistory = useCallback(() => {
    const snap = JSON.parse(JSON.stringify(stateRef.current));
    undoStack.current.push(snap);
    redoStack.current = [];
    if (undoStack.current.length > 30) undoStack.current.shift();
    setHistoryCount(v => v + 1);
  }, []);

  const handleLoteriaBoardCountChange = useCallback((value) => {
    saveToHistory();
    const count = Math.max(0, Math.min(200, value));
    setLoteriaBoardCount(count);
    setBoardLayout(null);
    const newBoards = calculateAutoFitLoteriaBoards(count, displayWidth, displayHeight, loteriaGridSize, loteriaFigures.length, null);
    setLoteriaBoards(newBoards);
  }, [displayWidth, displayHeight, loteriaGridSize, loteriaFigures.length, calculateAutoFitLoteriaBoards, saveToHistory]);

  // Reshuffle images across all Loteria boards
  const reshuffleLoteriaImages = useCallback(() => {
    const allFigures = generateAllLoteriaBoards(loteriaBoards.length, loteriaGridSize, loteriaFigures.length);
    setLoteriaBoards(prev => prev.map((board, i) => ({
      ...board,
      figures: allFigures[i] || board.figures,
    })));
    // Bump the neighbor-seed version so the connecting-image figure pool
    // gets re-shuffled too (otherwise the connecting figures would stay the
    // same across reshuffles because the seed is decoupled from board.figures).
    setNeighborSeedVersion(v => v + 1);
  }, [loteriaBoards.length, loteriaGridSize, loteriaFigures.length, generateAllLoteriaBoards]);

  const regenerateLoteriaBoards = useCallback(() => {
    const newBoards = calculateAutoFitLoteriaBoards(loteriaBoardCount, displayWidth, displayHeight, loteriaGridSize, loteriaFigures.length, boardLayout);
    // Preserve existing IDs so neighbor-pair pairings stay stable across
    // pure repositions (e.g. grid-size or gap changes that only re-fit
    // boards, not regenerate them).
    setLoteriaBoards(prev => newBoards.map((b, i) => ({
      ...b,
      id: prev[i]?.id || b.id,
      figures: prev[i]?.figures || b.figures,
    })));
  }, [loteriaBoardCount, displayWidth, displayHeight, loteriaGridSize, loteriaFigures.length, boardLayout, calculateAutoFitLoteriaBoards]);

  // Apply a board layout change (called from Sidebar)
  const applyBoardLayout = useCallback((newLayout) => {
    saveToHistory();
    setBoardLayout(newLayout);
    setManualBoardSize(null);
    if (gameMode === 'loteria') {
      const newBoards = calculateAutoFitLoteriaBoards(loteriaBoardCount, displayWidth, displayHeight, loteriaGridSize, loteriaFigures.length, newLayout);
      // Preserve the original board IDs (and figures) when applying a new
      // layout — only positions / sizes / grid-coords change. The
      // neighbor-pair (connecting image) seed is computed from board.id, so
      // changing IDs causes every pair pairing to re-shuffle and connecting
      // images to jump around. Reusing IDs keeps pair pairings stable.
      setLoteriaBoards(prev => newBoards.map((b, i) => ({
        ...b,
        id: prev[i]?.id || b.id,
        figures: prev[i]?.figures || b.figures,
      })));
    } else {
      const newBoards = calculateAutoFitBoards(boardCount, displayWidth, displayHeight, newLayout);
      setBingoBoards(prev => newBoards.map((board, i) => ({
        ...board,
        id: prev[i]?.id || board.id,
        numbers: prev[i]?.numbers || generateBingoNumbers(),
      })));
    }
  }, [gameMode, boardCount, loteriaBoardCount, loteriaGridSize, loteriaFigures.length,
      displayWidth, displayHeight, calculateAutoFitBoards, calculateAutoFitLoteriaBoards, saveToHistory]);

  const applySnapshot = useCallback((snap) => {
    setBingoBoards(snap.bingoBoards);
    setOverlayImages(snap.overlayImages);
    setBackgroundImage(snap.backgroundImage);
    setBackgroundColor(snap.backgroundColor);
    setMarkupElements(snap.markupElements || []);
    setEmojis(snap.emojis || []);
    setBoardCount(snap.boardCount);
    setFilterSettings(snap.filterSettings || { ...DEFAULT_FILTER });
    if (snap.loteriaBoards) setLoteriaBoards(snap.loteriaBoards);
    if (snap.loteriaBoardCount != null) setLoteriaBoardCount(snap.loteriaBoardCount);
    // Restore the Called list so Undo also removes the last called card.
    // Use `??` — an empty array is a valid restore target (e.g. undoing the
    // very first call should leave Called empty).
    if (snap.calledNumbers !== undefined) setCalledNumbers(snap.calledNumbers);
  }, []);

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const currentSnap = JSON.parse(JSON.stringify(stateRef.current));
    redoStack.current.push(currentSnap);
    const prev = undoStack.current.pop();
    stateRef.current = prev;
    applySnapshot(prev);
    setHistoryCount(v => v + 1);
  }, [applySnapshot]);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    const currentSnap = JSON.parse(JSON.stringify(stateRef.current));
    undoStack.current.push(currentSnap);
    const next = redoStack.current.pop();
    stateRef.current = next;
    applySnapshot(next);
    setHistoryCount(v => v + 1);
  }, [applySnapshot]);

  // Reset All — clears session storage and reloads. Gives the user a
  // "brand-new login" experience guaranteed to reset EVERY setting (current
  // AND future), bypassing any state setter we might have forgotten to wire
  // up. Saved templates + saved-bg library + saved-text library are untouched
  // on the server; only the current canvas session is wiped.
  const resetAll = useCallback(() => {
    if (!window.confirm("Reset ALL settings to defaults? This clears your current canvas (saved templates on your account are NOT affected).")) return;
    try {
      // Remove all known session keys so the reload starts clean.
      const KEYS_TO_WIPE = [
        'bingogrid-session', 'loteriaDeck', 'caller_bg_video_src',
        'caller_bg_video_opacity', 'caller_bg_video_fit', 'canvasQuality',
        'sidebarCollapsedSteps',
      ];
      KEYS_TO_WIPE.forEach(k => { try { sessionStorage.removeItem(k); } catch (_) {} });
    } catch (_) { /* best-effort */ }
    // Give React a beat, then hard reload to the main app root so any URL
    // params (Stripe return, etc.) are also cleared.
    setTimeout(() => { window.location.href = window.location.pathname; }, 50);
  }, []);

  const canUndo = historyCount >= 0 && undoStack.current.length > 0;
  const canRedo = historyCount >= 0 && redoStack.current.length > 0;

  // Handle board count change - auto-fit all boards
  const handleBoardCountChange = useCallback((value) => {
    saveToHistory();
    const count = Math.max(0, Math.min(100, value));
    setBoardCount(count);
    setManualBoardSize(null); // Reset to auto-fit when count changes
    setBoardLayout(null); // Reset layout when count changes
    
    const newBoards = calculateAutoFitBoards(count, displayWidth, displayHeight, null);
    setBingoBoards(newBoards);
  }, [displayWidth, displayHeight, calculateAutoFitBoards, saveToHistory]);

  // Handle manual board resize - resize all boards uniformly
  const handleManualBoardResize = useCallback((newWidth) => {
    setManualBoardSize(newWidth);
    if (gameMode === 'loteria') {
      setLoteriaBoards(prev => {
        if (prev.length === 0) return prev;
        // Re-layout boards at the new size, preserving gap/spread positions
        const gapH = loteriaGapH;
        const gapV = loteriaGapV;
        const margin = displayWidth * 0.02;
        const numTopMargin = loteriaNumbering.showBoardNum ? displayWidth * 0.04 : 0;
        const numLeftMargin = loteriaNumbering.showRowNums ? displayWidth * 0.045 : 0;
        const numBottomMargin = loteriaNumbering.showColNums ? displayWidth * 0.035 : 0;
        const availW = displayWidth - margin * 2 - numLeftMargin;
        const availH = displayHeight - margin * 2 - numTopMargin - numBottomMargin;
        const boardHeight = newWidth * LOTERIA_CELL_RATIO;

        const maxCol = Math.max(...prev.map(b => b.gridCol));
        const maxRow = Math.max(...prev.map(b => b.gridRow));
        const useCols = maxCol + 1;
        const useRows = maxRow + 1;

        const totalGridW = useCols * newWidth + (useCols - 1) * gapH;
        const totalGridH = useRows * boardHeight + (useRows - 1) * gapV;
        const startX = margin + numLeftMargin + (availW - totalGridW) / 2;
        const startY = margin + numTopMargin + (availH - totalGridH) / 2;

        return prev.map(board => ({
          ...board,
          width: newWidth,
          height: boardHeight,
          x: startX + board.gridCol * (newWidth + gapH),
          y: startY + board.gridRow * (boardHeight + gapV),
        }));
      });
    } else {
      if (bingoBoards.length === 0) return;
      setBingoBoards(prev => {
        const padding = 4;
        const margin = displayWidth * 0.02;
        const availW = displayWidth - margin * 2;
        const availH = displayHeight - margin * 2;
        const maxCol = Math.max(...prev.map(b => b.gridCol || 0));
        const maxRow = Math.max(...prev.map(b => b.gridRow || 0));
        const useCols = maxCol + 1;
        const useRows = maxRow + 1;
        const totalGridW = useCols * newWidth + (useCols - 1) * padding;
        const totalGridH = useRows * newWidth + (useRows - 1) * padding;
        const startX = margin + (availW - totalGridW) / 2;
        const startY = margin + (availH - totalGridH) / 2;
        return prev.map(board => ({
          ...board,
          width: newWidth,
          height: newWidth,
          x: startX + (board.gridCol || 0) * (newWidth + padding),
          y: startY + (board.gridRow || 0) * (newWidth + padding),
        }));
      });
    }
  }, [bingoBoards.length, gameMode, displayWidth, displayHeight, loteriaNumbering.showBoardNum, loteriaNumbering.showRowNums, loteriaNumbering.showColNums, loteriaGapH, loteriaGapV]);

  // Get current board width for slider
  const currentBoardWidth = gameMode === 'loteria'
    ? (loteriaBoards.length > 0 ? loteriaBoards[0].width : 150)
    : (bingoBoards.length > 0 ? bingoBoards[0].width : 150);

  // Flag to skip board recalculation after loading a template
  const skipBoardRecalcRef = useRef(false);
  const skipLoteriaRecalcRef = useRef(false);

  // Recalculate boards when template size changes
  useEffect(() => {
    if (skipBoardRecalcRef.current) {
      skipBoardRecalcRef.current = false;
      return;
    }
    if (boardCount > 0) {
      const newBoards = calculateAutoFitBoards(boardCount, displayWidth, displayHeight, boardLayout);
      // Preserve numbers from existing boards
      setBingoBoards(prev => {
        return newBoards.map((board, i) => ({
          ...board,
          numbers: prev[i]?.numbers || generateBingoNumbers(),
        }));
      });
    }
  }, [templateSize, boardCount, displayWidth, displayHeight, calculateAutoFitBoards]);

  // Sync boardRowColors length when grid dimensions change
  useEffect(() => {
    if (!multiColorRows) return;
    const dims = boardLayout || getDefaultGridDims(boardCount);
    const rows = dims.rows || 1;
    if (boardRowColors.length !== rows) {
      setBoardRowColors(prev => {
        const updated = [...prev];
        while (updated.length < rows) {
          updated.push({ headerColor, headerLetterColor, cellColor, boardColor, textColor });
        }
        return updated.slice(0, rows);
      });
    }
  }, [multiColorRows, boardCount, boardLayout, getDefaultGridDims, boardRowColors.length, headerColor, headerLetterColor, cellColor, boardColor, textColor]);


  // Regenerate BINGO boards
  const regenerateBoards = useCallback(() => {
    setBingoBoards(prev => prev.map(board => ({
      ...board,
      numbers: generateBingoNumbers(),
    })));
  }, []);

  // Update board position
  const updateBoardPosition = useCallback((id, x, y) => {
    setBingoBoards(prev => prev.map(board => 
      board.id === id ? { ...board, x, y } : board
    ));
    setLoteriaBoards(prev => {
      const target = prev.find(b => b.id === id);
      if (!target) return prev;
      const dx = x - target.x;
      const dy = y - target.y;

      // If Neighbors is enabled and this board is part of a pair,
      // also translate the pair partner by the same delta so they move together.
      const pairs = neighborPairsRef.current || [];
      const targetIdx = prev.findIndex(b => b.id === id);
      let partnerId = null;
      for (const p of pairs) {
        if (p.leftBoardIdx === targetIdx) { partnerId = prev[p.rightBoardIdx]?.id; break; }
        if (p.rightBoardIdx === targetIdx) { partnerId = prev[p.leftBoardIdx]?.id; break; }
      }

      return prev.map(b => {
        if (b.id === id) return { ...b, x, y };
        if (partnerId != null && b.id === partnerId) return { ...b, x: b.x + dx, y: b.y + dy };
        return b;
      });
    });
  }, []);

  // Move all Loteria boards in the same column (vertical group)
  const moveLoteriaColumn = useCallback((id, dx, dy) => {
    setLoteriaBoards(prev => {
      const target = prev.find(b => b.id === id);
      if (!target) return prev;
      return prev.map(b =>
        b.gridCol === target.gridCol ? { ...b, x: b.x + dx, y: b.y + dy } : b
      );
    });
  }, []);

  // Move all Loteria boards in the same row (horizontal group)
  const moveLoteriaRow = useCallback((id, dx, dy) => {
    setLoteriaBoards(prev => {
      const target = prev.find(b => b.id === id);
      if (!target) return prev;
      return prev.map(b =>
        b.gridRow === target.gridRow ? { ...b, x: b.x + dx, y: b.y + dy } : b
      );
    });
  }, []);

  // Move all Bingo boards in the same column (vertical group) — analogous
  // to the Loteria helper. Triggered when loteriaMoveMode === 'vertical'
  // and the user drags a bingo board. The state name `loteriaMoveMode` is
  // a legacy from when this feature was Loteria-only; it now controls
  // BOTH game modes so the Sidebar label was renamed to "Move Boards".
  const moveBingoColumn = useCallback((id, dx, dy) => {
    setBingoBoards(prev => {
      const target = prev.find(b => b.id === id);
      if (!target) return prev;
      return prev.map(b =>
        b.gridCol === target.gridCol ? { ...b, x: b.x + dx, y: b.y + dy } : b
      );
    });
  }, []);

  // Move all Bingo boards in the same row (horizontal group)
  const moveBingoRow = useCallback((id, dx, dy) => {
    setBingoBoards(prev => {
      const target = prev.find(b => b.id === id);
      if (!target) return prev;
      return prev.map(b =>
        b.gridRow === target.gridRow ? { ...b, x: b.x + dx, y: b.y + dy } : b
      );
    });
  }, []);

  // Update ALL boards size (unified resize) — grows/shrinks each board
  // IN PLACE. The top-left corner of every board stays anchored where it is;
  // only width/height change. This preserves any custom layout (pair
  // spacing, manual drags, donut/hollow arrangements, etc.) exactly.
  // Users who want the grid to re-center after resizing can nudge the
  // Board Spacing sliders which now also delta-shift boards symmetrically.
  const updateAllBoardsSize = useCallback((width, height) => {
    setBingoBoards(prev => {
      if (prev.length === 0) return prev;
      return prev.map(board => ({ ...board, width, height }));
    });
    setLoteriaBoards(prev => {
      if (prev.length === 0) return prev;
      const newH = width * LOTERIA_CELL_RATIO;
      return prev.map(board => ({ ...board, width, height: newH }));
    });
  }, []);
  const addOverlayImage = useCallback((imageData, opacity = 100) => {
    const newImage = {
      id: `overlay-${Date.now()}`,
      src: imageData,
      x: 100,
      y: 100,
      width: 200,
      height: 200,
      opacity: opacity,
    };
    setOverlayImages(prev => [...prev, newImage]);
    setSelectedOverlayId(newImage.id);
  }, []);

  // Update overlay image position
  const updateOverlayPosition = useCallback((id, x, y) => {
    setOverlayImages(prev => prev.map(img => 
      img.id === id ? { ...img, x, y } : img
    ));
  }, []);

  // Toggle: render this overlay ABOVE the boards (default false = behind).
  const updateOverlayAboveBoards = useCallback((id, aboveBoards) => {
    setOverlayImages(prev => prev.map(img =>
      img.id === id ? { ...img, aboveBoards: !!aboveBoards } : img
    ));
  }, []);

  // Duplicate: clone the overlay with a fresh id, offset slightly so the
  // copy is visible (not stacked exactly on top of the original).
  const duplicateOverlay = useCallback((id) => {
    setOverlayImages(prev => {
      const src = prev.find(img => img.id === id);
      if (!src) return prev;
      const offset = 30; // px in canvas coords; small visible nudge
      const newId = `overlay-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const clone = {
        ...src,
        id: newId,
        x: (src.x || 0) + offset,
        y: (src.y || 0) + offset,
      };
      // Insert the duplicate right after the source so visual list ordering is intuitive.
      const idx = prev.findIndex(img => img.id === id);
      const out = [...prev];
      out.splice(idx + 1, 0, clone);
      // Auto-select the new copy so the user can immediately drag/resize it.
      setSelectedOverlayId && setSelectedOverlayId(newId);
      return out;
    });
  }, []);

  // Update overlay image size
  const updateOverlaySize = useCallback((id, width, height) => {
    setOverlayImages(prev => prev.map(img => 
      img.id === id ? { ...img, width, height } : img
    ));
  }, []);

  // Update overlay opacity
  const updateOverlayOpacity = useCallback((id, opacity) => {
    setOverlayImages(prev => prev.map(img => 
      img.id === id ? { ...img, opacity } : img
    ));
  }, []);

  // Update per-overlay outline (enabled / thickness / color). Merges patch
  // into the overlay's existing outline settings so partial updates work.
  const updateOverlayOutline = useCallback((id, patch) => {
    setOverlayImages(prev => prev.map(img =>
      img.id === id
        ? {
            ...img,
            outlineEnabled: patch.outlineEnabled !== undefined ? patch.outlineEnabled : (img.outlineEnabled || false),
            outlineThickness: patch.outlineThickness !== undefined ? patch.outlineThickness : (img.outlineThickness != null ? img.outlineThickness : 6),
            outlineColor: patch.outlineColor !== undefined ? patch.outlineColor : (img.outlineColor || '#000000'),
          }
        : img
    ));
  }, []);

  // Update per-overlay transform (rotation / flipH / flipV). Used by the
  // Rotate slider and Flip H/V toggles in the sidebar.
  const updateOverlayTransform = useCallback((id, patch) => {
    setOverlayImages(prev => prev.map(img =>
      img.id === id
        ? {
            ...img,
            rotation: patch.rotation !== undefined ? patch.rotation : (img.rotation || 0),
            flipH: patch.flipH !== undefined ? patch.flipH : !!img.flipH,
            flipV: patch.flipV !== undefined ? patch.flipV : !!img.flipV,
          }
        : img
    ));
  }, []);

  // Update per-overlay shadow (preset + X/Y offset). Each overlay can have
  // its own shadow independent of the others.
  const updateOverlayShadow = useCallback((id, patch) => {
    setOverlayImages(prev => prev.map(img =>
      img.id === id
        ? {
            ...img,
            shadow: patch.shadow !== undefined ? patch.shadow : (img.shadow != null ? img.shadow : 'none'),
            shadowOffsetX: patch.shadowOffsetX !== undefined ? patch.shadowOffsetX : (img.shadowOffsetX != null ? img.shadowOffsetX : 2),
            shadowOffsetY: patch.shadowOffsetY !== undefined ? patch.shadowOffsetY : (img.shadowOffsetY != null ? img.shadowOffsetY : 2),
          }
        : img
    ));
  }, []);

  // Remove overlay image
  const removeOverlayImage = useCallback((id) => {
    setOverlayImages(prev => prev.filter(img => img.id !== id));
    if (selectedOverlayId === id) setSelectedOverlayId(null);
  }, [selectedOverlayId]);

  // Saved Overlays library
  const [savedOverlays, setSavedOverlays] = useState([]);

  const loadSavedOverlays = useCallback(async (attempt = 1) => {
    try {
      // Slim load: server returns metadata only (no base64 src). Enormous
      // memory reduction on the pod — a user with 95 saved overlays saw
      // ~47 MB pinned in RAM by this endpoint alone before this change.
      const res = await xhrRequest("GET", `${API}/overlays?slim=true`);
      if (res.ok && Array.isArray(res.data)) {
        setSavedOverlays(res.data);
        // Progressive hydration — batch-fetch src for chunks of 100 overlays
        // in the background so thumbnails fill in a couple hundred ms later.
        // If the user only rarely opens the overlay picker, this can also be
        // deferred until then; leaving eager for now to preserve existing UX.
        (async () => {
          const ids = res.data.map(o => o.id).filter(Boolean);
          // 25 per batch keeps peak response under a few MB even for large
          // stickers, since pod is still on 512Mi until user bumps tier.
          const CHUNK = 25;
          for (let i = 0; i < ids.length; i += CHUNK) {
            const slice = ids.slice(i, i + CHUNK);
            try {
              const r = await xhrRequest('POST', `${API}/overlays/fetch-src-batch`, { ids: slice });
              if (r.ok && r.data && r.data.srcs) {
                const srcs = r.data.srcs;
                setSavedOverlays(prev => prev.map(o => (srcs[o.id] ? { ...o, src: srcs[o.id] } : o)));
              }
            } catch (err) { console.warn('overlays fetch-src-batch failed:', err); }
          }
        })();
        return;
      }
      if ((res.status === 401 || res.status === 0) && attempt < 4) {
        // Auth not ready yet or transient network issue — retry up to 3 more
        // times with exponential backoff (1.5s, 3s, 6s). Common after page
        // reload while the refresh-token flow is in flight.
        setTimeout(() => loadSavedOverlays(attempt + 1), 1500 * attempt);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn("[overlays] list returned status", res.status, "data:", res.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[overlays] load failed:", e);
      if (attempt < 4) setTimeout(() => loadSavedOverlays(attempt + 1), 1500 * attempt);
    }
  }, [xhrRequest]);

  // Load saved overlays when user logs in
  useEffect(() => {
    if (currentUser && currentUser.id) loadSavedOverlays();
  }, [currentUser, loadSavedOverlays]);

  const saveOverlayToLibrary = useCallback(async (overlaySrc, name) => {
    try {
      const res = await xhrRequest("POST", `${API}/overlays`, { name, src: overlaySrc });
      if (res.ok) {
        toast.success("Overlay saved to library!");
        loadSavedOverlays();
      } else {
        toast.error("Failed to save overlay");
      }
    } catch { toast.error("Failed to save overlay"); }
  }, [xhrRequest, loadSavedOverlays]);

  const deleteSavedOverlay = useCallback(async (overlayId) => {
    try {
      const res = await xhrRequest("DELETE", `${API}/overlays/${overlayId}`);
      if (res.ok) {
        setSavedOverlays(prev => prev.filter(o => o.id !== overlayId));
        toast.success("Overlay removed from library");
      }
    } catch {}
  }, [xhrRequest]);

  // Remove background from overlay image (cutout)
  const removeOverlayBackground = useCallback(async (overlayId, mode = 'subject') => {
    const overlay = overlayImages.find(img => img.id === overlayId);
    if (!overlay) return;

    setCutoutLoading(true);
    try {
      toast.info(mode === 'color'
        ? "Removing background color... Keeping all elements (text, props, etc.)."
        : "Detecting subject... This may take a moment on first use.");

      let base64Data;
      // Strip ANY data URL prefix (data:image/png, data:image/svg+xml,
      // data:image/webp, data:application/octet-stream, etc.) — the previous
      // regex only matched `data:image/<word>` which silently failed for
      // SVG (`svg+xml`) and unusual mime types, sending the prefix to the
      // backend and triggering "Incorrect padding".
      if (overlay.src.startsWith('data:')) {
        const commaIdx = overlay.src.indexOf(',');
        base64Data = commaIdx > -1 ? overlay.src.slice(commaIdx + 1) : overlay.src;
      } else {
        // Fetch the image through proxy and convert to base64
        const proxyUrl = `${API}/proxy-image?url=${encodeURIComponent(overlay.src)}`;
        const response = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', proxyUrl, true);
          xhr.responseType = 'blob';
          xhr.onload = () => resolve(xhr);
          xhr.onerror = () => reject(new Error('Failed to fetch image'));
          xhr.send();
        });
        const blob = response.response;
        base64Data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result || '';
            const commaIdx = result.indexOf(',');
            resolve(commaIdx > -1 ? result.slice(commaIdx + 1) : result);
          };
          reader.readAsDataURL(blob);
        });
      }

      const res = await xhrRequest("POST", `${API}/remove-background`, { image_base64: base64Data, mode });

      if (!res.ok) {
        throw new Error((res.data && res.data.detail) || "Failed to remove background");
      }

      setOverlayImages(prev => prev.map(img =>
        img.id === overlayId ? { ...img, src: `data:image/png;base64,${res.data.image_base64}` } : img
      ));
      toast.success("Background removed! Image cutout ready.");
    } catch (error) {
      console.error("Error removing background:", error);
      toast.error(error.message || "Failed to remove background");
    } finally {
      setCutoutLoading(false);
    }
  }, [overlayImages]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => {
      if (!prev) setSidebarOpen(false);
      return !prev;
    });
  }, []);

  // Escape key to exit fullscreen + Ctrl+Z/Y for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, undo, redo]);

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(200, prev + 10));
  const handleZoomOut = () => setZoom(prev => Math.max(25, prev - 10));
  const handleZoomReset = () => setZoom(100);

  // --- MARKUP HANDLERS ---
  const onMarkupComplete = useCallback((element) => {
    if (!element) return;
    saveToHistory();
    const newEl = { ...element, id: `markup-${Date.now()}` };
    setMarkupElements(prev => [...prev, newEl]);
  }, [saveToHistory]);

  const removeMarkupElement = useCallback((id) => {
    saveToHistory();
    setMarkupElements(prev => prev.filter(el => el.id !== id));
  }, [saveToHistory]);

  const clearMarkup = useCallback(() => {
    if (markupElements.length === 0 && !markupStampTemplate) return;
    saveToHistory();
    setMarkupElements([]);
    setSelectedMarkupId(null);
    // Reset the stamp template so the next click drops at click position with
    // default size. The uploaded image itself is preserved so the user can
    // immediately re-stamp.
    setMarkupStampTemplate(null);
  }, [saveToHistory, markupElements.length, markupStampTemplate]);

  // Duplicate / Move / Resize / Delete helpers for markup elements.
  // Duplicate is no longer exposed in the UI but kept in case of future use.

  // Delete the currently-selected markup element.
  const deleteSelectedMarkup = useCallback(() => {
    if (!selectedMarkupId) return;
    saveToHistory();
    setMarkupElements(prev => prev.filter(el => el.id !== selectedMarkupId));
    setSelectedMarkupId(null);
  }, [selectedMarkupId, saveToHistory]);

  // Drag a markup element by a (dx, dy) delta (canvas-native units).
  const moveMarkupElement = useCallback((id, dx, dy) => {
    if (!id || (dx === 0 && dy === 0)) return;
    setMarkupElements(prev => prev.map(el => {
      if (el.id !== id) return el;
      const next = { ...el };
      if (next.points && Array.isArray(next.points)) {
        next.points = next.points.map(p => ({ x: p.x + dx, y: p.y + dy }));
      }
      if (next.startPoint) next.startPoint = { x: next.startPoint.x + dx, y: next.startPoint.y + dy };
      if (next.endPoint) next.endPoint = { x: next.endPoint.x + dx, y: next.endPoint.y + dy };
      return next;
    }));
  }, []);

  // Resize a markup element by dragging one of its 8 handles.
  // `handle` is one of: 'nw'|'n'|'ne'|'w'|'e'|'sw'|'s'|'se'.
  // `pos` is the absolute cursor position in canvas-native coords.
  // Only applies to circle/square; lines/arrows/freehand fall through.
  const resizeMarkupElement = useCallback((id, handle, pos) => {
    if (!id || !handle || !pos) return;
    setMarkupElements(prev => prev.map(el => {
      if (el.id !== id) return el;
      if ((el.type !== 'circle' && el.type !== 'square' && el.type !== 'stamp') || !el.startPoint || !el.endPoint) return el;
      // Normalize the box first so left=min, right=max, etc.
      let left = Math.min(el.startPoint.x, el.endPoint.x);
      let right = Math.max(el.startPoint.x, el.endPoint.x);
      let top = Math.min(el.startPoint.y, el.endPoint.y);
      let bottom = Math.max(el.startPoint.y, el.endPoint.y);
      if (handle.includes('n')) top = pos.y;
      if (handle.includes('s')) bottom = pos.y;
      if (handle.includes('w')) left = pos.x;
      if (handle.includes('e')) right = pos.x;
      // Disallow zero-or-negative dimensions — clamp to 4 px minimum.
      if (right - left < 4) {
        if (handle.includes('w')) left = right - 4; else right = left + 4;
      }
      if (bottom - top < 4) {
        if (handle.includes('n')) top = bottom - 4; else bottom = top + 4;
      }
      return { ...el, startPoint: { x: left, y: top }, endPoint: { x: right, y: bottom } };
    }));
  }, []);

  // Sync `markupStampTemplate` to the currently-edited stamp so subsequent
  // clicks on other boards auto-place at the same relative offset + size.
  // Runs after every drag/resize: finds the selected stamp (if any),
  // computes its rect relative to its boardId, and stores it as the template.
  useEffect(() => {
    if (!selectedMarkupId) return;
    const stamp = markupElements.find(el => el.id === selectedMarkupId && el.type === 'stamp');
    if (!stamp || !stamp.startPoint || !stamp.endPoint || stamp.boardId == null) return;
    const allBoards = [...(loteriaBoards || []), ...(bingoBoards || [])];
    const board = allBoards.find(b => b.id === stamp.boardId);
    if (!board) return;
    const left = Math.min(stamp.startPoint.x, stamp.endPoint.x);
    const top  = Math.min(stamp.startPoint.y, stamp.endPoint.y);
    const right = Math.max(stamp.startPoint.x, stamp.endPoint.x);
    const bottom = Math.max(stamp.startPoint.y, stamp.endPoint.y);
    const next = {
      relX: left - board.x,
      relY: top  - board.y,
      width: right - left,
      height: bottom - top,
    };
    // Skip set if unchanged — avoids needless re-renders.
    setMarkupStampTemplate(prev => {
      if (prev && prev.relX === next.relX && prev.relY === next.relY &&
          prev.width === next.width && prev.height === next.height) return prev;
      return next;
    });
  }, [selectedMarkupId, markupElements, loteriaBoards, bingoBoards]);

  // Resize ALL existing stamp markup elements (and the template) to a new
  // square size in canvas px, anchored at the original center of each stamp.
  // Used by the Stamp Size slider so users can scale the first stamp (and
  // every replicated stamp) from the sidebar without dragging corner handles.
  const resizeAllMarkupStamps = useCallback((newSize) => {
    const size = Math.max(8, Math.round(newSize));
    setMarkupElements(prev => prev.map(el => {
      if (el.type !== 'stamp' || !el.startPoint || !el.endPoint) return el;
      const cx = (el.startPoint.x + el.endPoint.x) / 2;
      const cy = (el.startPoint.y + el.endPoint.y) / 2;
      const half = size / 2;
      return {
        ...el,
        startPoint: { x: cx - half, y: cy - half },
        endPoint:   { x: cx + half, y: cy + half },
      };
    }));
    // Sync the per-board template so new clicks use the same size.
    setMarkupStampTemplate(prev => prev
      ? { ...prev, width: size, height: size }
      : { relX: 0, relY: 0, width: size, height: size });
  }, []);

  // Sync the global Opacity slider with the currently-selected markup
  // element so the user sees its current opacity value when they click it.
  // The slider's onChange then mutates BOTH the slider state AND the
  // selected element's opacity in lockstep.
  useEffect(() => {
    if (!selectedMarkupId) return;
    const el = markupElements.find(e => e.id === selectedMarkupId);
    if (el && typeof el.opacity === 'number') {
      setMarkupOpacity(el.opacity);
    }
  }, [selectedMarkupId, markupElements]);

  // Update the opacity of the currently-selected markup element (or all
  // future drawings via the global slider when nothing is selected).
  const updateSelectedMarkupOpacity = useCallback((nextOpacity) => {
    if (!selectedMarkupId) return;
    setMarkupElements(prev => prev.map(el => {
      if (el.id !== selectedMarkupId) return el;
      return { ...el, opacity: nextOpacity };
    }));
  }, [selectedMarkupId]);

  // Eraser: remove markup elements near the given point.
  // Handles freehand (point array), line/arrow (startPoint+endPoint segment),
  // and circle/square (startPoint+endPoint bounding rect — eraser inside the
  // box OR within `eraseRadius` of the rect edge wipes the shape).
  const onEraseMarkup = useCallback((pos) => {
    const eraseRadius = eraserThickness * 3;
    setMarkupElements(prev => {
      const filtered = prev.filter(el => {
        // Freehand
        if (el.points && el.points.length > 0) {
          return !el.points.some(p =>
            Math.abs(p.x - pos.x) < eraseRadius && Math.abs(p.y - pos.y) < eraseRadius
          );
        }
        // Line / arrow / double-arrow / circle / square — start+end based
        if (el.startPoint && el.endPoint) {
          const { startPoint: s, endPoint: e } = el;
          if (el.type === 'circle' || el.type === 'square') {
            const minX = Math.min(s.x, e.x), maxX = Math.max(s.x, e.x);
            const minY = Math.min(s.y, e.y), maxY = Math.max(s.y, e.y);
            const insideX = pos.x >= minX - eraseRadius && pos.x <= maxX + eraseRadius;
            const insideY = pos.y >= minY - eraseRadius && pos.y <= maxY + eraseRadius;
            return !(insideX && insideY);
          }
          // Distance from pos to the segment (s, e)
          const dx = e.x - s.x, dy = e.y - s.y;
          const lenSq = dx * dx + dy * dy;
          let t = 0;
          if (lenSq > 0) {
            t = ((pos.x - s.x) * dx + (pos.y - s.y) * dy) / lenSq;
            t = Math.max(0, Math.min(1, t));
          }
          const cx = s.x + t * dx, cy = s.y + t * dy;
          const dist = Math.hypot(pos.x - cx, pos.y - cy);
          return dist > eraseRadius;
        }
        return true;
      });
      if (filtered.length < prev.length) {
        saveToHistory();
        return filtered;
      }
      return prev;
    });
  }, [eraserThickness, saveToHistory]);

  // --- EMOJI HANDLERS ---
  const addEmoji = useCallback((emojiChar) => {
    saveToHistory();
    const newEmoji = {
      id: `emoji-${Date.now()}`,
      emoji: emojiChar,
      x: displayWidth / 2,
      y: displayHeight / 2,
      size: 40,
    };
    setEmojis(prev => [...prev, newEmoji]);
    setSelectedEmojiId(newEmoji.id);
  }, [saveToHistory, displayWidth, displayHeight]);

  const updateEmojiPosition = useCallback((id, x, y) => {
    setEmojis(prev => prev.map(e => e.id === id ? { ...e, x, y } : e));
  }, []);

  const updateEmojiSize = useCallback((id, size) => {
    setEmojis(prev => prev.map(e => e.id === id ? { ...e, size } : e));
  }, []);

  const removeEmoji = useCallback((id) => {
    saveToHistory();
    setEmojis(prev => prev.filter(e => e.id !== id));
    if (selectedEmojiId === id) setSelectedEmojiId(null);
  }, [saveToHistory, selectedEmojiId]);

  // --- FILTER HANDLERS ---
  const updateFilter = useCallback((key, value) => {
    setFilterSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const applyFilterPreset = useCallback((preset) => {
    saveToHistory();
    setFilterSettings({ ...preset.settings });
  }, [saveToHistory]);

  // --- AUTO-MARKUP HANDLERS (All at Once only) ---
  const BINGO_COLUMNS = { B: 0, I: 1, N: 2, G: 3, O: 4 };

  const markAllAtOnce = useCallback((callStr) => {
    const call = callStr.trim().toUpperCase();
    if (call.length < 2) return false;
    const letter = call[0];
    const num = parseInt(call.substring(1), 10);
    if (!BINGO_COLUMNS.hasOwnProperty(letter) || isNaN(num)) return false;
    const colIndex = BINGO_COLUMNS[letter];

    // Markup Mode: Off — still record the call in the tracker but skip
    // every visual side effect (no highlights, no marks, no border rings).
    if (autoMarkupMode === 'off') {
      // Confirm the call actually matches at least one board so we return
      // true/false consistently with the other paths.
      const hit = bingoBoards.some(b => {
        for (let row = 0; row < 5; row++) if (b.numbers[row][colIndex] === num) return true;
        return false;
      });
      if (hit) {
        saveToHistory();
        setCalledNumbers(prev => [...prev, call]);
      }
      return hit;
    }

    saveToHistory();
    const marksByBoard = [];
    const matchedBoardIds = new Set();
    bingoBoards.forEach((board, boardIndex) => {
      // Skip non-isolated boards when isolate markup is active
      if (isolateMarkupOnlyRef.current && isolatedBoardNumsRef.current && !isolatedBoardNumsRef.current.has(boardIndex + 1)) return;
      const boardMarks = [];
      for (let row = 0; row < 5; row++) {
        if (board.numbers[row][colIndex] === num) {
          matchedBoardIds.add(board.id);
          const cellSize = board.width / 5;
          const cx = board.x + colIndex * cellSize + cellSize / 2;
          const cy = board.y + cellSize + row * cellSize + cellSize / 2;
          const markSize = cellSize * 0.35 * (autoMarkupScale / 100);
          boardMarks.push({
            id: `automark-${Date.now()}-${board.id}-${row}-${colIndex}-${Math.random().toString(36).substr(2,5)}`,
            type: 'freehand',
            points: [{ x: cx, y: cy }],
            color: markupColor,
            thickness: markupThickness > 0 ? markupThickness : 3,
            tipStyle: markupTipStyle,
            opacity: markupOpacity,
            autoMarkSize: markSize,
            boardNum: boardIndex + 1,
            sourceCall: call,
          });
        }
      }
      if (boardMarks.length > 0) {
        marksByBoard.push({ boardIndex, boardId: board.id, marks: boardMarks });
      }
    });

    // Highlight ALL matching boards immediately (skip in border/border-markup modes)
    if (autoMarkupMode !== 'border' && autoMarkupMode !== 'border-markup') {
      setHighlightedBoards(matchedBoardIds);
    } else {
      setHighlightedBoards(new Set());
    }
    // Accumulator: remember every board that has been auto-marked this
    // session so the optional Board-Number badge highlight persists across
    // subsequent calls.
    if (matchedBoardIds && matchedBoardIds.size > 0) {
      setAutoMarkedBoardsEver(prev => {
        const next = new Set(prev);
        matchedBoardIds.forEach(id => next.add(id));
        return next;
      });
    }

    // Border highlight for Bingo (border or border-markup mode)
    if (autoMarkupMode === 'border' || autoMarkupMode === 'border-markup') {
      setHighlightedCells([]);
      const bingoCells = [];
      bingoBoards.forEach(board => {
        for (let row = 0; row < 5; row++) {
          if (board.numbers[row][colIndex] === num) {
            bingoCells.push({ boardId: board.id, row, col: colIndex });
          }
        }
      });
      if (autoMarkupMode === 'border' && autoMarkupTimed && bingoCells.length > 1) {
        // Border-only mode: timed board-by-board highlights
        if (timedMarkupRef.current) clearTimeout(timedMarkupRef.current);
        const cellsByBoard = {};
        const boardKeys = [];
        bingoCells.forEach(cell => {
          const bi = bingoBoards.findIndex(b => b.id === cell.boardId);
          if (!cellsByBoard[bi]) { cellsByBoard[bi] = []; boardKeys.push(bi); }
          cellsByBoard[bi].push(cell);
        });
        const sorted = [...new Set(boardKeys)].sort((a, b) => a - b);
        let idx = 0;
        const placeNext = () => {
          if (idx < sorted.length) {
            const cells = cellsByBoard[sorted[idx]];
            if (cells) setHighlightedCells(prev => [...prev, ...cells]);
            idx++;
            if (idx < sorted.length) {
              timedMarkupRef.current = setTimeout(placeNext, autoMarkupDelay);
            }
          }
        };
        placeNext();
      } else {
        // Border+Markup mode OR non-timed: ALL highlights at once instantly
        setHighlightedCells(bingoCells);
      }
    }

    if (marksByBoard.length > 0 && (autoMarkupMode === 'both' || autoMarkupMode === 'border-markup')) {
      marksByBoard.sort((a, b) => a.boardIndex - b.boardIndex);

      if (autoMarkupMode === 'border-markup') {
        const placeMarkups = () => {
          if (autoMarkupTimed && marksByBoard.length > 1) {
            const queue = marksByBoard.map(b => [...b.marks]);
            let idx = 0;
            setAutoMarkupInProgress(true);
            const placeNext = () => {
              if (idx < queue.length && queue[idx]) {
                const marks = queue[idx];
                setMarkupElements(prev => [...prev, ...marks]);
                idx++;
                if (idx < queue.length) {
                  timedMarkupRef.current = setTimeout(placeNext, autoMarkupDelay);
                } else {
                  setAutoMarkupInProgress(false);
                }
              } else {
                setAutoMarkupInProgress(false);
              }
            };
            // 1 second delay before first markup
            timedMarkupRef.current = setTimeout(placeNext, 1000);
          } else {
            const allMarks = marksByBoard.flatMap(b => b.marks);
            setMarkupElements(prev => [...prev, ...allMarks]);
          }
        };
        timedMarkupRef.current = setTimeout(placeMarkups, 600);
      } else {
        if (autoMarkupTimed && marksByBoard.length > 1) {
          if (timedMarkupRef.current) clearTimeout(timedMarkupRef.current);
          const queue = marksByBoard.map(b => [...b.marks]);
          let idx = 0;
          setAutoMarkupInProgress(true);
          const placeNext = () => {
            if (idx < queue.length && queue[idx]) {
              const marks = queue[idx];
              setMarkupElements(prev => [...prev, ...marks]);
              idx++;
              if (idx < queue.length) {
                timedMarkupRef.current = setTimeout(placeNext, autoMarkupDelay);
              } else {
                setAutoMarkupInProgress(false);
              }
            } else {
              setAutoMarkupInProgress(false);
            }
          };
          // 1 second delay before first markup
          timedMarkupRef.current = setTimeout(placeNext, 1000);
        } else {
          const allMarks = marksByBoard.flatMap(b => b.marks);
          setMarkupElements(prev => [...prev, ...allMarks]);
        }
      }
      setCalledNumbers(prev => [...prev, call]);
      return true;
    }
    if (matchedBoardIds.size > 0) {
      setCalledNumbers(prev => [...prev, call]);
      return true;
    }
    return false;
  }, [bingoBoards, markupColor, markupThickness, markupTipStyle, markupOpacity, autoMarkupScale, autoMarkupMode, autoMarkupTimed, autoMarkupDelay, saveToHistory]);

  // Build lookup map for Loteria figures: name (without El/La/Las) -> index, number -> index
  // Accepts BOTH formats:
  //   "N - El/La/Las Name"  (legacy user uploads)
  //   "El/La/Las Name"      (Enhanced built-in deck — number derived from index+1)
  const loteriaFigureLookup = useMemo(() => {
    const byName = {};
    const byNumber = {};
    loteriaFigures.forEach((fig, idx) => {
      const numbered = fig.name.match(/^(\d+)\s*-\s*(?:las|los|el|la|)\s*(.+)$/i);
      if (numbered) {
        const num = parseInt(numbered[1], 10);
        const cleanName = numbered[2].trim().toLowerCase();
        byName[cleanName] = idx;
        byNumber[num] = idx;
        return;
      }
      // Fallback: figure name w/o leading number. Strip optional El/La/Las prefix.
      const plain = fig.name.match(/^\s*(?:las|los|el|la)\s+(.+)$/i);
      const cleanName = (plain ? plain[1] : fig.name).trim().toLowerCase();
      const num = idx + 1;
      byName[cleanName] = idx;
      byNumber[num] = idx;
      // Also register the full name (with article) so callers reading
      // "el gallo" or just "gallo" both resolve.
      byName[fig.name.trim().toLowerCase()] = idx;
    });
    return { byName, byNumber };
  }, [loteriaFigures]);

  // Compute Neighbors Boards pairs (stable across renders).
  // Horizontal pairs (default): boards side-by-side in the same row. The
  // connecting image spans the last column of the left board + first
  // column of the right board, rowsCovered rows tall.
  // Vertical pairs: boards stacked above/below in the same column. The
  // connecting image spans the bottom row of the top board + top row of
  // the bottom board, colsCovered columns wide.
  // Per-board badge override map driven by Custom Vertical Pairs. Each pair
  // may set `badgeShiftX` / `badgeShiftY` (percent of board w/h) that move
  // the board-number badge ONLY for the two boards in that pair, leaving
  // every other board's numbering untouched. CanvasEditor merges these into
  // the loteriaNumbering object on a per-board basis.
  // NOTE: This memo is initially defined here WITHOUT neighborPairs (which is
  // declared further below). The full overrides map (incl. VPN position
  // overrides that depend on neighborPairs) is composed in a second memo
  // `loteriaBoardOverridesFull` below. CanvasEditor receives the FULL map.
  const loteriaBoardNumberingOverridesBase = useMemo(() => {
    const m = {};
    (customVerticalPairs || []).forEach(pair => {
      const sx = typeof pair.badgeShiftX === 'number' ? pair.badgeShiftX : 0;
      const sy = typeof pair.badgeShiftY === 'number' ? pair.badgeShiftY : 0;
      if (sx === 0 && sy === 0) return;
      const aIdx = (pair.a || 0) - 1;
      const bIdx = (pair.b || 0) - 1;
      if (aIdx >= 0) m[aIdx] = { ...(m[aIdx] || {}), badgeShiftX: sx, badgeShiftY: sy };
      if (bIdx >= 0) m[bIdx] = { ...(m[bIdx] || {}), badgeShiftX: sx, badgeShiftY: sy };
    });
    return m;
  }, [customVerticalPairs]);

  // Pick N distinct decorative filler figures, excluding the connecting
  // figure. Returns as many UNIQUE figures as possible — capped at the
  // available pool size so the loop CAN'T spin forever when the deck is
  // smaller than requested (which is the root cause of the 4x5-board
  // black-screen / freeze: an undersized deck combined with a wide
  // connecting-image area asked for more fillers than figures available).
  // After the unique pool is exhausted, remaining slots repeat figures —
  // the worst visual outcome is a duplicate decorative cell, NOT a frozen
  // browser tab.
  const pickFillerFigures = useCallback((neighborFig, count, totalFigs, startOffset = 13) => {
    const out = [];
    if (count <= 0 || totalFigs <= 0) return out;
    const maxUnique = Math.max(0, totalFigs - 1); // exclude neighborFig
    const uniqueLimit = Math.min(count, maxUnique);
    let fCand = (neighborFig + startOffset) % totalFigs;
    let safetyCounter = totalFigs * 4;
    while (out.length < uniqueLimit && safetyCounter > 0) {
      if (fCand !== neighborFig && !out.includes(fCand)) out.push(fCand);
      fCand = (fCand + 1) % totalFigs;
      if (fCand === neighborFig) fCand = (fCand + 1) % totalFigs;
      safetyCounter--;
    }
    // If we still need more (because count > available unique figures),
    // repeat the unique pool to fill the rest. Prevents the original
    // infinite loop while leaving callers free to ask for any count.
    if (out.length > 0) {
      let i = 0;
      while (out.length < count) {
        out.push(out[i % out.length]);
        i++;
      }
    }
    return out;
  }, []);

  const neighborPairs = useMemo(() => {
    if (!neighborsEnabled || gameMode !== 'loteria' || !loteriaBoards || loteriaBoards.length < 2) return [];
    const sorted = loteriaBoards.map((b, i) => ({ ...b, idx: i }));
    const gCols = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : (loteriaGridSize || 4);
    const gRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[1]) : (loteriaGridSize || 4);
    const gridN = gCols; // kept for backwards-compat in pair payloads
    const boardFigSets = loteriaBoards.map(b => new Set(b.figures || []));
    const used = new Set();
    const pairs = [];
    const totalFigs = loteriaFigures?.length || 54;

    // Build a DETERMINISTIC shuffled pool of figure indices. Same pattern as
    // `lastCardAssignments`. The seed is intentionally DECOUPLED from
    // loteriaBoards[i].figures so the conflict-dedup useEffect below can swap
    // board cells WITHOUT mutating the connecting-image figure (which would
    // otherwise trigger a recompute → infinite loop). Instead, `neighborSeedVersion`
    // is bumped explicitly by reshuffle/regenerate to roll the pool to a fresh
    // permutation, while figure mutations from dedup leave the pool untouched.
    //
    // ⚠️ DO NOT ADD `board.figures` BACK TO THIS SEED — see the dedup useEffect
    // right below this useMemo. Mixing figures here will cause an infinite
    // re-render loop because every dedup swap would change the seed → change
    // the connecting figure → require another swap → ...
    let seed = 0x9e3779b9;
    seed = ((seed ^ (neighborSeedVersion || 1)) * 0x01000193) >>> 0;
    for (let b = 0; b < loteriaBoards.length; b++) {
      const bid = loteriaBoards[b].id != null ? String(loteriaBoards[b].id) : String(b);
      for (let ci = 0; ci < bid.length; ci++) {
        seed = ((seed ^ bid.charCodeAt(ci)) * 0x01000193) >>> 0;
      }
    }
    const rng = (() => {
      let s = seed || 1;
      return () => {
        s |= 0; s = (s + 0x6D2B79F5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    })();
    const shuffledPool = Array.from({ length: totalFigs }, (_, i) => i);
    for (let i = shuffledPool.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [shuffledPool[i], shuffledPool[j]] = [shuffledPool[j], shuffledPool[i]];
    }

    const findNeighborFig = (excludeIdxs) => {
      // Pass 1: prefer a random (shuffled) figure not on any of the excluded boards.
      for (let k = 0; k < shuffledPool.length; k++) {
        const f = shuffledPool[k];
        if (used.has(f)) continue;
        if (excludeIdxs.every(bi => !boardFigSets[bi]?.has(f))) return f;
      }
      // Pass 2: fall back to any unused figure (still iterating the shuffled pool).
      for (let k = 0; k < shuffledPool.length; k++) {
        const f = shuffledPool[k];
        if (!used.has(f)) return f;
      }
      return null;
    };

    // ── QUAD MODE ─────────────────────────────────────────────────────────
    // 4 boards arranged in a 2x2 layout share ONE connecting image at their
    // meeting point (where their inner corners converge).
    if (neighborsQuadEnabled && loteriaBoards.length >= 4) {
      // Cluster boards into rows by similar Y, then columns by X within each row.
      const sortedByY = [...sorted].sort((a, b) => a.y - b.y || a.x - b.x);
      const rows = [];
      let currentRow = [sortedByY[0]];
      for (let i = 1; i < sortedByY.length; i++) {
        if (Math.abs(sortedByY[i].y - currentRow[0].y) < 20) currentRow.push(sortedByY[i]);
        else { rows.push(currentRow); currentRow = [sortedByY[i]]; }
      }
      rows.push(currentRow);
      // Sort each row left→right
      rows.forEach(r => r.sort((a, b) => a.x - b.x));

      // Disjoint 2x2 blocks: pick row pairs (0,1), (2,3), ... and column pairs (0,1), (2,3), ...
      for (let ri = 0; ri + 1 < rows.length; ri += 2) {
        const topRow = rows[ri];
        const botRow = rows[ri + 1];
        const cols = Math.min(topRow.length, botRow.length);
        for (let ci = 0; ci + 1 < cols; ci += 2) {
          const tl = topRow[ci];
          const tr = topRow[ci + 1];
          const bl = botRow[ci];
          const br = botRow[ci + 1];
          if (!tl || !tr || !bl || !br) continue;
          const excludeIdxs = [tl.idx, tr.idx, bl.idx, br.idx];
          const neighborFig = findNeighborFig(excludeIdxs);
          if (neighborFig === null) continue;
          used.add(neighborFig);

          const cellsPerBoard = (neighborsQuadCellsPerBoard === 2) ? 2 : 1;
          // Filler count: cellsPerBoard^2 cells per board × 4 boards.
          const fillerCount = cellsPerBoard * cellsPerBoard * 4;
          const fillerFigures = pickFillerFigures(neighborFig, fillerCount, totalFigs, 7);

          pairs.push({
            direction: 'quad',
            tlBoardIdx: tl.idx, trBoardIdx: tr.idx,
            blBoardIdx: bl.idx, brBoardIdx: br.idx,
            // Aliases so the existing leftBoardIdx/rightBoardIdx based shake
            // + isolation guards still receive non-null indices.
            leftBoardIdx: tl.idx, rightBoardIdx: br.idx,
            figure: neighborFig, fillerFigures,
            cellsPerBoard, gridN, gridRows: gRows,
          });
        }
      }
      return pairs;
    }

    const findPairFig = (aIdx, bIdx) => findNeighborFig([aIdx, bIdx]);

    // ── CUSTOM VERTICAL PAIR OVERRIDES ────────────────────────────────────
    // Process user-defined vertical pairs FIRST so those boards are removed
    // from the auto-pairing pool. Format: array of [a, b] 1-based board
    // numbers. Each entry forces a VERTICAL connecting image between the
    // two boards regardless of their grid position, then both are excluded
    // from the automatic same-row/column pair detection that follows.
    const consumedIdxs = new Set();
    if (Array.isArray(customVerticalPairs) && customVerticalPairs.length > 0) {
      for (const entry of customVerticalPairs) {
        // Support both legacy [a, b] arrays and the new {a, b, gap} object form.
        const aNum = parseInt(Array.isArray(entry) ? entry[0] : entry?.a, 10);
        const bNum = parseInt(Array.isArray(entry) ? entry[1] : entry?.b, 10);
        if (!Number.isFinite(aNum) || !Number.isFinite(bNum)) continue;
        const aIdx = aNum - 1, bIdx = bNum - 1;
        if (aIdx < 0 || bIdx < 0 || aIdx >= loteriaBoards.length || bIdx >= loteriaBoards.length) continue;
        if (consumedIdxs.has(aIdx) || consumedIdxs.has(bIdx) || aIdx === bIdx) continue;

        // Order them top→bottom by Y so the connecting image renders in the
        // gap between them with the correct top/bottom semantics.
        const aB = loteriaBoards[aIdx], bB = loteriaBoards[bIdx];
        const topIdx = aB.y <= bB.y ? aIdx : bIdx;
        const bottomIdx = aB.y <= bB.y ? bIdx : aIdx;

        const neighborFig = findPairFig(topIdx, bottomIdx);
        if (neighborFig === null) continue;
        used.add(neighborFig);
        const fillerCount = 2 * neighborsRowsPerBoard;
        const fillerFigures = pickFillerFigures(neighborFig, fillerCount, totalFigs, 13);
        const colsCovered = Math.min(neighborsRowsPerBoard, gCols);
        let startCol;
        if (neighborsPosition === 'top') startCol = 0;
        else if (neighborsPosition === 'bottom') startCol = Math.max(0, gCols - colsCovered);
        else startCol = Math.max(0, Math.floor((gCols - colsCovered) / 2));
        pairs.push({
          direction: 'vertical',
          topBoardIdx: topIdx, bottomBoardIdx: bottomIdx,
          leftBoardIdx: topIdx, rightBoardIdx: bottomIdx,
          figure: neighborFig, fillerFigures,
          startCol, colsCovered, gridN, gridRows: gRows,
          isCustom: true,
        });
        consumedIdxs.add(aIdx); consumedIdxs.add(bIdx);
      }
    }
    // Filter `sorted` to exclude boards already consumed by custom pairs so
    // the automatic algorithm below doesn't re-pair them.
    const remaining = sorted.filter(b => !consumedIdxs.has(b.idx));

    if (neighborsDirection === 'vertical') {
      // Cluster boards into COLUMNS (similar X coordinate), then pair
      // top→bottom inside each column. Excludes any boards already consumed
      // by custom vertical pair overrides above.
      remaining.sort((a, b) => {
        const xDiff = Math.abs(a.x - b.x);
        if (xDiff < 5) return a.y - b.y;
        return a.x - b.x;
      });
      const cols = [];
      if (remaining.length > 0) {
        let currentCol = [remaining[0]];
        for (let i = 1; i < remaining.length; i++) {
          if (Math.abs(remaining[i].x - currentCol[0].x) < 10) currentCol.push(remaining[i]);
          else { cols.push(currentCol); currentCol = [remaining[i]]; }
        }
        cols.push(currentCol);
      }
      cols.forEach(col => {
        for (let i = 0; i + 1 < col.length; i += 2) {
          const topIdx = col[i].idx;
          const bottomIdx = col[i + 1].idx;
          let neighborFig = findPairFig(topIdx, bottomIdx);
          if (neighborFig === null) continue;
          used.add(neighborFig);
          const fillerCount = 2 * neighborsRowsPerBoard;
          const fillerFigures = pickFillerFigures(neighborFig, fillerCount, totalFigs, 13);
          const colsCovered = Math.min(neighborsRowsPerBoard, gCols);
          let startCol;
          if (neighborsPosition === 'top') startCol = 0;
          else if (neighborsPosition === 'bottom') startCol = Math.max(0, gCols - colsCovered);
          else startCol = Math.max(0, Math.floor((gCols - colsCovered) / 2));
          pairs.push({
            direction: 'vertical',
            topBoardIdx: topIdx, bottomBoardIdx: bottomIdx,
            // Aliases so existing CanvasEditor code reading leftBoardIdx
            // /rightBoardIdx for shake/isolated checks keeps working.
            leftBoardIdx: topIdx, rightBoardIdx: bottomIdx,
            figure: neighborFig, fillerFigures,
            startCol, colsCovered, gridN, gridRows: gRows,
          });
        }
      });
      return pairs;
    }

    // Horizontal (default) — original behaviour. Excludes any boards already
    // consumed by custom vertical pair overrides above.
    remaining.sort((a, b) => {
      const yDiff = Math.abs(a.y - b.y);
      if (yDiff < 5) return a.x - b.x;
      return a.y - b.y;
    });
    const rows = [];
    if (remaining.length > 0) {
      let currentRow = [remaining[0]];
      for (let i = 1; i < remaining.length; i++) {
        if (Math.abs(remaining[i].y - currentRow[0].y) < 10) currentRow.push(remaining[i]);
        else { rows.push(currentRow); currentRow = [remaining[i]]; }
      }
      rows.push(currentRow);
    }

    rows.forEach(row => {
      // Disjoint pairs only: (1-2), (3-4), (5-6)... — each board belongs to at most one pair.
      // Odd board in a row is left unpaired.
      for (let i = 0; i + 1 < row.length; i += 2) {
        const leftIdx = row[i].idx;
        const rightIdx = row[i + 1].idx;
        let neighborFig = findPairFig(leftIdx, rightIdx);
        if (neighborFig === null) continue;
        used.add(neighborFig);

        // Pick (2 * rowsPerBoard) distinct decorative filler figures (one per
        // underlying cell). PURELY COSMETIC — not part of auto-markup, winner
        // detection, or board logic. Uses the safe helper so an undersized
        // deck cannot freeze the tab.
        const fillerCount = 2 * neighborsRowsPerBoard;
        const fillerFigures = pickFillerFigures(neighborFig, fillerCount, totalFigs, 13);

        const rowsCovered = Math.min(neighborsRowsPerBoard, gRows);
        let startRow;
        if (neighborsPosition === 'top') startRow = 0;
        else if (neighborsPosition === 'bottom') startRow = Math.max(0, gRows - rowsCovered);
        else startRow = Math.max(0, Math.floor((gRows - rowsCovered) / 2));

        pairs.push({ direction: 'horizontal', leftBoardIdx: leftIdx, rightBoardIdx: rightIdx, figure: neighborFig, fillerFigures, startRow, gridN, rowsCovered });
      }
    });
    return pairs;
  }, [neighborsEnabled, gameMode, loteriaBoards, loteriaGridSize, neighborsPosition, neighborsDirection, loteriaFigures, neighborsRowsPerBoard, neighborsQuadEnabled, neighborsQuadCellsPerBoard, neighborSeedVersion, customVerticalPairs, pickFillerFigures]);

  // Keep ref in sync for stale-closure-safe access inside caller callbacks
  neighborPairsRef.current = neighborPairs;

  // FULL per-board overrides — base (custom-vertical-pair shifts) merged with
  // VPN per-board boardNumPosition overrides (top board → top edge, bottom
  // board → bottom edge, aligned per verticalPairBadgeAlign).
  const loteriaBoardNumberingOverrides = useMemo(() => {
    const m = { ...loteriaBoardNumberingOverridesBase };
    const vertPairsActive = neighborsEnabled &&
      (neighborsDirection || 'horizontal') === 'vertical' &&
      neighborPairs && neighborPairs.length > 0;
    if (vertPairsActive && pairStackNumbersEnabled) {
      // Stack both pair numbers vertically, centered above the TOP board's top
      // edge: top board's number on top, bottom board's number just below it.
      // The whole stack moves together via the X/Y sliders.
      const parseC = (gs) => (typeof gs === 'string' && gs.includes('x')) ? parseInt(gs.split('x')[0]) : gs;
      const badgePct = (loteriaNumbering && loteriaNumbering.badgeSize) || 100;
      neighborPairs.forEach(p => {
        if (p.direction !== 'vertical') return;
        const top = loteriaBoards && loteriaBoards[p.topBoardIdx];
        const bot = loteriaBoards && loteriaBoards[p.bottomBoardIdx];
        if (!top || !bot) return;
        const W = top.width || 1;
        const H = top.height || 1;
        const gCols = parseC(top.gridSize || loteriaGridSize) || 1;
        const badgeSize = (W / gCols) * (badgePct / 100) * 0.6;
        const spacing = badgeSize * 1.9; // vertical gap between the two numbers
        const shiftXpct = (pairStackOffsetX || 0);
        // Top number: lift above the normal 'top' spot by one `spacing`.
        const topShiftYpct = (-spacing / H) * 100 + (pairStackOffsetY || 0);
        // Bottom number: move from its OWN top edge up to the top board's top
        // edge (exact distance = bot.y - top.y, regardless of the gap/band).
        const distUp = (bot.y || 0) - (top.y || 0);
        const botShiftYpct = (-distUp / (bot.height || H)) * 100 + (pairStackOffsetY || 0);
        if (typeof p.topBoardIdx === 'number')
          m[p.topBoardIdx] = { ...(m[p.topBoardIdx] || {}), boardNumPosition: 'top', badgeShiftX: shiftXpct, badgeShiftY: topShiftYpct };
        if (typeof p.bottomBoardIdx === 'number')
          m[p.bottomBoardIdx] = { ...(m[p.bottomBoardIdx] || {}), boardNumPosition: 'top', badgeShiftX: shiftXpct, badgeShiftY: botShiftYpct };
      });
    } else if (vertPairsActive && neighborsVerticalPairNumbering) {
      const align = verticalPairBadgeAlign || 'center';
      const topPos = align === 'left' ? 'top-left' : align === 'right' ? 'top-right' : 'top';
      const botPos = align === 'left' ? 'bottom-left' : align === 'right' ? 'bottom-right' : 'bottom';
      neighborPairs.forEach(p => {
        if (p.direction !== 'vertical') return;
        if (typeof p.topBoardIdx === 'number') m[p.topBoardIdx] = { ...(m[p.topBoardIdx] || {}), boardNumPosition: topPos };
        if (typeof p.bottomBoardIdx === 'number') m[p.bottomBoardIdx] = { ...(m[p.bottomBoardIdx] || {}), boardNumPosition: botPos };
      });
    }
    // Independent numbering for the one-off (different-size) board.
    if (loteriaOneOffEnabled && loteriaOneOffNumberingEnabled &&
        loteriaOneOffBoardIndex != null && loteriaOneOffBoardIndex >= 0) {
      m[loteriaOneOffBoardIndex] = {
        ...(m[loteriaOneOffBoardIndex] || {}),
        showRowNums: !!loteriaOneOffShowRowNums,
        showColNums: !!loteriaOneOffShowColNums,
        startingNum: loteriaOneOffStartingNum || 1,
        independentNumbering: true,
      };
    }
    return m;
  }, [loteriaBoardNumberingOverridesBase, neighborsEnabled, neighborsVerticalPairNumbering, neighborsDirection, neighborPairs, verticalPairBadgeAlign, pairStackNumbersEnabled, pairStackOffsetX, pairStackOffsetY, loteriaBoards, loteriaGridSize, loteriaNumbering, loteriaOneOffEnabled, loteriaOneOffNumberingEnabled, loteriaOneOffBoardIndex, loteriaOneOffShowRowNums, loteriaOneOffShowColNums, loteriaOneOffStartingNum]);

  // Vertical-pair-major board numbering (toggle: neighborsVerticalPairNumbering).
  // When enabled (and Neighbors + vertical direction is active), returns a
  // map: boardIdx → display number. Pairs are numbered top→bottom, then move
  // to the next pair within the same row-pair (column-major), continuing
  // across row-pairs. Unpaired boards get the next available number in
  // their (y, x) position. Returns null when the toggle is off so callers
  // fall back to the default index+1 numbering.
  const loteriaBoardDisplayNums = useMemo(() => {
    if (!neighborsEnabled || !neighborsVerticalPairNumbering) return null;
    if ((neighborsDirection || 'horizontal') !== 'vertical') return null;
    if (gameMode !== 'loteria' || !loteriaBoards || loteriaBoards.length === 0) return null;
    if (!neighborPairs || neighborPairs.length === 0) return null;
    const handled = new Set();
    const items = [];
    neighborPairs.forEach(p => {
      if (p.direction !== 'vertical') return;
      if (handled.has(p.topBoardIdx) || handled.has(p.bottomBoardIdx)) return;
      handled.add(p.topBoardIdx);
      handled.add(p.bottomBoardIdx);
      const topBoard = loteriaBoards[p.topBoardIdx];
      if (!topBoard) return;
      items.push({ kind: 'pair', y: topBoard.y, x: topBoard.x, topIdx: p.topBoardIdx, bottomIdx: p.bottomBoardIdx });
    });
    loteriaBoards.forEach((b, idx) => {
      if (handled.has(idx)) return;
      items.push({ kind: 'single', y: b.y, x: b.x, idx });
      handled.add(idx);
    });
    // Sort by row-pair (Y) first, then by column (X) — yields column-major
    // numbering within each row-pair, continuing across row-pairs.
    items.sort((a, b) => {
      const yd = a.y - b.y;
      if (Math.abs(yd) < 10) return a.x - b.x;
      return yd;
    });
    const map = {};
    let counter = 1;
    items.forEach(item => {
      if (item.kind === 'pair') {
        map[item.topIdx] = counter++;
        map[item.bottomIdx] = counter++;
      } else {
        map[item.idx] = counter++;
      }
    });
    return map;
  }, [neighborsEnabled, neighborsVerticalPairNumbering, neighborsDirection, gameMode, loteriaBoards, neighborPairs]);

  // Conflict dedup: when a connecting-image figure F also exists as a regular
  // cell on one of the connected boards, the connecting image is visually
  // duplicated (the user marks the big bridge AND a tiny cell underneath).
  // Since the connecting image already "counts as" F on all of its connected
  // boards, swap any conflicting cell on a connected board with a fresh
  // figure that's not already on that board.
  //
  // Loop safety: `neighborPairs` seed is decoupled from board.figures (only
  // depends on board.id + neighborSeedVersion), so mutating figures here does
  // NOT change which connecting figures are picked. After one mutation, the
  // useEffect re-runs (loteriaBoards changed), finds no conflicts, and stops.
  useEffect(() => {
    if (!neighborsEnabled || gameMode !== 'loteria') return;
    if (!neighborPairs || neighborPairs.length === 0) return;
    const totalFigs = loteriaFigures?.length || 54;
    // Collect conflicts as { boardIdx, cellIdx, newFig }
    const swaps = [];
    // For each board, accumulate all connecting figures it participates in so
    // the substitute we pick doesn't accidentally collide with another pair's
    // figure on the same board.
    const bannedByBoard = new Map();
    neighborPairs.forEach(pair => {
      const boardIdxs = pair.direction === 'quad'
        ? [pair.tlBoardIdx, pair.trBoardIdx, pair.blBoardIdx, pair.brBoardIdx]
        : [pair.leftBoardIdx, pair.rightBoardIdx];
      boardIdxs.forEach(bi => {
        if (!bannedByBoard.has(bi)) bannedByBoard.set(bi, new Set());
        bannedByBoard.get(bi).add(pair.figure);
      });
    });
    neighborPairs.forEach(pair => {
      const boardIdxs = pair.direction === 'quad'
        ? [pair.tlBoardIdx, pair.trBoardIdx, pair.blBoardIdx, pair.brBoardIdx]
        : [pair.leftBoardIdx, pair.rightBoardIdx];
      boardIdxs.forEach(bi => {
        const board = loteriaBoards[bi];
        if (!board?.figures) return;
        const cellIdx = board.figures.indexOf(pair.figure);
        if (cellIdx === -1) return;
        const onBoard = new Set(board.figures);
        const banned = bannedByBoard.get(bi) || new Set();
        let chosen = -1;
        for (let f = 0; f < totalFigs; f++) {
          if (onBoard.has(f)) continue;
          if (banned.has(f)) continue;
          chosen = f;
          break;
        }
        if (chosen === -1) {
          // Extreme fallback: any figure not on this board (ignoring banned).
          for (let f = 0; f < totalFigs; f++) {
            if (!onBoard.has(f)) { chosen = f; break; }
          }
        }
        if (chosen === -1) return;
        swaps.push({ boardIdx: bi, cellIdx, newFig: chosen });
      });
    });
    if (swaps.length === 0) return;
    setLoteriaBoards(prev => {
      // Mutate a single copy; multiple swaps on the same board are applied in
      // order. Each swap only changes one cell, so order doesn't really matter
      // for visual correctness (the cell already had the conflict figure).
      const next = prev.map(b => ({ ...b, figures: [...(b.figures || [])] }));
      swaps.forEach(s => {
        if (next[s.boardIdx]?.figures && s.cellIdx < next[s.boardIdx].figures.length) {
          next[s.boardIdx].figures[s.cellIdx] = s.newFig;
        }
      });
      return next;
    });
  }, [neighborPairs, neighborsEnabled, gameMode, loteriaBoards, loteriaFigures?.length]);

  // Compute per-board "Last Card Standing" focal figure assignments.
  // Each assigned figure is unique across all boards AND not already on that board.
  // Coverage is a small rectangle anchored in the chosen corner:
  //  size 2 → 1 col × 2 rows,  size 3 → 1 col × 3 rows,  size 4 → 2 cols × 2 rows.
  const lastCardAssignments = useMemo(() => {
    if (!lastCardEnabled || gameMode !== 'loteria' || !loteriaBoards || loteriaBoards.length === 0) return [];
    const gCols = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : (loteriaGridSize || 4);
    const gRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[1]) : (loteriaGridSize || 4);
    const gridN = gCols; // kept so downstream canvas code (using gridN as cols) still works
    const totalFigs = loteriaFigures?.length || 54;
    const used = new Set();
    const assignments = [];
    const boardFigSets = loteriaBoards.map(b => new Set(b.figures || []));

    // Coverage rect (widthCells × heightCells)
    let widthCells, heightCells;
    if (lastCardSize === 9) {
      // 3x3 — no orientation (square coverage)
      widthCells = 3; heightCells = 3;
    } else if (lastCardSize === 6) {
      // 6 cells = 2x3 vertical or 3x2 horizontal
      if (lastCardOrientation === 'horizontal') { widthCells = 3; heightCells = 2; }
      else { widthCells = 2; heightCells = 3; }
    } else if (lastCardSize === 4) {
      widthCells = 2; heightCells = 2;
    } else if (lastCardSize === 3) {
      if (lastCardOrientation === 'horizontal') { widthCells = 3; heightCells = 1; }
      else { widthCells = 1; heightCells = 3; }
    } else {
      if (lastCardOrientation === 'horizontal') { widthCells = 2; heightCells = 1; }
      else { widthCells = 1; heightCells = 2; }
    }
    // Clamp to grid dimensions
    widthCells = Math.min(widthCells, gCols);
    heightCells = Math.min(heightCells, gRows);

    // Compute cell coverage per corner
    const cornerCells = () => {
      const cells = [];
      let startRow, startCol;
      switch (lastCardCorner) {
        case 'top-right':    startRow = 0; startCol = gCols - widthCells; break;
        case 'bottom-left':  startRow = gRows - heightCells; startCol = 0; break;
        case 'bottom-right': startRow = gRows - heightCells; startCol = gCols - widthCells; break;
        case 'center':
          // Place the focal rect in the MIDDLE of the grid. For odd
          // leftover space we bias slightly upward/left so a 2×2 on a
          // 4×4 grid lands at rows/cols 1..2 (the true centre).
          startRow = Math.floor((gRows - heightCells) / 2);
          startCol = Math.floor((gCols - widthCells) / 2);
          break;
        case 'top-left':
        default:             startRow = 0; startCol = 0; break;
      }
      for (let r = 0; r < heightCells; r++) {
        for (let c = 0; c < widthCells; c++) {
          cells.push({ row: startRow + r, col: startCol + c });
        }
      }
      return { startRow, startCol, widthCells, heightCells, cells };
    };
    const rect = cornerCells();

    // Build a DETERMINISTIC shuffled pool of figure indices seeded by the
    // loteriaBoards contents. This ensures the same boards (e.g., after loading
    // a saved template) produce the same focal-image assignments. Regenerating
    // or reshuffling boards changes the seed, yielding fresh random focals.
    // Seed = cheap 32-bit hash over all board figure arrays + board ids.
    let seed = 0x9e3779b9;
    for (let b = 0; b < loteriaBoards.length; b++) {
      const figs = loteriaBoards[b].figures || [];
      // Mix board id (if any) to avoid collisions when two boards share figures.
      const bid = loteriaBoards[b].id != null ? String(loteriaBoards[b].id) : String(b);
      for (let ci = 0; ci < bid.length; ci++) {
        seed = ((seed ^ bid.charCodeAt(ci)) * 0x01000193) >>> 0;
      }
      for (let fi = 0; fi < figs.length; fi++) {
        seed = ((seed ^ (figs[fi] + 1)) * 0x01000193) >>> 0;
      }
    }
    // mulberry32 PRNG — deterministic given the seed.
    const rng = (() => {
      let s = seed || 1;
      return () => {
        s |= 0; s = (s + 0x6D2B79F5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    })();
    const shuffledPool = Array.from({ length: totalFigs }, (_, i) => i);
    for (let i = shuffledPool.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [shuffledPool[i], shuffledPool[j]] = [shuffledPool[j], shuffledPool[i]];
    }

    // Also shuffle the BOARD processing order so the same board doesn't
    // systematically get the leftover slot when the figure pool is tight.
    const boardOrder = Array.from({ length: loteriaBoards.length }, (_, i) => i);
    for (let i = boardOrder.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [boardOrder[i], boardOrder[j]] = [boardOrder[j], boardOrder[i]];
    }

    boardOrder.forEach(boardIdx => {
      const board = loteriaBoards[boardIdx];
      if (!board) return;
      let focalFig = null;
      // Pass 1: prefer a random figure that is NOT already on this board AND not yet used.
      for (let k = 0; k < shuffledPool.length; k++) {
        const f = shuffledPool[k];
        if (!boardFigSets[boardIdx]?.has(f) && !used.has(f)) {
          focalFig = f; break;
        }
      }
      // Pass 2: if every globally-unused figure is already on this board,
      // RELAX the global-unique constraint but KEEP the "not on this board"
      // constraint — that's what guarantees the big focal image never
      // duplicates a small cell on the same board. The post-processing
      // dedup pass below will then try to swap this out for a unique fig.
      if (focalFig === null) {
        for (let k = 0; k < shuffledPool.length; k++) {
          const f = shuffledPool[k];
          if (!boardFigSets[boardIdx]?.has(f)) { focalFig = f; break; }
        }
      }
      // Pass 3 (degenerate): if literally every figure is on this board
      // (impossible unless figureCount <= cellCount), fall back to any
      // unused figure. Caps the worst case to "no crash"; visual duplicate
      // is unavoidable here by definition.
      if (focalFig === null) {
        for (let k = 0; k < shuffledPool.length; k++) {
          const f = shuffledPool[k];
          if (!used.has(f)) { focalFig = f; break; }
        }
      }
      if (focalFig === null) return;
      used.add(focalFig);
      assignments.push({ boardIdx, figure: focalFig, ...rect, gridN });
    });

    // ── POST-PROCESSING DEDUP ────────────────────────────────────────────
    // Bug fix: previously when 54 boards × 54 figs, Pass 2 could fire on
    // the last few boards and reuse a figure already assigned earlier
    // (e.g., arpa on both board 3 and board 54). This pass walks duplicate
    // assignments and tries to resolve them by:
    //   (a) Reassigning to a still-unused figure not on the board, OR
    //   (b) Swapping figures with another assignment if the swap keeps
    //       both boards' "focal not on own board" invariant intact.
    // Math: total figs ≥ board count guarantees a perfect matching exists
    // by Hall's theorem in the vast majority of real-world configurations
    // (every board has ~totalFigs - cellCount candidates). Up to 4 passes.
    for (let pass = 0; pass < 4; pass++) {
      const figCount = new Map();
      assignments.forEach(a => figCount.set(a.figure, (figCount.get(a.figure) || 0) + 1));
      let didFix = false;
      for (let i = 0; i < assignments.length; i++) {
        const fig = assignments[i].figure;
        if ((figCount.get(fig) || 0) <= 1) continue;
        const bIdx = assignments[i].boardIdx;
        // (a) Try to grab a globally unused figure not on this board.
        const usedNow = new Set(assignments.map(a => a.figure));
        let reassigned = false;
        for (let k = 0; k < shuffledPool.length; k++) {
          const f = shuffledPool[k];
          if (!boardFigSets[bIdx]?.has(f) && !usedNow.has(f)) {
            figCount.set(fig, (figCount.get(fig) || 1) - 1);
            assignments[i].figure = f;
            figCount.set(f, 1);
            didFix = true;
            reassigned = true;
            break;
          }
        }
        if (reassigned) continue;
        // (b) Swap with another assignment whose figure is compatible with
        //     this board AND whose own board is compatible with our figure.
        for (let j = 0; j < assignments.length; j++) {
          if (j === i) continue;
          const jFig = assignments[j].figure;
          if (jFig === fig) continue;
          const jBoardIdx = assignments[j].boardIdx;
          if (!boardFigSets[bIdx]?.has(jFig) && !boardFigSets[jBoardIdx]?.has(fig)) {
            assignments[i].figure = jFig;
            assignments[j].figure = fig;
            figCount.clear();
            assignments.forEach(a => figCount.set(a.figure, (figCount.get(a.figure) || 0) + 1));
            didFix = true;
            break;
          }
        }
      }
      if (!didFix) break;
    }
    return assignments;
  }, [lastCardEnabled, gameMode, loteriaBoards, loteriaGridSize, loteriaFigures, lastCardCorner, lastCardSize, lastCardOrientation]);
  lastCardAssignmentsRef.current = lastCardAssignments;

  // Hidden cells per board for Last Card (decorative cells behind the big image)
  const lastCardHiddenCellsByBoard = useMemo(() => {
    const map = {};
    lastCardAssignments.forEach(a => {
      const set = new Set();
      a.cells.forEach(c => set.add(c.row * a.gridN + c.col));
      map[a.boardIdx] = set;
    });
    return map;
  }, [lastCardAssignments]);

  // Pair-shift sliders: control GAPS between pairs / rows / columns.
  // Boards that belong to a pair share an anchor with their pair-mate on
  // the axis that must stay locked (X for horizontal pairs, Y for vertical
  // pairs, both for quad blocks) so a pair always moves as ONE unit.
  // Boards NOT in any pair use their own coordinates as anchor, so they
  // stay locked to their row/column and don't get orphaned.
  useEffect(() => {
    const prev = prevNeighborsPairShiftRef.current;
    const dx = neighborsPairShiftX - prev.x;
    const dy = neighborsPairShiftY - prev.y;
    prevNeighborsPairShiftRef.current = { x: neighborsPairShiftX, y: neighborsPairShiftY };
    if (dx === 0 && dy === 0) return;
    if (!neighborsEnabled) return;
    const pairs = neighborPairsRef.current || [];

    setLoteriaBoards(prevBoards => {
      if (!prevBoards || prevBoards.length === 0) return prevBoards;

      // Per-board anchor for clustering. Default = board's own coords.
      // Overridden below so pair-mates share an anchor on the locked axis.
      const anchorFor = prevBoards.map(b => ({ ax: b.x, ay: b.y }));
      pairs.forEach(p => {
        if (p.direction === 'quad') {
          const tl = prevBoards[p.tlBoardIdx];
          if (!tl) return;
          [p.tlBoardIdx, p.trBoardIdx, p.blBoardIdx, p.brBoardIdx].forEach(bi => {
            if (prevBoards[bi]) anchorFor[bi] = { ax: tl.x, ay: tl.y };
          });
        } else if (p.direction === 'vertical' || p.topBoardIdx != null) {
          // Vertical pair — share Y anchor (top board's Y) so both move as a unit
          // on the Y-shift slider. X anchor stays per-board (they may be at the
          // same X, but leaving individual X allows column shifts to still work
          // if boards happen to be in different columns).
          const topIdx = p.topBoardIdx != null ? p.topBoardIdx : p.leftBoardIdx;
          const top = prevBoards[topIdx];
          if (!top) return;
          [p.leftBoardIdx, p.rightBoardIdx].forEach(bi => {
            if (prevBoards[bi]) anchorFor[bi] = { ax: prevBoards[bi].x, ay: top.y };
          });
        } else {
          // Horizontal pair — share X anchor (left board's X) so the pair
          // shifts together horizontally. Y anchor stays per-board so
          // vertical row spacing still works uniformly across the row.
          const leftBoard = prevBoards[p.leftBoardIdx];
          if (!leftBoard) return;
          [p.leftBoardIdx, p.rightBoardIdx].forEach(bi => {
            if (prevBoards[bi]) anchorFor[bi] = { ax: leftBoard.x, ay: prevBoards[bi].y };
          });
        }
      });

      const clusterValues = (vals, tolerance) => {
        const sorted = [...new Set(vals)].sort((a, b) => a - b);
        const bins = [];
        sorted.forEach(v => {
          const lastBin = bins[bins.length - 1];
          if (lastBin && Math.abs(v - lastBin[lastBin.length - 1]) <= tolerance) lastBin.push(v);
          else bins.push([v]);
        });
        return bins;
      };
      const binIndexFor = (bins, v) => bins.findIndex(b => b.some(x => Math.abs(x - v) <= 0.5));

      const avgW = prevBoards.reduce((s, b) => s + (b.width || 0), 0) / Math.max(1, prevBoards.length);
      const tol = Math.max(20, avgW * 0.5);

      const colBins = clusterValues(anchorFor.map(a => a.ax), tol);
      const rowBins = clusterValues(anchorFor.map(a => a.ay), tol);
      const numCols = colBins.length;
      const numRows = rowBins.length;

      return prevBoards.map((b, i) => {
        const a = anchorFor[i];
        const colIdx = binIndexFor(colBins, a.ax);
        const rowIdx = binIndexFor(rowBins, a.ay);
        const sDx = numCols > 0 ? (colIdx - (numCols - 1) / 2) * dx : 0;
        const sDy = numRows > 0 ? (rowIdx - (numRows - 1) / 2) * dy : 0;
        return { ...b, x: b.x + sDx, y: b.y + sDy };
      });
    });
  }, [neighborsPairShiftX, neighborsPairShiftY, neighborsEnabled]);

  // Compute hidden cell indices per board (cells covered by connecting images) for auto-markup suppression
  // Cells hidden by the Neighbors connecting image — per-cell auto-marks
  // are SKIPPED on these cells because the big connecting-image mark already
  // covers them. Handles both horizontal pairs (last col of left / first col
  // of right) AND vertical pairs (last row of top / first row of bottom).
  // Without this, vertical pairs (auto OR custom) get one mark per underlying
  // cell — 4 marks instead of 1.
  const neighborHiddenCellsByBoard = useMemo(() => {
    const map = {};
    neighborPairs.forEach(pair => {
      const gridN = pair.gridN;
      const gridRows = pair.gridRows || gridN;
      if (pair.direction === 'vertical') {
        const { topBoardIdx, bottomBoardIdx, startCol, colsCovered } = pair;
        const cols = colsCovered || 2;
        if (topBoardIdx == null || bottomBoardIdx == null) return;
        if (!map[topBoardIdx]) map[topBoardIdx] = new Set();
        if (!map[bottomBoardIdx]) map[bottomBoardIdx] = new Set();
        for (let dc = 0; dc < cols; dc++) {
          // Top board: last row, columns [startCol .. startCol+cols-1]
          map[topBoardIdx].add((gridRows - 1) * gridN + (startCol + dc));
          // Bottom board: first row, same columns
          map[bottomBoardIdx].add(0 * gridN + (startCol + dc));
        }
      } else if (pair.direction === 'quad') {
        // Quad pairs cover a kxk patch at each board's inner corner.
        const k = pair.cellsPerBoard || 2;
        const board = (idx, rowFromBottom, colFromRight) => {
          if (idx == null) return;
          if (!map[idx]) map[idx] = new Set();
          for (let dr = 0; dr < k; dr++) {
            for (let dc = 0; dc < k; dc++) {
              const r = rowFromBottom ? (gridRows - 1 - dr) : dr;
              const c = colFromRight ? (gridN - 1 - dc) : dc;
              map[idx].add(r * gridN + c);
            }
          }
        };
        board(pair.tlBoardIdx, true,  true);
        board(pair.trBoardIdx, true,  false);
        board(pair.blBoardIdx, false, true);
        board(pair.brBoardIdx, false, false);
      } else {
        // Horizontal (default)
        const { leftBoardIdx, rightBoardIdx, startRow, rowsCovered } = pair;
        const rows = rowsCovered || 2;
        if (leftBoardIdx == null || rightBoardIdx == null) return;
        if (!map[leftBoardIdx]) map[leftBoardIdx] = new Set();
        if (!map[rightBoardIdx]) map[rightBoardIdx] = new Set();
        for (let dr = 0; dr < rows; dr++) {
          map[leftBoardIdx].add((startRow + dr) * gridN + (gridN - 1));
          map[rightBoardIdx].add((startRow + dr) * gridN + 0);
        }
      }
    });
    return map;
  }, [neighborPairs]);

  // Mark a Loteria figure across all boards by name or number
  const markLoteriaFigure = useCallback((callStr) => {
    const input = callStr.trim().toLowerCase();
    if (!input) return false;

    // Try matching by number first
    const num = parseInt(input, 10);
    let targetIdx = -1;
    if (!isNaN(num) && loteriaFigureLookup.byNumber.hasOwnProperty(num)) {
      targetIdx = loteriaFigureLookup.byNumber[num];
    } else if (loteriaFigureLookup.byName.hasOwnProperty(input)) {
      targetIdx = loteriaFigureLookup.byName[input];
    }
    if (targetIdx < 0) return 'unknown';


    const gridSize = loteriaGridSize;
    const LOTERIA_CELL_RATIO = (loteriaCellShape === 'square' || loteriaDeck === 'square') ? 1.0 : 1.25;

    // Pre-compute the display label / source-call identifier so every auto
    // mark generated by THIS call can be tagged with it. That way the user
    // can click an "×" on a Called-list pill and we can precisely delete
    // the marks tied to that call (see removeCalledNumber below).
    const _fig = loteriaFigures[targetIdx];
    const sourceCall = _fig ? _fig.name : input.toUpperCase();

    // Markup Mode: Off — record the call in the tracker but skip every
    // visual side effect (no highlights, no marks, no border rings).
    if (autoMarkupMode === 'off') {
      saveToHistory();
      setCalledNumbers(prev => [...prev, sourceCall]);
      return true;
    }

    saveToHistory();
    const marksByBoard = [];
    const marksByBoardMap = {}; // boardIndex -> index in marksByBoard (for merging pair marks)
    const matchedBoardIds = new Set();
    const matchedCells = [];
    // Boards where the called figure has a BIG compound mark already (LCS focal
    // OR Neighbors connecting figure). Skip per-cell marks on those boards so
    // we don't get a "doubled" appearance — one big oval over the focal/connect
    // area + a smaller per-cell oval where the same figure happens to land
    // elsewhere on that board. The big mark is the user-facing one; suppressing
    // the small one is the cleanest way to keep the visual consistent across
    // normal and fullscreen modes (where the two would otherwise spread apart).
    // NOTE: This does NOT change Neighbors big-mark logic itself.
    const lcsFocalBoards = new Set();
    (lastCardAssignmentsRef.current || []).forEach(a => {
      if (a.figure === targetIdx) lcsFocalBoards.add(a.boardIdx);
    });
    (neighborPairsRef.current || []).forEach(pair => {
      if (pair.figure === targetIdx) {
        lcsFocalBoards.add(pair.leftBoardIdx);
        lcsFocalBoards.add(pair.rightBoardIdx);
      }
    });
    loteriaBoards.forEach((board, boardIndex) => {
      // Skip non-isolated boards when isolate markup is active
      if (isolateMarkupOnlyRef.current && isolatedBoardNumsRef.current && !isolatedBoardNumsRef.current.has(boardIndex + 1)) return;
      // Skip per-cell marks entirely when the BIG LCS or Neighbors mark covers this board
      if (lcsFocalBoards.has(boardIndex)) return;
      const hiddenCells = neighborHiddenCellsByBoard[boardIndex];
      const lcHiddenCells = lastCardHiddenCellsByBoard[boardIndex];
      const boardMarks = [];
      for (let i = 0; i < board.figures.length; i++) {
        // Skip cells hidden under a Neighbors connecting image or a Last Card focal image
        if (hiddenCells && hiddenCells.has(i)) continue;
        if (lcHiddenCells && lcHiddenCells.has(i)) continue;
        if (board.figures[i] === targetIdx) {
          matchedBoardIds.add(board.id);
          const gCols = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[0]) : gridSize;
          const col = i % gCols;
          const row = Math.floor(i / gCols);
          matchedCells.push({ boardId: board.id, row, col });
          const cw = board.width / gCols;
          const ch = cw * LOTERIA_CELL_RATIO;
          const cx = board.x + col * cw + cw / 2;
          const cy = board.y + row * ch + ch / 2;
          const markSize = cw * 0.35 * (autoMarkupScale / 100);
          boardMarks.push({
            id: `automark-${Date.now()}-${board.id}-${row}-${col}-${Math.random().toString(36).substr(2,5)}`,
            type: 'freehand',
            points: [{ x: cx, y: cy }],
            color: markupColor,
            thickness: markupThickness > 0 ? markupThickness : 3,
            tipStyle: markupTipStyle,
            opacity: markupOpacity,
            autoMarkSize: markSize,
            boardNum: boardIndex + 1,
            sourceCall,
          });
        }
      }
      if (boardMarks.length > 0) {
        marksByBoardMap[boardIndex] = marksByBoard.length;
        marksByBoard.push({ boardIndex, boardId: board.id, marks: boardMarks });
      }
    });

    // Last Card Standing: if targetIdx matches any board's focal figure,
    // place ONE big mark centered on that board's focal image.
    const lcOffX = lastCardOffsetX || 0;
    const lcOffY = lastCardOffsetY || 0;
    (lastCardAssignmentsRef.current || []).forEach(a => {
      if (a.figure !== targetIdx) return;
      if (isolateMarkupOnlyRef.current && isolatedBoardNumsRef.current && !isolatedBoardNumsRef.current.has(a.boardIdx + 1)) return;
      const board = loteriaBoards[a.boardIdx];
      if (!board) return;
      matchedBoardIds.add(board.id);
      const cw = board.width / a.gridN;
      const ch = cw * LOTERIA_CELL_RATIO;
      const connX = board.x + a.startCol * cw + lcOffX;
      const connY = board.y + a.startRow * ch + lcOffY;
      const connW = a.widthCells * cw;
      const connH = a.heightCells * ch;
      const markSize = Math.min(connW, connH) * 0.45 * (autoMarkupScale / 100);
      const bigMark = {
        id: `automark-${Date.now()}-lc-${board.id}-${Math.random().toString(36).substr(2,5)}`,
        type: 'freehand',
        points: [{ x: connX + connW / 2, y: connY + connH / 2 }],
        color: markupColor,
        thickness: markupThickness > 0 ? markupThickness : 3,
        tipStyle: markupTipStyle,
        opacity: markupOpacity,
        autoMarkSize: markSize,
        boardNum: a.boardIdx + 1,
        sourceCall,
      };
      if (marksByBoardMap[a.boardIdx] !== undefined) {
        marksByBoard[marksByBoardMap[a.boardIdx]].marks.push(bigMark);
      } else {
        marksByBoardMap[a.boardIdx] = marksByBoard.length;
        marksByBoard.push({ boardIndex: a.boardIdx, boardId: board.id, marks: [bigMark] });
      }
    });

    // Neighbors (mode 2b): if targetIdx matches a connecting-image figure,
    // place ONE big mark centered on the whole connecting image (not 4 per-cell marks).
    // Skipped entirely when a Custom Connecting Image is in use — the custom
    // image has no associated figure to call, so it must never receive the
    // big pair mark.
    (neighborPairsRef.current || []).forEach(pair => {
      if (pair.figure !== targetIdx) return;
      if (neighborsCustomImage) return;
      const isQuad = pair.direction === 'quad';
      const isVertical = pair.direction === 'vertical';
      const gCols = pair.gridN;
      const gRows = pair.gridRows || pair.gridN;
      let connX, connY, connW, connH;
      let quadBoardIdxs = [];

      if (isQuad) {
        const tl = loteriaBoards[pair.tlBoardIdx];
        const tr = loteriaBoards[pair.trBoardIdx];
        const bl = loteriaBoards[pair.blBoardIdx];
        const br = loteriaBoards[pair.brBoardIdx];
        if (!tl || !tr || !bl || !br) return;
        quadBoardIdxs = [pair.tlBoardIdx, pair.trBoardIdx, pair.blBoardIdx, pair.brBoardIdx];
        const k = pair.cellsPerBoard || 1;
        const cwTL = tl.width / gCols, chTL = cwTL * LOTERIA_CELL_RATIO;
        const cwTR = tr.width / gCols, chTR = cwTR * LOTERIA_CELL_RATIO;
        const cwBL = bl.width / gCols, chBL = cwBL * LOTERIA_CELL_RATIO;
        const cwBR = br.width / gCols, chBR = cwBR * LOTERIA_CELL_RATIO;
        // Inner corner rects (each board's nearest-to-center cell block)
        const tlRect = { x: tl.x + (gCols - k) * cwTL, y: tl.y + (gRows - k) * chTL, w: k * cwTL, h: k * chTL };
        const trRect = { x: tr.x,                      y: tr.y + (gRows - k) * chTR, w: k * cwTR, h: k * chTR };
        const blRect = { x: bl.x + (gCols - k) * cwBL, y: bl.y,                      w: k * cwBL, h: k * chBL };
        const brRect = { x: br.x,                      y: br.y,                      w: k * cwBR, h: k * chBR };
        connX = Math.min(tlRect.x, blRect.x);
        connY = Math.min(tlRect.y, trRect.y);
        const right = Math.max(trRect.x + trRect.w, brRect.x + brRect.w);
        const bottom = Math.max(blRect.y + blRect.h, brRect.y + brRect.h);
        connW = right - connX;
        connH = bottom - connY;
      } else {
        const aBoard = loteriaBoards[pair.leftBoardIdx];   // top (vertical) / left (horizontal)
        const bBoard = loteriaBoards[pair.rightBoardIdx];  // bottom (vertical) / right (horizontal)
        if (!aBoard || !bBoard) return;
        if (isVertical) {
          const cwT = aBoard.width / gCols;
          const cwB = bBoard.width / gCols;
          const chT = cwT * LOTERIA_CELL_RATIO;
          const chB = cwB * LOTERIA_CELL_RATIO;
          const colsCovered = pair.colsCovered || 2;
          const startCol = pair.startCol || 0;
          const topX  = aBoard.x + startCol * cwT;
          const topY  = aBoard.y + (gRows - 1) * chT;
          const botX  = bBoard.x + startCol * cwB;
          const botY  = bBoard.y;
          connX = Math.min(topX, botX);
          connY = topY;
          connW = colsCovered * Math.max(cwT, cwB);
          connH = (botY + chB) - topY;
        } else {
          const cwL = aBoard.width / gCols;
          const cwR = bBoard.width / gCols;
          const chL = cwL * LOTERIA_CELL_RATIO;
          const chR = bBoard.width / gCols * LOTERIA_CELL_RATIO;
          const rowsCovered = pair.rowsCovered || 2;
          const leftX  = aBoard.x + (gCols - 1) * cwL;
          const leftY  = aBoard.y + pair.startRow * chL;
          const rightX = bBoard.x;
          const rightY = bBoard.y + pair.startRow * chR;
          connX = leftX;
          connY = Math.min(leftY, rightY);
          connW = (rightX + cwR) - leftX;
          connH = rowsCovered * Math.max(chL, chR);
        }
      }
      const markCenterX = connX + connW / 2;
      const markCenterY = connY + connH / 2;
      // Size the mark to cover most of the connecting rect
      const markSize = Math.min(connW, connH) * 0.45 * (autoMarkupScale / 100);

      // Anchor the big mark to an ISOLATED board when Isolate Winners is active,
      // so the CanvasEditor mark-filter (which skips non-isolated boardNums) doesn't
      // drop the mark. Otherwise default to the first board of the pair/quad.
      const isolationActive = isolatedBoardNumsRef.current && isolatedBoardNumsRef.current.size > 0;
      const boardIdxsForMark = isQuad ? quadBoardIdxs : [pair.leftBoardIdx, pair.rightBoardIdx];
      const isolatedBoardIdxs = boardIdxsForMark.filter(bi => !isolationActive || isolatedBoardNumsRef.current.has(bi + 1));
      if (isolationActive && isolatedBoardIdxs.length === 0) return;

      const anchorBIdx = isolatedBoardIdxs[0] != null ? isolatedBoardIdxs[0] : boardIdxsForMark[0];
      const anchorBoard = loteriaBoards[anchorBIdx];
      matchedBoardIds.add(anchorBoard.id);
      // Highlight every other partner board (skip those filtered out by isolation)
      (isolatedBoardIdxs.length > 0 ? isolatedBoardIdxs : boardIdxsForMark).forEach(bi => {
        if (bi === anchorBIdx) return;
        const partnerBoard = loteriaBoards[bi];
        if (partnerBoard) matchedBoardIds.add(partnerBoard.id);
      });

      const bigMark = {
        id: `automark-${Date.now()}-nb-${isQuad ? 'quad' : 'pair'}-${anchorBoard.id}-${Math.random().toString(36).substr(2,5)}`,
        type: 'freehand',
        points: [{ x: markCenterX, y: markCenterY }],
        color: markupColor,
        thickness: markupThickness > 0 ? markupThickness : 3,
        tipStyle: markupTipStyle,
        opacity: markupOpacity,
        autoMarkSize: markSize,
        boardNum: anchorBIdx + 1,
        sourceCall,
      };
      if (marksByBoardMap[anchorBIdx] !== undefined) {
        marksByBoard[marksByBoardMap[anchorBIdx]].marks.push(bigMark);
      } else {
        marksByBoardMap[anchorBIdx] = marksByBoard.length;
        marksByBoard.push({ boardIndex: anchorBIdx, boardId: anchorBoard.id, marks: [bigMark] });
      }
    });

    // Highlight ALL matching boards immediately (skip in border/border-markup modes)
    if (autoMarkupMode !== 'border' && autoMarkupMode !== 'border-markup') {
      setHighlightedBoards(matchedBoardIds);
    } else {
      setHighlightedBoards(new Set());
    }
    // Accumulator: keep track of every board auto-marked across calls.
    if (matchedBoardIds && matchedBoardIds.size > 0) {
      setAutoMarkedBoardsEver(prev => {
        const next = new Set(prev);
        matchedBoardIds.forEach(id => next.add(id));
        return next;
      });
    }

    // Border highlight (border or border-markup mode): clear previous, then highlight cell borders
    if (autoMarkupMode === 'border' || autoMarkupMode === 'border-markup') {
      setHighlightedCells([]);

      if (autoMarkupMode === 'border' && autoMarkupTimed && matchedCells.length > 1) {
        // Border-only mode: timed board-by-board highlights
        if (timedMarkupRef.current) clearTimeout(timedMarkupRef.current);
        const cellsByBoard = [];
        const boardOrder = [];
        matchedCells.forEach(cell => {
          const bi = loteriaBoards.findIndex(b => b.id === cell.boardId);
          if (!cellsByBoard[bi]) { cellsByBoard[bi] = []; boardOrder.push(bi); }
          cellsByBoard[bi].push(cell);
        });
        const sortedKeys = [...new Set(boardOrder)].sort((a, b) => a - b);
        let idx = 0;
        const placeNext = () => {
          if (idx < sortedKeys.length) {
            const cells = cellsByBoard[sortedKeys[idx]];
            if (cells) setHighlightedCells(prev => [...prev, ...cells]);
            idx++;
            if (idx < sortedKeys.length) {
              timedMarkupRef.current = setTimeout(placeNext, autoMarkupDelay);
            }
          }
        };
        placeNext();
      } else {
        // Border+Markup mode OR non-timed: ALL highlights at once instantly
        setHighlightedCells(matchedCells);
      }
    }

    // Build display label: use figure name (sourceCall was computed at top)
    const displayLabel = sourceCall;

    if (marksByBoard.length > 0 && (autoMarkupMode === 'both' || autoMarkupMode === 'border-markup')) {
      marksByBoard.sort((a, b) => a.boardIndex - b.boardIndex);

      if (autoMarkupMode === 'border-markup') {
        // Border+Markup: show all highlights first, then place markups after a brief delay
        const placeMarkups = () => {
          if (autoMarkupTimed && marksByBoard.length > 1) {
            const queue = marksByBoard.map(b => [...b.marks]);
            let idx = 0;
            setAutoMarkupInProgress(true);
            const placeNext = () => {
              if (idx < queue.length && queue[idx]) {
                const marks = queue[idx];
                setMarkupElements(prev => [...prev, ...marks]);
                idx++;
                if (idx < queue.length) {
                  timedMarkupRef.current = setTimeout(placeNext, autoMarkupDelay);
                } else {
                  setAutoMarkupInProgress(false);
                }
              } else {
                setAutoMarkupInProgress(false);
              }
            };
            // 1 second delay before first markup
            timedMarkupRef.current = setTimeout(placeNext, 1000);
          } else {
            const allMarks = marksByBoard.flatMap(b => b.marks);
            setMarkupElements(prev => [...prev, ...allMarks]);
          }
        };
        // Delay markup placement so border highlights show first
        timedMarkupRef.current = setTimeout(placeMarkups, 600);
      } else {
        // 'both' mode: instant or timed as before
        if (autoMarkupTimed && marksByBoard.length > 1) {
          if (timedMarkupRef.current) clearTimeout(timedMarkupRef.current);
          const queue = marksByBoard.map(b => [...b.marks]);
          let idx = 0;
          setAutoMarkupInProgress(true);
          const placeNext = () => {
            if (idx < queue.length && queue[idx]) {
              const marks = queue[idx];
              setMarkupElements(prev => [...prev, ...marks]);
              idx++;
              if (idx < queue.length) {
                timedMarkupRef.current = setTimeout(placeNext, autoMarkupDelay);
              } else {
                setAutoMarkupInProgress(false);
              }
            } else {
              setAutoMarkupInProgress(false);
            }
          };
          // 1 second delay before first markup
          timedMarkupRef.current = setTimeout(placeNext, 1000);
        } else {
          const allMarks = marksByBoard.flatMap(b => b.marks);
          setMarkupElements(prev => [...prev, ...allMarks]);
        }
      }
      setCalledNumbers(prev => [...prev, displayLabel]);
      return true;
    }
    if (matchedBoardIds.size > 0) {
      setCalledNumbers(prev => [...prev, displayLabel]);
      return true;
    }
    return 'not_on_boards';
  }, [loteriaBoards, loteriaFigures, loteriaFigureLookup, loteriaGridSize, loteriaCellShape,
      markupColor, markupThickness, markupTipStyle, markupOpacity, autoMarkupScale, autoMarkupMode, autoMarkupTimed, autoMarkupDelay, saveToHistory, neighborHiddenCellsByBoard, neighborsCustomImage]);

  const markOddOrEven = useCallback((type) => {
    saveToHistory();
    const isOdd = type === 'odd';
    const allMarks = [];
    bingoBoards.forEach((board) => {
      for (let row = 0; row < 5; row++) {
        for (let col = 0; col < 5; col++) {
          const val = board.numbers[row][col];
          if (val === "FREE") continue;
          const num = typeof val === 'number' ? val : parseInt(val, 10);
          if (isNaN(num)) continue;
          if ((isOdd && num % 2 === 1) || (!isOdd && num % 2 === 0)) {
            const cellSize = board.width / 5;
            const cx = board.x + col * cellSize + cellSize / 2;
            const cy = board.y + cellSize + row * cellSize + cellSize / 2;
            const markSize = cellSize * 0.35 * (autoMarkupScale / 100);
            allMarks.push({
              id: `automark-${Date.now()}-${board.id}-${row}-${col}-${Math.random().toString(36).substr(2,5)}`,
              type: 'freehand',
              points: [{ x: cx, y: cy }],
              color: markupColor,
              thickness: markupThickness > 0 ? markupThickness : 3,
              tipStyle: markupTipStyle,
              opacity: markupOpacity,
              autoMarkSize: markSize,
            });
          }
        }
      }
    });
    if (allMarks.length > 0) {
      setMarkupElements(prev => [...prev, ...allMarks]);
      setCalledNumbers(prev => [...prev, type.toUpperCase()]);
      return allMarks.length;
    }
    return 0;
  }, [bingoBoards, markupColor, markupThickness, markupTipStyle, markupOpacity, autoMarkupScale, saveToHistory]);

  // Auto-Markup submit. Now takes the typed value as an argument (from
  // AutoMarkupInput) so the parent doesn't need a controlled state for every
  // keystroke. Returning value-as-arg lets us drop a top-level state that was
  // re-rendering the whole tree on every key press — the reason typing felt
  // sluggish under Streamlabs / OBS screen capture.
  const handleAutoMarkupSubmit = useCallback((rawValue) => {
    const raw = (rawValue || '').trim();
    if (!raw) return;
    if (!isPaidSubscriber()) {
      toast.error("Auto-Markup is a paid-only feature. Upgrade to unlock!");
      setShowSubscription(true);
      return;
    }

    if (gameMode === 'loteria') {
      // Loteria mode: match by figure name or number
      const calls = raw.split(/[,]+/).map(s => s.trim()).filter(Boolean);
      let matched = 0;
      let unknowns = [];
      let notOnBoards = [];
      calls.forEach(c => {
        const result = markLoteriaFigure(c);
        if (result === true) matched++;
        else if (result === 'unknown') unknowns.push(c);
        else if (result === 'not_on_boards') notOnBoards.push(c);
      });
      if (matched > 0 && showMarkNotifications) toast.success(`Marked ${matched} figure${matched > 1 ? 's' : ''}`);
      if (unknowns.length > 0) toast.error(`Unknown figure: ${unknowns.join(', ')}`);
      if (notOnBoards.length > 0) toast.info(`"${notOnBoards.join(', ')}" not on any board`);
      return;
    }

    const input = raw.toLowerCase();
    // Handle "odd" and "even" keywords
    if (input === 'odd' || input === 'even') {
      setHighlightedBoards(new Set());
      const count = markOddOrEven(input);
      if (count > 0 && showMarkNotifications) toast.success(`Marked ${count} ${input} number${count > 1 ? 's' : ''}`);
      else if (count === 0) toast.error(`No ${input} numbers found`);
      return;
    }
    const calls = raw.split(/[,\s]+/).filter(Boolean);
    let matched = 0;
    calls.forEach(c => { if (markAllAtOnce(c)) matched++; });
    if (matched > 0 && showMarkNotifications) toast.success(`Marked ${matched} call${matched > 1 ? 's' : ''}`);
    else if (matched === 0) toast.error(`No matches found`);
  }, [gameMode, markAllAtOnce, markLoteriaFigure, markOddOrEven, showMarkNotifications, isPaidSubscriber]);

  const clearCalledNumbers = useCallback(() => {
    saveToHistory();
    setMarkupElements(prev => prev.filter(el => !el.id.startsWith('automark-')));
    setCalledNumbers([]);
    setHighlightedBoards(new Set());
    setAutoMarkedBoardsEver(new Set());
    setHighlightedCells([]);
    setAlmostWinnerBoards(new Set());
    setAlmostWinnerRows({});
    setAlmostWinnerCols({});
    setBingoWinner(null);
    setLoteriaLineWinner(null);
    setPatternWinner(null);
    winnerShownOnceRef.current = false;
    lineWinnersShownRef.current = new Set();
    shownWinnerBoardsRef.current = new Set();
    lockedAlmostBoardsRef.current = new Set();
    winnerCelebrationsShownRef.current = 0;
    shownPatternWinnersRef.current = new Set();
    patternWinnerDismissedRef.current = false;
    if (autoMarkupTimerRef.current) {
      clearInterval(autoMarkupTimerRef.current);
      autoMarkupTimerRef.current = null;
    }
    autoMarkupQueueRef.current = [];
  }, [saveToHistory]);

  // Remove a single Called-list entry (the "×" pill button). Removes the
  // call from the tracker, deletes every auto-mark tagged with that call,
  // clears any active winner celebration and almost-winner state so those
  // aren't left showing stale wins that depend on the removed mark. The
  // almost-winner effect + winner-detection effects re-run automatically
  // when markupElements/calledNumbers change so no explicit recompute is
  // required here.
  const removeCalledNumber = useCallback((index) => {
    if (typeof index !== 'number' || index < 0) return;
    saveToHistory();
    // Snapshot the call string BEFORE trimming so we know which sourceCall
    // tag to strip from markupElements.
    setCalledNumbers(prev => {
      const removed = prev[index];
      if (removed == null) return prev;
      // Strip any auto-mark whose sourceCall matches (Bingo call OR Loteria
      // figure name). Manual (non-auto) marks are untouched because they
      // don't carry a sourceCall field.
      setMarkupElements(mprev => mprev.filter(el => !(el.id && el.id.startsWith('automark-') && el.sourceCall === removed)));
      return prev.filter((_, i) => i !== index);
    });
    // Dismiss active celebrations + almost-winner glow so the user isn't
    // staring at a Win/Almost overlay whose triggering call just vanished.
    setBingoWinner(null);
    setLoteriaLineWinner(null);
    setPatternWinner(null);
    setAlmostWinnerBoards(new Set());
    setAlmostWinnerRows({});
    setAlmostWinnerCols({});
    setHighlightedBoards(new Set());
    setHighlightedCells([]);
    // Reset the celebration bookkeeping so brand-new winners created by
    // subsequent calls can fire their overlay again.
    winnerShownOnceRef.current = false;
    lineWinnersShownRef.current = new Set();
    shownWinnerBoardsRef.current = new Set();
    lockedAlmostBoardsRef.current = new Set();
    winnerCelebrationsShownRef.current = 0;
    shownPatternWinnersRef.current = new Set();
    patternWinnerDismissedRef.current = false;
  }, [saveToHistory]);

  // --- LIVE BINGO CALLER → AUTO-MARKUP BRIDGE ---
  // Use a ref-based callback so the always-mounted caller page always calls the latest version
  const markAllAtOnceRef = useRef(markAllAtOnce);
  markAllAtOnceRef.current = markAllAtOnce;
  const markLoteriaFigureRef = useRef(markLoteriaFigure);
  markLoteriaFigureRef.current = markLoteriaFigure;
  const isPaidSubscriberRef = useRef(isPaidSubscriber);
  isPaidSubscriberRef.current = isPaidSubscriber;

  const handleCallerBallDraw = useCallback((ballOrCard) => {
    if (!isPaidSubscriberRef.current()) return;
    if (typeof ballOrCard === 'number') {
      const letter = ballOrCard <= 15 ? 'B' : ballOrCard <= 30 ? 'I' : ballOrCard <= 45 ? 'N' : ballOrCard <= 60 ? 'G' : 'O';
      markAllAtOnceRef.current(`${letter}${ballOrCard}`);
    } else if (ballOrCard && ballOrCard.name) {
      // Prefer leading number from the name ("1 - El Gallo" → "1"). For plain
      // Enhanced names ("El Gallo") fall back to the figure name itself —
      // markLoteriaFigure looks up by name when no number is present.
      const m = ballOrCard.name.match(/^(\d+)/);
      const lookup = m ? m[1] : ballOrCard.name;
      markLoteriaFigureRef.current(lookup);
    }
  }, []); // stable reference — reads latest via refs

  // Capture canvas image when entering caller page. When a Caller Background
  // animation is set, capture the canvas WITHOUT the background fill so the
  // looping video shows through everywhere except where boards/markups are
  // drawn — making the video effectively BE the template's background.
  // Live Caller canvas snapshot — capture canvas as PNG and feed it into the
  // BingoCallerPage. Wrapped so any failure (tainted canvas, OOM on huge
  // PNG strings, transient canvas-empty during re-render) never crashes the
  // page. On failure the caller page just keeps the last good image instead
  // of going black.
  useEffect(() => {
    if (showCallerPage && canvasRef.current) {
      const timer = setTimeout(() => {
        if (!canvasRef.current) return;
        const safeCapture = () => {
          try {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            if (dataUrl && dataUrl.length > 100) setCallerCanvasImage(dataUrl);
          } catch (e) {
            console.warn('Live Caller capture failed (canvas may be tainted):', e?.message || e);
          }
        };
        const wantTransparent = !!callerBgVideoSrc;
        if (wantTransparent) {
          setSkipBackgroundForExport(true);
          setTimeout(() => { safeCapture(); setSkipBackgroundForExport(false); }, 250);
        } else {
          safeCapture();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showCallerPage, callerBgVideoSrc]);

  // Re-capture canvas image after markups change while on caller page.
  // Same safe-capture guard so a single bad draw doesn't black-out the page.
  useEffect(() => {
    if (showCallerPage && canvasRef.current) {
      const timer = setTimeout(() => {
        if (!canvasRef.current) return;
        const safeCapture = () => {
          try {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            if (dataUrl && dataUrl.length > 100) setCallerCanvasImage(dataUrl);
          } catch (e) {
            console.warn('Live Caller re-capture failed:', e?.message || e);
          }
        };
        const wantTransparent = !!callerBgVideoSrc;
        if (wantTransparent) {
          setSkipBackgroundForExport(true);
          setTimeout(() => { safeCapture(); setSkipBackgroundForExport(false); }, 250);
        } else {
          safeCapture();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showCallerPage, markupElements, highlightedBoards, highlightedCells, callerBgVideoSrc]);

  // Check for BINGO winner and "almost winner" (1 away) after auto-markup changes
  useEffect(() => {
    if (bingoBoards.length === 0 || calledNumbers.length === 0) {
      setAlmostWinnerBoards(new Set());
      return;
    }
    // After winner dismissed, clear transient almost-winner stars but keep
    // locked stars on boards that already won (per user request).
    if (winnerShownOnceRef.current) {
      setAlmostWinnerBoards(new Set(lockedAlmostBoardsRef.current));
      return;
    }
    const autoMarks = markupElements.filter(el => el.id.startsWith('automark-'));
    const newAlmostWinners = new Set();
    const newWinners = [];

    for (let boardIndex = 0; boardIndex < bingoBoards.length; boardIndex++) {
      const board = bingoBoards[boardIndex];
      let markedCount = 0;
      for (let row = 0; row < 5; row++) {
        for (let col = 0; col < 5; col++) {
          const val = board.numbers[row][col];
          if (val === "FREE") { markedCount++; continue; }
          const cellSize = board.width / 5;
          const cx = board.x + col * cellSize + cellSize / 2;
          const cy = board.y + cellSize + row * cellSize + cellSize / 2;
          const hasMark = autoMarks.some(m =>
            m.id.includes(board.id) && m.points && m.points.length > 0 &&
            Math.abs(m.points[0].x - cx) < cellSize * 0.5 &&
            Math.abs(m.points[0].y - cy) < cellSize * 0.5
          );
          if (hasMark) markedCount++;
        }
      }
      // Full winner — collect ALL winning boards (but only if isolated when Isolate Winners is active)
      if (markedCount === 25 && !shownWinnerBoardsRef.current.has(board.id)) {
        const boardNum = boardIndex + 1;
        const isolateActive = isolatedBoardNumsRef.current && isolatedBoardNumsRef.current.size > 0;
        if (!isolateActive || isolatedBoardNumsRef.current.has(boardNum)) {
          newWinners.push({ boardIndex, boardNum });
          shownWinnerBoardsRef.current.add(board.id);
        }
      }
      // Almost winner: 24 of 25 marked (1 away). Isolate Winners filter respected.
      if (markedCount === 24) {
        const boardNum = boardIndex + 1;
        const isolateActive = isolatedBoardNumsRef.current && isolatedBoardNumsRef.current.size > 0;
        if (!isolateActive || isolatedBoardNumsRef.current.has(boardNum)) {
          newAlmostWinners.add(board.id);
        }
      }
      // Keep the Almost-Winner star on boards that have already been
      // celebrated as full winners — so the star doesn't disappear the moment
      // the win animation fires. Sticks until calls are cleared / reset.
      if (markedCount === 25 && shownWinnerBoardsRef.current.has(board.id)) {
        lockedAlmostBoardsRef.current.add(board.id);
        newAlmostWinners.add(board.id);
      }
    }
    if (newWinners.length > 0 && !autoMarkupInProgress) {
      // Delay winner celebration so Almost-Winner stars are visible first
      setTimeout(() => {
        setBingoWinner(prev => {
          if (prev && prev.length > 0) {
            // Existing overlay — append without consuming another slot.
            return [...prev, ...newWinners];
          }
          // New overlay: only the FIRST batch celebrates unless "Continue
          // Announcing After Dismiss" is on.
          if (!continueAnnouncing && winnerCelebrationsShownRef.current >= 1) {
            return null; // already shown one — skip
          }
          winnerCelebrationsShownRef.current += 1;
          return newWinners;
        });
      }, 2500);
    } else if (newWinners.length > 0 && autoMarkupInProgress) {
      // Auto-markup still running — rollback the "shown" bookkeeping for these
      // so we re-detect them once marking finishes and fire a single batch
      // celebration at the end of the round.
      newWinners.forEach(w => {
        const bid = bingoBoards[w.boardIndex]?.id;
        if (bid) shownWinnerBoardsRef.current.delete(bid);
      });
    }
    setAlmostWinnerBoards(newAlmostWinners);
  }, [markupElements, bingoBoards, calledNumbers, autoMarkupInProgress]);

  // Check for Loteria winner and "almost winner" (1 away) after auto-markup changes
  useEffect(() => {
    if (gameMode !== 'loteria' || loteriaBoards.length === 0 || calledNumbers.length === 0) {
      if (gameMode === 'loteria') {
        setAlmostWinnerBoards(new Set());
        setAlmostWinnerRows({});
        setAlmostWinnerCols({});
      }
      return;
    }
    if (winnerShownOnceRef.current) {
      // Keep locked almost-stars on boards that were already celebrated.
      setAlmostWinnerBoards(new Set(lockedAlmostBoardsRef.current));
      setAlmostWinnerRows({});
      setAlmostWinnerCols({});
      return;
    }
    const autoMarks = markupElements.filter(el => el.id.startsWith('automark-'));
    const newAlmostWinners = new Set();
    const newAlmostRows = {};
    const newAlmostCols = {};
    const newWinners = [];
    const newLineWinners = []; // batched row + column winners across all boards
    const newPatternWinners = []; // batched pattern winners (X, T, Square, 4 Corners)
    // Pattern-cell helper — returns the Set of "r-c" strings that must all be
    // marked for the given pattern to be complete on a gRows×gCols board.
    const getPatternCells = (pattern, gR, gC) => {
      const cells = new Set();
      if (pattern === 'x') {
        // Two diagonals — sample max(gR, gC) points and pick the nearest cell,
        // so the X shape looks visually balanced on rectangular grids too.
        const n = Math.max(gR, gC);
        for (let i = 0; i < n; i++) {
          const r = Math.round(i * (gR - 1) / Math.max(1, n - 1));
          const c = Math.round(i * (gC - 1) / Math.max(1, n - 1));
          cells.add(`${r}-${c}`);
          cells.add(`${r}-${gC - 1 - c}`);
        }
      } else if (pattern === 't') {
        // Top row + center column
        for (let c = 0; c < gC; c++) cells.add(`0-${c}`);
        const cc = Math.floor(gC / 2);
        for (let r = 0; r < gR; r++) cells.add(`${r}-${cc}`);
      } else if (pattern === 'square') {
        // All outer-border cells
        for (let c = 0; c < gC; c++) {
          cells.add(`0-${c}`);
          cells.add(`${gR - 1}-${c}`);
        }
        for (let r = 0; r < gR; r++) {
          cells.add(`${r}-0`);
          cells.add(`${r}-${gC - 1}`);
        }
      } else if (pattern === 'fourCorners') {
        cells.add(`0-0`);
        cells.add(`0-${gC - 1}`);
        cells.add(`${gR - 1}-0`);
        cells.add(`${gR - 1}-${gC - 1}`);
      } else if (pattern === 'l') {
        // Left column + bottom row (the L shape)
        for (let r = 0; r < gR; r++) cells.add(`${r}-0`);
        for (let c = 0; c < gC; c++) cells.add(`${gR - 1}-${c}`);
      } else if (pattern === 'u') {
        // Left column + bottom row + right column (the U shape)
        for (let r = 0; r < gR; r++) {
          cells.add(`${r}-0`);
          cells.add(`${r}-${gC - 1}`);
        }
        for (let c = 0; c < gC; c++) cells.add(`${gR - 1}-${c}`);
      }
      return cells;
    };
    // Derive cols / rows independently — supports non-square Loteria grids
    // like "4x5", "3x6" etc. The whole winner-detection function below uses
    // gCols + gRows separately; the previous `gridSize * gridSize`
    // assumption blew up on 4x5 boards because `marked[row]` would be
    // undefined for row=4 (array was only 4 rows), throwing an unhandled
    // TypeError → black-screen freeze for the user.
    const gCols = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x')
      ? parseInt(loteriaGridSize.split('x')[0], 10)
      : loteriaGridSize;
    const gRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x')
      ? parseInt(loteriaGridSize.split('x')[1], 10)
      : loteriaGridSize;
    const gridSize = gCols; // kept for downstream code that still reads gridSize
    const LOTERIA_CELL_RATIO = (loteriaCellShape === 'square' || loteriaDeck === 'square') ? 1.0 : 1.25;
    const totalCells = gCols * gRows;

    // Build a map: boardIndex -> { hiddenCells: Set<cellIdx>, pairCalled: bool }
    // so the Neighbors connecting image counts as a marked card for BOTH paired
    // boards when its figure has been called. The 4 hidden cells underneath the
    // image are treated as marked iff the pair's figure was called.
    // Custom Connecting Image mode: when the user uploaded their own image,
    // there's no loteria figure to call — the covered cells are treated as
    // ALWAYS marked for row/col/pattern detection, but they DON'T add a
    // virtual "+1" card to the perBoardTotal (the image itself is not a
    // markable card, just decorative).
    const pairs = neighborPairsRef.current || [];
    const usingCustomNeighborImg = !!neighborsCustomImage;
    const boardPairMeta = {};
    pairs.forEach(pair => {
      const { leftBoardIdx, rightBoardIdx, startRow, gridN } = pair;
      const leftBoard = loteriaBoards[leftBoardIdx];
      const rightBoard = loteriaBoards[rightBoardIdx];
      if (!leftBoard || !rightBoard) return;
      // Detect whether this pair's connecting figure was called by looking for
      // the pair-mark id pattern (set in markLoteriaFigure).
      const pairCalled = autoMarks.some(m =>
        m.id.includes('nb-pair') && (m.id.includes(leftBoard.id) || m.id.includes(rightBoard.id))
      );
      // Handle all pair shapes — horizontal pairs hide last-col of left +
      // first-col of right; vertical pairs hide last-row of top + first-row
      // of bottom; quad pairs hide a kxk corner. Without this, vertical /
      // custom-vertical pairs gave 0 hidden cells which broke the win count
      // (board never completed even after all visible cells were marked).
      let leftHidden = new Set();
      let rightHidden = new Set();
      if (pair.direction === 'vertical') {
        const cols = pair.colsCovered || 2;
        const startCol = pair.startCol || 0;
        const topRow = gRows - 1, bottomRow = 0;
        for (let dc = 0; dc < cols; dc++) {
          leftHidden.add(topRow * gridN + (startCol + dc));
          rightHidden.add(bottomRow * gridN + (startCol + dc));
        }
      } else if (pair.direction === 'quad') {
        // Quad pairs are handled separately (4 boards) — skip horizontal
        // 2-board metadata for them.
        return;
      } else {
        const rows = pair.rowsCovered || 2;
        for (let dr = 0; dr < rows; dr++) {
          leftHidden.add((startRow + dr) * gridN + (gridN - 1));
          rightHidden.add((startRow + dr) * gridN + 0);
        }
      }
      boardPairMeta[leftBoardIdx] = { hiddenCells: leftHidden, pairCalled };
      boardPairMeta[rightBoardIdx] = { hiddenCells: rightHidden, pairCalled };
    });

    for (let boardIndex = 0; boardIndex < loteriaBoards.length; boardIndex++) {
      const board = loteriaBoards[boardIndex];
      const meta = boardPairMeta[boardIndex];
      const hiddenCells = meta?.hiddenCells || new Set();
      const pairCalled = !!meta?.pairCalled;

      // Last Card Standing: this board's focal figure + hidden cells + whether it was called.
      const lcAssign = (lastCardAssignmentsRef.current || []).find(a => a.boardIdx === boardIndex);
      const lcHiddenCells = lcAssign ? new Set(lcAssign.cells.map(c => c.row * lcAssign.gridN + c.col)) : new Set();
      const lcCalled = lcAssign ? autoMarks.some(m => m.id.includes(`lc-${board.id}`)) : false;

      // Per-board effective total cells.
      // - Neighbors (standard figure): 2 hidden cells replaced by 1 virtual pair card (-2+1).
      // - Neighbors (custom image): cells are simply excluded from the total (no virtual card,
      //   because there's no figure to be called — the image is decorative only).
      // - Last Card: N hidden cells replaced by 1 virtual focal card (-N+1).
      let perBoardTotal = gCols * gRows;
      if (meta) perBoardTotal = perBoardTotal - hiddenCells.size + (usingCustomNeighborImg ? 0 : 1);
      if (lcAssign) perBoardTotal = perBoardTotal - lcHiddenCells.size + 1;
      // Build a grid of marked/unmarked cells. ROWS-first so marked[row]
      // exists for every actual row index (gRows can differ from gCols).
      const marked = Array.from({ length: gRows }, () => Array(gCols).fill(false));
      let markedCount = 0;
      for (let i = 0; i < board.figures.length; i++) {
        const col = i % gCols;
        const row = Math.floor(i / gCols);
        if (row >= gRows) break; // safety — extra figures beyond grid are ignored
        // Hidden cells under a Neighbors connecting image: treat as "marked" for
        // row/col detection when pair's figure was called; DON'T add to count.
        // With a CUSTOM connecting image, the covered cells are ALWAYS treated
        // as marked (there's no figure to call — the image itself is the mark).
        if (hiddenCells.has(i)) {
          if (usingCustomNeighborImg || pairCalled) marked[row][col] = true;
          continue;
        }
        // Hidden cells under a Last Card focal image: same idea — marked for
        // row/col detection when focal figure was called, not added to count.
        if (lcHiddenCells.has(i)) {
          if (lcCalled) marked[row][col] = true;
          continue;
        }
        const cw = board.width / gCols;
        const ch = cw * LOTERIA_CELL_RATIO;
        const cx = board.x + col * cw + cw / 2;
        const cy = board.y + row * ch + ch / 2;
        const hasMark = autoMarks.some(m =>
          m.id.includes(board.id) && m.points && m.points.length > 0 &&
          Math.abs(m.points[0].x - cx) < cw * 0.5 &&
          Math.abs(m.points[0].y - cy) < ch * 0.5
        );
        if (hasMark) {
          marked[row][col] = true;
          markedCount++;
        }
      }
      // Add virtual cards. Custom connecting image does NOT add a +1 (there's
      // no figure to be called — the image is not a markable card).
      if (meta && pairCalled && !usingCustomNeighborImg) markedCount++;
      if (lcAssign && lcCalled) markedCount++;

      // Full winner — collect ALL winning boards (Isolate Winners filter respected)
      const boardNum = boardIndex + 1;
      const isolateActive = isolatedBoardNumsRef.current && isolatedBoardNumsRef.current.size > 0;
      const boardPassesIsolation = !isolateActive || isolatedBoardNumsRef.current.has(boardNum);

      // Pattern winners (X, T, Square, 4 Corners) — independent from Full/Row/Col
      // winners. Each board fires each enabled pattern exactly once (unless
      // continueAnnouncing is on and the key is cleared elsewhere). Extra marks
      // outside the pattern don't disqualify — we just need every cell of the
      // pattern to be marked. Winner celebrations for patterns use their OWN
      // gate so the Full-Board celebration can still fire afterwards.
      if (boardPassesIsolation && !autoMarkupInProgress && !patternWinnerDismissedRef.current) {
        ['fourCorners', 'l', 'square', 't', 'u', 'x'].forEach(pattern => {
          if (!patternWinModes[pattern]) return;
          const key = `${board.id}-${pattern}`;
          if (shownPatternWinnersRef.current.has(key)) return;
          const cells = getPatternCells(pattern, gRows, gCols);
          let allMarked = true;
          for (const rc of cells) {
            const [pr, pc] = rc.split('-').map(Number);
            if (!marked[pr] || !marked[pr][pc]) { allMarked = false; break; }
          }
          if (allMarked) {
            shownPatternWinnersRef.current.add(key);
            newPatternWinners.push({ boardIndex, boardNum, boardId: board.id, pattern });
          }
        });
      }
      if (markedCount === perBoardTotal && !shownWinnerBoardsRef.current.has(board.id) && winnerBoardEnabled && boardPassesIsolation) {
        newWinners.push({ boardIndex, boardNum, boardId: board.id });
        shownWinnerBoardsRef.current.add(board.id);
      }
      // Board-level almost winner: all marked except 1 (Isolate Winners filter respected)
      if (markedCount === perBoardTotal - 1 && boardPassesIsolation) {
        newAlmostWinners.add(board.id);
      }
      // Sticky star for boards that have already been celebrated as full
      // winners — the Almost-Winner star stays on the Board Number badge
      // after the winner animation fires (until the user clears calls).
      if (markedCount === perBoardTotal && shownWinnerBoardsRef.current.has(board.id) && boardPassesIsolation) {
        lockedAlmostBoardsRef.current.add(board.id);
        newAlmostWinners.add(board.id);
      }

      // Row-level: check for winner (all marked) and almost winner (gCols-1 marked)
      const rowAlmost = new Set();
      for (let row = 0; row < gRows; row++) {
        const rowMarked = marked[row].filter(Boolean).length;
        if (rowMarked === gCols) {
          // Row winner! Compute the row number
          if (winnerRowEnabled && boardPassesIsolation) {
            const boardSeqOffset = boardIndex * (gCols + gRows);
            const startNum = loteriaNumbering.startingNum || 1;
            const rowNum = startNum + boardSeqOffset + row;
            const key = `row-${board.id}-${row}`;
            // Defer line-winner announcement until auto-markup batch finishes
            // so every row/col completed during a single call fires after the
            // marks have finished animating. NO singleton guard — rows AND
            // columns completed in the same effect run are all batched into
            // one announcement.
            if (!lineWinnersShownRef.current.has(key) && !autoMarkupInProgress) {
              lineWinnersShownRef.current.add(key);
              newLineWinners.push({ type: 'row', boardIndex, lineNum: rowNum });
            }
          }
        } else if (rowMarked === gCols - 1) {
          rowAlmost.add(row);
        }
      }
      if (rowAlmost.size > 0 && boardPassesIsolation) newAlmostRows[board.id] = rowAlmost;

      // Column-level: check for winner (all marked) and almost winner (gRows-1 marked)
      const colAlmost = new Set();
      for (let col = 0; col < gCols; col++) {
        let colMarked = 0;
        for (let row = 0; row < gRows; row++) {
          if (marked[row][col]) colMarked++;
        }
        if (colMarked === gRows) {
          // Column winner! Compute the column number
          if (winnerColEnabled && boardPassesIsolation) {
            const boardSeqOffset = boardIndex * (gCols + gRows);
            const startNum = loteriaNumbering.startingNum || 1;
            const colOffset = loteriaNumbering.showRowNums ? gRows : 0;
            const colNum = startNum + boardSeqOffset + colOffset + col;
            const key = `col-${board.id}-${col}`;
            if (!lineWinnersShownRef.current.has(key) && !autoMarkupInProgress) {
              lineWinnersShownRef.current.add(key);
              newLineWinners.push({ type: 'col', boardIndex, lineNum: colNum });
            }
          }
        } else if (colMarked === gRows - 1) {
          colAlmost.add(col);
        }
      }
      if (colAlmost.size > 0 && boardPassesIsolation) newAlmostCols[board.id] = colAlmost;
    }
    if (newWinners.length > 0 && !autoMarkupInProgress) {
      setTimeout(() => {
        setBingoWinner(prev => {
          if (prev && prev.length > 0) {
            return [...prev, ...newWinners];
          }
          if (!continueAnnouncing && winnerCelebrationsShownRef.current >= 1) {
            return null;
          }
          winnerCelebrationsShownRef.current += 1;
          return newWinners;
        });
      }, 2500);
    } else if (newWinners.length > 0 && autoMarkupInProgress) {
      // Auto-markup still running — rollback bookkeeping so the winners are
      // re-detected once the batch finishes (the effect re-runs when
      // autoMarkupInProgress flips to false).
      newWinners.forEach(w => {
        if (w.boardId) shownWinnerBoardsRef.current.delete(w.boardId);
      });
    }
    // Batched line winners (rows + columns from any board). All complete
    // lines detected in this run go into one celebration so the user sees
    // every winning row AND column announced together.
    //
    // Only the FIRST batch celebrates unless "Continue Announcing After
    // Dismiss" is on. A new overlay = 1 celebration. Appending to an already-
    // open overlay = 0 (same celebration).
    if (newLineWinners.length > 0) {
      setLoteriaLineWinner(prev => {
        if (Array.isArray(prev) && prev.length > 0) {
          // Existing overlay — append without consuming another slot.
          return [...prev, ...newLineWinners];
        }
        if (!continueAnnouncing && winnerCelebrationsShownRef.current >= 1) {
          return null; // already shown one — skip
        }
        winnerCelebrationsShownRef.current += 1;
        return newLineWinners;
      });
    }
    // Pattern winners (X, T, Square, 4 Corners) — fire their OWN overlay,
    // independent from Full/Row/Col celebrations, so dismissing a pattern win
    // does NOT prevent the First-to-Fill (full board) celebration later.
    // 2.5 s delay so all auto-mark animations finish landing on every board
    // BEFORE the celebration takes over the screen (matches the Full-Board
    // celebration cadence).
    if (newPatternWinners.length > 0) {
      // Recolor only the marks on the winning pattern's cells so the shape
      // pops visually. Other marks keep the user's original markupColor.
      // Recolor is applied immediately so the shape is visible while the
      // 2.5 s delay counts down toward the overlay.
      setMarkupElements(prev => prev.map(el => {
        if (!el.id || !el.id.startsWith('automark-') || !el.points || !el.points[0]) return el;
        for (const w of newPatternWinners) {
          if (!el.id.includes(loteriaBoards[w.boardIndex]?.id || '__none__')) continue;
          const board = loteriaBoards[w.boardIndex];
          if (!board) continue;
          const cells = getPatternCells(w.pattern, gRows, gCols);
          const cw = board.width / gCols;
          const ch = cw * LOTERIA_CELL_RATIO;
          for (const rc of cells) {
            const [pr, pc] = rc.split('-').map(Number);
            const cx = board.x + pc * cw + cw / 2;
            const cy = board.y + pr * ch + ch / 2;
            if (Math.abs(el.points[0].x - cx) < cw * 0.5 &&
                Math.abs(el.points[0].y - cy) < ch * 0.5) {
              return { ...el, color: patternWinColors[w.pattern] || el.color };
            }
          }
        }
        return el;
      }));
      setTimeout(() => {
        setPatternWinner(prev => {
          if (Array.isArray(prev) && prev.length > 0) return [...prev, ...newPatternWinners];
          return newPatternWinners;
        });
      }, 2500);
    }
    setAlmostWinnerBoards(newAlmostWinners);
    setAlmostWinnerRows(newAlmostRows);
    setAlmostWinnerCols(newAlmostCols);
  }, [gameMode, markupElements, loteriaBoards, loteriaGridSize, loteriaCellShape, calledNumbers, loteriaNumbering, loteriaLineWinner, winnerBoardEnabled, winnerRowEnabled, winnerColEnabled, autoMarkupInProgress, patternWinModes, patternWinColors, neighborsCustomImage]);

  // Generate AI image for BACKGROUND
  const generateBackgroundImage = async (prompt, provider = "openai") => {
    setBackgroundImageLoading(true);
    try {
      const res = await xhrRequest("POST", `${API}/generate-image`, { prompt, provider });

      if (!res.ok) {
        throw new Error((res.data && res.data.detail) || "Failed to generate image");
      }

      setBackgroundImage(`data:image/png;base64,${res.data.image_base64}`);
      toast.success("Background image generated!");
    } catch (error) {
      console.error("Error generating background:", error);
      toast.error(error.message || "Failed to generate background");
    } finally {
      setBackgroundImageLoading(false);
    }
  };

  // Generate AI image for OVERLAY
  const generateOverlayImage = async (prompt, provider = "openai") => {
    setOverlayImageLoading(true);
    try {
      const res = await xhrRequest("POST", `${API}/generate-image`, { prompt, provider });

      if (!res.ok) {
        throw new Error((res.data && res.data.detail) || "Failed to generate image");
      }

      addOverlayImage(`data:image/png;base64,${res.data.image_base64}`, 100);
      toast.success("Overlay image generated!");
    } catch (error) {
      console.error("Error generating overlay:", error);
      toast.error(error.message || "Failed to generate overlay");
    } finally {
      setOverlayImageLoading(false);
    }
  };

  // Animated overlay (fal.ai image-to-video) — Phase 1+2
  const [overlayAiMode, setOverlayAiMode] = useState('static');
  const [animatedSourceImage, setAnimatedSourceImage] = useState(null);
  const [animatedOverlayPrompt, setAnimatedOverlayPrompt] = useState('');
  const [animatedOverlayDuration, setAnimatedOverlayDuration] = useState(5);
  const [animatedOverlayLoading, setAnimatedOverlayLoading] = useState(false);
  const [animatedOverlayProgress, setAnimatedOverlayProgress] = useState('');

  const generateAnimatedOverlay = useCallback(async () => {
    if (!animatedSourceImage || !animatedOverlayPrompt.trim()) {
      toast.error('Upload an image and enter a prompt first');
      return;
    }
    if (!requireSubscription('Generating animated overlays')) return;
    setAnimatedOverlayLoading(true);
    setAnimatedOverlayProgress('starting…');
    try {
      const submit = await xhrRequest('POST', `${API}/animated-overlay/generate`, {
        image_data_url: animatedSourceImage,
        prompt: animatedOverlayPrompt,
        duration: animatedOverlayDuration,
      });
      if (!submit.ok || !submit.data?.job_id) {
        throw new Error(submit.data?.detail || 'Failed to start generation');
      }
      const jobId = submit.data.job_id;
      let attempts = 0;
      while (attempts < 60) {
        attempts += 1;
        await new Promise(r => setTimeout(r, 5000));
        setAnimatedOverlayProgress(`${attempts * 5}s elapsed`);
        const poll = await xhrRequest('GET', `${API}/animated-overlay/status/${jobId}`);
        if (!poll.ok) continue;
        if (poll.data?.status === 'done') {
          const dataUrl = `data:video/mp4;base64,${poll.data.video_b64}`;
          const newOverlay = {
            id: `overlay-anim-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            isAnimated: true,
            videoSrc: dataUrl,
            src: animatedSourceImage,
            x: 200, y: 200, width: 280, height: Math.round(280 * 9 / 16),
            opacity: 100,
            rotation: 0,
          };
          setOverlayImages(prev => [...prev, newOverlay]);
          toast.success(`Animated overlay generated (${poll.data.duration}s loop)`);
          setAnimatedOverlayLoading(false);
          setAnimatedOverlayProgress('');
          return;
        }
        if (poll.data?.status === 'error') {
          throw new Error(poll.data?.error || 'Generation failed');
        }
      }
      throw new Error('Generation timed out after 5 minutes');
    } catch (err) {
      console.error('Animated overlay generation error:', err);
      toast.error(err.message || 'Failed to generate animated overlay');
    } finally {
      setAnimatedOverlayLoading(false);
      setAnimatedOverlayProgress('');
    }
  }, [animatedSourceImage, animatedOverlayPrompt, animatedOverlayDuration, requireSubscription]);

  // Search stock images
  const searchStockImages = async (query) => {
    try {
      const res = await xhrRequest("POST", `${API}/search-stock-images`, { query, count: 10 });

      if (!res.ok) throw new Error("Failed to search images");

      return res.data.images;
    } catch (error) {
      console.error("Error searching stock images:", error);
      toast.error("Failed to search images");
      return [];
    }
  };

  // Select stock image for background
  const selectBackgroundStockImage = async (imageUrl) => {
    setBackgroundImageLoading(true);
    try {
      const proxyUrl = `${API}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      const res = await xhrRequest("GET", proxyUrl, null, { responseType: 'blob' });
      
      if (!res.ok) throw new Error("Failed to load image");
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setBackgroundImage(reader.result);
        setBackgroundImageLoading(false);
        toast.success("Background image loaded!");
      };
      reader.readAsDataURL(res.data);
    } catch (error) {
      console.error("Error loading stock image:", error);
      toast.error("Failed to load image");
      setBackgroundImageLoading(false);
    }
  };

  // Generate background from reference image
  const generateBackgroundFromReference = async (referenceBase64, provider = "openai", extraPrompt = "") => {
    setBackgroundImageLoading(true);
    try {
      // Resize reference image to max 1024px to reduce payload and speed up processing
      const resized = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const maxDim = 1024;
          let w = img.width, h = img.height;
          if (w > maxDim || h > maxDim) {
            const scale = maxDim / Math.max(w, h);
            w = Math.round(w * scale);
            h = Math.round(h * scale);
          }
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/png'));
        };
        img.src = referenceBase64;
      });

      const res = await xhrRequest("POST", `${API}/generate-image-from-reference`, {
        reference_image: resized,
        provider,
        prompt: extraPrompt,
      });
      if (!res.ok) throw new Error(res.data?.detail || "Failed to generate");
      const b64 = res.data.image_base64;
      const dataUrl = b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
      setBackgroundImage(dataUrl);
      toast.success(`Background recreated via ${res.data.provider || provider}!`);
    } catch (error) {
      console.error("Reference generation error:", error);
      toast.error(error.message || "Failed to generate from reference");
    } finally {
      setBackgroundImageLoading(false);
    }
  };

  // Select stock image for overlay
  const selectOverlayStockImage = async (imageUrl) => {
    setOverlayImageLoading(true);
    try {
      const proxyUrl = `${API}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      const res = await xhrRequest("GET", proxyUrl, null, { responseType: 'blob' });
      
      if (!res.ok) throw new Error("Failed to load image");
      
      const reader = new FileReader();
      reader.onloadend = () => {
        addOverlayImage(reader.result, 100);
        setOverlayImageLoading(false);
        toast.success("Overlay image added!");
      };
      reader.readAsDataURL(res.data);
    } catch (error) {
      console.error("Error loading overlay:", error);
      toast.error("Failed to load overlay");
      setOverlayImageLoading(false);
    }
  };

  // Upload image for background
  const uploadBackgroundImage = async (file) => {
    setBackgroundImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await xhrRequest("POST", `${API}/upload-image`, formData);

      if (!res.ok) {
        throw new Error((res.data && res.data.detail) || "Failed to upload image");
      }

      setBackgroundImage(`data:image/png;base64,${res.data.image_base64}`);
      toast.success("Background uploaded!");
    } catch (error) {
      console.error("Error uploading background:", error);
      toast.error(error.message || "Failed to upload background");
    } finally {
      setBackgroundImageLoading(false);
    }
  };

  // Upload image for overlay
  const uploadOverlayImage = async (file) => {
    setOverlayImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await xhrRequest("POST", `${API}/upload-image`, formData);

      if (!res.ok) {
        throw new Error((res.data && res.data.detail) || "Failed to upload image");
      }

      addOverlayImage(`data:image/png;base64,${res.data.image_base64}`, 100);
      toast.success("Overlay uploaded!");
    } catch (error) {
      console.error("Error uploading overlay:", error);
      toast.error(error.message || "Failed to upload overlay");
    } finally {
      setOverlayImageLoading(false);
    }
  };

  // Prepare overlays for the save payload so a template with many heavy
  // base64 images doesn't blow past the ingress proxy's body-size limit
  // (HTTP 413). For each overlay:
  //   • If `srcHash` is already set → drop `src` (backend rehydrates on load).
  //   • If `src` is a heavy data URL and `srcHash` is missing → pre-upload
  //     the asset to `/api/overlay-assets/upload`, get back `srcHash`, then
  //     drop `src`. Unique srcs are uploaded only once (memoized in-flight).
  //   • Else (short http URL, empty src) → leave untouched.
  const prepareOverlaysForSave = useCallback(async (overlays) => {
    if (!Array.isArray(overlays) || overlays.length === 0) return overlays || [];
    const HEAVY_BYTES = 1024; // matches backend _externalize threshold
    const hashCache = new Map(); // src -> srcHash (avoids duplicate uploads)
    const out = [];
    for (const o of overlays) {
      // Animated overlays — also pre-upload `videoSrc` (MP4 base64) which is
      // typically several MB. Use the same overlay-assets endpoint; the hash
      // is stored on `videoSrcHash` so the load path can rehydrate it.
      let nextO = o;
      if (o?.isAnimated && o?.videoSrc && typeof o.videoSrc === 'string' && o.videoSrc.startsWith('data:') && !o.videoSrcHash) {
        try {
          const r = await xhrRequest("POST", `${API}/overlay-assets/upload`, { src: o.videoSrc });
          if (r.ok && r.data?.srcHash) {
            nextO = { ...o, videoSrc: null, videoSrcHash: r.data.srcHash };
          }
        } catch (_) { /* fall through — keep inline */ }
      } else if (o?.isAnimated && o?.videoSrcHash) {
        nextO = { ...o, videoSrc: null };
      }

      const src = nextO?.src;
      const hash = nextO?.srcHash;
      // Case 1: already have hash — drop heavy src to keep payload small.
      if (hash) {
        out.push({ ...nextO, src: null });
        continue;
      }
      // Case 2: heavy inline data URL with no hash — pre-upload once.
      if (typeof src === "string" && src.startsWith("data:") && src.length > HEAVY_BYTES) {
        let h = hashCache.get(src);
        if (!h) {
          try {
            const r = await xhrRequest("POST", `${API}/overlay-assets/upload`, { src });
            if (!r.ok) throw new Error(`upload failed (${r.status})`);
            h = r.data?.srcHash;
            if (!h) throw new Error("upload missing srcHash");
            hashCache.set(src, h);
          } catch (err) {
            out.push(nextO);
            continue;
          }
        }
        out.push({ ...nextO, src: null, srcHash: h });
        continue;
      }
      // Case 3: small/empty/http URL — pass through unchanged.
      out.push(nextO);
    }
    return out;
  }, []);

  // ── Import Template From Image (AI-vision) ────────────────────────────
  // Send a flat image of a finished Loteria template to the backend, which
  // uses GPT-5 vision to extract board count, grid size, and the standard
  // Loteria card (1-54) in every cell. We then populate `loteriaBoards`
  // using whichever figures are currently loaded in `loteriaFigures` —
  // matching by index (card #1 → figure index 0, etc.). After import the
  // template behaves exactly like one the app generated: Auto-Markup and
  // the Live Bingo Caller work out of the box.
  const importTemplateFromImage = useCallback(async (file) => {
    if (!requireSubscription("Importing templates")) return;
    if (!file) return;
    if (!(loteriaFigures && loteriaFigures.length >= 54)) {
      toast.error("Please load the standard 54-card Loteria deck before importing a template image.");
      return;
    }
    setImportingTemplate(true);
    const fd = new FormData();
    fd.append("file", file);
    // Send the user's loaded figure names so the AI matches against THIS
    // deck (rather than the standard Don Clemente order). Vertical bar
    // separator — safer than commas because some Loteria names contain
    // commas or Spanish punctuation.
    if (loteriaFigures && loteriaFigures.length > 0) {
      const names = loteriaFigures.map(f => (f && f.name) ? String(f.name).replace(/\|/g, '-') : '').join('|');
      fd.append("figure_names", names);
    }
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/templates/import-image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Failed to analyze the template image");
        return;
      }
      const { board_count, rows, cols, boards } = data || {};
      if (!Array.isArray(boards) || boards.length === 0 || !rows || !cols) {
        toast.error("Could not detect any boards in the image");
        return;
      }
      // Switch to Loteria mode and apply detected layout.
      if (gameMode !== 'loteria' && setGameMode) setGameMode('loteria');
      const sameSize = rows === cols;
      const gridValue = sameSize ? rows : `${cols}x${rows}`;
      // Compute the new board positions (x, y, width) at the current canvas
      // size BEFORE the auto-recalc useEffect fires — then we slot in the
      // recognized figure indices and atomically replace the boards array.
      const layoutBoards = calculateAutoFitLoteriaBoards(
        boards.length, displayWidth, displayHeight, gridValue, loteriaFigures.length, null
      ) || [];
      const newBoards = layoutBoards.map((b, idx) => {
        const cardNums = boards[idx] || [];
        // Map 1-based vocab index → 0-based figure index. Card #0 means
        // GPT couldn't recognize that cell; we fall back to figure 0 so
        // the board still renders (the user can manually fix afterward
        // via the existing cell-swap UI). Clamp into valid range.
        const figs = cardNums.map(n => {
          const v = (n | 0);
          if (v <= 0) return 0;
          return Math.max(0, Math.min(loteriaFigures.length - 1, v - 1));
        });
        return { ...b, figures: figs };
      });
      // Prevent the loteriaGridSize-watching useEffect from overwriting
      // the figures we just imported.
      skipLoteriaRecalcRef.current = true;
      setLoteriaGridSize(gridValue);
      setLoteriaBoardCount && setLoteriaBoardCount(boards.length);
      setLoteriaBoards(newBoards);
      toast.success(`Imported ${boards.length} board${boards.length === 1 ? '' : 's'} (${rows}×${cols}) from image!`);
    } catch (e) {
      console.error("Template image import failed:", e);
      toast.error("Failed to import template image");
    } finally {
      setImportingTemplate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loteriaFigures]);



  // Save template
  const saveTemplate = async (isFinished = false) => {
    if (!requireSubscription("Saving templates")) return;
    if (!currentTemplateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    try {
      // Strip heavy base64 from overlay payload so the request body stays
      // below the ingress proxy's body limit (otherwise: HTTP 413).
      const slimOverlays = await prepareOverlaysForSave(overlayImages);

      const res = await xhrRequest("POST", `${API}/templates/save`, {
        name: currentTemplateName,
        settings: {
          templateSize,
          backgroundColor,
          backgroundImage,
          backgroundImageFit,
          boardStyle,
          boardColor,
          headerColor,
          headerLetterColor,
          cellColor,
          textColor,
          boardFont,
          freeSpaceText,
          freeSpaceColor,
          freeSpaceTextColor,
          freeSpaceNumbering,
          watermarkEnabled,
          watermarkText,
          watermarkOpacity,
          watermarkColor,
          watermarkFont,
          templateTextEnabled,
          templateText,
          templateTextFont,
          templateTextColor,
          templateTextOutlineEnabled,
          templateTextOutlineColor,
          templateTextOutlineWidth,
          templateTextSize,
          templateTextX,
          templateTextY,
          templateTextLetterSpacing,
          templateTextOpacity,
          extraTemplateTexts,
          headerOpacity,
          cellOpacity,
          freeSpaceOpacity,
          boardOpacity,
          markupColor,
          markupThickness,
          eraserThickness,
          markupTipStyle,
          markupOpacity,
          showMarkNotifications,
          gameMode,
          loteriaGridSize,
          loteriaOneOffEnabled,
          loteriaOneOffBoardIndex,
          loteriaOneOffGridSize,
          loteriaOneOffNumberingEnabled,
          loteriaOneOffShowRowNums,
          loteriaOneOffShowColNums,
          loteriaOneOffStartingNum,
          loteriaSingleRepeatEnabled,
          loteriaSingleRepeatBoardIndex,
          loteriaSingleRepeatMode,
          loteriaSingleRepeatGridSize,
          loteriaDeck,
          loteriaRepeatMode,
          loteriaCenterOrientation,
          loteriaCellColors,
          loteriaColorMode,
          loteriaRowColors,
          loteriaColColors,
          loteriaStripeColors,
          loteriaBorderColor,
          loteriaGridColor,
          loteriaCenterHighlight,
          loteriaCenterHighlightColor,
          loteriaRepeatHighlight,
          loteriaRepeatHighlightColor,
          loteriaCenterHighlightThickness,
          loteriaRepeatHighlightThickness,
          loteriaCenterHighlightOffsetX,
          loteriaCenterHighlightOffsetY,
          loteriaBorderStyle,
          loteriaCornerRadius,
          loteriaBorderWidth,
          loteriaGridWidth,
          loteriaCellShape,
          loteriaBoardShadow,
          loteriaNumbering,
          // Caller / Fullscreen background animation (owner-only)
          callerBgVideoSrc,
          callerBgVideoOpacity,
          callerBgVideoFit,
          // Neighbors Boards state
          neighborsEnabled,
          neighborsPosition,
          neighborsDirection,
          neighborsHighlightColor,
          neighborsShape,
          neighborsBorderThickness,
          neighborsShadowEnabled,
          neighborsOutlineStyle,
          neighborsRowsPerBoard,
          neighborsQuadEnabled,
          neighborsQuadCellsPerBoard,
          customVerticalPairs,
          neighborSeedVersion,
          neighborsVerticalPairNumbering,
          verticalPairBadgeAlign,
          pairStackNumbersEnabled, pairStackOffsetX, pairStackOffsetY,
          markupStampImage,
          markupStampTemplate,
          neighborsPairShiftX,
          neighborsPairShiftY,
          lastCardEnabled,
          lastCardCorner,
          lastCardSize,
          lastCardShape,
          lastCardHighlightColor,
          lastCardBorderThickness,
          lastCardShadowEnabled,
          lastCardOutlineStyle,
          lastCardTilt,
          lastCardOffsetX,
          lastCardOffsetY,
          lastCardOrientation,
          lastCardScale,
          // Overlay shadow (global) + per-template offset overrides
          overlayShadow,
          overlayShadowOffsetX,
          overlayShadowOffsetY,
          // Prize Dots (owner-only feature) — size, spacing, colors, texts,
          // outline settings, position all persisted so the row is restored
          // exactly as configured when the template is reopened.
          prizeDots,
          // Almost-winner star style + custom neighbor image settings.
          starStyle,
          neighborsCustomImage,
          neighborsCustomImageSize,
          neighborsCustomOutlineWidth,
          neighborsCustomOutlineColor,
        },
        boards: bingoBoards,
        loteriaBoards,
        overlayImages: slimOverlays,
        markupElements,
        emojis,
        filterSettings,
        calledNumbers,
        isFinished,
      });

      if (!res.ok) {
        // Try to extract a useful message from the backend so the user
        // sees what's actually wrong (e.g. payload too large, asset
        // upsert failure) instead of the generic toast.
        let detail = "";
        try {
          const txt = res.responseText || (await res.text?.());
          if (txt) {
            try { detail = JSON.parse(txt).detail || ""; } catch { detail = txt; }
          }
        } catch { /* ignore */ }
        throw new Error(detail || `Failed to save template (HTTP ${res.status})`);
      }

      toast.success(isFinished ? "Template saved as finished!" : "Progress saved!");
      // Cache the freshly-computed srcHashes back into in-memory overlays so
      // subsequent saves skip the pre-upload step entirely.
      setOverlayImages(prev => prev.map((o) => {
        const slim = slimOverlays.find(s => s.id === o.id);
        if (slim?.srcHash && !o.srcHash) return { ...o, srcHash: slim.srcHash };
        return o;
      }));
      loadTemplatesList();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(error?.message || "Failed to save template");
    }
  };

  // Load template
  const loadTemplate = async (templateId) => {
    try {
      // Slim load: skips overlay-asset inlining server-side. Cuts the load
      // response by orders of magnitude for templates with many overlays
      // and avoids Cloudflare 520s from oversized responses. Overlay src
      // values are hydrated below via /overlay-assets/fetch-batch.
      const res = await xhrRequest("GET", `${API}/templates/${templateId}?slim=true`);
      if (!res.ok) {
        // Surface the actual backend error so the user (and we) can diagnose
        // production failures instead of just seeing the generic toast.
        const serverMsg = res.data?.detail || res.data?.message || `HTTP ${res.status}`;
        const truncated = String(serverMsg).slice(0, 240);
        console.error('Template load failed:', res.status, res.data);
        throw new Error(truncated);
      }

      const data = res.data;

      // Progressive overlay-asset hydration — fire off a background batch
      // fetch for all overlay src hashes; when it resolves, patch `src`
      // (and `videoSrc`) onto the corresponding overlay objects in state.
      // The template UI is fully interactive immediately; overlays fade in
      // as their assets arrive. Batched in chunks of 100 to respect the
      // server-side cap.
      const hashes = new Set();
      (data.overlayImages || []).forEach(o => {
        if (o.srcHash && !o.src) hashes.add(o.srcHash);
        if (o.videoSrcHash && !o.videoSrc) hashes.add(o.videoSrcHash);
      });
      if (hashes.size > 0) {
        (async () => {
          const all = Array.from(hashes);
          const CHUNK = 100;
          const merged = {};
          for (let i = 0; i < all.length; i += CHUNK) {
            const slice = all.slice(i, i + CHUNK);
            try {
              const r = await xhrRequest('POST', `${API}/overlay-assets/fetch-batch`, { hashes: slice });
              if (r.ok && r.data && r.data.assets) Object.assign(merged, r.data.assets);
            } catch (err) { console.warn('overlay-assets batch fetch failed:', err); }
          }
          setOverlayImages(prev => prev.map(o => {
            const patch = {};
            if (o.srcHash && !o.src && merged[o.srcHash]) patch.src = merged[o.srcHash];
            if (o.videoSrcHash && !o.videoSrc && merged[o.videoSrcHash]) patch.videoSrc = merged[o.videoSrcHash];
            return Object.keys(patch).length ? { ...o, ...patch } : o;
          }));
        })();
      }
      
      setCurrentTemplateName(data.name);
      setTemplateSize(data.settings.templateSize);
      setBackgroundColor(data.settings.backgroundColor);
      setBackgroundImage(data.settings.backgroundImage);
      if (data.settings.backgroundImageFit) setBackgroundImageFit(data.settings.backgroundImageFit);
      if (typeof data.settings.backgroundImageScale === 'number') setBackgroundImageScale(data.settings.backgroundImageScale);
      setBoardStyle(data.settings.boardStyle);
      setBoardColor(data.settings.boardColor);
      setHeaderColor(data.settings.headerColor);
      setHeaderLetterColor(data.settings.headerLetterColor || "#FFFFFF");
      setCellColor(data.settings.cellColor);
      setTextColor(data.settings.textColor);
      setBoardFont(data.settings.boardFont);
      setFreeSpaceText(data.settings.freeSpaceText);
      setFreeSpaceColor(data.settings.freeSpaceColor);
      setFreeSpaceTextColor(data.settings.freeSpaceTextColor || "#000000");
      setFreeSpaceNumbering(data.settings.freeSpaceNumbering);
      setWatermarkEnabled(data.settings.watermarkEnabled);
      setWatermarkText(data.settings.watermarkText);
      setWatermarkOpacity(data.settings.watermarkOpacity);
      setWatermarkColor(data.settings.watermarkColor || "#000000");
      setWatermarkFont(data.settings.watermarkFont || "outfit");
      if (data.settings.templateTextEnabled != null) setTemplateTextEnabled(data.settings.templateTextEnabled);
      if (data.settings.templateText != null) setTemplateText(data.settings.templateText);
      if (data.settings.templateTextFont) setTemplateTextFont(data.settings.templateTextFont);
      if (data.settings.templateTextColor) setTemplateTextColor(data.settings.templateTextColor);
      if (data.settings.templateTextOutlineEnabled != null) setTemplateTextOutlineEnabled(data.settings.templateTextOutlineEnabled);
      if (data.settings.templateTextOutlineColor) setTemplateTextOutlineColor(data.settings.templateTextOutlineColor);
      if (data.settings.templateTextOutlineWidth != null) setTemplateTextOutlineWidth(data.settings.templateTextOutlineWidth);
      if (data.settings.templateTextSize != null) setTemplateTextSize(data.settings.templateTextSize);
      setTemplateTextX(data.settings.templateTextX != null ? data.settings.templateTextX : null);
      setTemplateTextY(data.settings.templateTextY != null ? data.settings.templateTextY : null);
      if (data.settings.templateTextLetterSpacing != null) setTemplateTextLetterSpacing(data.settings.templateTextLetterSpacing);
      else setTemplateTextLetterSpacing(0);
      if (data.settings.templateTextOpacity != null) setTemplateTextOpacity(data.settings.templateTextOpacity);
      else setTemplateTextOpacity(100);
      if (Array.isArray(data.settings.extraTemplateTexts)) setExtraTemplateTexts(data.settings.extraTemplateTexts);
      else setExtraTemplateTexts([]);
      setHeaderOpacity(data.settings.headerOpacity ?? 100);
      setCellOpacity(data.settings.cellOpacity ?? 100);
      setFreeSpaceOpacity(data.settings.freeSpaceOpacity ?? 100);
      setBoardOpacity(data.settings.boardOpacity ?? 100);
      setMarkupColor(data.settings.markupColor || "#000000");
      setMarkupThickness(data.settings.markupThickness ?? 3);
      setEraserThickness(data.settings.eraserThickness ?? 10);
      setMarkupTipStyle(data.settings.markupTipStyle || "round");
      setMarkupOpacity(data.settings.markupOpacity ?? 100);
      setShowMarkNotifications(data.settings.showMarkNotifications ?? true);
      
      // Load boards exactly as saved (preserve positions, sizes, numbers)
      skipBoardRecalcRef.current = true;
      skipLoteriaRecalcRef.current = true;
      setBingoBoards(data.boards);
      setBoardCount(data.boards.length);
      setManualBoardSize(data.boards.length > 0 ? data.boards[0].width : null);
      setOverlayImages(data.overlayImages.map(img => ({
        ...img,
        opacity: img.opacity || 100,
      })));

      // Restore Loteria data
      const savedMode = data.settings.gameMode || 'bingo';
      setGameMode(savedMode);
      if (data.loteriaBoards && data.loteriaBoards.length > 0) {
        setLoteriaBoards(data.loteriaBoards);
        setLoteriaBoardCount(data.loteriaBoards.length);
      } else {
        setLoteriaBoards([]);
        setLoteriaBoardCount(0);
      }
      if (data.settings.loteriaGridSize) setLoteriaGridSize(data.settings.loteriaGridSize);
      if (data.settings.loteriaOneOffEnabled != null) setLoteriaOneOffEnabled(data.settings.loteriaOneOffEnabled);
      if (data.settings.loteriaOneOffBoardIndex != null) setLoteriaOneOffBoardIndex(data.settings.loteriaOneOffBoardIndex);
      if (data.settings.loteriaOneOffGridSize != null) setLoteriaOneOffGridSize(data.settings.loteriaOneOffGridSize);
      if (data.settings.loteriaOneOffNumberingEnabled != null) setLoteriaOneOffNumberingEnabled(data.settings.loteriaOneOffNumberingEnabled);
      if (data.settings.loteriaOneOffShowRowNums != null) setLoteriaOneOffShowRowNums(data.settings.loteriaOneOffShowRowNums);
      if (data.settings.loteriaOneOffShowColNums != null) setLoteriaOneOffShowColNums(data.settings.loteriaOneOffShowColNums);
      if (data.settings.loteriaOneOffStartingNum != null) setLoteriaOneOffStartingNum(data.settings.loteriaOneOffStartingNum);
      if (data.settings.loteriaSingleRepeatEnabled != null) setLoteriaSingleRepeatEnabled(data.settings.loteriaSingleRepeatEnabled);
      if (data.settings.loteriaSingleRepeatBoardIndex != null) setLoteriaSingleRepeatBoardIndex(data.settings.loteriaSingleRepeatBoardIndex);
      if (data.settings.loteriaSingleRepeatMode) setLoteriaSingleRepeatMode(data.settings.loteriaSingleRepeatMode);
      if (data.settings.loteriaSingleRepeatGridSize != null) setLoteriaSingleRepeatGridSize(data.settings.loteriaSingleRepeatGridSize);
      if (data.settings.loteriaCellColors) setLoteriaCellColors(data.settings.loteriaCellColors);
      if (data.settings.loteriaDeck) setLoteriaDeck(data.settings.loteriaDeck);
      if (data.settings.loteriaRepeatMode) setLoteriaRepeatMode(data.settings.loteriaRepeatMode);
      if (data.settings.loteriaCenterOrientation) setLoteriaCenterOrientation(data.settings.loteriaCenterOrientation);
      if (data.settings.loteriaColorMode) setLoteriaColorMode(data.settings.loteriaColorMode);
      if (data.settings.loteriaRowColors) setLoteriaRowColors(data.settings.loteriaRowColors);
      if (data.settings.loteriaColColors) setLoteriaColColors(data.settings.loteriaColColors);
      if (data.settings.loteriaStripeColors) setLoteriaStripeColors(data.settings.loteriaStripeColors);
      if (data.settings.loteriaBorderColor) setLoteriaBorderColor(data.settings.loteriaBorderColor);
      if (data.settings.loteriaGridColor) setLoteriaGridColor(data.settings.loteriaGridColor);
      if (data.settings.loteriaCenterHighlight != null) setLoteriaCenterHighlight(data.settings.loteriaCenterHighlight);
      if (data.settings.loteriaCenterHighlightColor) setLoteriaCenterHighlightColor(data.settings.loteriaCenterHighlightColor);
      if (data.settings.loteriaRepeatHighlight != null) setLoteriaRepeatHighlight(data.settings.loteriaRepeatHighlight);
      if (data.settings.loteriaRepeatHighlightColor) setLoteriaRepeatHighlightColor(data.settings.loteriaRepeatHighlightColor);
      if (data.settings.loteriaCenterHighlightThickness != null) setLoteriaCenterHighlightThickness(data.settings.loteriaCenterHighlightThickness);
      if (data.settings.loteriaRepeatHighlightThickness != null) setLoteriaRepeatHighlightThickness(data.settings.loteriaRepeatHighlightThickness);
      if (data.settings.loteriaCenterHighlightOffsetX != null) setLoteriaCenterHighlightOffsetX(data.settings.loteriaCenterHighlightOffsetX);
      if (data.settings.loteriaCenterHighlightOffsetY != null) setLoteriaCenterHighlightOffsetY(data.settings.loteriaCenterHighlightOffsetY);
      if (data.settings.loteriaBorderStyle) setLoteriaBorderStyle(data.settings.loteriaBorderStyle);
      if (data.settings.loteriaCornerRadius !== undefined) setLoteriaCornerRadius(data.settings.loteriaCornerRadius);
      if (data.settings.loteriaBorderWidth != null) setLoteriaBorderWidth(data.settings.loteriaBorderWidth);
      if (data.settings.loteriaGridWidth != null) setLoteriaGridWidth(data.settings.loteriaGridWidth);
      if (data.settings.loteriaCellShape) setLoteriaCellShape(data.settings.loteriaCellShape);
      if (data.settings.loteriaBoardShadow) setLoteriaBoardShadow(data.settings.loteriaBoardShadow);
      if (data.settings.loteriaNumbering) setLoteriaNumbering(data.settings.loteriaNumbering);

      // Neighbors Boards state
      if (data.settings.neighborsEnabled != null) setNeighborsEnabled(data.settings.neighborsEnabled);
      if (data.settings.neighborsPosition) setNeighborsPosition(data.settings.neighborsPosition);
      if (data.settings.neighborsDirection) setNeighborsDirection(data.settings.neighborsDirection);
      if (data.settings.neighborsHighlightColor) setNeighborsHighlightColor(data.settings.neighborsHighlightColor);
      if (data.settings.neighborsShape) setNeighborsShape(data.settings.neighborsShape);
      if (data.settings.neighborsBorderThickness != null) setNeighborsBorderThickness(data.settings.neighborsBorderThickness);
      if (data.settings.neighborsShadowEnabled != null) setNeighborsShadowEnabled(data.settings.neighborsShadowEnabled);
      if (data.settings.neighborsOutlineStyle) setNeighborsOutlineStyle(data.settings.neighborsOutlineStyle);
      if (data.settings.neighborsRowsPerBoard != null) setNeighborsRowsPerBoard(data.settings.neighborsRowsPerBoard);
      if (data.settings.neighborSeedVersion != null) setNeighborSeedVersion(data.settings.neighborSeedVersion);
      if (data.settings.neighborsVerticalPairNumbering != null) setNeighborsVerticalPairNumbering(data.settings.neighborsVerticalPairNumbering);
      if (data.settings.verticalPairBadgeAlign) setVerticalPairBadgeAlign(data.settings.verticalPairBadgeAlign);
      if (data.settings.pairStackNumbersEnabled != null) setPairStackNumbersEnabled(data.settings.pairStackNumbersEnabled);
      if (data.settings.pairStackOffsetX != null) setPairStackOffsetX(data.settings.pairStackOffsetX);
      if (data.settings.pairStackOffsetY != null) setPairStackOffsetY(data.settings.pairStackOffsetY);
      if (data.settings.markupStampImage !== undefined) setMarkupStampImage(data.settings.markupStampImage);
      if (data.settings.markupStampTemplate !== undefined) setMarkupStampTemplate(data.settings.markupStampTemplate);
      if (data.settings.neighborsQuadEnabled != null) setNeighborsQuadEnabled(data.settings.neighborsQuadEnabled);
      if (Array.isArray(data.settings.customVerticalPairs)) setCustomVerticalPairs(normalizeCustomPairs(data.settings.customVerticalPairs));
      if (data.settings.neighborsQuadCellsPerBoard != null) setNeighborsQuadCellsPerBoard(data.settings.neighborsQuadCellsPerBoard);
      // Last Card Standing
      if (data.settings.lastCardEnabled != null) setLastCardEnabled(data.settings.lastCardEnabled);
      if (data.settings.lastCardCorner) setLastCardCorner(data.settings.lastCardCorner);
      if (data.settings.lastCardSize != null) setLastCardSize(data.settings.lastCardSize);
      if (data.settings.lastCardShape) setLastCardShape(data.settings.lastCardShape);
      if (data.settings.lastCardHighlightColor) setLastCardHighlightColor(data.settings.lastCardHighlightColor);
      if (data.settings.lastCardBorderThickness != null) setLastCardBorderThickness(data.settings.lastCardBorderThickness);
      if (data.settings.lastCardShadowEnabled != null) setLastCardShadowEnabled(data.settings.lastCardShadowEnabled);
      if (data.settings.lastCardOutlineStyle) setLastCardOutlineStyle(data.settings.lastCardOutlineStyle);
      if (data.settings.lastCardTilt != null) setLastCardTilt(data.settings.lastCardTilt);
      if (data.settings.lastCardOffsetX != null) setLastCardOffsetX(data.settings.lastCardOffsetX);
      if (data.settings.lastCardOffsetY != null) setLastCardOffsetY(data.settings.lastCardOffsetY);
      if (data.settings.lastCardOrientation) setLastCardOrientation(data.settings.lastCardOrientation);
      if (data.settings.lastCardScale != null) setLastCardScale(data.settings.lastCardScale);
      // Caller / Fullscreen background animation (owner-only)
      if (data.settings.callerBgVideoSrc !== undefined) setCallerBgVideoSrc(data.settings.callerBgVideoSrc);
      if (data.settings.callerBgVideoOpacity != null) setCallerBgVideoOpacity(data.settings.callerBgVideoOpacity);
      if (data.settings.callerBgVideoFit) setCallerBgVideoFit(data.settings.callerBgVideoFit);
      if (data.settings.neighborsPairShiftX != null) {
        setNeighborsPairShiftX(data.settings.neighborsPairShiftX);
        prevNeighborsPairShiftRef.current.x = data.settings.neighborsPairShiftX;
      }
      if (data.settings.neighborsPairShiftY != null) {
        setNeighborsPairShiftY(data.settings.neighborsPairShiftY);
        prevNeighborsPairShiftRef.current.y = data.settings.neighborsPairShiftY;
      }

      // Prize Dots — ALWAYS reset to the template's saved config (or to the
      // fresh default if this template was saved before Prize Dots existed).
      // Merging onto `prev` would leak the PREVIOUS template's colors/size/
      // count/etc. into this template, which is exactly what the user does
      // NOT want. Each template must own its own Prize Dots state.
      if (data.settings.prizeDots && typeof data.settings.prizeDots === 'object') {
        setPrizeDots({ ...DEFAULT_PRIZE_DOTS, ...data.settings.prizeDots });
      } else {
        setPrizeDots(DEFAULT_PRIZE_DOTS);
      }
      // Same reset semantics for star style + custom neighbor image — if the
      // template didn't store them, fall back to a clean baseline so old
      // per-template values don't bleed into the newly-loaded template.
      setStarStyle(data.settings.starStyle || 'filled');
      setNeighborsCustomImage(data.settings.neighborsCustomImage !== undefined ? data.settings.neighborsCustomImage : null);
      setNeighborsCustomImageSize(data.settings.neighborsCustomImageSize != null ? data.settings.neighborsCustomImageSize : 100);
      setNeighborsCustomOutlineWidth(data.settings.neighborsCustomOutlineWidth != null ? data.settings.neighborsCustomOutlineWidth : 0);
      setNeighborsCustomOutlineColor(data.settings.neighborsCustomOutlineColor || '#000000');

      // Restore markup, emojis, filters, and called numbers
      setMarkupElements(data.markupElements || []);
      setEmojis(data.emojis || []);
      setFilterSettings(data.filterSettings || { ...DEFAULT_FILTER });
      setCalledNumbers(data.calledNumbers || []);
      // Reset winner state for loaded template
      setBingoWinner(null);
      setLoteriaLineWinner(null);
      winnerShownOnceRef.current = false;
      lineWinnersShownRef.current = new Set();
      lockedAlmostBoardsRef.current = new Set();
      winnerCelebrationsShownRef.current = 0;
      
      toast.success("Template loaded!");
    } catch (error) {
      console.error("Error loading template:", error);
      // Show the actual error message (HTTP status, server detail, or JS exception)
      // so production issues can be diagnosed at a glance instead of always
      // showing the generic 'Failed to load template' toast.
      const msg = error?.message ? `Failed to load: ${error.message}` : "Failed to load template";
      toast.error(msg);
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    // Optimistic UI — remove from local state IMMEDIATELY so the list
    // updates instantly even with 290+ saved templates. We snapshot the
    // current list in case the server delete fails, in which case we
    // restore. We do NOT re-fetch the full list afterwards (that was the
    // source of the perceived "slow delete" — a 200-row Pydantic round
    // trip on every delete made the whole app feel frozen).
    const prevList = savedTemplates;
    setSavedTemplates(prev => prev.filter(t => t.id !== templateId));
    try {
      const res = await xhrRequest("DELETE", `${API}/templates/${templateId}`);
      if (!res.ok) throw new Error("Failed to delete template");
      toast.success("Template deleted!");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
      setSavedTemplates(prevList); // rollback
    }
  };

  // ─── PNG download helper (iPad / iOS Safari compatible) ───
  // iOS Safari has 2 quirks that break `canvas.toDataURL(...) + <a download>`:
  //  1. Canvases larger than ~4096 × 4096 (≈ 16 MP) fail `toDataURL` silently
  //     and return an empty string, so the export appears to do nothing.
  //  2. The `download` attribute on `<a>` is ignored — the file opens in the
  //     current tab as a full-page image and users don't know how to save it.
  // Fix strategy:
  //  • If the source canvas exceeds 4096 px on either axis, first downscale
  //    to a safe temp canvas.
  //  • Use `toBlob()` + `URL.createObjectURL()` when available (memory-safe).
  //  • On iOS, try `navigator.share` (opens the native Share Sheet with
  //    "Save Image" as an option). If Share isn't available, open the blob
  //    URL in a new tab so the user can long-press → "Save to Photos".
  //  • On desktop, fall back to the classic `<a download>` click.
  const downloadCanvasAsPNG = async (sourceCanvas, filename) => {
    if (!sourceCanvas) throw new Error("No canvas available");
    // ── iOS-safe downscale ─────────────────────────────────────────
    const MAX_DIM = 4096;
    let exportCanvas = sourceCanvas;
    if (sourceCanvas.width > MAX_DIM || sourceCanvas.height > MAX_DIM) {
      const scale = Math.min(MAX_DIM / sourceCanvas.width, MAX_DIM / sourceCanvas.height);
      const tmp = document.createElement("canvas");
      tmp.width = Math.round(sourceCanvas.width * scale);
      tmp.height = Math.round(sourceCanvas.height * scale);
      const tctx = tmp.getContext("2d");
      tctx.imageSmoothingEnabled = true;
      tctx.imageSmoothingQuality = "high";
      tctx.drawImage(sourceCanvas, 0, 0, tmp.width, tmp.height);
      exportCanvas = tmp;
    }
    // ── Serialize to Blob (fallback to data URL if toBlob is missing) ──
    const blob = await new Promise((resolve, reject) => {
      if (typeof exportCanvas.toBlob === "function") {
        exportCanvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))), "image/png");
      } else {
        try {
          const url = exportCanvas.toDataURL("image/png");
          if (!url || url === "data:,") return reject(new Error("Canvas too large for this device"));
          // Convert data URL → Blob for uniform downstream code.
          const bin = atob(url.split(",")[1]);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          resolve(new Blob([arr], { type: "image/png" }));
        } catch (e) { reject(e); }
      }
    });
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
    // ── iOS: prefer native Share Sheet, else open in new tab ──
    if (isIOS) {
      try {
        if (navigator.canShare && navigator.share) {
          const file = new File([blob], filename, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: filename });
            return;
          }
        }
      } catch (_) { /* user cancelled Share Sheet — silently fall through to new tab */ }
      // Fallback: open the image in a new tab so user can long-press → Save
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        // Popup blocked — navigate current tab instead
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      return;
    }
    // ── Desktop / Android: classic <a download> click ──
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // Export as PNG
  const exportAsPNG = async () => {
    if (!requirePaidSubscription("Export PNG/PDF")) return;
    if (!canvasRef.current) return;
    try {
      await downloadCanvasAsPNG(canvasRef.current, `bingo-template-${Date.now()}.png`);
      toast.success("PNG exported!");
    } catch (error) {
      console.error("Error exporting PNG:", error);
      toast.error("Failed to export PNG. " + (error.message || ""));
    }
  };

  // Export as transparent PNG — boards + overlays + markup only, no background.
  const exportAsPNGNoBackground = async () => {
    if (!requirePaidSubscription("Export PNG/PDF")) return;
    if (!canvasRef.current) return;
    try {
      setSkipBackgroundForExport(true);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, 300));
      await downloadCanvasAsPNG(canvasRef.current, `bingo-template-transparent-${Date.now()}.png`);
      toast.success("Transparent PNG exported!");
    } catch (error) {
      console.error("Error exporting transparent PNG:", error);
      toast.error("Failed to export transparent PNG. " + (error.message || ""));
    } finally {
      setSkipBackgroundForExport(false);
    }
  };

  // Export as PDF
  const exportAsPDF = async () => {
    if (!requirePaidSubscription("Export PNG/PDF")) return;
    if (!canvasRef.current) return;
    try {
      // Downscale to iOS-safe dims (same 4096 cap as PNG export) so iPad's
      // toDataURL doesn't return an empty string on large templates.
      const sourceCanvas = canvasRef.current;
      const MAX_DIM = 4096;
      let exportCanvas = sourceCanvas;
      if (sourceCanvas.width > MAX_DIM || sourceCanvas.height > MAX_DIM) {
        const scale = Math.min(MAX_DIM / sourceCanvas.width, MAX_DIM / sourceCanvas.height);
        const tmp = document.createElement("canvas");
        tmp.width = Math.round(sourceCanvas.width * scale);
        tmp.height = Math.round(sourceCanvas.height * scale);
        const tctx = tmp.getContext("2d");
        tctx.imageSmoothingEnabled = true;
        tctx.imageSmoothingQuality = "high";
        tctx.drawImage(sourceCanvas, 0, 0, tmp.width, tmp.height);
        exportCanvas = tmp;
      }
      const imgData = exportCanvas.toDataURL("image/png");
      if (!imgData || imgData === "data:,") {
        throw new Error("Canvas too large for this device — please reduce template size");
      }
      const { jsPDF } = await import("jspdf");
      const orientation = currentSize.width > currentSize.height ? 'landscape' : 'portrait';
      const pdf = new jsPDF({
        orientation,
        unit: 'in',
        format: [currentSize.width, currentSize.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, currentSize.width, currentSize.height);
      pdf.save(`bingo-template-${Date.now()}.pdf`);

      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. " + (error.message || ""));
    }
  };

  // Clear background
  const clearBackground = () => {
    setBackgroundImage(null);
    toast.info("Background image cleared");
  };

  const currentFont = FONT_OPTIONS.find(f => f.id === boardFont) || FONT_OPTIONS[0];

  // Auth gate: show loading or login screen
  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <AuthModal onLogin={(user) => setCurrentUser(user)} />;
  }

  // Show subscription page (manual navigation OR forced by expired access)
  if (showSubscription) {
    return (
      <SubscriptionPage
        currentUser={currentUser}
        onBack={hasActiveAccess ? () => setShowSubscription(false) : null}
        onLogout={handleLogout}
        onSubscriptionChange={async () => {
          // Refresh user data
          try {
            const res = await xhrRequest("GET", `${API}/auth/me`);
            if (res.ok && res.data) {
              setCurrentUser(res.data);
              setShowSubscription(false);
            }
          } catch {}
        }}
      />
    );
  }

  // HARD SUBSCRIPTION WALL: if user has no active access (expired trial/sub), force subscription page
  if (!hasActiveAccess) {
    return (
      <SubscriptionPage
        currentUser={currentUser}
        onBack={null}
        onLogout={handleLogout}
        onSubscriptionChange={async () => {
          try {
            const res = await xhrRequest("GET", `${API}/auth/me`);
            if (res.ok && res.data) setCurrentUser(res.data);
          } catch {}
        }}
      />
    );
  }

  return (
    <>
    {/* Caller page - always mounted to preserve state, hidden via CSS when inactive */}
    <div style={showCallerPage ? {} : { position: 'fixed', left: '-99999px', top: 0, width: '100vw', height: '100vh', opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
      <BingoCallerPage
        onBack={() => setShowCallerPage(false)}
        isDarkMode={isDarkMode}
        canvasImage={callerCanvasImage}
        onBallDrawn={handleCallerBallDraw}
        onCallerReset={clearCalledNumbers}
        gameMode={gameMode}
        handleAutoMarkupSubmit={handleAutoMarkupSubmit}
        autoMarkupResetKey={autoMarkupResetKey}
        calledNumbers={calledNumbers}
        loteriaFigures={loteriaFigures}
        callerBgVideoSrc={callerBgVideoObjectUrl}
        callerBgVideoOpacity={callerBgVideoOpacity}
        callerBgVideoFit={callerBgVideoFit}
        obsMode={obsMode}
      />
    </div>

    {/* Main UI - hidden off-screen when caller page is active (keeps canvas alive for live markup) */}
    <div 
      data-testid="bingo-generator" 
      className={`flex h-screen overflow-hidden transition-colors duration-300 ${isDarkMode ? 'dark bg-neutral-950' : 'bg-[#FFFDF8]'}`}
      style={{
        ...(showCallerPage ? { position: 'fixed', left: '-99999px', top: 0, width: '100vw', height: '100vh', opacity: 0, pointerEvents: 'none', zIndex: -1 } : {}),
        ...(!isPaidSubscriber() ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}),
      }}
      onContextMenu={!isPaidSubscriber() ? (e) => { e.preventDefault(); toast.error('Right-click disabled. Subscribe to unlock!'); } : undefined}
    >
      {/* Black screen overlay for screenshot protection */}
      {screenshotBlack && !isPaidSubscriber() && (
        <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center" style={{ pointerEvents: 'auto' }}>
          <div className="text-center">
            <Lock className="w-16 h-16 text-rose-500 mx-auto mb-4" />
            <p className="text-white text-xl font-bold mb-2">Content Protected</p>
            <p className="text-zinc-400 text-sm">Subscribe to BingoGrid AI to access this content.</p>
          </div>
        </div>
      )}
      <div className={`transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-[340px] min-w-[340px]' : 'w-0 min-w-0'} overflow-hidden`}>
        <ErrorBoundary>
        <Sidebar
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        templateSize={templateSize}
        setTemplateSize={setTemplateSize}
        templateSizes={TEMPLATE_SIZES}
        backgroundColor={backgroundColor}
        setBackgroundColor={setBackgroundColor}
        backgroundImage={backgroundImage}
        backgroundImageFit={backgroundImageFit}
        setBackgroundImageFit={setBackgroundImageFit}
        backgroundImageScale={backgroundImageScale}
        setBackgroundImageScale={setBackgroundImageScale}
        backgroundImageLoading={backgroundImageLoading}
        generateBackgroundImage={generateBackgroundImage}
        generateBackgroundFromReference={generateBackgroundFromReference}
        selectBackgroundStockImage={selectBackgroundStockImage}
        uploadBackgroundImage={uploadBackgroundImage}
        clearBackground={clearBackground}
        hasBackground={!!backgroundImage}
        searchStockImages={searchStockImages}
        overlayImages={overlayImages}
        selectedOverlayId={selectedOverlayId}
        setSelectedOverlayId={setSelectedOverlayId}
        overlayImageLoading={overlayImageLoading}
        generateOverlayImage={generateOverlayImage}
        // Animated Overlay (fal.ai image-to-video)
        overlayAiMode={overlayAiMode}
        setOverlayAiMode={setOverlayAiMode}
        animatedSourceImage={animatedSourceImage}
        setAnimatedSourceImage={setAnimatedSourceImage}
        animatedOverlayPrompt={animatedOverlayPrompt}
        setAnimatedOverlayPrompt={setAnimatedOverlayPrompt}
        animatedOverlayDuration={animatedOverlayDuration}
        setAnimatedOverlayDuration={setAnimatedOverlayDuration}
        animatedOverlayLoading={animatedOverlayLoading}
        animatedOverlayProgress={animatedOverlayProgress}
        generateAnimatedOverlay={generateAnimatedOverlay}
        selectOverlayStockImage={selectOverlayStockImage}
        uploadOverlayImage={uploadOverlayImage}
        removeOverlayImage={removeOverlayImage}
        savedOverlays={savedOverlays}
        loadSavedOverlays={loadSavedOverlays}
        saveOverlayToLibrary={saveOverlayToLibrary}
        deleteSavedOverlay={deleteSavedOverlay}
        addOverlayImage={addOverlayImage}
        removeOverlayBackground={removeOverlayBackground}
        cutoutLoading={cutoutLoading}
        updateOverlayOpacity={updateOverlayOpacity}
        updateOverlayAboveBoards={updateOverlayAboveBoards}
        updateOverlayOutline={updateOverlayOutline}
        updateOverlayTransform={updateOverlayTransform}
        updateOverlayShadow={updateOverlayShadow}
        duplicateOverlay={duplicateOverlay}
        overlayShadow={overlayShadow}
        setOverlayShadow={setOverlayShadow}
        overlayShadowOffsetX={overlayShadowOffsetX}
        setOverlayShadowOffsetX={setOverlayShadowOffsetX}
        overlayShadowOffsetY={overlayShadowOffsetY}
        setOverlayShadowOffsetY={setOverlayShadowOffsetY}
        boardCount={boardCount}        handleBoardCountChange={handleBoardCountChange}
        boardLayout={boardLayout}
        getDefaultGridDims={getDefaultGridDims}
        applyBoardLayout={applyBoardLayout}
        boardOpacity={boardOpacity}
        setBoardOpacity={setBoardOpacity}
        transparentBoards={transparentBoards}
        setTransparentBoards={setTransparentBoards}
        transparentKeepHeader={transparentKeepHeader}
        setTransparentKeepHeader={setTransparentKeepHeader}
        transparentKeepFreeSpace={transparentKeepFreeSpace}
        setTransparentKeepFreeSpace={setTransparentKeepFreeSpace}
        multiColorRows={multiColorRows}
        setMultiColorRows={setMultiColorRows}
        boardRowColors={boardRowColors}
        setBoardRowColors={setBoardRowColors}
        currentBoardWidth={currentBoardWidth}
        handleManualBoardResize={handleManualBoardResize}
        loteriaMoveMode={loteriaMoveMode}
        setLoteriaMoveMode={setLoteriaMoveMode}
        neighborsEnabled={neighborsEnabled}
        setNeighborsEnabled={setNeighborsEnabled}
        neighborsRowsPerBoard={neighborsRowsPerBoard}
        setNeighborsRowsPerBoard={setNeighborsRowsPerBoard}
        neighborsQuadEnabled={neighborsQuadEnabled}
        setNeighborsQuadEnabled={setNeighborsQuadEnabled}
        customVerticalPairs={customVerticalPairs}
        setCustomVerticalPairs={setCustomVerticalPairs}
        neighborsQuadCellsPerBoard={neighborsQuadCellsPerBoard}
        setNeighborsQuadCellsPerBoard={setNeighborsQuadCellsPerBoard}
        neighborsPosition={neighborsPosition}
        setNeighborsPosition={setNeighborsPosition}
        neighborsDirection={neighborsDirection}
        setNeighborsDirection={setNeighborsDirection}
        neighborsVerticalPairNumbering={neighborsVerticalPairNumbering}
        setNeighborsVerticalPairNumbering={setNeighborsVerticalPairNumbering}
        verticalPairBadgeAlign={verticalPairBadgeAlign}
        setVerticalPairBadgeAlign={setVerticalPairBadgeAlign}
        pairStackNumbersEnabled={pairStackNumbersEnabled}
        setPairStackNumbersEnabled={setPairStackNumbersEnabled}
        pairStackOffsetX={pairStackOffsetX}
        setPairStackOffsetX={setPairStackOffsetX}
        pairStackOffsetY={pairStackOffsetY}
        setPairStackOffsetY={setPairStackOffsetY}
        neighborsHighlightColor={neighborsHighlightColor}
        setNeighborsHighlightColor={setNeighborsHighlightColor}
        neighborsShape={neighborsShape}
        setNeighborsShape={setNeighborsShape}
        neighborsBorderThickness={neighborsBorderThickness}
        setNeighborsBorderThickness={setNeighborsBorderThickness}
        neighborsShadowEnabled={neighborsShadowEnabled}
        setNeighborsShadowEnabled={setNeighborsShadowEnabled}
        neighborsOutlineStyle={neighborsOutlineStyle}
        setNeighborsOutlineStyle={setNeighborsOutlineStyle}
        neighborsCustomImage={neighborsCustomImage}
        setNeighborsCustomImage={setNeighborsCustomImage}
        neighborsCustomImageSize={neighborsCustomImageSize}
        setNeighborsCustomImageSize={setNeighborsCustomImageSize}
        neighborsCustomOutlineWidth={neighborsCustomOutlineWidth}
        setNeighborsCustomOutlineWidth={setNeighborsCustomOutlineWidth}
        neighborsCustomOutlineColor={neighborsCustomOutlineColor}
        setNeighborsCustomOutlineColor={setNeighborsCustomOutlineColor}
        lastCardEnabled={lastCardEnabled}
        setLastCardEnabled={setLastCardEnabled}
        lastCardCorner={lastCardCorner}
        setLastCardCorner={setLastCardCorner}
        lastCardSize={lastCardSize}
        setLastCardSize={setLastCardSize}
        lastCardShape={lastCardShape}
        setLastCardShape={setLastCardShape}
        lastCardHighlightColor={lastCardHighlightColor}
        setLastCardHighlightColor={setLastCardHighlightColor}
        lastCardBorderThickness={lastCardBorderThickness}
        setLastCardBorderThickness={setLastCardBorderThickness}
        lastCardShadowEnabled={lastCardShadowEnabled}
        setLastCardShadowEnabled={setLastCardShadowEnabled}
        lastCardOutlineStyle={lastCardOutlineStyle}
        setLastCardOutlineStyle={setLastCardOutlineStyle}
        lastCardTilt={lastCardTilt}
        setLastCardTilt={setLastCardTilt}
        lastCardOffsetX={lastCardOffsetX}
        setLastCardOffsetX={setLastCardOffsetX}
        lastCardOffsetY={lastCardOffsetY}
        setLastCardOffsetY={setLastCardOffsetY}
        lastCardOrientation={lastCardOrientation}
        setLastCardOrientation={setLastCardOrientation}
        lastCardScale={lastCardScale}
        setLastCardScale={setLastCardScale}
        neighborsPairShiftX={neighborsPairShiftX}
        setNeighborsPairShiftX={setNeighborsPairShiftX}
        neighborsPairShiftY={neighborsPairShiftY}
        setNeighborsPairShiftY={setNeighborsPairShiftY}
        boardStyle={boardStyle}
        setBoardStyle={setBoardStyle}
        boardStyles={BOARD_STYLES}
        boardColor={boardColor}
        setBoardColor={setBoardColor}
        headerColor={headerColor}
        setHeaderColor={setHeaderColor}
        headerLetterColor={headerLetterColor}
        setHeaderLetterColor={setHeaderLetterColor}
        cellColor={cellColor}
        setCellColor={setCellColor}
        textColor={textColor}
        setTextColor={setTextColor}
        headerOpacity={headerOpacity}
        setHeaderOpacity={setHeaderOpacity}
        cellOpacity={cellOpacity}
        setCellOpacity={setCellOpacity}
        freeSpaceOpacity={freeSpaceOpacity}
        setFreeSpaceOpacity={setFreeSpaceOpacity}
        boardFont={boardFont}
        setBoardFont={setBoardFont}
        headerFont={headerFont}
        setHeaderFont={setHeaderFont}
        fontOptions={visibleFontOptions}
        freeSpaceText={freeSpaceText}
        setFreeSpaceText={setFreeSpaceText}
        showFreeSpaceEditor={showFreeSpaceEditor}
        setShowFreeSpaceEditor={setShowFreeSpaceEditor}
        freeSpaceColor={freeSpaceColor}
        setFreeSpaceColor={setFreeSpaceColor}
        freeSpaceTextColor={freeSpaceTextColor}
        setFreeSpaceTextColor={setFreeSpaceTextColor}
        freeSpaceNumbering={freeSpaceNumbering}
        setFreeSpaceNumbering={setFreeSpaceNumbering}
        freeSpaceTextEffect={freeSpaceTextEffect}
        setFreeSpaceTextEffect={setFreeSpaceTextEffect}
        freeSpaceTextThickness={freeSpaceTextThickness}
        setFreeSpaceTextThickness={setFreeSpaceTextThickness}
        watermarkEnabled={watermarkEnabled}
        setWatermarkEnabled={setWatermarkEnabled}
        watermarkText={watermarkText}
        setWatermarkText={setWatermarkText}
        watermarkOpacity={watermarkOpacity}
        setWatermarkOpacity={setWatermarkOpacity}
        watermarkColor={watermarkColor}
        setWatermarkColor={setWatermarkColor}
        watermarkFont={watermarkFont}
        setWatermarkFont={setWatermarkFont}
        templateTextEnabled={templateTextEnabled}
        setTemplateTextEnabled={setTemplateTextEnabled}
        prizeDots={prizeDots}
        setPrizeDots={setPrizeDots}
        templateText={templateText}
        setTemplateText={setTemplateText}
        templateTextFont={templateTextFont}
        setTemplateTextFont={setTemplateTextFont}
        templateTextColor={templateTextColor}
        setTemplateTextColor={setTemplateTextColor}
        templateTextOutlineEnabled={templateTextOutlineEnabled}
        setTemplateTextOutlineEnabled={setTemplateTextOutlineEnabled}
        templateTextOutlineColor={templateTextOutlineColor}
        setTemplateTextOutlineColor={setTemplateTextOutlineColor}
        templateTextOutlineWidth={templateTextOutlineWidth}
        setTemplateTextOutlineWidth={setTemplateTextOutlineWidth}
        templateTextSize={templateTextSize}
        setTemplateTextSize={setTemplateTextSize}
        templateTextLetterSpacing={templateTextLetterSpacing}
        setTemplateTextLetterSpacing={setTemplateTextLetterSpacing}
        templateTextOpacity={templateTextOpacity}
        setTemplateTextOpacity={setTemplateTextOpacity}
        extraTemplateTexts={extraTemplateTexts}
        addExtraTemplateText={addExtraTemplateText}
        updateExtraTemplateText={updateExtraTemplateText}
        removeExtraTemplateText={removeExtraTemplateText}
        savedTemplateTexts={savedTemplateTexts}
        saveTemplateTextBundle={saveTemplateTextBundle}
        saveTemplateTextName={saveTemplateTextName}
        setSaveTemplateTextName={setSaveTemplateTextName}
        loadTemplateTextBundle={loadTemplateTextBundle}
        deleteTemplateTextBundle={deleteTemplateTextBundle}
        savedTemplateTextBusy={savedTemplateTextBusy}
        callerBgVideoSrc={callerBgVideoSrc}
        setCallerBgVideoSrc={setCallerBgVideoSrc}
        callerBgVideoOpacity={callerBgVideoOpacity}
        setCallerBgVideoOpacity={setCallerBgVideoOpacity}
        callerBgVideoFit={callerBgVideoFit}
        setCallerBgVideoFit={setCallerBgVideoFit}
        obsMode={obsMode}
        setObsMode={setObsMode}
        handleCallerBgVideoUpload={handleCallerBgVideoUpload}
        aiVideoPrompt={aiVideoPrompt}
        setAiVideoPrompt={setAiVideoPrompt}
        aiVideoLoading={aiVideoLoading}
        aiVideoElapsed={aiVideoElapsed}
        generateAiCallerVideo={generateAiCallerVideo}
        isOwner={isOwner}
        savedCallerVideos={savedCallerVideos}
        saveCallerVideo={saveCallerVideo}
        loadCallerVideo={loadCallerVideo}
        deleteCallerVideo={deleteCallerVideo}
        savedCallerVideoBusy={savedCallerVideoBusy}
        exportAsPNG={exportAsPNG}
        exportAsPNGNoBackground={exportAsPNGNoBackground}
        exportAsPDF={exportAsPDF}
        isPaidSubscriber={isPaidSubscriber()}
        onOpenCaller={isOwner ? () => setShowCallerPage(true) : undefined}
        regenerateBoards={regenerateBoards}
        currentTemplateName={currentTemplateName}
        setCurrentTemplateName={setCurrentTemplateName}
        savedTemplates={savedTemplates}
        isLoadingTemplates={isLoadingTemplates}
        retryLoadTemplates={() => loadTemplatesList(0)}
        saveTemplate={saveTemplate}
        loadTemplate={loadTemplate}
        deleteTemplate={deleteTemplate}
        importTemplateFromImage={importTemplateFromImage}
        importingTemplate={importingTemplate}
        folders={folders}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        templateSearchQuery={templateSearchQuery}
        setTemplateSearchQuery={setTemplateSearchQuery}
        createFolder={createFolder}
        renameFolder={renameFolder}
        deleteFolder={deleteFolder}
        moveTemplateToFolder={moveTemplateToFolder}
        markupElements={markupElements}
        activeMarkupTool={activeMarkupTool}
        setActiveMarkupTool={setActiveMarkupTool}
        markupColor={markupColor}
        setMarkupColor={setMarkupColor}
        markupThickness={markupThickness}
        setMarkupThickness={setMarkupThickness}
        eraserThickness={eraserThickness}
        setEraserThickness={setEraserThickness}
        markupTipStyle={markupTipStyle}
        setMarkupTipStyle={setMarkupTipStyle}
        markupOpacity={markupOpacity}
        setMarkupOpacity={(v) => {
          // Update the slider's value AND any currently-selected markup element's
          // opacity in real time. New drawings continue to use `markupOpacity`
          // by default. This unifies the slider behaviour: it always shows the
          // "active" opacity — for the selected shape if one is selected,
          // otherwise for the next-drawn shape.
          setMarkupOpacity(v);
          updateSelectedMarkupOpacity(v);
        }}
        clearMarkup={clearMarkup}
        deleteSelectedMarkup={deleteSelectedMarkup}
        selectedMarkupId={selectedMarkupId}
        markupStampImage={markupStampImage}
        setMarkupStampImage={setMarkupStampImage}
        markupStampTemplate={markupStampTemplate}
        resizeAllMarkupStamps={resizeAllMarkupStamps}
        emojis={emojis}
        addEmoji={addEmoji}
        selectedEmojiId={selectedEmojiId}
        updateEmojiSize={updateEmojiSize}
        removeEmoji={removeEmoji}
        filterSettings={filterSettings}
        updateFilter={updateFilter}
        applyFilterPreset={applyFilterPreset}
        EMOJI_OPTIONS={EMOJI_OPTIONS}
        FILTER_PRESETS={FILTER_PRESETS}
        handleAutoMarkupSubmit={handleAutoMarkupSubmit}
        autoMarkupResetKey={autoMarkupResetKey}
        autoMarkupScale={autoMarkupScale}
        setAutoMarkupScale={setAutoMarkupScale}
        calledNumbers={calledNumbers}
        clearCalledNumbers={clearCalledNumbers}
        removeCalledNumber={removeCalledNumber}
        gameMode={gameMode}
        setGameMode={setGameMode}
        loteriaFigures={loteriaFigures}
        uploadLoteriaFigure={uploadLoteriaFigure}
        deleteLoteriaFigure={deleteLoteriaFigure}
        setLoteriaFigures={setLoteriaFigures}
        loteriaDeck={loteriaDeck}
        setLoteriaDeck={setLoteriaDeck}
        canvasQuality={canvasQuality}
        setCanvasQuality={setCanvasQuality}
        loteriaGridSize={loteriaGridSize}
        setLoteriaGridSize={setLoteriaGridSize}
        loteriaOneOffEnabled={loteriaOneOffEnabled}
        setLoteriaOneOffEnabled={setLoteriaOneOffEnabled}
        loteriaOneOffBoardIndex={loteriaOneOffBoardIndex}
        setLoteriaOneOffBoardIndex={setLoteriaOneOffBoardIndex}
        loteriaOneOffGridSize={loteriaOneOffGridSize}
        setLoteriaOneOffGridSize={setLoteriaOneOffGridSize}
        loteriaOneOffNumberingEnabled={loteriaOneOffNumberingEnabled}
        setLoteriaOneOffNumberingEnabled={setLoteriaOneOffNumberingEnabled}
        loteriaOneOffShowRowNums={loteriaOneOffShowRowNums}
        setLoteriaOneOffShowRowNums={setLoteriaOneOffShowRowNums}
        loteriaOneOffShowColNums={loteriaOneOffShowColNums}
        setLoteriaOneOffShowColNums={setLoteriaOneOffShowColNums}
        loteriaOneOffStartingNum={loteriaOneOffStartingNum}
        setLoteriaOneOffStartingNum={setLoteriaOneOffStartingNum}
        loteriaSingleRepeatEnabled={loteriaSingleRepeatEnabled}
        setLoteriaSingleRepeatEnabled={setLoteriaSingleRepeatEnabled}
        loteriaSingleRepeatBoardIndex={loteriaSingleRepeatBoardIndex}
        setLoteriaSingleRepeatBoardIndex={setLoteriaSingleRepeatBoardIndex}
        loteriaSingleRepeatMode={loteriaSingleRepeatMode}
        setLoteriaSingleRepeatMode={setLoteriaSingleRepeatMode}
        loteriaSingleRepeatGridSize={loteriaSingleRepeatGridSize}
        setLoteriaSingleRepeatGridSize={setLoteriaSingleRepeatGridSize}
        loteriaBoardCount={loteriaBoardCount}
        handleLoteriaBoardCountChange={handleLoteriaBoardCountChange}
        loteriaCellColors={loteriaCellColors}
        setLoteriaCellColors={setLoteriaCellColors}
        loteriaColorMode={loteriaColorMode}
        setLoteriaColorMode={setLoteriaColorMode}
        loteriaRowColors={loteriaRowColors}
        setLoteriaRowColors={setLoteriaRowColors}
        loteriaColColors={loteriaColColors}
        setLoteriaColColors={setLoteriaColColors}
        loteriaStripeColors={loteriaStripeColors}
        setLoteriaStripeColors={setLoteriaStripeColors}
        loteriaBorderColor={loteriaBorderColor}
        setLoteriaBorderColor={setLoteriaBorderColor}
        loteriaGridColor={loteriaGridColor}
        setLoteriaGridColor={setLoteriaGridColor}
        loteriaCenterHighlight={loteriaCenterHighlight}
        setLoteriaCenterHighlight={setLoteriaCenterHighlight}
        loteriaCenterHighlightColor={loteriaCenterHighlightColor}
        setLoteriaCenterHighlightColor={setLoteriaCenterHighlightColor}
        loteriaCenterHighlightThickness={loteriaCenterHighlightThickness}
        setLoteriaCenterHighlightThickness={setLoteriaCenterHighlightThickness}
        loteriaCenterHighlightOffsetX={loteriaCenterHighlightOffsetX}
        setLoteriaCenterHighlightOffsetX={setLoteriaCenterHighlightOffsetX}
        loteriaCenterHighlightOffsetY={loteriaCenterHighlightOffsetY}
        setLoteriaCenterHighlightOffsetY={setLoteriaCenterHighlightOffsetY}
        loteriaRepeatHighlight={loteriaRepeatHighlight}
        setLoteriaRepeatHighlight={setLoteriaRepeatHighlight}
        loteriaRepeatHighlightColor={loteriaRepeatHighlightColor}
        setLoteriaRepeatHighlightColor={setLoteriaRepeatHighlightColor}
        loteriaRepeatHighlightThickness={loteriaRepeatHighlightThickness}
        setLoteriaRepeatHighlightThickness={setLoteriaRepeatHighlightThickness}
        loteriaBorderStyle={loteriaBorderStyle}
        setLoteriaBorderStyle={setLoteriaBorderStyle}
        loteriaBorderWidth={loteriaBorderWidth}
        setLoteriaBorderWidth={setLoteriaBorderWidth}
        loteriaGridWidth={loteriaGridWidth}
        setLoteriaGridWidth={setLoteriaGridWidth}
        loteriaBoardShadow={loteriaBoardShadow}
        setLoteriaBoardShadow={setLoteriaBoardShadow}
        loteriaGapH={loteriaGapH}
        setLoteriaGapH={setLoteriaGapH}
        loteriaGapV={loteriaGapV}
        setLoteriaGapV={setLoteriaGapV}
        loteriaRepeatMode={loteriaRepeatMode}
        setLoteriaRepeatMode={setLoteriaRepeatMode}
        loteriaCenterOrientation={loteriaCenterOrientation}
        setLoteriaCenterOrientation={setLoteriaCenterOrientation}
        loteriaNumbering={loteriaNumbering}
        setLoteriaNumbering={setLoteriaNumbering}
        regenerateLoteriaBoards={regenerateLoteriaBoards}
        reshuffleLoteriaImages={reshuffleLoteriaImages}
      />
        </ErrorBoundary>
      </div>

      <div className={`flex-1 flex flex-col overflow-hidden relative ${isDarkMode ? 'bg-neutral-900' : 'dotted-bg'}`}>
        <div className={`flex items-center justify-between px-4 py-2 border-b-2 ${isDarkMode ? 'border-neutral-800 bg-neutral-950' : 'border-black bg-white'} ${isFullscreen ? 'hidden' : ''}`}>
          <div className="flex items-center gap-2">
            <button
              data-testid="sidebar-toggle-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'}`}
              title={sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
            >
              {sidebarOpen ? <PanelLeftClose className="w-4 h-4" strokeWidth={2.5} /> : <PanelLeft className="w-4 h-4" strokeWidth={2.5} />}
            </button>
            <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
              {currentSize.label} &bull; {gameMode === 'bingo' ? bingoBoards.length : loteriaBoards.length} Board{(gameMode === 'bingo' ? bingoBoards.length : loteriaBoards.length) !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Undo/Redo/Reset */}
            <button data-testid="undo-btn" onClick={undo} disabled={!canUndo}
              className={`p-2 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'} ${!canUndo ? 'opacity-30 cursor-not-allowed' : ''}`} title="Undo">
              <Undo2 className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button data-testid="redo-btn" onClick={redo} disabled={!canRedo}
              className={`p-2 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'} ${!canRedo ? 'opacity-30 cursor-not-allowed' : ''}`} title="Redo">
              <Redo2 className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button data-testid="reset-btn" onClick={resetAll}
              className={`p-2 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-red-400 hover:bg-red-950/30' : 'border-black bg-white text-red-500 hover:bg-red-50'}`} title="Reset All">
              <RotateCcw className="w-4 h-4" strokeWidth={2.5} />
            </button>

            <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-neutral-700' : 'bg-gray-300'}`} />

            {/* Markup shortcuts */}
            <button data-testid="toolbar-markup-freehand"
              onClick={() => setActiveMarkupTool(activeMarkupTool === 'freehand' ? null : 'freehand')}
              className={`p-2 border-2 transition-colors ${
                activeMarkupTool === 'freehand'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'
              }`} title="Draw">
              <Pencil className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button data-testid="toolbar-eraser"
              onClick={() => setActiveMarkupTool(activeMarkupTool === 'eraser' ? null : 'eraser')}
              className={`p-2 border-2 transition-colors ${
                activeMarkupTool === 'eraser'
                  ? 'bg-rose-600 text-white border-rose-600'
                  : isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'
              }`} title="Eraser">
              <Eraser className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button data-testid="toolbar-clear-markup"
              onClick={() => { setMarkupElements([]); setActiveMarkupTool(null); }}
              disabled={markupElements.length === 0}
              className={`p-2 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-red-400 hover:bg-red-950/30' : 'border-black bg-white text-red-500 hover:bg-red-50'} ${markupElements.length === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
              title="Clear Markup">
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button data-testid="zoom-out-btn" onClick={handleZoomOut}
              className={`p-1.5 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'} transition-colors`}>
              <ZoomOut className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <button data-testid="zoom-reset-btn" onClick={handleZoomReset}
              className={`px-2 py-1 border-2 font-bold text-xs ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'} transition-colors`}>
              {zoom}%
            </button>
            <button data-testid="zoom-in-btn" onClick={handleZoomIn}
              className={`p-1.5 border-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'} transition-colors`}>
              <ZoomIn className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <div className={`w-px h-6 ${isDarkMode ? 'bg-neutral-700' : 'bg-black'}`} />
            <button data-testid="fullscreen-btn" onClick={toggleFullscreen}
              className={`p-1.5 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800' : 'border-black bg-white hover:bg-gray-100'}`} title="Full Screen">
              <Maximize className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <button data-testid="theme-toggle-btn" onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-1.5 border-2 ${isDarkMode ? 'border-neutral-700 bg-yellow-500 text-neutral-900 hover:bg-yellow-400' : 'border-black bg-neutral-900 text-white hover:bg-neutral-800'} transition-colors`}>
              {isDarkMode ? <Sun className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Moon className="w-3.5 h-3.5" strokeWidth={2.5} />}
            </button>
            <div className={`w-px h-6 ${isDarkMode ? 'bg-neutral-700' : 'bg-gray-300'}`} />
            <div className="flex items-center gap-1">
              <button data-testid="subscription-btn" onClick={() => setShowSubscription(true)}
                className={`p-1.5 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-amber-400 hover:bg-amber-950/30' : 'border-black bg-white text-amber-600 hover:bg-amber-50'}`} title="Subscription">
                <CreditCard className="w-3 h-3" strokeWidth={2.5} />
              </button>
              <span data-testid="user-display" className={`text-[10px] font-bold truncate max-w-[70px] ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
                {currentUser.username || currentUser.email}
              </span>
              {isOwner && (
                <button
                  data-testid="owner-subscribers-btn"
                  onClick={openSubscribersPanel}
                  className={`p-1.5 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-amber-300 hover:bg-amber-950/30' : 'border-black bg-white text-amber-600 hover:bg-amber-50'}`}
                  title="View Subscribers (Owner only)"
                >
                  <Users className="w-3 h-3" strokeWidth={2.5} />
                </button>
              )}
              <button data-testid="logout-btn" onClick={handleLogout}
                className={`p-1.5 border-2 transition-colors ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-red-400 hover:bg-red-950/30' : 'border-black bg-white text-red-500 hover:bg-red-50'}`} title="Log Out">
                <LogOut className="w-3 h-3" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>

        {/* Fullscreen exit bar + auto-markup (auto-hide). The auto-markup
            panel is draggable and opacity-adjustable when in fullscreen,
            useful for streamers who want to move it out of the camera shot
            or dim it while on-air. Position + opacity persist for the
            session via state; cleared on Reset All. */}
        {isFullscreen && (
          <>
            {/* Exit fullscreen button stays pinned top-right — not draggable. */}
            <div className={`absolute top-3 right-3 z-50 flex flex-col items-end gap-2 transition-opacity duration-300 ${fsUiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <button data-testid="exit-fullscreen-btn" onClick={toggleFullscreen}
                className="p-2 bg-black/70 hover:bg-black text-white rounded-lg shadow-lg transition-colors" title="Exit Full Screen (Esc)">
                <Minimize className="w-5 h-5" />
              </button>
              {!autoMarkupPanelOpen && (
                <button
                  data-testid="show-auto-markup-panel-fs"
                  onClick={() => setAutoMarkupPanelOpen(true)}
                  className="p-2 bg-black/70 hover:bg-black text-purple-300 rounded-lg shadow-lg transition-colors"
                  title="Show Auto-Markup"
                >
                  <Target className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Draggable Auto-Markup panel — independent of the exit bar. */}
            {autoMarkupPanelOpen && (
              <div
                data-testid="auto-markup-panel-fs"
                onMouseEnter={() => {
                  // Hover brings the panel back to full visibility regardless
                  // of the auto-hide timer. Timer resets on mouseleave.
                  setFsAutoMarkupHidden(false);
                  if (fsAutoMarkupHideTimerRef.current) clearTimeout(fsAutoMarkupHideTimerRef.current);
                }}
                onMouseLeave={() => {
                  if (fsAutoMarkupHideTimerRef.current) clearTimeout(fsAutoMarkupHideTimerRef.current);
                  fsAutoMarkupHideTimerRef.current = setTimeout(() => setFsAutoMarkupHidden(true), 1000);
                }}
                className={`absolute z-50 w-32 ${isDarkMode ? 'bg-neutral-950 border-neutral-700' : 'bg-white border-gray-300'} border-2 rounded-lg shadow-lg backdrop-blur-sm transition-opacity duration-300`}
                style={{
                  top: fsAutoMarkupPos ? `${fsAutoMarkupPos.y}px` : '3.5rem',
                  left: fsAutoMarkupPos ? `${fsAutoMarkupPos.x}px` : undefined,
                  right: fsAutoMarkupPos ? undefined : '0.75rem',
                  // When auto-hidden, opacity is 0 (completely gone) but
                  // the panel keeps pointer-events:auto so hovering its
                  // bounding rect (i.e. directly over the panel area)
                  // triggers onMouseEnter and brings it back.
                  opacity: fsAutoMarkupHidden
                    ? 0
                    : Math.max(0.15, Math.min(1, fsAutoMarkupOpacity / 100)),
                  pointerEvents: 'auto',
                }}
              >
                <div className="p-1.5 space-y-1">
                  <div
                    className={`flex items-center justify-between cursor-move select-none ${isDarkMode ? 'hover:bg-neutral-900' : 'hover:bg-gray-50'} rounded px-0.5`}
                    data-testid="auto-markup-fs-drag-handle"
                    onMouseDown={(e) => {
                      // Ignore drag when starting on buttons/inputs.
                      if (e.target.closest('button') || e.target.closest('input')) return;
                      const panel = e.currentTarget.closest('[data-testid="auto-markup-panel-fs"]');
                      if (!panel) return;
                      const rect = panel.getBoundingClientRect();
                      fsAutoMarkupDragRef.current = {
                        dragging: true,
                        offX: e.clientX - rect.left,
                        offY: e.clientY - rect.top,
                      };
                      const move = (ev) => {
                        if (!fsAutoMarkupDragRef.current.dragging) return;
                        const nx = Math.max(0, Math.min(window.innerWidth - rect.width, ev.clientX - fsAutoMarkupDragRef.current.offX));
                        const ny = Math.max(0, Math.min(window.innerHeight - rect.height, ev.clientY - fsAutoMarkupDragRef.current.offY));
                        setFsAutoMarkupPos({ x: nx, y: ny });
                      };
                      const up = () => {
                        fsAutoMarkupDragRef.current.dragging = false;
                        window.removeEventListener('mousemove', move);
                        window.removeEventListener('mouseup', up);
                      };
                      window.addEventListener('mousemove', move);
                      window.addEventListener('mouseup', up);
                      e.preventDefault();
                    }}
                    title="Drag to reposition"
                  >
                    <div className="flex items-center gap-1">
                      <Target className={`w-2.5 h-2.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                      <span className={`text-[9px] font-black ${isDarkMode ? 'text-white' : 'text-black'}`}>Auto-Markup</span>
                      {!isPaidSubscriber() && <span className="text-[7px] font-black px-0.5 rounded bg-amber-500 text-black">PRO</span>}
                    </div>
                    <button
                      data-testid="hide-auto-markup-panel-fs"
                      onClick={(e) => { e.stopPropagation(); setAutoMarkupPanelOpen(false); }}
                      className={`p-0.5 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                    >
                      <PanelRightClose className="w-2.5 h-2.5" />
                    </button>
                  </div>
                  <AutoMarkupInput
                    data-testid="auto-markup-fs-input"
                    placeholder={gameMode === 'loteria' ? 'Chalupa, 48...' : 'B5, N35, Odd, Even'}
                    onSubmit={handleAutoMarkupSubmit}
                    resetKey={autoMarkupResetKey}
                    variant="native"
                    className={`w-full px-1 py-0.5 border font-bold text-center text-[10px] uppercase rounded ${isDarkMode ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-400' : 'border-gray-400 placeholder-gray-400'}`}
                  />
                  {/* Transparent toggle — off = 100% (fully visible),
                      on = 5% (near-invisible) for streamers who want the
                      panel out of the camera shot without closing it. */}
                  <label className="flex items-center gap-1 pt-0.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="auto-markup-fs-transparent"
                      checked={fsAutoMarkupOpacity <= 15}
                      onChange={(e) => setFsAutoMarkupOpacity(e.target.checked ? 15 : 100)}
                      className="w-2.5 h-2.5 accent-purple-500"
                    />
                    <span className={`text-[8px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>
                      Transparent
                    </span>
                  </label>
                  {fsAutoMarkupPos && (
                    <button
                      data-testid="auto-markup-fs-reset-pos"
                      onClick={() => setFsAutoMarkupPos(null)}
                      className={`w-full text-[7px] font-bold py-0.5 rounded ${isDarkMode ? 'text-neutral-400 hover:text-white hover:bg-neutral-800' : 'text-gray-500 hover:text-black hover:bg-gray-100'}`}
                      title="Reset position to top-right"
                    >
                      ↺ Reset position
                    </button>
                  )}
                  {calledNumbers.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between">
                        <span className={`text-[8px] font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>({calledNumbers.length})</span>
                        <button data-testid="shortcut-clear-called-fs" onClick={clearCalledNumbers} className="text-red-500 text-[8px] font-bold">Clear</button>
                      </div>
                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {calledNumbers.slice(-6).map((num, i) => (
                          <span key={i} className={`px-0.5 text-[7px] font-bold border rounded ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white' : 'border-gray-300 bg-gray-50'}`}>{num}</span>
                        ))}
                        {calledNumbers.length > 6 && <span className={`text-[7px] ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>+{calledNumbers.length - 6}</span>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fullscreen Hammer Mode panel — draggable, hover-only reveal.
                Anchored top-right beneath the Auto-Markup panel by default. */}
            <div
              data-testid="hammer-panel-fs"
              onMouseEnter={() => {
                setFsHammerHidden(false);
                if (fsHammerHideTimerRef.current) clearTimeout(fsHammerHideTimerRef.current);
              }}
              onMouseLeave={() => {
                if (fsHammerHideTimerRef.current) clearTimeout(fsHammerHideTimerRef.current);
                fsHammerHideTimerRef.current = setTimeout(() => setFsHammerHidden(true), 1000);
              }}
              className={`absolute z-50 w-32 ${isDarkMode ? 'bg-neutral-950 border-orange-800' : 'bg-white border-orange-300'} border-2 rounded-lg shadow-lg backdrop-blur-sm transition-opacity duration-300`}
              style={{
                top: fsHammerPos ? `${fsHammerPos.y}px` : '14rem',
                left: fsHammerPos ? `${fsHammerPos.x}px` : undefined,
                right: fsHammerPos ? undefined : '0.75rem',
                opacity: fsHammerHidden ? 0 : 1,
                pointerEvents: 'auto',
              }}
            >
              <div className="p-1.5 space-y-1">
                <div
                  data-testid="hammer-fs-drag-handle"
                  className={`flex items-center justify-between cursor-move select-none ${isDarkMode ? 'hover:bg-neutral-900' : 'hover:bg-gray-50'} rounded px-0.5`}
                  onMouseDown={(e) => {
                    if (e.target.closest('button') || e.target.closest('input')) return;
                    const panel = e.currentTarget.closest('[data-testid="hammer-panel-fs"]');
                    if (!panel) return;
                    const rect = panel.getBoundingClientRect();
                    fsHammerDragRef.current = { dragging: true, offX: e.clientX - rect.left, offY: e.clientY - rect.top };
                    const move = (ev) => {
                      if (!fsHammerDragRef.current.dragging) return;
                      const nx = Math.max(0, Math.min(window.innerWidth - rect.width, ev.clientX - fsHammerDragRef.current.offX));
                      const ny = Math.max(0, Math.min(window.innerHeight - rect.height, ev.clientY - fsHammerDragRef.current.offY));
                      setFsHammerPos({ x: nx, y: ny });
                    };
                    const up = () => {
                      fsHammerDragRef.current.dragging = false;
                      window.removeEventListener('mousemove', move);
                      window.removeEventListener('mouseup', up);
                    };
                    window.addEventListener('mousemove', move);
                    window.addEventListener('mouseup', up);
                    e.preventDefault();
                  }}
                  title="Drag to reposition"
                >
                  <span className={`text-[10px] font-black ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>{boardEffectType === 'shamrocks' ? '🍀 Shamrocks' : '🔨 Hammer'}</span>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="hammer-mode-toggle-fs"
                    checked={!!hammerMode}
                    onChange={(e) => setHammerMode(e.target.checked)}
                    className="w-3 h-3 accent-orange-500"
                  />
                  <span className={`text-[9px] font-bold ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                    {hammerMode ? 'ON — click a board' : 'OFF'}
                  </span>
                </label>
                {hammerMode && (
                  <select
                    data-testid="board-effect-type-select-fs"
                    value={boardEffectType}
                    onChange={(e) => setBoardEffectType(e.target.value)}
                    className={`w-full text-[9px] font-bold rounded px-1 py-0.5 border ${isDarkMode ? 'bg-neutral-900 text-orange-200 border-orange-800' : 'bg-white text-orange-700 border-orange-300'}`}
                  >
                    <option value="hammer">🔨 Hammer</option>
                    <option value="shamrocks">🍀 Shamrocks</option>
                  </select>
                )}
                {fsHammerPos && (
                  <button
                    data-testid="hammer-fs-reset-pos"
                    onClick={() => setFsHammerPos(null)}
                    className={`w-full text-[8px] font-bold py-0.5 rounded ${isDarkMode ? 'text-orange-400 hover:text-orange-300 hover:bg-neutral-800' : 'text-orange-700 hover:text-orange-900 hover:bg-orange-50'}`}
                    title="Reset panel position"
                  >
                    ↺ Reset position
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        <div className={`flex-1 overflow-hidden flex ${isFullscreen ? '' : ''}`}>
          {/* Canvas area */}
          <div className={`relative flex-1 overflow-hidden flex items-center justify-center ${isFullscreen ? `p-0 ${isDarkMode ? 'bg-neutral-950' : 'bg-white'}` : 'p-8 overflow-auto'}`}>
            <div className={`${isFullscreen ? 'w-full h-full flex items-center justify-center' : 'relative'} ${isFullscreen ? 'relative z-10' : ''}`} style={isFullscreen ? {} : { transform: `scale(${zoom / 100})`, transformOrigin: 'center center', transition: 'transform 0.2s ease' }}>
              {/* Screenshot protection watermark for non-paid users */}
              {!isPaidSubscriber() && (
                <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden" style={{ mixBlendMode: 'multiply' }}>
                  <div className="w-full h-full flex flex-wrap items-center justify-center gap-8 rotate-[-30deg] scale-150 opacity-[0.35]">
                    {Array.from({ length: 30 }).map((_, i) => (
                      <span key={i} className="text-red-500 font-black text-2xl whitespace-nowrap select-none" style={{ userSelect: 'none', pointerEvents: 'none' }}>
                        BINGOGRID AI - SUBSCRIBE TO REMOVE
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <CanvasEditor
              ref={canvasRef}
              templateSize={currentSize}
              canvasDPI={DPI}
              skipBackground={skipBackgroundForExport || (isOwner && !!callerBgVideoSrc)}
              backgroundColor={backgroundColor}
              backgroundImage={backgroundImage}
              backgroundImageFit={backgroundImageFit}
              backgroundImageScale={backgroundImageScale}
              overlayImages={overlayImages}
              selectedOverlayId={selectedOverlayId}
              setSelectedOverlayId={setSelectedOverlayId}
              overlayShadow={overlayShadow}
              overlayShadowOffsetX={overlayShadowOffsetX}
              overlayShadowOffsetY={overlayShadowOffsetY}
              updateOverlayPosition={updateOverlayPosition}
              updateOverlaySize={updateOverlaySize}
              bingoBoards={bingoBoards}
              selectedBoardId={selectedBoardId}
              setSelectedBoardId={setSelectedBoardId}
              updateBoardPosition={updateBoardPosition}
              moveLoteriaColumn={moveLoteriaColumn}
              moveLoteriaRow={moveLoteriaRow}
              moveBingoColumn={moveBingoColumn}
              moveBingoRow={moveBingoRow}
              loteriaMoveMode={loteriaMoveMode}
              updateAllBoardsSize={updateAllBoardsSize}
              boardStyle={boardStyle}
              boardColor={boardColor}
              headerColor={headerColor}
              headerLetterColor={headerLetterColor}
              cellColor={cellColor}
              textColor={textColor}
              headerOpacity={headerOpacity}
              cellOpacity={cellOpacity}
              freeSpaceOpacity={freeSpaceOpacity}
              fontFamily={currentFont.family}
              headerFontFamily={(FONT_OPTIONS.find(f => f.id === headerFont) || currentFont).family}
              freeSpaceText={freeSpaceText}
              freeSpaceColor={freeSpaceColor}
              freeSpaceTextColor={freeSpaceTextColor}
              freeSpaceNumbering={freeSpaceNumbering}
              freeSpaceTextEffect={freeSpaceTextEffect}
              freeSpaceTextThickness={freeSpaceTextThickness}
              boardOpacity={boardOpacity}
              isolatedBoardNums={isolatedBoardNums}
              neighborsEnabled={neighborsEnabled}
              neighborsPosition={neighborsPosition}
              neighborsDirection={neighborsDirection}
              neighborsHighlightColor={neighborsHighlightColor}
              neighborsShape={neighborsShape}
              neighborsBorderThickness={neighborsBorderThickness}
              neighborsShadowEnabled={neighborsShadowEnabled}
              neighborsOutlineStyle={neighborsOutlineStyle}
              neighborsCustomImage={neighborsCustomImage}
              neighborsCustomImageSize={neighborsCustomImageSize}
              neighborsCustomOutlineWidth={neighborsCustomOutlineWidth}
              neighborsCustomOutlineColor={neighborsCustomOutlineColor}
              neighborPairs={neighborPairs}
              lastCardEnabled={lastCardEnabled}
              lastCardShape={lastCardShape}
              lastCardHighlightColor={lastCardHighlightColor}
              lastCardBorderThickness={lastCardBorderThickness}
              lastCardShadowEnabled={lastCardShadowEnabled}
              lastCardOutlineStyle={lastCardOutlineStyle}
              lastCardTilt={lastCardTilt}
              lastCardOffsetX={lastCardOffsetX}
              lastCardOffsetY={lastCardOffsetY}
              lastCardScale={lastCardScale}
              lastCardAssignments={lastCardAssignments}
              transparentBoards={transparentBoards}
              transparentKeepHeader={transparentKeepHeader}
              transparentKeepFreeSpace={transparentKeepFreeSpace}
              multiColorRows={multiColorRows}
              boardRowColors={boardRowColors}
              watermarkEnabled={watermarkEnabled}
              watermarkText={watermarkText}
              watermarkOpacity={watermarkOpacity}
              watermarkColor={watermarkColor}
              watermarkFont={watermarkFont}
              templateTextEnabled={templateTextEnabled}
              templateText={templateText}
              templateTextFont={templateTextFont}
              templateTextColor={templateTextColor}
              templateTextOutlineEnabled={templateTextOutlineEnabled}
              templateTextOutlineColor={templateTextOutlineColor}
              templateTextOutlineWidth={templateTextOutlineWidth}
              templateTextSize={templateTextSize}
              templateTextX={templateTextX}
              prizeDots={isOwner ? prizeDots : { enabled: false, count: 0 }}
              setPrizeDots={isOwner ? setPrizeDots : undefined}
              templateTextY={templateTextY}
              templateTextLetterSpacing={templateTextLetterSpacing}
              templateTextOpacity={templateTextOpacity}
              onTemplateTextMove={(x, y) => { setTemplateTextX(x); setTemplateTextY(y); }}
              extraTemplateTexts={extraTemplateTexts}
              onExtraTextMove={moveExtraTemplateText}
              markupElements={markupElements}
              activeMarkupTool={activeMarkupTool}
              markupColor={markupColor}
              markupThickness={markupThickness}
              eraserThickness={eraserThickness}
              markupTipStyle={markupTipStyle}
              markupOpacity={markupOpacity}
              onMarkupComplete={onMarkupComplete}
              onEraseMarkup={onEraseMarkup}
              selectedMarkupId={selectedMarkupId}
              setSelectedMarkupId={setSelectedMarkupId}
              moveMarkupElement={moveMarkupElement}
              resizeMarkupElement={resizeMarkupElement}
              markupStampImage={markupStampImage}
              markupStampTemplate={markupStampTemplate}
              setMarkupStampTemplate={setMarkupStampTemplate}
              emojis={emojis}
              selectedEmojiId={selectedEmojiId}
              setSelectedEmojiId={setSelectedEmojiId}
              updateEmojiPosition={updateEmojiPosition}
              updateEmojiSize={updateEmojiSize}
              filterSettings={filterSettings}
              saveToHistory={saveToHistory}
              isFullscreen={isFullscreen}
              normalDisplayWidth={displayWidth}
              normalDisplayHeight={displayHeight}
              callerBgVideoSrc={callerBgVideoObjectUrl}
              callerBgVideoOpacity={callerBgVideoOpacity}
              callerBgVideoFit={callerBgVideoFit}
              obsMode={obsMode}
              isOwner={isOwner}
              highlightedBoards={highlightEnabled ? highlightedBoards : null}
              highlightedCells={highlightedCells}
              highlightColor={highlightColor}
              highlightBoardNumOnAutoMarkup={highlightBoardNumOnAutoMarkup}
              autoMarkedBoards={autoMarkedBoardsEver}
              hammerMode={hammerMode}
              boardEffectType={boardEffectType}
              shakingBoards={shakingBoards}
              shakeTick={shakeTick}
              onHammerStrike={handleHammerStrike}
              hammerStrike={hammerStrike}
              scratchDecals={scratchDecals}
              shamrockParticles={shamrockParticles}
              waterStrikes={waterStrikes}
              almostWinnerBoards={almostWinnerBoards}
              almostWinnerRows={almostWinnerRows}
              almostWinnerCols={almostWinnerCols}
              loteriaStarBoard={loteriaStarBoard}
              loteriaStarRowCol={loteriaStarRowCol}
              starColor={starColor}
              starOpacity={starOpacity}
              starStyle={starStyle}
              gameMode={gameMode}
              loteriaBoards={loteriaBoards}
              loteriaFigures={loteriaFigures}
              loteriaGridSize={loteriaGridSize}
              loteriaCellColors={loteriaCellColors}
              loteriaColorMode={loteriaColorMode}
              loteriaRowColors={loteriaRowColors}
              loteriaColColors={loteriaColColors}
              loteriaStripeColors={loteriaStripeColors}
              loteriaBorderColor={loteriaBorderColor}
              loteriaGridColor={loteriaGridColor}
              loteriaCenterHighlight={loteriaCenterHighlight}
              loteriaCenterHighlightColor={loteriaCenterHighlightColor}
              loteriaCenterHighlightThickness={loteriaCenterHighlightThickness}
              loteriaCenterHighlightOffsetX={loteriaCenterHighlightOffsetX}
              loteriaCenterHighlightOffsetY={loteriaCenterHighlightOffsetY}
              loteriaRepeatMode={loteriaRepeatMode}
              loteriaRepeatHighlight={loteriaRepeatHighlight}
              loteriaRepeatHighlightColor={loteriaRepeatHighlightColor}
              loteriaRepeatHighlightThickness={loteriaRepeatHighlightThickness}
              loteriaBorderStyle={loteriaBorderStyle}
              loteriaCornerRadius={loteriaCornerRadius}
              loteriaBorderWidth={loteriaBorderWidth}
              loteriaGridWidth={loteriaGridWidth}
              loteriaCellShape={loteriaCellShape}
              loteriaDeck={loteriaDeck}
              loteriaBoardShadow={loteriaBoardShadow}
              loteriaNumbering={loteriaNumbering}
              loteriaBoardNumberingOverrides={loteriaBoardNumberingOverrides}
              loteriaBoardDisplayNums={loteriaBoardDisplayNums}
              loteriaFigureImagesRef={loteriaFigureImagesRef}
              loteriaImagesLoaded={loteriaImagesLoaded}
            />
          </div>
        </div>

          {/* Right-side Auto-Markup Shortcut Panel (normal view only) */}
          {!isFullscreen && autoMarkupPanelOpen ? (
            <div className={`w-52 min-w-[208px] border-l-2 ${isDarkMode ? 'border-neutral-800 bg-neutral-950' : 'border-black bg-white'} flex flex-col`}
              data-testid="auto-markup-panel"
            >
              <div className="p-2.5 space-y-2 overflow-y-auto flex-1">
                {/* 1. Show Notifications (on top) */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input data-testid="toggle-mark-notifications" type="checkbox" checked={showMarkNotifications} onChange={(e) => setShowMarkNotifications(e.target.checked)}
                      className="w-3 h-3 accent-purple-600" />
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Show Notifications</span>
                  </label>
                  <button
                    data-testid="hide-auto-markup-panel"
                    onClick={() => setAutoMarkupPanelOpen(false)}
                    className={`p-1 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                    title="Hide Panel"
                  >
                    <PanelRightClose className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 2. Auto-Markup */}
                <div className="flex items-center gap-1.5">
                  <Target className={`w-3.5 h-3.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  <h3 className={`text-xs font-black ${isDarkMode ? 'text-white' : 'text-black'}`}>Auto-Markup</h3>
                  {!isPaidSubscriber() && <span className="text-[8px] font-black px-1 py-0.5 rounded bg-amber-500 text-black">PRO</span>}
                </div>
                <AutoMarkupInput
                  data-testid="auto-markup-shortcut-input"
                  placeholder={gameMode === 'loteria' ? 'Chalupa, 48, Gallo...' : 'B5, N35, Odd, Even...'}
                  onSubmit={handleAutoMarkupSubmit}
                  resetKey={autoMarkupResetKey}
                  variant="native"
                  className={`w-full px-2 py-1 border-2 font-bold text-center text-sm uppercase ${isDarkMode ? 'bg-neutral-700 border-neutral-600 text-white placeholder-neutral-400' : 'border-black placeholder-gray-400'}`}
                />
                <div>
                  <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Markup Size: {autoMarkupScale}%</span>
                  <input data-testid="auto-markup-scale-range" type="range" min={50} max={300} step={10} value={autoMarkupScale}
                    onChange={(e) => setAutoMarkupScale(Number(e.target.value))}
                    className="w-full h-1.5 accent-purple-600 cursor-pointer" />
                </div>

                {/* Auto-Markup Mode */}
                <div className="mt-2">
                  <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Markup Mode</span>
                  <div className="grid grid-cols-2 gap-1">
                    <button data-testid="markup-mode-both"
                      onClick={() => setAutoMarkupMode('both')}
                      className={`px-1 py-1 text-[9px] font-bold border-2 transition-colors ${
                        autoMarkupMode === 'both'
                          ? 'bg-purple-600 text-white border-purple-600'
                          : isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                      }`}>Markup + Highlight</button>
                    <button data-testid="markup-mode-highlight"
                      onClick={() => setAutoMarkupMode('highlight')}
                      className={`px-1 py-1 text-[9px] font-bold border-2 transition-colors ${
                        autoMarkupMode === 'highlight'
                          ? 'bg-green-600 text-white border-green-600'
                          : isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                      }`}>Highlight Only</button>
                    <button data-testid="markup-mode-border"
                      onClick={() => setAutoMarkupMode('border')}
                      className={`px-1 py-1 text-[9px] font-bold border-2 transition-colors ${
                        autoMarkupMode === 'border'
                          ? 'bg-cyan-600 text-white border-cyan-600'
                          : isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                      }`}>Border Highlight</button>
                    <button data-testid="markup-mode-border-markup"
                      onClick={() => setAutoMarkupMode('border-markup')}
                      className={`px-1 py-1 text-[9px] font-bold border-2 transition-colors ${
                        autoMarkupMode === 'border-markup'
                          ? 'bg-amber-500 text-black border-amber-500'
                          : isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                      }`}>Border + Markup</button>
                    <button data-testid="markup-mode-off"
                      onClick={() => setAutoMarkupMode('off')}
                      title="Off — the caller still tracks called numbers, but boards show no highlights, marks, or borders."
                      className={`col-span-2 px-1 py-1 text-[9px] font-bold border-2 transition-colors ${
                        autoMarkupMode === 'off'
                          ? 'bg-neutral-700 text-white border-neutral-700'
                          : isDarkMode ? 'border-neutral-600 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 hover:bg-gray-100'
                      }`}>Off</button>
                  </div>
                </div>

                {/* Timed Sequential Markup */}
                {(autoMarkupMode === 'both' || autoMarkupMode === 'border' || autoMarkupMode === 'border-markup') && (
                <div className="mt-2">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input data-testid="timed-markup-toggle" type="checkbox" checked={autoMarkupTimed}
                      onChange={(e) => setAutoMarkupTimed(e.target.checked)}
                      className="w-3 h-3 accent-amber-500" />
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Timed Board-by-Board Markup</span>
                  </label>
                  {autoMarkupTimed && (
                    <div className="mt-1">
                      <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                        Delay: {(autoMarkupDelay / 1000).toFixed(1)}s per board
                      </span>
                      <input data-testid="timed-markup-delay" type="range" min={200} max={5000} step={100} value={autoMarkupDelay}
                        onChange={(e) => setAutoMarkupDelay(Number(e.target.value))}
                        className="w-full h-1.5 accent-amber-500 cursor-pointer" />
                    </div>
                  )}
                </div>
                )}

                {/* 3. Draw Settings */}
                <div className={`pt-2 mt-1 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Pencil className={`w-3.5 h-3.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    <h3 className={`text-xs font-black ${isDarkMode ? 'text-white' : 'text-black'}`}>Draw Settings</h3>
                  </div>
                  <div className="mb-2">
                    <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Color</span>
                    <div className="flex flex-wrap gap-1">
                      {['#000000','#FF0000','#0000FF','#00AA00','#FF6600','#9900CC','#FF1493','#FFD700'].map(c => (
                        <button key={c} data-testid={`draw-color-${c}`}
                          onClick={() => setMarkupColor(c)}
                          className={`w-5 h-5 rounded-full border-2 transition-transform ${markupColor === c ? 'scale-125 border-white shadow-md' : isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <input data-testid="draw-color-custom" type="color" value={markupColor} onChange={(e) => setMarkupColor(e.target.value)}
                        className="w-5 h-5 border-0 p-0 cursor-pointer rounded" />
                      <span className={`text-[9px] font-mono font-bold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>{markupColor.toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="mb-2">
                    <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Draw: {markupThickness}px</span>
                    <input data-testid="draw-thickness-range" type="range" min={1} max={50} step={1} value={markupThickness}
                      onChange={(e) => setMarkupThickness(Number(e.target.value))}
                      className="w-full h-1.5 accent-purple-600 cursor-pointer" />
                  </div>
                  <div className="mb-2">
                    <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Opacity: {markupOpacity}%</span>
                    <input data-testid="draw-opacity-range" type="range" min={10} max={100} step={5} value={markupOpacity}
                      onChange={(e) => setMarkupOpacity(Number(e.target.value))}
                      className="w-full h-1.5 accent-purple-600 cursor-pointer" />
                  </div>
                  <div className="mb-1">
                    <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Eraser: {eraserThickness}px</span>
                    <input data-testid="eraser-thickness-range" type="range" min={3} max={40} step={1} value={eraserThickness}
                      onChange={(e) => setEraserThickness(Number(e.target.value))}
                      className="w-full h-1.5 accent-rose-500 cursor-pointer" />
                  </div>
                </div>

                {/* 4. Board Highlight */}
                <div className={`pt-2 mt-1 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none mb-1">
                    <input data-testid="toggle-board-highlight" type="checkbox" checked={highlightEnabled} onChange={(e) => setHighlightEnabled(e.target.checked)}
                      className="w-3 h-3 accent-green-500" />
                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Board Highlight</span>
                  </label>
                  {highlightEnabled && (
                    <>
                      <div className="flex flex-wrap gap-1">
                        {['#00FF00','#FF0000','#00BFFF','#FFD700','#FF00FF','#FF6600','#00FFFF'].map(c => (
                          <button key={c} data-testid={`highlight-color-${c}`}
                            onClick={() => setHighlightColor(c)}
                            className={`w-5 h-5 rounded-full border-2 transition-transform ${highlightColor === c ? 'scale-125 border-white shadow-md' : isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <input data-testid="highlight-color-custom" type="color" value={highlightColor} onChange={(e) => setHighlightColor(e.target.value)}
                          className="w-5 h-5 border-0 p-0 cursor-pointer rounded" />
                        <span className={`text-[9px] font-mono font-bold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>{highlightColor.toUpperCase()}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* 5. Almost-Winner Star */}
                <div className={`pt-2 mt-1 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                  <span className={`text-[10px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Almost-Winner Star</span>
                  <div className="flex flex-wrap gap-1">
                    {['#FFD700','#FF0000','#00FF00','#00BFFF','#FF00FF','#FF6600','#FF1493'].map(c => (
                      <button key={c} data-testid={`star-color-${c}`}
                        onClick={() => setStarColor(c)}
                        className={`w-5 h-5 rounded-full border-2 transition-transform ${starColor === c ? 'scale-125 border-white shadow-md' : isDarkMode ? 'border-neutral-700' : 'border-gray-300'}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <input data-testid="star-color-custom" type="color" value={starColor} onChange={(e) => setStarColor(e.target.value)}
                      className="w-5 h-5 border-0 p-0 cursor-pointer rounded" />
                    <span className={`text-[9px] font-mono font-bold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>{starColor.toUpperCase()}</span>
                  </div>
                  <div className="mt-1.5">
                    <span className={`text-[9px] font-bold ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>Opacity: {starOpacity}%</span>
                    <input data-testid="star-opacity-slider" type="range" min="10" max="100" value={starOpacity}
                      onChange={(e) => setStarOpacity(Number(e.target.value))}
                      className="w-full h-1 mt-0.5 accent-amber-400" />
                  </div>
                  {/* Star Style — how the almost-winner marker renders on the badge:
                      Filled (star sits ON the badge), Circle (ring surrounds it), or
                      Star (star outline surrounds it). Same color + opacity applied. */}
                  <div className="mt-1.5">
                    <span className={`text-[9px] font-bold block mb-1 ${isDarkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>Style</span>
                    <div className="grid grid-cols-3 gap-1">
                      {[
                        { id: 'filled', label: 'Filled' },
                        { id: 'circle', label: 'Circle' },
                        { id: 'star', label: 'Star Ring' },
                      ].map(opt => (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`star-style-${opt.id}`}
                          onClick={() => setStarStyle(opt.id)}
                          className={`py-1 text-[10px] font-bold border-2 transition-colors ${
                            (starStyle || 'filled') === opt.id
                              ? 'bg-amber-500 text-black border-amber-600'
                              : isDarkMode ? 'border-neutral-600 text-neutral-300 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                        >{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  {gameMode === 'loteria' && (
                    <div className="mt-2 space-y-1">
                      <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input data-testid="toggle-star-board-num" type="checkbox" checked={loteriaStarBoard} onChange={(e) => setLoteriaStarBoard(e.target.checked)}
                          className="w-3 h-3 accent-amber-500" />
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Board Number (Top)</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input data-testid="toggle-star-row-col-num" type="checkbox" checked={loteriaStarRowCol} onChange={(e) => setLoteriaStarRowCol(e.target.checked)}
                          className="w-3 h-3 accent-amber-500" />
                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Row/Column Numbers</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* 6. Winner Celebrations */}
                <div className={`pt-2 mt-1 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                  <span className={`text-[10px] font-bold block mb-1.5 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Winner Celebrations</span>
                  <div className="space-y-1">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input data-testid="toggle-winner-board" type="checkbox" checked={winnerBoardEnabled} onChange={(e) => setWinnerBoardEnabled(e.target.checked)}
                        className="w-3 h-3 accent-green-500" />
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Full Board Winner</span>
                    </label>
                    {gameMode === 'loteria' && (
                      <>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input data-testid="toggle-winner-row" type="checkbox" checked={winnerRowEnabled} onChange={(e) => setWinnerRowEnabled(e.target.checked)}
                            className="w-3 h-3 accent-green-500" />
                          <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Row Winner</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input data-testid="toggle-winner-col" type="checkbox" checked={winnerColEnabled} onChange={(e) => setWinnerColEnabled(e.target.checked)}
                            className="w-3 h-3 accent-green-500" />
                          <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Column Winner</span>
                        </label>

                        {/* Pattern Winners — X, T, Square (border), 4 Corners.
                            Collapsible so it doesn't clutter the panel. Each
                            pattern fires its OWN celebration; Full-Board still
                            fires afterward so the game keeps going. */}
                        <div className={`mt-1 pt-1 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                          <button
                            type="button"
                            data-testid="pattern-winners-collapse-toggle"
                            onClick={() => setPatternWinCollapsed(v => !v)}
                            className={`w-full flex items-center justify-between text-[10px] font-bold py-1 ${isDarkMode ? 'text-fuchsia-300 hover:text-fuchsia-200' : 'text-fuchsia-700 hover:text-fuchsia-900'}`}
                          >
                            <span>Pattern Winners{Object.values(patternWinModes).filter(Boolean).length > 0 ? ` (${Object.values(patternWinModes).filter(Boolean).length})` : ''}</span>
                            <span className="text-[9px] opacity-80">{patternWinCollapsed ? '▸ Show' : '▾ Hide'}</span>
                          </button>
                          {!patternWinCollapsed && (
                            <div className="mt-1 space-y-1 pl-1">
                              {[
                                { key: 'fourCorners', label: '4 Corners' },
                                { key: 'l', label: 'L (left column + bottom row)' },
                                { key: 'square', label: 'Square (border)' },
                                { key: 't', label: 'T (top row + center column)' },
                                { key: 'u', label: 'U (left + bottom + right)' },
                                { key: 'x', label: 'X (both diagonals)' },
                              ].map(({ key, label }) => (
                                <div key={key} className="flex items-center gap-1.5">
                                  <label className="flex items-center gap-1.5 cursor-pointer select-none flex-1">
                                    <input
                                      data-testid={`toggle-pattern-${key}`}
                                      type="checkbox"
                                      checked={!!patternWinModes[key]}
                                      onChange={(e) => {
                                        setPatternWinModes(prev => ({ ...prev, [key]: e.target.checked }));
                                        if (!e.target.checked) {
                                          const next = new Set();
                                          shownPatternWinnersRef.current.forEach(k => {
                                            if (!k.endsWith(`-${key}`)) next.add(k);
                                          });
                                          shownPatternWinnersRef.current = next;
                                        }
                                      }}
                                      className="w-3 h-3 accent-fuchsia-500"
                                    />
                                    <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>{label}</span>
                                  </label>
                                  <input
                                    data-testid={`color-pattern-${key}`}
                                    type="color"
                                    value={patternWinColors[key] || '#FFD700'}
                                    onChange={(e) => setPatternWinColors(prev => ({ ...prev, [key]: e.target.value }))}
                                    title={`Recolor marks when the ${label} pattern completes`}
                                    className="w-6 h-5 rounded cursor-pointer border border-neutral-500 shrink-0"
                                  />
                                </div>
                              ))}
                              <p className={`text-[9px] mt-1 leading-snug ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                When a pattern completes, only the marks on that shape are repainted with its color — the rest stay in your original markup color.
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    <label className="flex items-center gap-1.5 cursor-pointer select-none mt-1">
                      <input data-testid="toggle-continue-announcing" type="checkbox" checked={continueAnnouncing} onChange={(e) => {
                        setContinueAnnouncing(e.target.checked);
                        if (e.target.checked) {
                          winnerShownOnceRef.current = false;
                        }
                      }}
                        className="w-3 h-3 accent-amber-500" />
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Continue Announcing After Dismiss</span>
                    </label>
                  </div>

                  {/* Isolate Winners */}
                  <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-[10px] font-bold block mb-1.5 ${isDarkMode ? 'text-neutral-300' : 'text-gray-600'}`}>Isolate Winner Boards</span>
                    <div className="flex gap-1">
                      <input
                        data-testid="isolate-winner-input"
                        type="text"
                        placeholder="Board #s (e.g. 3, 7, 12)"
                        value={isolateInput}
                        onChange={(e) => setIsolateInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const nums = isolateInput.split(/[,\s]+/).map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
                            if (nums.length > 0) {
                              setIsolatedBoardNums(new Set(nums));
                            }
                          }
                        }}
                        className={`flex-1 px-2 py-1 rounded text-[10px] ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-black placeholder-gray-400'} border focus:outline-none focus:border-amber-500`}
                      />
                      <button
                        data-testid="isolate-winner-apply"
                        onClick={() => {
                          const nums = isolateInput.split(/[,\s]+/).map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
                          if (nums.length > 0) setIsolatedBoardNums(new Set(nums));
                        }}
                        className="px-2 py-1 bg-amber-500 text-black text-[9px] font-black rounded hover:bg-amber-400 transition-colors"
                      >Show</button>
                    </div>
                    {isolatedBoardNums && (
                      <div className="mt-1 flex items-center justify-between">
                        <span className={`text-[9px] ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          Showing: #{[...isolatedBoardNums].join(', #')}
                        </span>
                        <button
                          data-testid="isolate-winner-clear"
                          onClick={() => { setIsolatedBoardNums(null); setIsolateInput(''); setIsolateMarkupOnly(false); }}
                          className="text-[9px] text-red-500 hover:text-red-400 font-bold"
                        >Show All</button>
                      </div>
                    )}
                    {isolatedBoardNums && (
                      <label className="flex items-center gap-1.5 cursor-pointer select-none mt-1.5">
                        <input
                          data-testid="isolate-markup-only"
                          type="checkbox"
                          checked={isolateMarkupOnly}
                          onChange={(e) => setIsolateMarkupOnly(e.target.checked)}
                          className="w-3 h-3 accent-amber-500"
                        />
                        <span className={`text-[9px] font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Only mark isolated boards</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* 7. Called Numbers */}
                {calledNumbers.length > 0 && (
                  <div className={`pt-2 mt-1 border-t ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-300' : ''}`}>
                        Called ({calledNumbers.length})
                      </span>
                      <button
                        data-testid="shortcut-clear-called"
                        onClick={clearCalledNumbers}
                        className="text-red-500 hover:text-red-700 text-[10px] font-bold"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-0.5">
                      {calledNumbers.map((num, i) => (
                        <span
                          key={i}
                          data-testid={`shortcut-called-pill-${i}`}
                          className={`inline-flex items-center gap-0.5 pl-1 pr-0.5 py-0.5 text-[9px] font-bold border ${
                            isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white' : 'border-gray-300 bg-gray-50'
                          }`}
                        >
                          <span>{num}</span>
                          <button
                            type="button"
                            data-testid={`shortcut-remove-called-${i}`}
                            onClick={() => removeCalledNumber(i)}
                            title={`Remove ${num}`}
                            className={`w-3 h-3 inline-flex items-center justify-center rounded-sm leading-none text-[10px] font-black transition-colors ${
                              isDarkMode ? 'text-red-400 hover:bg-red-900/40' : 'text-red-600 hover:bg-red-100'
                            }`}
                            aria-label={`Remove ${num}`}
                          >×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : !isFullscreen ? (
            <button
              data-testid="show-auto-markup-panel"
              onClick={() => setAutoMarkupPanelOpen(true)}
              className={`p-2 border-2 transition-colors self-start mt-2 mr-2 ${isDarkMode ? 'border-neutral-700 bg-neutral-900 text-purple-400 hover:bg-neutral-800' : 'border-black bg-white text-purple-600 hover:bg-purple-50'}`}
              title="Show Auto-Markup"
            >
              <PanelRight className="w-4 h-4" />
            </button>
          ) : null}
        </div>

        {/* Winner overlays removed from here - moved to top level */}
      </div>
    </div>

    {/* BINGO / Board Winner Celebration Overlay - top level so it shows on caller page too */}
    {bingoWinner && bingoWinner.length > 0 && (
      <div data-testid="bingo-winner-overlay" className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => {
        const usedUp = winnerCelebrationsShownRef.current >= 1;
        if (!continueAnnouncing && usedUp) winnerShownOnceRef.current = true;
        setBingoWinner(null);
        if (!continueAnnouncing && usedUp) setAlmostWinnerBoards(new Set(lockedAlmostBoardsRef.current));
      }}>
        <div className="text-center animate-bounce-slow" onClick={(e) => e.stopPropagation()}>
          <div className="text-6xl mb-4">🎆🎆🎆</div>
          <div className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-black px-10 py-6 rounded-2xl shadow-2xl border-4 border-yellow-500">
            <h2 className="text-3xl font-black tracking-tight mb-1">CONGRATULATIONS</h2>
            <h1 className="text-5xl font-black tracking-tight">
              {bingoWinner.map(w => `#${w.boardNum}`).join(', ')} YOU WON!!!
            </h1>
          </div>
          <div className="text-6xl mt-4">🎆🎆🎆</div>
          <button data-testid="dismiss-winner" onClick={() => {
            const usedUp = winnerCelebrationsShownRef.current >= 1;
            if (!continueAnnouncing && usedUp) winnerShownOnceRef.current = true;
            setBingoWinner(null);
            if (!continueAnnouncing && usedUp) setAlmostWinnerBoards(new Set(lockedAlmostBoardsRef.current));
          }}
            className="mt-6 px-6 py-2 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors text-sm">
            Dismiss
          </button>
        </div>
      </div>
    )}

    {/* Loteria Row/Column Winner Celebration Overlay - top level so it shows on caller page too.
        Renders ALL row + column winners detected in the same batch on one card. */}
    {Array.isArray(loteriaLineWinner) && loteriaLineWinner.length > 0 && (
      <div data-testid="loteria-line-winner-overlay" className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setLoteriaLineWinner(null)}>
        <div className="text-center animate-bounce-slow" onClick={(e) => e.stopPropagation()}>
          <div className="text-6xl mb-4">🎆🎆🎆</div>
          <div className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-black px-10 py-6 rounded-2xl shadow-2xl border-4 border-yellow-500">
            <h2 className="text-3xl font-black tracking-tight mb-1">CONGRATULATIONS</h2>
            <h1 className="text-5xl font-black tracking-tight">
              {(() => {
                const rows = loteriaLineWinner.filter(w => w.type === 'row').map(w => `#${w.lineNum}`);
                const cols = loteriaLineWinner.filter(w => w.type === 'col').map(w => `#${w.lineNum}`);
                const parts = [];
                if (rows.length) parts.push(`${rows.join(', ')} ${rows.length > 1 ? 'ROWS' : 'ROW'}`);
                if (cols.length) parts.push(`${cols.join(', ')} ${cols.length > 1 ? 'COLUMNS' : 'COLUMN'}`);
                return `${parts.join(' & ')} WON!!!`;
              })()}
            </h1>
          </div>
          <div className="text-6xl mt-4">🎆🎆🎆</div>
          <button data-testid="dismiss-line-winner" onClick={() => setLoteriaLineWinner(null)}
            className="mt-6 px-6 py-2 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors text-sm">
            Dismiss
          </button>
        </div>
      </div>
    )}
    {/* Pattern Winner Overlay (X, T, Square, 4 Corners) — fires independently
        from the Full-Board / Row / Column celebrations so the game can
        continue and still trigger a First-to-Fill celebration later. */}
    {Array.isArray(patternWinner) && patternWinner.length > 0 && (
      <div data-testid="pattern-winner-overlay" className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { patternWinnerDismissedRef.current = true; setPatternWinner(null); }}>
        <div className="text-center animate-bounce-slow" onClick={(e) => e.stopPropagation()}>
          <div className="text-6xl mb-4">✨✨✨</div>
          <div className="bg-gradient-to-r from-fuchsia-500 via-purple-400 to-fuchsia-500 text-white px-10 py-6 rounded-2xl shadow-2xl border-4 border-fuchsia-300">
            <h2 className="text-2xl font-black tracking-tight mb-1">CONGRATULATIONS</h2>
            <h1 className="text-5xl font-black tracking-tight uppercase">
              {(() => {
                const articles = { fourCorners: '4 CORNERS', l: 'AN L', square: 'A SQUARE', t: 'A T', u: 'A U', x: 'AN X' };
                // Group by boardNum so a board that completes X + 4 Corners in
                // the same call reads "#3, YOU MADE AN X & 4 CORNERS!!!"
                const byBoard = {};
                patternWinner.forEach(w => {
                  (byBoard[w.boardNum] = byBoard[w.boardNum] || []).push(articles[w.pattern] || w.pattern.toUpperCase());
                });
                return Object.entries(byBoard).map(([bn, arts]) =>
                  `#${bn}, YOU MADE ${arts.join(' & ')}!!!`
                ).join('   •   ');
              })()}
            </h1>
            <div className="text-lg font-bold mt-3 opacity-90">Game continues — the first to fill the board still wins!</div>
          </div>
          <div className="text-6xl mt-4">✨✨✨</div>
          <button data-testid="dismiss-pattern-winner" onClick={() => { patternWinnerDismissedRef.current = true; setPatternWinner(null); }}
            className="mt-6 px-6 py-2 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors text-sm">
            Dismiss
          </button>
        </div>
      </div>
    )}

    {/* ── Owner-only Subscribers Panel ─────────────────────────────────
        Compact modal listing every non-owner user with subscription
        status, when they signed up, and when their next payment is due
        (subscription_started + 30 days for active subs). Trial users show
        their trial_end. Sortable to help hosts see who's about to lapse.
    */}
    {showSubscribers && (
      <div
        data-testid="subscribers-modal"
        className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
        onClick={() => setShowSubscribers(false)}
      >
        <div
          className={`w-full max-w-3xl max-h-[85vh] flex flex-col rounded-lg border-2 ${isDarkMode ? 'bg-neutral-950 border-neutral-700' : 'bg-white border-black'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-4 py-2.5 border-b-2 ${isDarkMode ? 'border-neutral-800' : 'border-black'}`}>
            <div className="flex items-center gap-2">
              <Users className={`w-4 h-4 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`} />
              <h2 className={`font-black text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Subscribers {subscribersData ? `(${subscribersData.total})` : ''}</h2>
            </div>
            <button
              data-testid="subscribers-close"
              onClick={() => setShowSubscribers(false)}
              className={`px-2 py-1 text-xs font-bold border-2 ${isDarkMode ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800' : 'border-black text-black hover:bg-gray-100'}`}
            >Close ✕</button>
          </div>
          <div className="flex-1 overflow-auto p-3">
            {isLoadingSubscribers ? (
              <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Loading subscribers…</div>
            ) : !subscribersData ? (
              <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>No data.</div>
            ) : subscribersData.subscribers.length === 0 ? (
              <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-neutral-400' : 'text-gray-500'}`}>No subscribers yet.</div>
            ) : (
              <>
              {subscribersData.summary && (
                <div className="mb-3" data-testid="subscribers-summary">
                  <div className={`grid grid-cols-4 gap-1.5 mb-2 text-center`}>
                    {[
                      { key: 'paying', label: 'Paying', val: subscribersData.summary.paying, cls: isDarkMode ? 'text-green-300' : 'text-green-700' },
                      { key: 'free', label: 'Free', val: (subscribersData.summary.free_comp || 0) + (subscribersData.summary.free_legacy_grace || 0) + (subscribersData.summary.free_stale_comp || 0), cls: isDarkMode ? 'text-amber-300' : 'text-amber-700' },
                      { key: 'blocked', label: 'Blocked', val: subscribersData.summary.blocked, cls: isDarkMode ? 'text-rose-300' : 'text-rose-700' },
                      { key: 'all', label: 'Total', val: subscribersData.total, cls: isDarkMode ? 'text-neutral-300' : 'text-gray-700' },
                    ].map(c => (
                      <div key={c.key} className={`rounded px-2 py-1 border ${isDarkMode ? 'border-neutral-800 bg-neutral-900/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className={`text-sm font-black ${c.cls}`}>{c.val}</div>
                        <div className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{c.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'free', label: 'Free users' },
                      { id: 'paying', label: 'Paying' },
                      { id: 'blocked', label: 'Blocked' },
                    ].map(f => (
                      <button key={f.id}
                        data-testid={`subscribers-filter-${f.id}`}
                        onClick={() => setSubscriberFilter(f.id)}
                        className={`px-2 py-0.5 text-[10px] font-bold rounded border transition-colors ${
                          subscriberFilter === f.id
                            ? (isDarkMode ? 'bg-amber-600 border-amber-500 text-white' : 'bg-amber-500 border-amber-600 text-white')
                            : (isDarkMode ? 'border-neutral-700 text-neutral-400 hover:bg-neutral-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100')
                        }`}>{f.label}</button>
                    ))}
                  </div>
                </div>
              )}
              <table className="w-full text-[11px] table-auto">
                <thead>
                  <tr className={`border-b-2 ${isDarkMode ? 'border-neutral-800 text-neutral-400' : 'border-gray-300 text-gray-600'}`}>
                    <th className="text-left px-2 py-1.5 font-bold">User</th>
                    <th className="text-left px-2 py-1.5 font-bold">Status</th>
                    <th className="text-left px-2 py-1.5 font-bold">Started</th>
                    <th className="text-left px-2 py-1.5 font-bold">Next Payment</th>
                    <th className="text-left px-2 py-1.5 font-bold">Trial Ends</th>
                    <th className="text-right px-2 py-1.5 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribersData.subscribers.filter((s) => {
                    if (subscriberFilter === 'free') return ['free_comp', 'free_legacy_grace', 'free_stale_comp'].includes(s.access_category);
                    if (subscriberFilter === 'paying') return s.access_category === 'paying';
                    if (subscriberFilter === 'blocked') return s.access_category === 'blocked';
                    return true;
                  }).map((s) => {
                    const statusColor = s.status === 'active'
                      ? (isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700')
                      : s.status === 'trial'
                        ? (isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700')
                        : s.status === 'cancelled'
                          ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700')
                          : (isDarkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-600');
                    const fmt = (iso) => {
                      if (!iso) return '—';
                      try {
                        const d = new Date(iso);
                        if (isNaN(d.getTime())) return '—';
                        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                      } catch { return '—'; }
                    };
                    const canRevoke = s.status === 'active' || s.status === 'past_due' || s.status === 'trial' || s.is_legacy_grace;
                    return (
                      <tr key={s.id} className={`border-b ${isDarkMode ? 'border-neutral-800/60 hover:bg-neutral-900/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <td className={`px-2 py-1.5 ${isDarkMode ? 'text-neutral-200' : 'text-black'}`}>
                          <div className="font-bold truncate">{s.username || s.email?.split('@')[0]}</div>
                          <div className={`text-[9px] ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'} truncate`}>{s.email}</div>
                          {['free_comp', 'free_legacy_grace', 'free_stale_comp'].includes(s.access_category) && (
                            <span data-testid={`access-tag-${s.email}`} className={`inline-block mt-0.5 px-1 py-0.5 text-[8px] font-bold rounded ${isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                              {s.access_category === 'free_comp' ? 'FREE · COMP' : s.access_category === 'free_legacy_grace' ? 'FREE · GRACE' : 'FREE · OLD COMP'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${statusColor}`}>{s.status.toUpperCase()}</span>
                        </td>
                        <td className={`px-2 py-1.5 ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>{fmt(s.subscription_started)}</td>
                        <td className={`px-2 py-1.5 font-bold ${s.next_billing ? (isDarkMode ? 'text-amber-300' : 'text-amber-700') : (isDarkMode ? 'text-neutral-600' : 'text-gray-400')}`}>{fmt(s.next_billing)}</td>
                        <td className={`px-2 py-1.5 ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>{fmt(s.trial_end)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid={`set-password-${s.email}`}
                              onClick={() => setSubscriberPassword(s.email)}
                              className={`px-2 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-blue-800 text-blue-300 hover:bg-blue-900/40' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                              title="Set a new password for this user (they can change it after logging in)"
                            >
                              Set Pass
                            </button>
                            {canRevoke ? (
                              <button
                                data-testid={`revoke-subscriber-${s.email}`}
                                onClick={() => revokeSubscriber(s.email)}
                                className={`px-2 py-0.5 text-[10px] font-bold rounded border ${isDarkMode ? 'border-rose-800 text-rose-300 hover:bg-rose-900/40' : 'border-rose-300 text-rose-700 hover:bg-rose-50'}`}
                                title="Cancel Stripe subscription and block access until they subscribe again"
                              >
                                Revoke
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
